// Interfaz V2 — pantalla de exploración del nuevo flujo de generación.
//
// Esta pantalla es un prototipo visual con data mockeada (ver `mockData.ts`).
// En el sidebar se muestra para la experiencia V2 (ver lib/userGroups):
// `company_profiles.grupo_testing = 'B'` (etiquetada "Estudio virtual") y dev
// testers (etiquetada "Interfaz V2").
//
// Flujo de usuario:
//   1. Browsing — saludo personalizado + barra de prompt + grilla de
//      colecciones de presets (cada una scrolleable horizontal con
//      "Ver más" para abrir la colección completa).
//   2. Preset detail — al tocar un preset se abre un sheet de pantalla
//      completa con la imagen del preset en grande y un container que
//      pide subir/elegir un producto.
//   3. Configure — después de elegir producto, ese container muta para
//      mostrar opciones de Pose, Formato y un campo libre, más un
//      botón final de Generar.
//
// La pantalla NO genera nada todavía: el botón Generar solo cierra el
// sheet y vuelve a browsing. Cuando se valide la dirección, se cablea
// con el agente / webhook de generación real.
//
// Animaciones:
//   • Prompt bar: focus expande el container a un panel de prompt con
//     textarea + "sorprendeme" + Generar. Salida con fade-out cuando se
//     hace blur sin escribir nada.
//   • Preset detail: sheet con backdrop fade + slide-up de 300ms.
//   • Configure swap: el contenido del config panel (subir producto vs.
//     configurar generación) usa `key` + `animate-mode-swap` para
//     transicionar suave entre estados.

import { formatoEstandarDe } from '../../utils/formatoPersonalizado';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEsEscritorio } from '../../chat-atlas/ui/useEsEscritorio';
// Prompt libre y Creador de video abren Crear en escritorio
// (ADR-2026-10-07-prompt-libre-y-creador-abren-crear).
import { destinoEnCrear } from './modes/redes/entradaACrear';
import { leerVolverA } from '../../utils/volverA';
import { useHomeBanners } from '../../hooks/useHomeBanners';
import { HomeBannersView } from '../../components/HomeBanners';
import { CreditsTopBar } from '../../components/CreditsTopBar';
import type { MotivoPricing } from '../../components/pricing/pricingContexts';
import { PRODUCTS_CACHE_KEY } from './productsCache';
import { estadoDeCreditos } from '../../lib/creditsStatus';
import { SeccionesHome, useCuentaShell } from '../estilos/EstiloMinimal';
// La home de desktop. Vive en su propio archivo desde
// ADR-2026-09-01-la-version-2-es-la-home, cuando la variante 2 del sandbox pasó a ser
// LA home y las otras dos se borraron.
import { HomeMinimal } from '../estilos/HomeMinimal';
import { useRotacionPlantillas } from '../../hooks/useRotacionPlantillas';

/**
 * Descarte de la barra superior de "sin créditos"
 * (ADR-2026-07-30-barra-superior-creditos-home-ancho), corregido por
 * ADR-2026-08-04-el-cartel-de-creditos-vuelve-al-recargar.
 *
 * Vive en el MÓDULO y no en `sessionStorage`. La diferencia se ve al recargar: con
 * `sessionStorage` el cartel no volvía en toda la pestaña —una X y el aviso se perdía
 * para siempre, aunque el saldo siguiera en cero—, mientras que en desktop el shell lo
 * descarta sólo en memoria y vuelve en cada recarga. Era la misma decisión resuelta de
 * dos maneras según el dispositivo.
 *
 * Tampoco puede ser `useState` a secas: en mobile esta pantalla se desmonta al navegar,
 * así que el cartel volvería en cada vuelta al inicio. En el módulo sobrevive a la
 * navegación —como el estado del shell en desktop— y se va con la recarga.
 */
let topBarDescartadaEnEstaCarga = false;
import { supabase } from '../../lib/supabase';
import { getAppScrollY, setAppScrollY } from '../../utils/appScroll';
import { useSidebar } from '../../context/SidebarContext';
import {
  GENERATE_AUTH_TOKEN,
  GENERATE_AD_V3_URL,
  GENERATE_VIDEO_INFLUENCER_URL,
  USE_ATLAS_BACKEND,
} from '../../config';
import {
  postGenerateAd,
  GenerateAdError,
  shouldRouteToAtlas,
  mapErrorTypeToMessage,
  type GenerateAdErrorType,
} from '../../lib/generateAdRequest';
import { ReferenciaIrrecuperable } from './referenciasDeGeneracion';
import {
  fetchSuggestedVideoPrompt,
  generateVideoFromImage,
  generateVideoFromTemplate,
} from '../../lib/videoFromImage';
import { atlasBackendFetch } from '../../lib/atlasBackend';
import LoadingAnimation from '../../components/LoadingAnimation';
import { DURACION_BARRA_IMAGEN_MS } from '../../components/loading/useGenerationProgress';
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from '../../utils/preloadGeneratedImages';
import { preloadVideosDeTanda } from '../../utils/preloadVideosDeTanda';
import type { FiltroContenido } from '../../utils/filtroContenido';
import { marcaParaFiltrar } from '../../utils/filtroDeMarca';
import { postGenerateAngles } from '../../lib/generateAngles';
import type { GeneracionDelegada } from './components/generacionDelegada';
import { useAnalisisAutomatico, useVerificacionCalidad } from '../../hooks/useFuncionesAvanzadas';
import {
  type CollectionV2,
  type MockProductV2,
  type PresetV2,
} from './mockData';
import { fetchTemplatesV2 } from './templatesApi';
import { fetchTemplatesBelleza } from './templatesBellezaApi';
import { fetchUserProducts } from './productsApi';
import { uploadNewProduct } from './productUploadApi';
import { recordPromptHistory } from './promptHistory';
import {
  ProductsContext,
  ProductsLoadedContext,
  ProductEditContext,
} from './productsContext';
import { ModelsContext } from './modelsContext';
import { AgentEnvContext } from './agentEnvContext';
import { SinProveedorDeImagenForzado } from './proveedorDeImagenContext';
import { fetchAllPoses, type PoseV2 } from './posesApi';
import {
  fetchMyCustomPoses,
  uploadCustomPose,
  deleteCustomPose,
} from './posesCustomApi';
import {
  type VistaCatalogo,
  buildVistasSoloProductoPresets,
  vistasSoloProductoParaConjunto,
  VISTA_PRESET_PREFIX,
} from './vistasCatalogo';
import {
  fetchBibliotecaFondos,
  fetchFondosGuardados,
  type FondoBiblioteca,
  type FondoGuardado,
} from './fondosApi';
import { PosePickerSheet } from './PosePicker';
import {
  FondoPickerSheet,
  type FondoSelection,
  type FondoPickerView,
} from './FondoPicker';
import { COLORES_FONDO_LISO } from './components/FondoProductoPicker';
import { backgroundDeFondo } from './fondoDelPayload';
import { useV2Chat } from './useV2Chat';
import { ChatPanelV2 } from './ChatPanelV2';
import SubscriptionLimitModal from '../../components/SubscriptionLimitModal';
import { TarjetaDeErrorDeGeneracion } from './components/TarjetaDeErrorDeGeneracion';
import { FeatureLockedModal } from '../../components/FeatureLockedModal';
import { VideoPlanRestrictionModal } from '../../components/VideoPlanRestrictionModal';
import {
  getAgenteLock,
  getModePlanLock,
  getPlanRequirementNote,
  isProOrUltra,
  type ModePlanLock,
} from '../../lib/planCapabilities';
import { getModeExamples, type ModeExample } from './modeExamples';
import { HERRAMIENTAS_EXTRA } from '../estilos/contenidoAtlas';
import { ModeExamplesModal } from './components/ModeExamplesModal';
import {
  ModelPickerSheet,
  type ModelSelection,
  type ModelPickerView,
} from './ModelPicker';
import { FormatPicker } from './FormatPicker';
import {
  formatoParaVideoPlantilla,
  SLUGS_VIDEO_PLANTILLA,
} from './formatosDeVideoPlantilla';
import { fetchUserModels, type ModelV2 } from './modelsApi';
import { AsistenteMarketingSheet } from './modes/AsistenteMode';
import { PromptLibreSheet } from './modes/promptLibre/PromptLibreSheet';
import { CreadorVideoSheet } from './modes/creadorVideo/CreadorVideoSheet';
import {
  VideoConfigSheet,
  VIDEO_INFLUENCER_DURATION_MIN,
  VIDEO_INFLUENCER_DURATION_MAX,
  VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO,
  VIDEO_INFLUENCER_DURATION_DEFAULT,
  VIDEO_DURATION_DEFAULT,
} from './components/VideoConfigSheet';
import { isSinGancho } from './ganchosCatalog';
import {
  rubroVePlantillas,
  rubroVePlantillasBelleza,
  esRubroBelleza,
  rubroVeCatalogoConAgente,
} from '../../lib/rubro';
import { rubroVisible } from '../../lib/onboardingPendiente';
import {
  MAX_MODELOS_SERIE,
  resolveDesignModeloCount,
  resolveTandaModelo,
} from './designModeloCount';
import { totalTandaDeLaCorrida } from './totalTandaDeLaCorrida';
import { sonarListo } from '../../utils/sonarListo';
import {
  RESOLUCION_DEFAULT,
  creditosPorResolucion,
  type ResolucionImagen,
} from '../../lib/resolucionImagen';
import { ajustarPosesPorCantidadDeModelos } from './posesCompatibilidad';
import { fetchInfluencerDialogo } from './dialogoApi';
import {
  MODES,
  VIDEO_INFLUENCER_ENABLED,
  anuncioTercero,
  MODE_KIND_TO_PATH,
  esCatalogoIA,
  esDesignModelo,
  esAsistente,
  usaBibliotecaDeFondos,
  nombreCortoModo,
  rutaDeModo,
  caratulaModo,
  kindDeModoParaRubro,
  kindsHomeGratis,
} from './modesCatalog';
// El modo Modelo tiene SU PROPIO sheet —el rediseño que salió del banco de pruebas y hoy
// es el modo (ADR-2026-09-02-modelo-v2-es-el-modo-modelo)—; el resto de los modos siguen
// con `PresetDetailSheet`.
import { PresetDetailSheetModelo } from './modelo/PresetDetailSheetModelo';
import { plantillaEligeCalidad } from './calidadDePlantilla';
// La clave de la "vuelta atrás" vive en su propio módulo: la escribe esta pantalla y la
// borra el "Listo" de los resultados (ADR-2026-09-04-listo-no-reabre-la-configuracion-del-modo).
import { aplicarRestoreV2, CLAVE_RESTORE_V2, seLimpianLasVistasDelModo, type RestoreV2 } from './restoreV2';
// En qué paso se abre un modo que YA tiene su configuración resuelta. Un solo lugar
// donde eso se decide (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
import { pasoDeConfiguracion, pasoSeguro, type PasoDelPanel } from './pasoDeModo';
import type { ConfiguracionDeRegeneracion } from './verificacion/configurarRegeneracion';
import { leerSaldo, marcaQuePaga } from '../../lib/saldo';
import { useRubroMarcaActiva } from '../../hooks/useRubroMarca';
import { palabraDeModelo } from '../../lib/nombreDeModelo';
import {
  esRubroHogarDeco,
  esRubroModa,
  homeAbreConElAgente,
  homeMuestraPresentacionDelAgente,
} from '../../lib/rubro';
import { medidasParaPayload, type Medidas } from '../../lib/medidas';
import { pedirNuevosAngulos } from '../../lib/carruselRequest';
import {
  VISTA_MUEBLE_CONTEXTO,
  VISTA_MUEBLE_INFOGRAFIA,
  VISTAS_MUEBLES,
  backgroundParaVistaMueble,
} from './vistasMuebles';
import {
  pollGeneratedByExecutionId,
  ANUNCIO_POLL_TIMEOUT_MS,
  ANUNCIO_STABLE_ROUNDS,
} from './pollGeneratedByExecutionId';
import { awaitImageGeneration } from './imageGenerationRecovery';
import {
  creditosComprometidos,
  repartirCreditosDeProduccion,
  generacionesStore,
  MAX_TANDAS_EN_CURSO,
  timeoutPollTanda,
} from './generacionesEnCurso';
import { useTandasPanel } from './useGeneracionesEnCurso';
import { recordarEmpresa } from './empresaDeLaSesion';
import { MultiSlotProductPickerSheet } from './components/MultiSlotProductPickerSheet';
import { AgregarVariosModal } from '../../components/AgregarVariosModal';
import {
  contarImagenesCatalogo,
  type CatalogoCard,
} from './components/CatalogoCardsFullscreen';
import { PresetDetailSheet } from './components/PresetDetailSheet';
import { useControladorDelModo } from './puente/useControladorDelModo';
import { PanelTandaCreaciones } from './components/PanelTandaCreaciones';
import {
  getVoiceGuionDefault,
  getGuionDurationSec,
  sanitizeEstiloVoz,
  saveLastUsedVoice,
  type VoiceGuionValue,
} from './components/VoiceGuionSection';
/* `HomeTemplatesSection` (chips + masonry) salió de la home con
   ADR-2026-07-31-inicio-se-funde-interfaz-v2: el diseño nuevo muestra las plantillas
   como una tarjeta por colección. El componente sigue existiendo y es el que sirve
   `/interfaz-v2/plantillas`, que es a donde lleva el "Ver todo". */
import AddToHomescreenPrompt from '../../components/AddToHomescreenPrompt';
import {
  HomeSkeleton,
  MobileModeHero,
} from './components/HomeCards';
import { useAnalisisAPedido } from './useAnalisisAPedido';
// El hero del Agente de la home: el mismo en escritorio (vía `HomeMinimal`) y en el
// teléfono — ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente.
import { HeroAgenteHome } from './components/HeroAgenteHome';
// La barra que presenta el Agente en la home del teléfono — la misma que `HomeMinimal`
// monta en escritorio (ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final).
import { PresentacionDelAgente } from './components/PresentacionDelAgente';
import { CardAcademyHome } from '../academia/CardAcademyHome';
import { EstelaDelAgente } from './components/EstelaDelAgente';
// El MISMO saludo de la home de escritorio, para el hero del teléfono
// (ADR-2026-09-21-el-saludo-tambien-va-en-el-telefono). El nombre sale de `shortName`
// (`profiles.name_short`), que esta pantalla ya trae: acá abajo no existe el contexto de
// cuenta del shell, que es de donde lo saca `HomeMinimal`.
import { SaludoDeLaHome } from './components/SaludoDeLaHome';
import { HerramientasMobile, HomeMobilePorDestino } from './components/HomeMobilePorDestino';
import { useShellMobile, useUiNueva } from './components/shellMobile';
/* La barra inferior (`NavInferiorMobile`) y el sheet de "Generar" ya no se importan
   acá: los monta el shell (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell). */
import { loadLastCrearAnuncioState } from '../../utils/crearAnuncioStorage';
import {
  normalizeBrandHexColors,
  resolveBrandFont,
} from './panels/AnuncioPanel';

/* `AR_TZ` + `getGreeting()` (la franja horaria → "Buenos días" / "Buenas tardes" /
   "Buenas noches") vivían acá y se fueron con ADR-2026-07-31-inicio-se-funde-interfaz-v2:
   el saludo lo arma `HomeMinimal`, que usa `saludo()` de `contenidoAtlas` — la misma
   lógica, con la misma zona horaria, ya compartida con el resto del shell. Eran dos
   copias del mismo cálculo. */

// Cuántos presets se muestran por colección. En desktop entran más
// thumbnails porque hay más espacio horizontal. Si una colección tiene
// menos o igual que el count del device, NO se muestra "Ver más"
// (renderizamos todos los presets y no tiene sentido el link).
const PRESETS_PREVIEW_COUNT_MOBILE = 6;
const PRESETS_PREVIEW_COUNT_DESKTOP = 10;

// Key del sessionStorage donde guardamos scroll position al navegar
// desde la home V2 a una sub-ruta (categoria, plantillas, etc.). Al
// volver, restauramos antes del paint para evitar el "salto" del top
// al punto guardado.
const HOME_SCROLL_KEY = 'atlas-v2-home-scroll';
// Cache de las collections fetcheadas — sobrevive a mounts/unmounts
// dentro de la sesión. Sin esto, al volver desde una categoría el
// home arranca con la grilla vacía (mientras el fetch corre) y el
// scroll restore no puede aplicarse porque el documento es muy corto.
const HOME_COLLECTIONS_CACHE_KEY = 'atlas-v2-home-collections-cache';
// Cache de poses para la fila virtual "E-commerce". Sin esto, al
// volver desde una sub-ruta, las plantillas de DB cargan desde el
// cache instantáneo pero E-commerce aparece con delay (porque
// `allPoses` es otro fetch async). Caché las dos para que toda la
// home se renderice de una.
const HOME_POSES_CACHE_KEY = 'atlas-v2-home-poses-cache';
// Cache de `isDesignRubro` — sino, aunque tengamos `allPoses`
// cacheadas, la condicional de la fila E-commerce evalúa false en el
// primer paint hasta que el fetch de user info resuelve.
const HOME_DESIGN_RUBRO_CACHE_KEY = 'atlas-v2-home-design-rubro';
// Cache del catálogo de productos del user. Sin esto, al abrir un modo el
// CTA "Elegir de mis productos" y sus thumbnails aparecían recién cuando
// resolvía `fetchUserProducts` (cadena de user-info + firmado de URLs). Con
// el cache, el primer paint ya muestra los productos; igual disparamos el
// fetch en cada mount para refrescar las signed URLs (TTL 1h), así si el
// cache quedó viejo las URLs se reemplazan a los pocos segundos.
// La llave vive en `productsCache` desde ADR-2026-08-04-la-marca-nueva-arranca-vacia:
// el alta de marca también la necesita para borrar el catálogo de la marca anterior.

// El cache de productos puede tener dos formatos en sessionStorage: el viejo
// (array plano de productos) o el nuevo envelope `{ brandId, products }`. El
// envelope guarda la marca a la que pertenece el catálogo cacheado: como ahora
// V2 filtra por marca activa, necesitamos saber de qué marca era el cache para
// no pintar productos de OTRA marca en el primer paint. Normaliza ambos.
const readProductsCache = (): {
  products: MockProductV2[];
  brandId: string | null;
} => {
  try {
    const raw = sessionStorage.getItem(PRODUCTS_CACHE_KEY);
    if (!raw) return { products: [], brandId: null };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { products: parsed as MockProductV2[], brandId: null };
    }
    if (parsed && Array.isArray(parsed.products)) {
      return {
        products: parsed.products as MockProductV2[],
        brandId: (parsed.brandId as string) ?? null,
      };
    }
  } catch {
    /* noop */
  }
  return { products: [], brandId: null };
};

// Mapeo bidireccional entre el slug de URL y el `kind` del preset sintético.
// Cada uno de estos 4 modos tiene su propia URL (`/interfaz-v2/modo/<slug>`)
// para que el browser back/forward funcione por modo y los deeplinks sean
// compartibles. Otros kinds (`template`, `design` de E-commerce) NO usan
// URL — abren el sheet vía state interno como antes.
const MODE_PATH_TO_KIND: Record<
  string,
  | 'design-auto'
  | 'design-modelo'
  | 'fotos-producto'
  | 'catalogo-ia'
  | 'catalogo-muebles'
  | 'infografia'
  | 'asistente'
  | 'asistente-v2'
  | 'prompt-libre'
  | 'creador-video'
  | 'anuncio'
  | 'video-influencer'
> = {
  auto: 'design-auto',
  modelo: 'design-modelo',
  'fotos-producto': 'fotos-producto',
  catalogo: 'catalogo-ia',
  'catalogo-muebles': 'catalogo-muebles',
  infografia: 'infografia',
  asistente: 'asistente',
  // Variante en prueba del asistente — misma pantalla, sin el switch Imagen/Video ni el
  // desplegable de Ayuda de IA (ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo).
  // El modo es `devOnly` en el catálogo, o sea que no aparece en ninguna lista; esta
  // entrada es la que hace que el deep-link `/modo/asistente-v2` abra algo.
  'asistente-v2': 'asistente-v2',
  'prompt-libre': 'prompt-libre',
  'creador-video': 'creador-video',
  anuncio: 'anuncio',
  'video-influencer': 'video-influencer',
};
/**
 * Los dos modos de catálogo (`catalogo-ia` y `catalogo-muebles`) comparten el
 * MISMO flujo: subida masiva de productos, paso intermedio de "elegir vistas" y
 * review de cards en vez del ConfigurePanel. Lo único que cambia es el catálogo
 * de vistas y, en muebles, la pantalla de medidas.
 *
 * Este helper existe para no repetir el `=== 'catalogo-ia' || === 'catalogo-muebles'`
 * en los ~6 puntos del archivo donde se decide flujo. Ver ADR-582.
 */
const esModoCatalogo = (kind?: string | null): boolean =>
  esCatalogoIA(kind) ||
  kind === 'catalogo-muebles' ||
  kind === 'infografia';


/* `MODE_KIND_TO_PATH` estaba acá, escrito a mano. Pasó a `modesCatalog`, derivado de
   `MODES` (ADR-2026-08-04-nombres-de-modo-iguales-en-mobile): era idéntico, y dos
   listas iguales sólo esperan a que alguien agregue un modo en una y no en la otra. */

/**
 * Nombre del paso para el último nivel del breadcrumb de las pantallas de modo
 * (ADR-2026-08-03-breadcrumbs-pantallas-de-modo).
 *
 * `pick-product` y `choose-product` comparten etiqueta a propósito: para el usuario
 * son el mismo momento —elegir con qué producto genera—, y la diferencia (venir de
 * la bienvenida del modo o de la grilla de productos) es de implementación.
 */
const ETIQUETA_PASO: Record<string, string> = {
  preview: 'Vista previa',
  'choose-product': 'Elegir producto',
  'pick-product': 'Elegir producto',
  'upload-product': 'Subir producto',
  'select-vistas': 'Elegir vistas',
  configure: 'Configurar',
  asistente: 'Asistente',
};

// Modos con pantalla PROPIA fuera de InterfazV2 (declaran `route` en el catálogo,
// ej. Animar imagen y Shorts Studio): la home navega ahí en vez de abrir un sheet.
// Se deriva de MODES para no mantener una tercera lista.
const MODE_KIND_TO_ROUTE: Record<string, string> = Object.fromEntries(
  MODES.filter((m) => m.route).map((m) => [
    m.preset.kind as string,
    m.route as string,
  ]),
);

// Pose default cuando el producto no tiene `modelView` (no se puede
// recomendar por área): preferimos esta pose de cuerpo completo de pie en
// vez de la primera por `created_at` ("Contra-picado dramático"). Match por
// nombre (lowercase) contra la tabla `poses`.
const DEFAULT_FULLBODY_POSE_NAME = 'editorial de pie';

// Modo "Video con influencer" (UGC): tope del poll por `execution_id` y cada
// cuánto se reintenta. Si el video no aparece en la DB dentro de este tope, se
// corta y se muestra error. SOLO aplica a este modo.
// Tope del poll: hubo generaciones reales de MÁS de 15 min con el backend
// todavía trabajando (el corte anterior tiraba error prematuro, ADR-543).
// 30 min da margen holgado; si el backend no escribió la fila para entonces,
// el mensaje aclara que puede seguir generándose y aparecer en el historial.
const VIDEO_INFLUENCER_POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 min
const VIDEO_INFLUENCER_POLL_INTERVAL_MS = 5000; // 5 s
// Duración VISUAL de la barra de la pantalla de carga: atada al tope del
// poll (un solo knob). Es solo estética: la barra capea en 97% y recién
// completa cuando el poll encuentra el video (`completeNow`).
const VIDEO_INFLUENCER_LOADING_BAR_MS = VIDEO_INFLUENCER_POLL_TIMEOUT_MS;

/**
 * Acá vivía `versionHome`, la prop que montaba las tres variantes de home del sandbox
 * bajo `/interfaz-v2-version1|2|3` (ADR-2026-08-31-home-tres-variantes-imagen-video).
 * La comparación terminó: la versión 2 es la home y las otras dos se borraron
 * (ADR-2026-09-01-la-version-2-es-la-home), así que esta pantalla vuelve a montar una
 * sola, `HomeMinimal`.
 */
export const InterfazV2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode: routeMode } = useParams<{ mode?: string }>();

  /**
   * Prompt libre y Creador de video SON Crear en escritorio
   * (ADR-2026-10-07-prompt-libre-y-creador-abren-crear): `/modo/prompt-libre` va a
   * `/redes` con Imagen y `/modo/creador-video` con Video. Un solo punto que cubre la
   * home de siempre, la sidebar vieja, `/modos`, el agente (`abrir_modo`) y los deep
   * links. En el teléfono no pasa nada: Redes es sólo de computadora y los sheets siguen.
   */
  const esEscritorio = useEsEscritorio();
  const destinoCrear = destinoEnCrear(routeMode, esEscritorio);
  useEffect(() => {
    if (!destinoCrear) return;
    navigate('/redes', { replace: true, state: { medio: destinoCrear.medio } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinoCrear?.medio, routeMode]);

  // ── Rutas internas — ADR-2026-07-31-inicio-se-funde-interfaz-v2 ──
  //
  // Acá había una traducción de rutas: esta pantalla servía `/interfaz-v2/modo/:mode`
  // bajo `DashboardLayout` y un clon `/modo/:mode` hospedado en el shell nuevo, y
  // cada navegación interna tenía que elegir el par correcto para no expulsar al
  // usuario del shell donde estaba (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell).
  //
  // Ya no hay dos familias: el shell nuevo se aplicó SOBRE estas mismas rutas
  // (ADR-2026-07-31-shell-nuevo-urls-viejas) y los clones quedaron como alias que
  // redirigen acá. Navegar a un alias sólo agregaría un salto, así que las rutas son
  // siempre las canónicas.
  //
  // `rutaHome` es `/interfaz-v2`: ESTA misma pantalla sin modo abierto, que es contra
  // la que está pensada la animación de cierre del sheet.
  const rutaHome = '/interfaz-v2';
  const rutaModo = (p: string) => `/interfaz-v2/modo/${p}`;
  /**
   * "Ver más" de una fila de plantillas — ADR-2026-08-03-ver-mas-de-una-fila-filtra-plantillas.
   *
   * Antes iba a `/interfaz-v2/coleccion/:id` (`ColeccionDetalle`), una pantalla
   * distinta que muestra UNA colección y de la que sólo se sale por "Volver". Ahora
   * lleva a la misma `/interfaz-v2/plantillas` con su chip ya activo: se ve lo mismo
   * y además el resto de las categorías queda a un toque.
   *
   * La categoría va en la URL —y no en un `state` de navegación— porque es la misma
   * llave que restaura el filtro al volver de un preset: el back del browser
   * reconstruye la URL, no el state.
   */
  //
  // La `base` es cuál de los dos modos de plantillas ve esta marca
  // (ADR-2026-09-10-plantillas-belleza-modo-propio): la pantalla de Moda y la de
  // Belleza son dos, con rutas distintas, y el "Ver más" tiene que caer en la que
  // el usuario efectivamente puede abrir — si no, rebota contra el guard de la otra.
  const rutaPlantillasCategoria = (id: string, base: string) =>
    `${base}?categoria=${encodeURIComponent(id)}`;
  // Resultados de la generación — destino de ~9 handlers de fin de generación.
  const rutaResultados = '/redes-sociales/imagenes-generadas';

  // ── Chrome que el shell nuevo ya pinta (ADR-2026-07-31-shell-provee-flag-hospedaje) ──
  // En desktop esta pantalla se monta dentro de `EstiloMinimal`, que ya trae la barra
  // de créditos y el panel CONTEXTO DEV. En mobile la monta `DashboardLayout`, que no.
  // Sin esta pregunta, un usuario sin créditos en desktop veía DOS barras iguales.
  const { dentroDelShell, uiNueva } = useCuentaShell();

  // Carteles de home (configuración de la cuenta paga / estado de la
  // suscripción) — mismas condiciones que la pantalla clásica. Al volver de
  // describir la marca, ese paso se da por hecho (`marcaRecienCompletada` en el
  // state de navegación).
  const homeBanners = useHomeBanners({
    marcaRecienCompletada:
      (location.state as any)?.marcaRecienCompletada === true,
  });
  // Barra superior de "sin créditos" (ADR-2026-07-30-barra-superior-creditos-home-ancho).
  // El descarte dura lo que la carga de la página y vuelve al recargar, igual que en
  // desktop — ver `topBarDescartadaEnEstaCarga` arriba.
  const [topBarDescartada, setTopBarDescartada] = useState(
    topBarDescartadaEnEstaCarga,
  );
  const descartarTopBar = () => {
    topBarDescartadaEnEstaCarga = true;
    setTopBarDescartada(true);
  };
  const {
    setMobileBackOverride,
    setMobileTitleOverride,
    setMobileHeaderTransparent,
    setMobileHeaderCenter,
    setMobileHeaderCompacto,
    setMobileHideBack,
    setMobileHideBottomNav,
    sidebarWidth,
  } = useSidebar();

  // La barra inferior y el sheet de "Generar" los monta el SHELL
  // (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell), no esta pantalla: son
  // navegación de la app y se ven en todas sus pantallas, no sólo en el inicio. Lo
  // que sí decide esta pantalla es cuándo se aparta — ver el efecto de
  // `mobileHideBottomNav` más abajo, cuando ya existen `selectedPreset` y `panelStep`.
  // Toggle imagen/video del header en preview. Cuando está en 'video' y el
  // preset es 'template', el ConfigurePanel muestra el botón "Configurar
  // video", el costo suma la animación, y `handleGenerateFinal` encadena
  // imagen → prompt → video después de generar.
  //
  // Arranca en Video cuando se entró a Plantillas desde la familia Videos del sheet de
  // Generar (ADR-2026-08-04-plantillas-es-una-opcion-de-generar): el catálogo pasa
  // `mediaInicial` en el state de navegación. Se lee del `history.state` y no de
  // `useLocation` por lo mismo que `initialNavPreset`: tiene que estar resuelto en el
  // PRIMER render, si no el toggle se vería saltar de Imagen a Video.
  const [previewMediaTab, setPreviewMediaTab] = useState<'imagen' | 'video'>(() => {
    try {
      return (window.history.state?.usr as { mediaInicial?: 'imagen' | 'video' })
        ?.mediaInicial === 'video'
        ? 'video'
        : 'imagen';
    } catch {
      return 'imagen';
    }
  });
  // Configuración de video para el flow de plantillas (kind 'template').
  // - `videoDuration`: segundos por escena (default 8 — mismo que animar
  //   imagen en el historial).
  // - `videoMultiScene`: si true, genera multi-shot (Kling vía atlas
  //   /v1/videos/from-image, ADR-582) y duplica el costo en créditos.
  const [videoDuration, setVideoDuration] = useState<number>(8);
  // Gancho elegido en el modo "Video con influencer" (id de `video_hooks`). Se
  // sube desde el `GanchoSection` (vía PresetDetailSheet → ConfigurePanel) para
  // poder mandarlo como `gancho_id` en el payload del webhook. Ver ADR-504.
  const [videoGanchoId, setVideoGanchoId] = useState<string | null>(null);
  // Cambio de gancho: al elegir "Sin gancho" la barra de Duración topa en 14s
  // (ADR-671) porque sin el clip de apertura el total del video ES el
  // principal, así que si venía más arriba la bajamos. Al volver a elegir un
  // gancho NO la subimos sola: sería cobrarle más sin que lo haya pedido.
  const handleGanchoIdChange = (id: string | null) => {
    setVideoGanchoId(id);
    if (isSinGancho(id)) {
      setVideoDuration((d) =>
        Math.min(d, VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO),
      );
    }
  };
  // Voz + velocidad + guion del modo "Video con influencer" (sección "Voz y
  // guion" del ConfigurePanel). Agrupado en un solo objeto para no engordar la
  // cadena de props (ADR-525/527). Se mapea al payload como `voz_id`,
  // `velocidad_voz`, `dialogo_input_mode`, `dialogo` / `audio_url`.
  const [videoVoiceGuion, setVideoVoiceGuion] = useState<VoiceGuionValue>(
    // Preselecciona la última voz usada (localStorage) — la voz es
    // obligatoria en este modo (ADR-544).
    () => getVoiceGuionDefault(),
  );
  const [videoMultiScene, setVideoMultiScene] = useState<boolean>(false);
  const [showVideoConfigSheet, setShowVideoConfigSheet] = useState<boolean>(
    false,
  );
  // Costo por video = duración (segundos) × multiplicador. Espeja la
  // fórmula de GeneratedImagesHistory: single-scene 1×, multi-scene 2×.
  const videoCreditCostPerImage = videoMultiScene
    ? videoDuration * 2
    : videoDuration;

  // Scroll save/restore para la home V2.
  //
  // Save: lo llamamos antes de cada navegación a sub-rutas. Guarda
  // `window.scrollY` en sessionStorage.
  //
  // Restore: el contenido de la home se llena en fases async
  // (collections cache → user info + design-row → allPoses + e-commerce),
  // así que el documento crece después del primer paint. Para evitar
  // mostrar contenido intermedio o saltos:
  //   1) Inicializamos `isRestoringHomeScroll = true` si hay scroll
  //      guardado.
  //   2) Mientras es true, renderizamos un SKELETON en vez del home
  //      real — el user ve "loading" en lugar de un home parcial.
  //   3) En cada render, un useLayoutEffect chequea si el documento
  //      es suficientemente alto para alcanzar la posición. Cuando sí,
  //      aplica scrollTo y baja el flag → swap a contenido real, ya
  //      scrolleado a la posición correcta. Sin flash, sin saltos.
  //   4) Safety timeout: 1.5s para no quedar atrapado en el skeleton si
  //      las async deps no resuelven.
  const saveHomeScroll = useCallback(() => {
    try {
      sessionStorage.setItem(HOME_SCROLL_KEY, String(window.scrollY));
    } catch {
      /* noop */
    }
  }, []);
  const pendingScrollRef = useRef<number | null>(null);
  const [isRestoringHomeScroll, setIsRestoringHomeScroll] = useState<boolean>(
    () => {
      try {
        return !!sessionStorage.getItem(HOME_SCROLL_KEY);
      } catch {
        return false;
      }
    },
  );
  useLayoutEffect(() => {
    if (!isRestoringHomeScroll) return;
    try {
      const sy = sessionStorage.getItem(HOME_SCROLL_KEY);
      if (sy) {
        pendingScrollRef.current = Number(sy);
        sessionStorage.removeItem(HOME_SCROLL_KEY);
      } else {
        setIsRestoringHomeScroll(false);
      }
    } catch {
      setIsRestoringHomeScroll(false);
    }
    // Safety timeout — si los datos async no llegan, levantamos el
    // skeleton igual para no dejar al user mirando placeholders.
    const t = window.setTimeout(() => {
      setIsRestoringHomeScroll(false);
      pendingScrollRef.current = null;
    }, 1500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    if (!isRestoringHomeScroll) return;
    const y = pendingScrollRef.current;
    if (y == null) {
      setIsRestoringHomeScroll(false);
      return;
    }
    const maxY = document.documentElement.scrollHeight - window.innerHeight;
    if (maxY >= y) {
      window.scrollTo({ top: y });
      document.documentElement.scrollTo({ top: y });
      pendingScrollRef.current = null;
      setIsRestoringHomeScroll(false);
    }
    // Si el documento todavía no es suficientemente alto, NO scrolleamos
    // a nada — esperamos al próximo render. El skeleton sigue tapando
    // el contenido intermedio.
  });

  // ── User greeting + rubro ─────────────────────────────────────────────
  const [shortName, setShortName] = useState<string>('');
  // Rubros eligibles para la fila "Diseña tu imagen" (poses como cards).
  // Si el user no es de uno de estos, esa fila no aparece.
  const DESIGN_RUBROS = new Set([
    'Moda e Indumentaria',
    'Belleza y Cuidado Personal',
  ]);
  // Modos "Diseña tu imagen": visibles para TODOS los rubros. Antes se
  // gateaba a moda/belleza (DESIGN_RUBROS) y cualquier otro rubro veía la
  // home vacía (ej. "agencia de marketing"). Se fuerza a `true` para que la
  // fila de modos aparezca siempre. El resto de la lógica de rubro (cache,
  // fetch de templates) queda intacta.
  const [isDesignRubro, setIsDesignRubro] = useState<boolean>(true);
  // Rubro de la MARCA activa (no el de la empresa): gatea los modos de muebles
  // (catálogo de muebles + infografías) — ADR-2026-07-31-modos-muebles-por-rubro-de-marca.
  // Mientras no se conoce (`null`) las cards no salen; el cache del hook evita
  // que aparezcan un frame tarde para quien sí califica.
  const { rubro: rubroMarcaActiva } = useRubroMarcaActiva();
  const esMarcaHogarDeco = esRubroHogarDeco(rubroMarcaActiva);
  // Hogar y los rubros no principales ven el Catálogo con el agente
  // (ADR-2026-10-26-catalogo-con-el-agente-para-los-rubros-no-principales).
  const veCatalogoConAgente = rubroVeCatalogoConAgente(rubroMarcaActiva);
  // Cómo se llama en pantalla la persona que aparece en la foto: "Modelo" en Moda e
  // Indumentaria, "Avatar" en el resto
  // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo). Sale del rubro que
  // esta pantalla YA tiene, no del hook de la palabra: acá no hace falta el cache.
  const palabraModelo = palabraDeModelo(rubroMarcaActiva);
  // Qué modos entran en el hero de la home de mobile durante la prueba gratis. La
  // decisión vive en `modesCatalog` y no acá — ver `kindsHomeGratis`.
  const kindsGratisHome = React.useMemo(
    () => kindsHomeGratis(esMarcaHogarDeco),
    [esMarcaHogarDeco],
  );
  // Nombre de la empresa — se usa en el copy del flujo "sorpréndeme" para
  // que el agente se refiera a la marca por su nombre. Si no está, el
  // texto cae a una versión genérica.
  const [companyName, setCompanyName] = useState<string>('');
  // Rubro + id_company del user — necesarios para el fetch de templates.
  // Loaded = true significa que ya intentamos cargarlos (incluso si están
  // vacíos), para evitar que el effect de templates corra antes de tiempo.
  const [userRubro, setUserRubro] = useState<string | null>(null);
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);

  /**
   * Cuál de los dos modos de plantillas ve esta marca, y adónde llevan sus accesos
   * — ADR-2026-09-10-plantillas-belleza-modo-propio.
   *
   * Moda tiene el suyo (`/interfaz-v2/plantillas`, 10 categorías) y Belleza y Cuidado
   * Personal el suyo (`/interfaz-v2/plantillas-belleza`, dos categorías curadas). Son
   * pantallas distintas y EXCLUYENTES: cada rubro ve una y el guard de cada una
   * rebota al otro rubro, así que la home tiene que apuntar a la que corresponde o el
   * "Ver más" cae en un rebote (el mismo problema de
   * ADR-2026-08-21-plantillas-rebotaban-por-dos-marcas-activas, por otra causa).
   *
   * `base` en `null` = esta marca no ve plantillas: ni filas en la home, ni cards en
   * el sheet, ni "Ver todo".
   *
   * El rubro es el de la MARCA activa con fallback al de la empresa — el mismo orden
   * que usan los guards de las cuatro pantallas.
   */
  const rubroDePlantillas = rubroMarcaActiva ?? userRubro;
  const basePlantillas: string | null = rubroVePlantillas(rubroDePlantillas)
    ? '/interfaz-v2/plantillas'
    : rubroVePlantillasBelleza(rubroDePlantillas)
      ? '/interfaz-v2/plantillas-belleza'
      : null;
  // Plan del usuario (gratis / emprendimiento / pro / negocio / enterprise…)
  // — para el gating de funciones por plan al generar.
  // Seed desde el cache de localStorage (mismo que llena el Sidebar) para tener
  // el plan al instante en el primer render → el gating de modos funciona desde
  // el vamos (taps + chokepoint de URL directa), sin esperar el fetch async.
  const [userPlanReal, setUserPlanReal] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  /**
   * El plan con el que decide esta pantalla: el simulado por el panel CONTEXTO DEV
   * si hay uno, y si no el real — ADR-2026-08-03-el-plan-simulado-tambien-gatea.
   *
   * SUPERSEDIDO: el panel CONTEXTO DEV y sus overrides se eliminaron al mergear
   * `principal` (ADR-2026-08-05-fuera-los-overrides-de-dev). Queda el plan real y
   * nada más. El alias se conserva porque lo leen ~40 lugares de esta pantalla y
   * renombrarlos no cambiaría nada.
   */
  const userPlan = userPlanReal;
  // Modal de créditos agotados (reusa el de toda la app).
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] =
    useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<
    'free-trial-ended' | 'plan-limit-reached'
  >('free-trial-ended');
  // Copy del catálogo según el saldo (ADR-2026-07-31-copy-catalogo-sigue-saldo-real). Los caminos que chocan contra la
  // falta de créditos lo dejan en `sin`; sólo el CTA de la barra lo pisa, porque
  // puede abrirse con saldo todavía disponible.
  const [motivoPricing, setMotivoPricing] = useState<MotivoPricing>('sin');
  // Modal de "función exclusiva" (gating por plan). null = cerrado.
  const [featureLock, setFeatureLock] = useState<{
    title: string;
    message: string;
  } | null>(null);
  // Visor de ejemplos para modos BLOQUEADOS por plan: al tocar la card con
  // candado se abre esto (ejemplos + nota del plan + CTA "Mejorar plan") en
  // vez del FeatureLockedModal — mismo comportamiento que la pantalla Modos.
  const [lockedModeExamples, setLockedModeExamples] = useState<{
    title: string;
    description?: string;
    examples: ModeExample[];
    planNote: string;
  } | null>(null);
  // Devuelve true si abrió el visor (el modo tiene ejemplos cargados); si no,
  // el caller cae al FeatureLockedModal clásico.
  const openLockedModeExamples = (
    preset: PresetV2,
    lock: ModePlanLock,
  ): boolean => {
    const examples = getModeExamples(preset.kind);
    if (examples.length === 0) return false;
    setLockedModeExamples({
      title: preset.title,
      description: preset.description,
      examples,
      planNote: getPlanRequirementNote(lock.feature),
    });
    return true;
  };
  // Modal de "función exclusiva" para video (mismo que el flujo de video).
  const [showVideoPlanRestriction, setShowVideoPlanRestriction] =
    useState<boolean>(false);
  /* Acá vivía `showContactQR`, el modal con QR del botón flotante "Contactanos".
     Se fue con el botón (ADR-2026-08-03-fuera-boton-flotante-contactanos): era su
     único disparador. */
  const [userInfoLoaded, setUserInfoLoaded] = useState<boolean>(false);
  // ── Cursor "hand pointer" en los elementos clickeables de esta pantalla ──
  // Mientras InterfazV2 está montada, marcamos <body> con `v2-cursor-scope`.
  // El scope vive en <body> (y no en el contenedor de la página) a propósito:
  // los pickers/sheets/modales se montan con React portals a `document.body`,
  // así que sólo scopeando desde <body> el selector alcanza también ese
  // contenido portaleado. La regla CSS está en src/index.css (ver ADR-575).
  useEffect(() => {
    document.body.classList.add('v2-cursor-scope');
    return () => {
      document.body.classList.remove('v2-cursor-scope');
    };
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('name_short, id_company')
          .eq('id', user.id)
          .single();
        if (profile && (profile as any).name_short) {
          setShortName((profile as any).name_short as string);
        }
        // Rubro + nombre de empresa viven en company_profiles. Una sola query.
        const idCompany = (profile as any)?.id_company as string | undefined;
        if (idCompany) {
          setUserCompanyId(idCompany);
          // Queda anotada para toda la pestaña: los modos con ruta propia arrancan con
          // ella en vez de esperar su propia consulta, y así la columna de resultados no
          // parpadea al cambiar de modo
          // (ADR-2026-09-08-la-portada-no-parpadea-mientras-se-resuelve-la-empresa).
          recordarEmpresa(idCompany, user.id);
          const { data: company } = await supabase
            .from('company_profiles')
            .select('rubro, nombre, plan')
            .eq('id_company', idCompany)
            .single();
          const planVal = (company as any)?.plan as string | undefined;
          if (planVal) setUserPlanReal(planVal);
          // Saneado: el centinela del alta por link no es un rubro, y de esta variable
          // salen `userRubro` (que viaja a los paneles y al backend) y el flag de diseño.
          const rubro = rubroVisible((company as any)?.rubro as string | undefined);
          if (rubro) {
            setUserRubro(rubro);
            const designRubro = DESIGN_RUBROS.has(rubro);
            if (designRubro) setIsDesignRubro(true);
            try {
              sessionStorage.setItem(
                HOME_DESIGN_RUBRO_CACHE_KEY,
                designRubro ? '1' : '0',
              );
            } catch {
              /* noop */
            }
          }
          const nombre = (company as any)?.nombre as string | undefined;
          if (nombre) setCompanyName(nombre);
        }
      } catch {
        // silent — sin nombre el greeting muestra solo "Buenos días."
      } finally {
        setUserInfoLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Productos reales del user ───────────────────────────────────────
  // Antes los pickers usaban `MOCK_USER_PRODUCTS`. Ahora fetcheamos
  // variantes reales una vez que sabemos el companyId del user. El array
  // se sirve a los descendientes vía `ProductsContext`.
  // Initializer lee el cache de sessionStorage: si el user ya abrió V2 en
  // esta sesión, arrancamos con el catálogo lleno en el primer paint (CTA +
  // thumbnails sin esperar al fetch).
  const [userProducts, setUserProducts] = useState<MockProductV2[]>(
    () => readProductsCache().products,
  );
  // Marca a la que pertenecen los productos cacheados (para detectar si el
  // primer paint pintó productos de otra marca y limpiarlos antes del fetch).
  const cachedBrandRef = useRef<string | null>(readProductsCache().brandId);
  // Espejo de `currentBrandId` accesible desde `persistUserProducts` (que es un
  // useCallback estable) sin recrearlo en cada cambio de marca.
  const currentBrandIdRef = useRef<string | null>(null);
  // `true` una vez que `fetchUserProducts` resolvió (aunque sea vacío). La
  // UI usa esto para mostrar el CTA "Elegir de mis productos" de forma
  // optimista durante la carga. Ver [[ProductsLoadedContext]]. Si hay cache,
  // arrancamos en `true` así el CTA aparece desde el primer paint.
  const [userProductsLoaded, setUserProductsLoaded] = useState<boolean>(() => {
    try {
      return !!sessionStorage.getItem(PRODUCTS_CACHE_KEY);
    } catch {
      return false;
    }
  });
  // Setter único de productos: actualiza el state, marca `loaded` y refresca
  // el cache de sessionStorage. Lo usan tanto el fetch inicial como los
  // refetch post-upload, así el cache nunca queda viejo (p.ej. sin el
  // producto recién subido).
  const persistUserProducts = useCallback((products: MockProductV2[]) => {
    setUserProducts(products);
    setUserProductsLoaded(true);
    cachedBrandRef.current = currentBrandIdRef.current;
    try {
      // Envelope `{ brandId, products }`: guardamos a qué marca pertenece el
      // catálogo para que el primer paint de la próxima visita no muestre
      // productos de otra marca.
      sessionStorage.setItem(
        PRODUCTS_CACHE_KEY,
        JSON.stringify({ brandId: currentBrandIdRef.current, products }),
      );
    } catch {
      /* sessionStorage puede fallar en modo privado — non-fatal */
    }
  }, []);

  // Refresca el catálogo de productos (re-fetch + persist). Lo usan los pickers
  // tras editar / borrar un producto desde la modal "Editar producto".
  const refreshUserProducts = useCallback(async () => {
    if (!userCompanyId) return;
    const refreshed = await fetchUserProducts(
      userCompanyId,
      currentBrandIdRef.current,
    );
    persistUserProducts(refreshed);
  }, [userCompanyId, persistUserProducts]);

  // Value estable para ProductEditContext (companyId + refresh) que consumen los
  // pickers (PickProductPanel) para abrir la modal "Editar producto".
  const productEditContextValue = useMemo(
    () => ({ companyId: userCompanyId, refreshProducts: refreshUserProducts }),
    [userCompanyId, refreshUserProducts],
  );
  // Marca activa del user (profiles.current_brand_id). Se usa para (1) filtrar
  // el catálogo por marca — igual que Estudio Virtual — y (2) asociar `brand_id`
  // al crear productos desde V2. `brandResolved` pasa a `true` una vez que el
  // fetch del profile terminó (con marca o sin ella), para no fetchear productos
  // antes de saber por qué marca filtrar.
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [brandResolved, setBrandResolved] = useState(false);
  // La cuenta tiene 2+ marcas. Es la condición con la que Creaciones decide FILTRAR por
  // marca: con una sola, filtrar no distingue nada y sí esconde las generaciones sin marca
  // (94k históricas). La columna del modo la adopta
  // (ADR-2026-09-13-brand-id-en-imagenes-generadas, D8).
  const [cuentaMultiMarca, setCuentaMultiMarca] = useState(false);
  // Rubro de la MARCA activa (`brand_profiles.rubro`), distinto del de la
  // empresa (`userRubro`, que sale de company_profiles).
  //
  // POR QUÉ EXISTE: en n8n, `If es moda1` decide qué familia de prompts usar
  // leyendo el rubro de la EMPRESA. En cuentas con varias marcas eso es
  // incorrecto — una empresa "Moda e Indumentaria" con una marca de muebles
  // generaba el mueble con los prompts de moda. El modo muebles manda este
  // valor en el payload para que n8n rutee por la marca que el usuario está
  // usando en ese momento. Ver ADR-589.
  const [brandRubro, setBrandRubro] = useState<string | null>(null);
  useEffect(() => {
    if (!currentBrandId) {
      setBrandRubro(null);
      return;
    }
    let cancelado = false;
    (async () => {
      const { data } = await supabase
        .from('brand_profiles')
        .select('rubro')
        .eq('id', currentBrandId)
        .single();
      if (!cancelado) {
        setBrandRubro(rubroVisible((data as any)?.rubro as string | null));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [currentBrandId]);
  useEffect(() => {
    currentBrandIdRef.current = currentBrandId;
  }, [currentBrandId]);
  // Esperamos a que la marca activa esté resuelta (`brandResolved`) antes de
  // fetchear, así filtramos por la marca correcta desde el primer fetch. Si el
  // cache pintó productos de OTRA marca (o de "toda la empresa", cache viejo),
  // los limpiamos antes de re-fetchear para no mostrar productos ajenos.
  useEffect(() => {
    if (!userCompanyId || !brandResolved) return;
    let alive = true;
    if ((cachedBrandRef.current ?? null) !== (currentBrandId ?? null)) {
      setUserProducts([]);
    }
    (async () => {
      const products = await fetchUserProducts(userCompanyId, currentBrandId);
      if (alive) persistUserProducts(products);
    })();
    return () => {
      alive = false;
    };
  }, [userCompanyId, brandResolved, currentBrandId, persistUserProducts]);

  // ── Modelos guardados (globales + privados) ─────────────────────────
  // Fetcheamos vía `fetchUserModels` que ya filtra por id_company y firma
  // las URLs. `userAuthId` se captura del auth — hoy solo lo necesita el
  // fallback n8n de crear modelo (flag OFF).
  const [userModels, setUserModels] = useState<ModelV2[]>([]);
  const [userAuthId, setUserAuthId] = useState<string | null>(null);
  // Paleta de la marca activa (`brand_profiles.colors`) — array de hex
  // strings tipo `['#1644EB', '#000', ...]`. Sirve como default para el
  // color picker del modo Anuncio. Vacío si la marca no tiene paleta
  // cargada o si todavía no se resolvió el fetch.
  const [brandColors, setBrandColors] = useState<string[]>([]);
  // Fuente de marca renderizable (`brand_profiles.fonts.suggested[0]` que
  // exista en TEXT_FONT_OPTIONS). Se usa como default de la tipografía de
  // los 3 textos del modo Anuncio. '' = la marca no tiene una fuente
  // renderizable → los campos quedan en "Automática".
  const [brandFont, setBrandFont] = useState<string>('');
  // Ref siempre-al-día con el brandFont, para los reset points (evita
  // closures stale al resetear el state del anuncio).
  const brandFontRef = useRef<string>('');
  useEffect(() => {
    brandFontRef.current = brandFont;
  }, [brandFont]);
  // Siembra one-shot: cuando se resuelve la fuente de marca, los 3 campos
  // del anuncio que sigan sin fuente elegida adoptan la de la marca.
  const brandFontSeededRef = useRef(false);
  const refreshUserModels = async () => {
    if (!userCompanyId) return;
    const models = await fetchUserModels(userCompanyId);
    setUserModels(models);
  };
  useEffect(() => {
    if (!userCompanyId) return;
    let alive = true;
    (async () => {
      const [{ data: { user } }, models] = await Promise.all([
        supabase.auth.getUser(),
        fetchUserModels(userCompanyId),
      ]);
      if (!alive) return;
      setUserAuthId(user?.id || null);
      setUserModels(models);
      // current_brand_id del profile — para asociar brand a productos
      // creados desde V2.
      if (user?.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('current_brand_id')
          .eq('id', user.id)
          .single();
        if (alive) {
          const brandId =
            ((profile as any)?.current_brand_id as string) || null;
          setCurrentBrandId(brandId);
          // Marca resuelta → habilita el fetch de productos filtrado por marca.
          setBrandResolved(true);
          // Cantidad de marcas no-draft de la empresa (mismo universo que useBrandProfile):
          // decide si la columna del modo filtra por marca (D8).
          const { data: marcasDeLaEmpresa } = await supabase
            .from('brand_profiles')
            .select('id')
            .eq('id_company', userCompanyId)
            .eq('is_draft', false);
          if (alive) {
            setCuentaMultiMarca(
              ((marcasDeLaEmpresa as { id: string }[] | null)?.length ?? 0) > 1,
            );
          }
          // Paleta + fuente del brand para los defaults del modo Anuncio.
          // Si NO hay marca activa (current_brand_id null — típico de cuentas
          // nuevas que nunca cambiaron de marca), caemos a la marca DEFAULT de
          // la empresa (mismo fallback que useBrandProfile); si no, el anuncio
          // quedaba sin colores ni fuente aunque la marca exista. NO tocamos
          // `currentBrandId` (el filtro de productos con null = sin filtrar).
          let colorsBrandId = brandId;
          if (!colorsBrandId) {
            const { data: def } = await supabase
              .from('brand_profiles')
              .select('id')
              .eq('id_company', userCompanyId)
              .eq('is_default', true)
              .eq('is_draft', false)
              .maybeSingle();
            if (alive) colorsBrandId = ((def as any)?.id as string) || null;
          }
          if (colorsBrandId) {
            const { data: brand } = await supabase
              .from('brand_profiles')
              .select('colors, fonts')
              .eq('id', colorsBrandId)
              .maybeSingle();
            if (alive) {
              // `colors` llega como `{hex,name,role}[]` (Estudio Virtual)
              // o `string[]` (editor), a veces sin `#` o en 3 dígitos.
              // Normalizamos a `#RRGGBB` válidos para el color picker.
              setBrandColors(normalizeBrandHexColors((brand as any)?.colors));
              // `fonts.suggested[0]` renderizable → default de tipografía.
              setBrandFont(resolveBrandFont((brand as any)?.fonts));
            }
          }
        }
      } else if (alive) {
        // Sin user no hay marca que resolver, pero igual habilitamos el fetch
        // (caerá en el catálogo sin filtro de marca).
        setBrandResolved(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [userCompanyId]);

  // ── Templates desde DB ──────────────────────────────────────────────
  // Se reemplazó la mock COLLECTIONS_V2 por un fetch real a Supabase.
  // El fetch espera a que el user info esté loaded para poder pasar
  // rubro y companyId correctos al filtro. Categorías sin templates que
  // pasen los filtros NO se renderizan (las skippea templatesApi).
  // Initializer lee el cache de sessionStorage: si el user volvió desde
  // una sub-ruta dentro de la sesión, arrancamos con la grilla llena en
  // el primer paint. Esto es clave para que el scroll-restore al
  // useLayoutEffect funcione (sino el documento es muy corto y
  // `scrollTo(y)` se clampea al maxScroll).
  const [fetchedCollections, setFetchedCollections] = useState<CollectionV2[]>(
    () => {
      try {
        const raw = sessionStorage.getItem(HOME_COLLECTIONS_CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed as CollectionV2[];
        }
      } catch {
        /* noop */
      }
      return [];
    },
  );
  useEffect(() => {
    if (!userInfoLoaded) return;
    let alive = true;
    (async () => {
      // Cada modo trae sus propias colecciones: el de Belleza son dos categorías
      // curadas y no salen del catálogo por rubro
      // (ADR-2026-09-10-plantillas-belleza-modo-propio). Sin rubro de plantillas se
      // fetchea igual el de Moda —es lo que había— y el gate de más abajo decide si
      // se muestra.
      const collections =
        basePlantillas === '/interfaz-v2/plantillas-belleza'
          ? await fetchTemplatesBelleza({ companyId: userCompanyId })
          : await fetchTemplatesV2({
              rubro: userRubro,
              companyId: userCompanyId,
            });
      if (!alive) return;
      setFetchedCollections(collections);
      try {
        sessionStorage.setItem(
          HOME_COLLECTIONS_CACHE_KEY,
          JSON.stringify(collections),
        );
      } catch {
        /* noop */
      }
    })();
    return () => {
      alive = false;
    };
  }, [userInfoLoaded, userRubro, userCompanyId, basePlantillas]);
  /* Acá se armaba el saludo de la home vieja ("Buenas tardes, Maxi."). Se fue con
     ADR-2026-07-31-inicio-se-funde-interfaz-v2: ahora lo arma `HomeMinimal`, que saca
     el nombre de `profiles` y la franja horaria de `saludo()`. Ver el comentario del
     `getGreeting` borrado, arriba del todo. */
  // Plan gratis. Conservador: sólo cuando el plan es explícitamente 'gratis', o
  // cuando ya cargamos el perfil y no hay plan — así no le parpadea nada a un usuario
  // pago mientras resuelve el fetch en un cold-load sin cache de plan en localStorage.
  // Lo usaba además el botón flotante "Contactanos", que ya no está
  // (ADR-2026-08-03-fuera-boton-flotante-contactanos); hoy sólo decide filas de la home.
  const isFreePlan = userPlan === 'gratis' || (userInfoLoaded && !userPlan);
  /* Acá vivía `contactWaHref`, el link de WhatsApp con mensaje pre-armado del botón
     flotante y su QR. Se fue con los dos. El contacto por WhatsApp sigue estando en
     el shell (pastilla "Whatsapp" del encabezado) y en `/ayuda`. */

  // ── Poses (para el modo "Imagen con modelo") ────────────────────────
  // Cargamos todas las poses una vez si el rubro califica. Se usan en:
  //   1. La card "Imagen con modelo" de la fila "Diseña tu imagen"
  //      (tomamos la primera pose como thumbnail temporal).
  //   2. El PosePicker (multi-select grilla con tabs).
  // El catálogo de poses cambia poco y es público — fetch único acá.
  // Initializer lee el cache para que la fila E-commerce aparezca en
  // el primer paint sin esperar a `fetchAllPoses`.
  const [allPoses, setAllPoses] = useState<PoseV2[]>(() => {
    try {
      const raw = sessionStorage.getItem(HOME_POSES_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed as PoseV2[];
      }
    } catch {
      /* noop */
    }
    return [];
  });
  useEffect(() => {
    if (!isDesignRubro) return;
    let alive = true;
    (async () => {
      const poses = await fetchAllPoses();
      if (!alive) return;
      setAllPoses(poses);
      try {
        sessionStorage.setItem(HOME_POSES_CACHE_KEY, JSON.stringify(poses));
      } catch {
        /* noop */
      }
    })();
    return () => {
      alive = false;
    };
  }, [isDesignRubro]);

  // Lista final de colecciones a renderizar: si el rubro califica,
  // prepende una fila "Diseña tu imagen" con una sola card
  // "Imagen con modelo" (kind 'design-modelo') antes de las plantillas
  // tradicionales. La imagen de la card es temporalmente la de una pose
  // del catálogo.
  //
  // NO se muestra a las marcas de Hogar y Decoración
  // (ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo): el modo pone el
  // producto sobre un modelo IA, que para un mueble no aplica.
  const designModeloCard: PresetV2 | null =
    isDesignRubro && !esMarcaHogarDeco
    ? {
        id: 'imagen-con-modelo',
        title: 'Generar imagen personalizada',
        description: `Tu producto sobre un ${palabraModelo.singular} IA en la pose y el fondo que vos elijas.`,
        // Carátula del modo (ADR-2026-08-04-modelo-portada-unica): el collage
        // "producto + fondo + cara → resultado" reemplaza a `fot_modelo.png`.
        //
        // Duplicada con la entrada `modelo` de `modesCatalog.ts`: si tocás una,
        // tocá la otra.
        imageUrl: '/images/modo_con_modelo/caratula.jpg',
        // Hero VERTICAL de la pantalla del modo — ver el porqué en la entrada `modelo`
        // de `modesCatalog.ts`, que esta card espeja.
        imageUrlFull: '/images/modo_con_modelo/modo_modelo_portada2.png',
        kind: 'design-modelo' as const,
        // Costo base por imagen — el creditCost computado en
        // PresetDetailSheet multiplica esto por la cantidad real de
        // imágenes a generar (1 por defecto en este modo).
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Generar imagen automáticamente" para la fila
  // "Diseña tu imagen". Visible en mobile y desktop — el entrypoint
  // del modo auto vive solo acá (antes había un botón azul arriba
  // del greeting que ya se removió).
  const designAutoCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'imagen-auto-card',
        title: 'Generar imagen automáticamente',
        description:
          'Sumá tu producto y la IA arma todo: fondo, escena y luz por vos.',
        // Carátula GENÉRICA del modo (ADR-2026-08-05-automatica-va-con-foto-y-sin-video).
        // La del rubro Hogar y Decoración la pisa `PORTADA_POR_RUBRO` en las superficies
        // que dibujan la carátula.
        imageUrl: '/images/modo_automatico/caratula.jpg',
        imageUrlFull: '/images/modo_automatico/modo_auto.png',
        kind: 'design-auto' as const,
        // Costo base por imagen — el creditCost de PresetDetailSheet
        // multiplica esto por `autoImageCount` (1-6 imágenes).
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Fotos de producto" — el hermano SIN modelo de "Imagen
  // personalizada": un producto, las vistas que elija el user y fondo blanco
  // o escena descrita.
  //
  // NO se muestra a Hogar y Decoración — ADR-2026-08-28-fotos-de-producto-no-va-en-muebles.
  // Acá decía lo contrario ("un mueble también se fotografía solo") y se revirtió:
  // el modo se define por su picker de VISTAS, y ese picker es de indumentaria
  // (maniquí frontal/trasero/ángulo, maniquí mujer, prenda doblada). Mismo gate
  // que la card de "Imagen personalizada", unas líneas más arriba.
  //
  // DUPLICADA con `modesCatalog.ts` — es el patrón de todas las cards de modo:
  // la de acá alimenta la home V2 y la de allá la pantalla /modos. Si tocás
  // una, tocá la otra. Ver ADR-2026-08-13-modo-fotos-producto.
  const fotosProductoCard: PresetV2 | null =
    isDesignRubro && !esMarcaHogarDeco
    ? {
        id: 'fotos-producto-card',
        title: 'Fotos de producto',
        description:
          'Tu producto solo, en las vistas que necesites: fondo blanco o la escena que describas.',
        // Carátula PROPIA desde ADR-2026-09-23-las-cuatro-que-faltaban: las cuatro
        // vistas del mismo producto. Era un PLACEHOLDER — un thumb suelto del catálogo
        // de vistas, o sea UNA foto para un modo que promete varias. El `imageUrlFull`
        // no cambia: es el hero de "Elegí el producto", el collage antes → después.
        imageUrl: '/images/modo_fotos_producto/caratula.jpg',
        imageUrlFull: '/images/modo_fotos_producto/hero_producto.png',
        kind: 'fotos-producto' as const,
        // Costo base por imagen — PresetDetailSheet lo multiplica por la
        // cantidad de vistas elegidas (una imagen por vista).
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Animar imagen" — foto (subida o generada) → video. Tiene
  // pantalla propia (/animar-imagen), así que el click NO abre sheet: navega
  // (ver MODE_KIND_TO_ROUTE). Visible para todos los rubros: animar una foto no
  // depende de tener productos cargados.
  //
  // DUPLICADA con `modesCatalog.ts` — es el patrón de todas las cards de modo: la
  // de acá alimenta la home V2 y la de allá la pantalla /modos. Si tocás una,
  // tocá la otra. Ver ADR-2026-08-14-modo-animar-imagen.
  const animarImagenCard: PresetV2 = {
    id: 'animar-imagen-card',
    title: 'Animar imagen',
    description: 'Convertí cualquier foto en un video con movimiento realista.',
    // El cuadro 0 de su propio clip, no la miniatura prestada de Video con influencer
    // — ADR-2026-09-23-animar-imagen-deja-de-usar-el-clip-de-todos.
    imageUrl: '/images/modo_animar_imagen/caratula_poster.jpg',
    imageUrlFull: '/images/modo_animar_imagen/caratula_poster.jpg',
    kind: 'animar-imagen' as const,
    outputType: 'video' as const,
    creditosPorUnidad: 1,
    unidadConsumo: 'segundo' as const,
  };
  // Card del modo "Carrusel" — foto (subida o generada) → 2-6 tomas nuevas del
  // mismo producto. Tiene pantalla propia (/carrusel), así que el click NO abre
  // sheet: navega (ver MODE_KIND_TO_ROUTE). Visible para todos los rubros: partir
  // de una foto no depende de tener productos cargados.
  //
  // DUPLICADA con `modesCatalog.ts` — es el patrón de todas las cards de modo: la
  // de acá alimenta la home V2 y la de allá la pantalla /modos. Si tocás una,
  // tocá la otra. Ver ADR-2026-08-14-modo-carrusel.
  const carruselCard: PresetV2 = {
    id: 'carrusel-card',
    title: 'Carrusel de imágenes',
    description:
      'De una foto, varias: nuevas tomas del mismo producto con otros ángulos y encuadres.',
    // Carátula NUEVA desde ADR-2026-09-23-las-cuatro-que-faltaban: la fila de cuatro
    // tomas del mismo momento, cortada por los bordes. Reemplaza al lienzo 1:1 con el
    // abanico chiquito en el medio, que en el slot 3:2 quedaba diminuto y encima
    // dibujaba la misma figura que Plantillas. MISMO archivo que antes (`caratula.jpg`),
    // contenido distinto.
    imageUrl: '/images/modo_carrusel/caratula.jpg',
    imageUrlFull: '/images/modo_carrusel/caratula.jpg',
    kind: 'carrusel' as const,
    creditosPorUnidad: 1,
    unidadConsumo: 'imagen' as const,
  };
  // Card del modo "Catálogo IA" — tercera card del design-row. Pensada
  // para generar photoshoots de catálogo: el user elige productos +
  // varias vistas (poses con-modelo + ángulos solo-producto) y el sheet
  // dispara una llamada V3 por cada (vista, producto) compatible. El
  // entrypoint visible está siempre que `isDesignRubro` califica (igual
  // que las otras dos cards de modo), MENOS para las marcas de Hogar y
  // Decoración: ahí su lugar lo ocupa el catálogo de muebles, que se muestra
  // con este mismo título — ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
  const catalogoIACard: PresetV2 | null =
    isDesignRubro && !esMarcaHogarDeco
    ? {
        id: 'catalogo-ia-card',
        title: 'Generar catálogo con IA',
        description:
          'Photoshoots por producto con varias vistas y poses en un solo proceso.',
        // El `imageUrl` (thumbnail home) es la MISMA grilla de vistas que la card de
        // /modos resuelve por `CARATULA_PROPIA` — ADR-2026-09-23-las-cuatro-que-faltaban.
        // Hasta acá era `fot_modelo.png`, un thumbnail PRESTADO del modo Modelo: en la
        // home, Catálogo se presentaba con la cara de otro modo.
        imageUrl: '/images/modo_catalogo_ia/caratula.jpg',
        // Hero PROPIO del modo. El `modo_catalogo_hero.png` viejo sigue en pie: lo usan
        // muebles, infografía y el asistente (ADR-2026-08-26-catalogo-hero-propio).
        imageUrlFull: '/images/modo_catalogo_ia/modo_catalogo_hero2.png',
        kind: 'catalogo-ia' as const,
        // Costo base por imagen — el creditCost de PresetDetailSheet
        // lo multiplica por la cantidad de (vista × producto) cards
        // confirmadas en el step final.
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Catálogo de muebles" (ADR-582). Clona el flujo del Catálogo
  // IA pero con vistas propias (fondo blanco / infografía con medidas / foto en
  // contexto), sin poses ni modelo, y con una pantalla donde el user carga las
  // medidas de cada producto antes de generar.
  //
  // Esta definición está DUPLICADA con la de `modesCatalog.ts` — es el patrón
  // (feo) que siguen todas las cards de modo: la de acá alimenta la home V2 y la
  // de allá la pantalla /modos. Si tocás una, tocá la otra.
  const catalogoMueblesCard: PresetV2 | null =
    esMarcaHogarDeco && isDesignRubro
      ? {
          id: 'catalogo-muebles-card',
          // Mismo título que el Catálogo IA: este modo solo lo ven las marcas de
          // Hogar y Decoración, y para ellas el Catálogo IA no existe — ver
          // ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
          title: 'Generar catálogo con IA',
          description:
            'Fondo blanco, infografía con medidas y foto en contexto, para cada uno de tus productos.',
          // Carátula propia (el ropero en contexto, ADR-817) — la card no tiene
          // video, así que HomeCards cae a este `imageUrl`. El `imageUrlFull` no
          // se ve: el hero de "Elegí el producto" lo arma ProductHeroCollage.
          imageUrl: '/images/modo_catalogo_muebles/hero_contexto.jpg',
          imageUrlFull: '/images/modo_catalogo_ia/modo_catalogo_hero.png',
          kind: 'catalogo-muebles' as const,
          creditosPorUnidad: 1,
          unidadConsumo: 'imagen' as const,
        }
      : null;
  // Card del modo "Infografía" (ADR-814). Mismo gate de rubro de marca que el
  // catálogo de muebles: son el mismo flujo y el mismo público.
  //
  // Antes solo existía en `/modos`; al habilitar los modos se sumó también acá
  // (ADR-2026-07-31-modos-muebles-por-rubro-de-marca) para que la home y Modos
  // muestren lo mismo y para que el deep-link `/interfaz-v2/modo/infografia`
  // pueda resolver su card. Duplicada con `modesCatalog.ts` — si tocás una,
  // tocá la otra.
  //
  // El rediseño había llegado a la misma card con `rubroVeInfografia(brandRubro ??
  // userRubro)`; gana esta versión, que usa el gate único
  // (ADR-2026-08-05-un-solo-gate-de-rubro-para-los-modos). La diferencia real es el
  // `?? userRubro`: acá NO se cae al rubro de la empresa, que es la decisión
  // explícita de `principal`.
  const infografiaCard: PresetV2 | null =
    esMarcaHogarDeco && isDesignRubro
      ? {
          id: 'infografia-card',
          title: 'Infografías de producto con IA',
          description:
            'Fichas técnicas con las medidas y características de cada uno de tus productos.',
          imageUrl: '/images/modo_infografia/hero_foto.jpg',
          imageUrlFull: '/images/modo_catalogo_ia/modo_catalogo_hero.png',
          kind: 'infografia' as const,
          creditosPorUnidad: 1,
          unidadConsumo: 'imagen' as const,
        }
      : null;
  // Card del modo "Asistente de marketing" — cuarta card del design-row.
  // Un flow tipo chat con input libre + adjuntos (productos / modelos /
  // imágenes anteriores) + pill options abajo (formato, cantidad,
  // duración video, escena múltiple). El backend / costo no están
  // wireados todavía — esta primera iteración es el skeleton visual.
  const asistenteCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'asistente-marketing-card',
        title: 'Crear desde cero',
        description: 'Generá, analizá y planificá tu contenido',
        // Placeholders — el user va a subir assets dedicados cuando
        // los tenga. Mientras, reusamos los de catalogo para no romper
        // el render.
        imageUrl: '/images/modo_con_modelo/fot_modelo.png',
        imageUrlFull: '/images/modo_catalogo_ia/modo_catalogo_hero.png',
        kind: 'asistente' as const,
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // La MISMA card, para la variante en prueba `asistente-v2`
  // (ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo). Existe porque el
  // effect de sync de URL busca la card por `kind` en `CARD_POR_KIND`: sin ella,
  // `/modo/asistente-v2` corta en el `if (!card) return` y la pantalla no abre nunca.
  // Sale del catálogo en vez de repetir el literal acá: el modo lo declara `MODES`, y
  // dos copias del mismo preset es justo lo que se rompe cuando alguien toca una sola.
  const asistenteV2Card: PresetV2 | null = isDesignRubro
    ? (MODES.find((m) => m.path === 'asistente-v2')?.preset ?? null)
    : null;
  // Card del modo "Prompt libre" — ADR-2026-09-07-modo-prompt-libre. El camino directo
  // de imagen del asistente en pantalla propia; el primero de los tres modos que lo
  // reemplazan. Duplicada con la entrada de `MODES` (patrón de todos los modos): si
  // tocás una, tocá la otra. Carátula PLACEHOLDER: el póster del asistente.
  const promptLibreCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'prompt-libre-card',
        title: 'Prompt libre',
        description: `Describí la imagen que querés y Atlas la genera. Sumá un producto, un ${palabraModelo.singular} o una foto como referencia.`,
        imageUrl: '/images/modo_asistente/modo_asistente_poster.jpg',
        imageUrlFull: '/images/modo_asistente/modo_asistente_poster.jpg',
        kind: 'prompt-libre' as const,
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Creador de video" — ADR-2026-09-07-modo-creador-video. El asistente
  // en video, en pantalla propia. `outputType: 'video'` lo lista en la familia Videos.
  // Duplicada con la entrada de `MODES`: si tocás una, tocá la otra.
  // Card del modo "Diseño de indumentaria" — ADR-2026-09-18-el-modo-diseno-se-abre-a-moda-desde-pro.
  // Sólo marcas de Moda (el mismo `soloModa` del catálogo); el piso Pro/Ultra lo pone
  // `getModePlanLock` al armar `modosHome`, como a todas. El preset sale de `MODES`, así
  // no hay una copia más que mantener; la card abre su ruta propia por
  // `MODE_KIND_TO_ROUTE`. Faltaba acá: la tira de "Crear imágenes" no sale de `MODES`
  // sino de esta lista a mano, y el modo no se veía en la home.
  const disenoIndumentariaCard: PresetV2 | null =
    isDesignRubro && esRubroModa(rubroMarcaActiva)
      ? (MODES.find((m) => m.path === 'diseno-indumentaria')?.preset ?? null)
      : null;
  // Card del modo "Replicar una foto" — ADR-2026-09-25-replicar-una-foto-entra-a-la-home.
  // Es de TODOS los rubros (ADR-2026-09-13-replicar-una-foto-sale-del-rubro-belleza),
  // así que va sin condición, como Animar y Carrusel. Faltaba por lo mismo que Diseño:
  // esta lista se arma a mano y no sale de `MODES`, y el modo no se veía en el popup de
  // "Crear imágenes" de escritorio ni en la solapa Herramientas del teléfono. El preset
  // sale del catálogo (sin copia que mantener) y la card abre su ruta propia por
  // `MODE_KIND_TO_ROUTE`.
  const replicarFotoCard: PresetV2 | null =
    MODES.find((m) => m.path === 'replicar-foto')?.preset ?? null;
  // Card del modo "Mostrar cómo funciona" de Belleza —
  // ADR-2026-10-12-mostrar-como-funciona-belleza-en-escritorio. Sólo marcas de Belleza y
  // Cuidado Personal (el mismo `soloBelleza` del catálogo); el piso Pro/Ultra lo pone
  // `getModePlanLock` al armar `modosHome`. Faltaba por lo mismo que Diseño y Replicar: esta
  // lista se arma a mano y el modo no llegaba al popup de Catálogos de escritorio (el sheet
  // del teléfono sí lo tenía, porque sale de `MODES`).
  const mostrarComoFuncionaBellezaCard: PresetV2 | null = esRubroBelleza(rubroMarcaActiva)
    ? (MODES.find((m) => m.path === 'mostrar-como-funciona-belleza')?.preset ?? null)
    : null;
  // Card del modo "Videos cortos" — ADR-2026-10-31-videos-cortos-en-la-home-de-escritorio. Es de todos los
  // rubros, así que va sin condición; el piso Pro/Ultra lo pone `getModePlanLock` al armar `modosHome`. Faltaba por lo
  // mismo que Diseño, Replicar y Mostrar cómo funciona: esta lista se arma a mano y el modo, aunque salió de `devOnly`,
  // no llegaba al popup de Catálogos de escritorio. El preset sale del catálogo y la card abre su ruta propia por
  // `MODE_KIND_TO_ROUTE`.
  const videosCortosCard: PresetV2 | null = MODES.find((m) => m.path === 'videos-cortos')?.preset ?? null;
  const creadorVideoCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'creador-video-card',
        title: 'Creador de video',
        description: `Describí el video que querés y Atlas lo genera. Sumá un producto, un ${palabraModelo.singular} o una foto como referencia.`,
        imageUrl: '/images/modo_asistente/modo_asistente_poster.jpg',
        imageUrlFull: '/images/modo_asistente/modo_asistente_poster.jpg',
        kind: 'creador-video' as const,
        outputType: 'video' as const,
        creditosPorUnidad: 1,
        unidadConsumo: 'segundo' as const,
      }
    : null;
  // Card del modo "Anuncio" — quinta card del design-row. El user elige
  // producto(s), después configura formato + 3 campos de texto (título,
  // descripción, llamada a acción) con opción de "automático" o de
  // quitar cada uno, y hasta 4 colores (default = paleta del
  // brand_profile activo). El backend / costo todavía no están wireados.
  const anuncioCard: PresetV2 | null = isDesignRubro
    ? {
        id: 'anuncio-card',
        title: 'Anuncio',
        description:
          'Texto, colores y formato — armá un anuncio listo para publicar.',
        // Carátula del row de modos. Era un placeholder prestado de modelo; ahora es
        // la foto propia del modo (ADR-2026-08-04-anuncio-va-con-foto-y-sin-video),
        // la misma que sirve `MODES` para las otras superficies. Es la GENÉRICA: la del
        // aviso de mueble quedó para Hogar y Decoración y la pisa `PORTADA_POR_RUBRO`
        // en las superficies que dibujan la carátula
        // (ADR-2026-08-05-la-portada-de-anuncio-es-de-hogar).
        imageUrl: '/images/modo_anuncio/modo_anuncio_portada.jpg',
        // Hero dedicado del detalle del modo anuncio (mobile + desktop).
        imageUrlFull: '/images/modo_anuncio/modo_anuncio_hero.png',
        kind: 'anuncio' as const,
        creditosPorUnidad: 1,
        unidadConsumo: 'imagen' as const,
      }
    : null;
  // Card del modo "Video con influencer". Pantalla 1 = selección de
  // productos (igual que los otros modos); pantalla 2 = el mismo layout de
  // plantilla-video del ConfigurePanel + la sección "Gancho"/"Voz y guion"
  // vía `showGancho`. Visible para TODOS los planes (ADR-561): la
  // restricción es al GENERAR — `checkGenerationGuards('video')` muestra el
  // VideoPlanRestrictionModal si el plan no es Pro/Ultra.
  const videoInfluencerCard: PresetV2 | null =
    VIDEO_INFLUENCER_ENABLED && isDesignRubro
      ? {
          id: 'video-influencer-card',
          title: 'Video con influencer',
          description:
            'Generá un video de tu producto presentado por un influencer IA, con un gancho que enganche en los primeros segundos.',
          imageUrl: '/images/modo_video_ugc/modo_video_ugc_miniatura.png',
          imageUrlFull: '/images/modo_video_ugc/modo_video_ugc_miniatura.png',
          // Poster landscape para la carátula en video del hero desktop.
          poster: '/images/modo_video_ugc/modo_video_ugc_caratula_desktop.jpg',
          kind: 'video-influencer' as const,
          outputType: 'video' as const,
          creditosPorUnidad: 1,
          unidadConsumo: 'segundo' as const,
        }
      : null;
  // Colección "E-commerce" — cada pose del catálogo se renderiza como
  // una card con `kind: 'design'` (legacy single-pose flow). Cuando el
  // user tapea una card, abre el sheet del preset con esa pose como
  // hero. Solo se muestra para rubros que califican (isDesignRubro) y
  // si hay poses cargadas. Se inserta entre el design-row (modos) y
  // las categorías de templates de la DB.
  // Orden custom de las poses dentro del row de E-commerce:
  //   1. cuerpo_completo
  //   2. plano_medio
  //   3. resto (recomendado, sin categoría, etc.)
  //   4. detalle_close-up (al final)
  // Pedido del producto para guiar al user de "general → particular".
  const ecommerceCategoryRank = (cat: string | null): number => {
    if (cat === 'cuerpo_completo') return 0;
    if (cat === 'plano_medio') return 1;
    if (cat === 'detalle_close-up') return 3;
    return 2;
  };
  const ecommercePosesCollection: CollectionV2 | null =
    isDesignRubro && allPoses.length > 0
      ? {
          id: 'e-commerce',
          title: 'Fondo blanco',
          // "Fondo blanco" no es una categoría de la base: se arma acá con las poses, así
          // que su descripción también se escribe acá. Va con el mismo criterio que las
          // de `template_categorias`: menos de 100 caracteres
          // (ADR-2026-08-03-descripciones-para-las-categorias-sin-una).
          subtitle:
            'Tu producto sobre fondo blanco, en distintas poses y planos, listo para el e-commerce.',
          presets: [
            ...[...allPoses]
              .sort(
                (a, b) =>
                  ecommerceCategoryRank(a.categoria) -
                  ecommerceCategoryRank(b.categoria),
              )
              .map(
                (p): PresetV2 => ({
                  id: `pose-${p.id}`,
                  title: p.nombre,
                  imageUrl: p.imageUrl,
                  imageUrlFull: p.imageUrl,
                  kind: 'design' as const,
                  personCount: p.cantidadPersonas,
                }),
              ),
            // Vistas "solo producto" (mismo catálogo que Catálogo IA) al
            // final del row — cards kind 'design-producto'.
            ...buildVistasSoloProductoPresets(),
          ],
        }
      : null;
  // Flag "promover Imagen personalizada": viaja en el navigation state
  // desde los CTAs "Seguir generando"/"Listo" de la pantalla de resultados
  // cuando la tanda se generó en modo AUTOMÁTICO. Se captura una vez al
  // montar (useState initializer, sobrevive al clear de abajo) y se limpia
  // del history para que un refresh o un back a esta entrada no lo
  // re-aplique. Efecto: la fila de modos muestra modelo primero (primer
  // slide del hero mobile + primera card del row desktop), solo esa visita.
  const [promoteModeloFirst] = useState<boolean>(() => {
    try {
      const navState = window.history.state?.usr as
        | { promoteModeloFirst?: boolean }
        | undefined;
      return navState?.promoteModeloFirst === true;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!promoteModeloFirst) return;
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Orden de la fila: [auto, modelo, catalogo-ia, catalogo-muebles,
  // infografia, video-influencer, asistente, anuncio]. Con
  // `promoteModeloFirst`, modelo pasa al frente: [modelo, auto, …]. Cada card
  // decide sola si existe (rubro / plan / marca), así que acá solo se
  // concatenan las que quedaron.
  const designRowPresets: PresetV2[] = [
    ...(promoteModeloFirst
      ? [
          ...(designModeloCard ? [designModeloCard] : []),
          ...(designAutoCard ? [designAutoCard] : []),
        ]
      : [
          ...(designAutoCard ? [designAutoCard] : []),
          ...(designModeloCard ? [designModeloCard] : []),
        ]),
    // Fotos de producto va pegado a los dos modos de imagen suelta: es el
    // mismo gesto (una generación puntual) pero sin modelo.
    ...(fotosProductoCard ? [fotosProductoCard] : []),
    ...(catalogoIACard ? [catalogoIACard] : []),
    // Va justo después del Catálogo IA: son hermanos conceptuales y
    // conviene que se lean juntos en el row.
    ...(catalogoMueblesCard ? [catalogoMueblesCard] : []),
    // Infografías va pegado al catálogo de muebles: comparten flujo,
    // motor y público (marcas de Hogar y Decoración).
    ...(infografiaCard ? [infografiaCard] : []),
    ...(videoInfluencerCard ? [videoInfluencerCard] : []),
    // Animar imagen va pegado al video con influencer: son los dos modos que
    // devuelven un video.
    animarImagenCard,
    // Carrusel va pegado a Animar: son los dos modos que parten de una imagen que
    // el usuario ya tiene (subida o generada) en vez de un producto del catálogo.
    carruselCard,
    // Replicar una foto, al lado: también parte de una foto que trae el usuario (la
    // escena a copiar), aunque le sume un producto del catálogo.
    ...(replicarFotoCard ? [replicarFotoCard] : []),
    ...(mostrarComoFuncionaBellezaCard ? [mostrarComoFuncionaBellezaCard] : []),
    ...(videosCortosCard ? [videosCortosCard] : []),
    // "Crear desde cero" ya NO va en la tira —
    // ADR-2026-09-13-crear-desde-cero-sale-de-las-listas. Acá iba `asistenteCard`.
    //
    // Misma trampa que con Anuncio, anotada abajo: esta lista se arma a mano y no sale de
    // `MODES`, así que el `ocultoEnListas` del catálogo no la alcanza y hay que sacarlo
    // acá también. `asistenteCard` se sigue calculando más arriba porque `CARD_POR_KIND`
    // la busca por `kind` para que el deep-link `/modo/asistente` abra la pantalla.
    //
    // `modosDeVertiente` además lo repetía en la familia Videos leyendo esta misma
    // colección; esa entrada se sacó ahí.
    ...(promptLibreCard ? [promptLibreCard] : []),
    ...(disenoIndumentariaCard ? [disenoIndumentariaCard] : []),
    ...(creadorVideoCard ? [creadorVideoCard] : []),
    // "Anuncio" ya NO va en la tira —
    // ADR-2026-08-20-anuncio-sale-de-las-listas-sin-salir-del-catalogo.
    //
    // Esta lista se arma a mano y no sale de `MODES`, así que el flag `ocultoEnListas`
    // del catálogo no la alcanza: es la quinta superficie y hay que sacarlo acá.
    // `anuncioCard` se sigue calculando más arriba porque de él salen el hero de mobile
    // y el visor de modo, que no son listas de descubrimiento.
    //
    // El modo sigue vivo: lo abren el atajo de "Ediciones rápidas", los botones del
    // visor y del historial, y el deep-link `/interfaz-v2/modo/anuncio`.
  ];
  // Las plantillas las ve SÓLO el rubro Moda — ADR-2026-08-03-plantillas-solo-para-moda,
  // que al mergear le gana al `!esMarcaHogarDeco` de `principal`
  // (ADR-2026-08-05-plantillas-solo-moda-gana-al-merge). Los dos apuntaban a lo mismo
  // desde lados opuestos: son escenas y poses con modelo, que no le sirven a un mueble
  // —ni a un plato, ni a un frasco de crema—. Esta regla es más estricta y cubre el
  // caso de Hogar y Decoración igual, así que no quedan dos condiciones discutiendo.
  //
  // Apaga TODO de una vez: la sección embebida de desktop, los rows de mobile, el
  // "Fondo blanco" de poses y el "Ver todo". Los datos se siguen fetcheando (el fetch
  // no depende de esto); acá se decide qué se renderiza.
  // `rubroMarcaActiva` y NO `brandRubro` — ADR-2026-08-21-plantillas-rebotaban-por-dos-marcas-activas.
  //
  // `brandRubro` resuelve la marca SOLO por `profiles.current_brand_id`; cuando está
  // en `null` (el 98,5% de las cuentas) se queda en `null` y el `??` cae al rubro de
  // la EMPRESA, salteándose la marca. El guard de `/plantillas` usa el criterio
  // canónico (`current_brand_id` → `is_default` → la primera, ver `useRubroMarca.ts`),
  // así que leía el rubro de la marca default. Con empresa Moda + marca default de
  // otro rubro, la home mostraba las filas y el "Ver más" rebotaba a la home.
  //
  // El `?? userRubro` SÍ se conserva (a diferencia del gate de Infografía): para
  // plantillas el fallback a empresa es parte de la regla (`lib/rubro.ts`) y es
  // exactamente lo que hace el guard (`activeBrand?.rubro ?? perfilEmpresa?.rubro`).
  const mostrarPlantillas = rubroVePlantillas(rubroMarcaActiva ?? userRubro);

  /**
   * ¿La home abre con el AGENTE o con los modos? —
   * ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente.
   *
   * Vale para las DOS homes (el `HomeMinimal` de escritorio y el cuerpo de mobile): el
   * pedido era que el teléfono se comportara igual, así que el rubro se lee una vez y
   * las dos ramas leen el mismo booleano.
   *
   * El rubro que manda es el MISMO que el de `mostrarPlantillas` —marca activa con
   * fallback a la empresa—, y no es casualidad: `homeAbreConElAgente` es la negación de
   * ese gate y de los otros dos (Belleza y Hogar y Decoración). Si acá se leyera otro
   * valor, una marca de Moda podría ver la home del Agente con la sección de plantillas
   * abajo, o al revés.
   */
  const abreConElAgente = homeAbreConElAgente(rubroMarcaActiva ?? userRubro);
  // La interfaz nueva también en el teléfono (ADR-2026-10-08-la-interfaz-nueva-llega-al-telefono):
  // el encabezado mobile pasa a ser el saludo y las cards por destino, en lugar del carrusel
  // de modos y del hero del Agente.
  const uiNuevaEnElTelefono = useUiNueva();
  const { gratisQueRecienEntra, cuentaCargando } = useShellMobile();
  /**
   * ¿Se le PRESENTA el Agente a esta marca? — ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final.
   *
   * La contracara EXACTA de `abreConElAgente`: los rubros sin catálogo propio ya abren
   * la home con el Agente arriba, y a los otros tres —Moda, Belleza y Hogar y
   * Decoración, que abren con sus modos— hay que mostrárselo de alguna forma. La barra
   * no despliega nada: es una puerta al Agente, con el pedido ya escrito.
   *
   * Mismo rubro que las otras decisiones de la home, por lo mismo: para que no puedan
   * contradecirse entre sí. Se dibuja en las DOS homes —la de escritorio la monta
   * `HomeMinimal`, la del teléfono se monta más abajo— porque el teléfono tiene que
   * comportarse igual.
   */
  const conPresentacionDelAgente = homeMuestraPresentacionDelAgente(
    rubroMarcaActiva ?? userRubro,
  );
  /** El piso de plan del Agente (Pro y Ultra — ADR-2026-09-14-el-agente-es-de-pro-y-ultra).
   *  Es el MISMO helper que usa la card del sheet de Generar de mobile. */
  const agenteBloqueado = !!getAgenteLock(userPlan);
  const displayCollections: CollectionV2[] = [
    ...(designRowPresets.length > 0
      ? [
          {
            id: 'design-row',
            title: 'Diseña tu imagen',
            subtitle: undefined,
            presets: designRowPresets,
          },
        ]
      : []),
    ...(mostrarPlantillas && ecommercePosesCollection
      ? [ecommercePosesCollection]
      : []),
    // Las dos variantes del modo comparten esta lista: `fetchedCollections` ya trae
    // las que le tocan al rubro (ver el fetch de arriba).
    ...(basePlantillas ? fetchedCollections : []),
  ];

  /* ── Sección Plantillas embebida (desktop) ────────────────────────────
     Acá vivía `plantillasChipsStuck`, en `false`: escondía el botón flotante
     "Contactanos" cuando la chip-bar de `HomeTemplatesSection` se volvía `fixed`. La
     sección se había ido de la home (ADR-2026-07-31-inicio-se-funde-interfaz-v2) y la
     constante se conservaba sólo porque el botón la leía. Sin el botón
     (ADR-2026-08-03-fuera-boton-flotante-contactanos) no queda ni una cosa ni la
     otra. */

  // ── Prompt bar ────────────────────────────────────────────────────────
  const [promptFocused, setPromptFocused] = useState(false);
  const [prompt, setPrompt] = useState('');
  const promptRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize del textarea cuando crece el contenido
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [prompt, promptFocused]);

  // ── Chat agent ───────────────────────────────────────────────────────
  // Le pasamos los productos REALES del usuario para que el agente
  // preseleccione IDs que existen en el catálogo (antes usaba mock).
  const chat = useV2Chat(shortName, userProducts);
  const [chatOpen, setChatOpen] = useState(false);

  // "Sorpréndeme": abre el chat con un flujo SCRIPTED (sin pegarle al
  // agente). Se ve como una conversación normal — user dice "No tengo
  // idea, sorpréndeme", el agente "responde" con texto pre-armado, y
  // se le muestran las tool cards de pick_product → confirm_generate.
  // El texto del agente varía si hace poco generó algo (sessionStorage),
  // así suena natural y no repetitivo.
  const LAST_SURPRISE_KEY = 'atlas-v2-last-surprise-ts';
  const handleSurpriseMe = () => {
    setChatOpen(true);
    setPrompt('');
    setPromptFocused(false);
    promptRef.current?.blur();

    let recentSurprise = false;
    try {
      const last = sessionStorage.getItem(LAST_SURPRISE_KEY);
      if (last) {
        const diffMin = (Date.now() - parseInt(last, 10)) / 1000 / 60;
        // < 10 minutos = "hace poco". Si pasó más, vamos con el texto
        // estándar.
        recentSurprise = diffMin < 10;
      }
    } catch {
      /* noop */
    }

    // Corre el script en background (la UI ya está abierta). El delay
    // chico antes del primer push asegura que el chat panel ya animó in.
    (async () => {
      await new Promise((r) => setTimeout(r, 250));
      chat.pushUserMessage('No tengo idea, sorpréndeme');

      await new Promise((r) => setTimeout(r, 350));
      chat.pushAssistantText(
        recentSurprise
          ? '¡Otra vamos! ¿Qué producto usamos esta vez?'
          : 'Perfecto. ¿Qué producto querés que use?',
      );

      await new Promise((r) => setTimeout(r, 200));
      const productResp = await chat.pushToolCard('pick_product', {
        message: 'Elegí un producto',
        allow_multi: false,
      });
      if (productResp?.cancelled) return;

      await new Promise((r) => setTimeout(r, 350));
      // Copy explica qué pasa al tocar Generar — el agente "se encarga
      // de pensar y generar". Si tenemos companyName lo usamos para que
      // se sienta personal; sino cae al fallback "tu marca".
      const brand = companyName || 'tu marca';
      chat.pushAssistantText(
        recentSurprise
          ? `Dale a generar y sigo armando imágenes para ${brand} que puedas subir a tus redes.`
          : `Ok, dale a generar entonces y yo me encargaré de pensar y generar imágenes para ${brand} que puedas subir a tus redes.`,
      );

      await new Promise((r) => setTimeout(r, 200));
      const lastFormat = (() => {
        try {
          // Un formato personalizado del chat se lee por su proporción estándar
          // (ADR-2026-10-20-formatos-personalizados): esta pantalla no los ofrece.
          return formatoEstandarDe(localStorage.getItem('atlas-v2-last-format')) || 'square';
        } catch {
          return 'square';
        }
      })();
      const confirmResp = await chat.pushToolCard('confirm_generate', {
        format: lastFormat,
        scene_summary: recentSurprise
          ? 'Una escena sorpresa nueva con el producto elegido'
          : 'Una escena sorpresa con el producto elegido',
      });
      if (confirmResp?.cancelled) return;

      // Persistir timestamp para que la próxima sorpresa sepa que el
      // user generó hace poco.
      try {
        sessionStorage.setItem(LAST_SURPRISE_KEY, String(Date.now()));
      } catch {
        /* noop */
      }

      await new Promise((r) => setTimeout(r, 200));
      chat.pushAssistantText('¡Listo! Estamos generando tu imagen.');
    })();
  };

  // Cierra el chat y resetea el historial. Es lo que pasa al tocar X o
  // el back mobile. Volvemos a la home V2.
  const closeChat = () => {
    setChatOpen(false);
    setMobileBackOverride(null);
    setMobileTitleOverride(null);
    // Reset del chat con un pequeño delay para no ver el wipe del historial
    // mientras la animación de salida del panel termina.
    setTimeout(() => chat.reset(), 350);
  };

  // Cuando se abre el chat, instalamos el back override mobile para que
  // el botón "<" de la top bar también lo cierre.
  useEffect(() => {
    if (chatOpen) {
      setMobileBackOverride(() => () => closeChat());
      setMobileTitleOverride(' ');
      return () => {
        setMobileBackOverride(null);
        setMobileTitleOverride(null);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen]);

  const handleGeneratePrompt = () => {
    if (!prompt.trim()) return;
    // Abre el chat fullscreen + manda el primer mensaje al agente. La
    // animación de "expand" del prompt bar es el panel fullscreen
    // apareciendo con fade + translateY.
    const firstMsg = prompt.trim();
    chat.sendMessage(firstMsg);
    setChatOpen(true);
    setPrompt('');
    setPromptFocused(false);
    promptRef.current?.blur();
  };

  // ── Preset flow ───────────────────────────────────────────────────────
  // Initializer sincrónico: si el user entró desde ColeccionDetalle /
  // InterfazV2Plantillas con `{ presetData, collectionPresets }` en el
  // navigation state, abrimos el sheet en el PRIMER paint. Así no hay
  // ni un frame de home visible, ni hace falta overlay blanco — el
  // sheet sustituye al home directamente.
  const initialNavPreset = (() => {
    // Se va a Redes (Prompt libre / Creador de video en escritorio,
    // ADR-2026-10-07-prompt-libre-y-creador-abren-crear): el sheet viejo que trae el
    // estado de navegación (`/modos`, el drawer de Mi marca, `abrir_modo` del agente) no
    // se abre ni un frame — todo lo que arranca de este preset arranca vacío.
    if (destinoCrear) return null;
    try {
      const navState = window.history.state?.usr as
        | { presetData?: PresetV2; collectionPresets?: PresetV2[] }
        | undefined;
      return navState?.presetData ?? null;
    } catch {
      return null;
    }
  })();
  const initialNavPresetList = (() => {
    try {
      const navState = window.history.state?.usr as
        | { presetData?: PresetV2; collectionPresets?: PresetV2[] }
        | undefined;
      return Array.isArray(navState?.collectionPresets)
        ? (navState!.collectionPresets as PresetV2[])
        : [];
    } catch {
      return [];
    }
  })();
  /**
   * Productos ya elegidos afuera — ADR-2026-08-04-el-producto-entra-con-el-modo.
   *
   * Los manda el sheet de modos de "Editar marca", donde el usuario eligió PRIMERO el
   * producto y después el modo: pedirle el producto otra vez al entrar sería repetir
   * el paso que acaba de dar.
   *
   * Viajan como objetos y no como ids porque acá se leen SINCRÓNICAMENTE, en el
   * initializer: son el valor inicial de `selectedProducts` y lo que decide el paso en
   * el que abre el panel. Resolver ids contra `userProducts` —lo que hace el restore
   * post-generación— llega tarde para las dos cosas y dejaría ver el paso de elegir
   * producto antes de saltearlo.
   */
  const initialNavProducts = (() => {
    try {
      const navState = window.history.state?.usr as
        | { productosPreseleccionados?: MockProductV2[] }
        | undefined;
      return Array.isArray(navState?.productosPreseleccionados)
        ? (navState!.productosPreseleccionados as MockProductV2[])
        : [];
    } catch {
      return [];
    }
  })();
  /**
   * "Abrí esto sin pasar por el preview" — lo manda la pantalla de Plantillas
   * (ADR-2026-09-07-plantillas-abre-directo-en-el-paso-de-producto).
   *
   * Se lee SINCRÓNICO, como los otros tres de acá: decide el paso con el que abre el
   * panel, y leerlo en un efecto llegaría tarde —se vería el preview un frame antes de
   * saltearlo—.
   */
  /**
   * La plantilla se abrió desde el modo Redes (ADR-2026-10-03-redes-las-migas-vuelven-a-redes):
   * entonces "Plantillas" en las migas vuelve a Redes, a su sección de plantillas, y no a
   * la grilla de Plantillas. Se lee al montar, como `saltearPreview`.
   */
  const initialNavDesdeRedes = (() => {
    try {
      return (window.history.state?.usr as { desdeRedes?: boolean } | undefined)?.desdeRedes === true;
    } catch {
      return false;
    }
  })();
  const initialNavSaltearPreview = (() => {
    try {
      const navState = window.history.state?.usr as
        | { saltearPreview?: boolean }
        | undefined;
      return navState?.saltearPreview === true;
    } catch {
      return false;
    }
  })();
  /**
   * La creación desde la que se abrió el anuncio —
   * ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo.
   *
   * Mismo canal y misma urgencia que los dos de acá abajo: se lee sincrónico porque de
   * él dependen el paso en el que abre el panel y lo que se muestra como sujeto. Trae
   * `url` para pintarla e `id` porque es lo que viaja al backend en vez del producto.
   */
  const initialNavImagenBase = (() => {
    try {
      const navState = window.history.state?.usr as
        | { imagenBase?: { id: string; url: string } }
        | undefined;
      const img = navState?.imagenBase;
      return img && img.id && img.url ? img : null;
    } catch {
      return null;
    }
  })();

  /**
   * Modelo ya elegido afuera — ADR-2026-08-04-desde-un-modelo-se-elige-el-modo.
   *
   * El gemelo del de arriba, para el otro camino de entrada de "Marca": tocar un
   * modelo de la grilla y elegir el modo. Viaja por el mismo canal —el `state` de la
   * navegación— y se lee igual de sincrónico, porque es el valor inicial de
   * `modelSelection` y el efecto de defaults sólo completa lo que sigue en `null`.
   *
   * Es una `ModelSelection` armada ya en el sheet y no el id del modelo: resolverlo
   * contra `userModels` es asíncrono, y hasta que llegara el efecto de defaults ya le
   * habría puesto el modelo de la última generación.
   */
  const initialNavModel = (() => {
    try {
      const navState = window.history.state?.usr as
        | { modeloPreseleccionado?: ModelSelection }
        | undefined;
      const sel = navState?.modeloPreseleccionado;
      return sel && typeof sel === 'object' && typeof sel.mode === 'string'
        ? (sel as ModelSelection)
        : null;
    } catch {
      return null;
    }
  })();
  const [selectedPreset, setSelectedPreset] = useState<PresetV2 | null>(
    initialNavPreset,
  );
  // Para deep-link (InterfazV2Plantillas / ColeccionDetalle) arrancamos
  // `animated = true` así el sheet aparece ya posicionado desde el
  // primer paint, sin la animación de slide-up. Si dejáramos `false`,
  // el sheet entra desde abajo durante ~300ms y en esos frames se ve la
  // home detrás (el "flash" reportado). Para clicks dentro de la home
  // arrancamos en `false` y el `useEffect` de selectedPreset dispara la
  // animación de entrada normal.
  const [presetSheetAnimated, setPresetSheetAnimated] = useState(
    !!initialNavPreset,
  );

  /**
   * La plantilla abierta es de BELLEZA — ADR-2026-09-14-la-plantilla-de-belleza-se-genera-con-un-producto.
   *
   * Gatea dos cosas: la selección de UN solo producto (el sheet lo baja a `singleSelect`)
   * y el formato 9:16 por defecto, más abajo en el efecto de auto-relleno.
   *
   * El rubro es `rubroDePlantillas` —marca activa con fallback a la empresa—, el mismo
   * que decide CUÁL de las dos pantallas de plantillas ve la marca. Como son excluyentes,
   * `kind === 'template'` + ese rubro no deja ambigüedad sobre cuál está abierta. Y es
   * `rubroMarcaActiva` y no `brandRubro` por ADR-2026-08-21-plantillas-rebotaban-por-dos-marcas-activas.
   */
  const esPlantillaDeBelleza =
    selectedPreset?.kind === 'template' && esRubroBelleza(rubroDePlantillas);

  // Barra superior de créditos (ADR-2026-07-30-barra-superior-creditos-home-ancho, criterio de ADR-2026-07-30-solo-criterio-saldo-toda-app). Ya NO depende del
  // plan ni de `kind`: sale para cualquiera que esté sin créditos o por debajo del
  // 5% de la capacidad de SU plan. No va con un preset abierto: ahí la home está
  // oculta y la barra quedaría flotando arriba de otra pantalla.
  //
  // Ya no hay flag de dev para forzarla (supersede ADR-2026-07-30-flag-dev-forzar-barra-creditos):
  // el panel CONTEXTO DEV se eliminó — ADR-2026-08-05-fuera-los-overrides-de-dev.
  const estadoCreditos = estadoDeCreditos(
    homeBanners.freeImagesRemaining,
    homeBanners.creditsTotal,
  );
  const estadoTopBar: "sin" | "pocos" =
    estadoCreditos === "ok" ? "sin" : estadoCreditos;
  const mostrarTopBar =
    estadoCreditos !== "ok" &&
    !topBarDescartada &&
    !selectedPreset &&
    // El shell nuevo pinta la suya, con los mismos datos y el mismo CTA
    // (ADR-2026-07-31-shell-provee-flag-hospedaje). En desktop las dos se apilaban.
    !dentroDelShell;

  /**
   * El alto de la barra de créditos de mobile, publicado como
   * `--barra-creditos-mobile` — ADR-2026-08-04-la-pastilla-de-creditos-va-abajo-del-cartel.
   *
   * La barra es `fixed` arriba de todo, así que no empuja nada; el header mobile —donde
   * vive la pastilla de créditos— se corre solo leyendo esta variable. Se MIDE y no se
   * escribe a mano porque el alto depende del texto: "Te quedan 2 créditos…" con
   * descuento envuelve en dos líneas y sin descuento en una.
   *
   * Sólo en mobile: de `sm:` para arriba la barra vuelve al flujo y no hay nada que
   * correr, así que la variable vale 0 y el header queda como estaba.
   */
  const refBarraCreditos = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const raiz = document.documentElement;
    const limpiar = () => raiz.style.removeProperty('--barra-creditos-mobile');
    const el = refBarraCreditos.current;
    if (!mostrarTopBar || !el) {
      limpiar();
      return;
    }
    const medir = () => {
      const enMobile = window.matchMedia('(max-width: 639.98px)').matches;
      raiz.style.setProperty(
        '--barra-creditos-mobile',
        enMobile ? `${el.offsetHeight}px` : '0px',
      );
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      observador.disconnect();
      window.removeEventListener('resize', medir);
      limpiar();
    };
  }, [mostrarTopBar]);
  /**
   * La tarjeta al lado del saludo — ADR-2026-07-31-banners-desktop-solo-upsell-cede.
   *
   * Ese ADR hacía que el **upsell** cediera ante la barra superior: los dos hablaban
   * de créditos y del descuento, así que juntos decían lo mismo dos veces. Ya no hay
   * nada que ceder — el upsell se borró y la barra ocupó su lugar
   * (ADR-2026-08-03-barra-creditos-reemplaza-upsell)—, y los tres que quedan
   * —los pasos de configuración de la cuenta— son temas distintos de los créditos:
   * conviven con la barra sin repetirse.
   */
  const bannerLateral = !!homeBanners.kind;

  // ── Preservar scroll de la home al abrir/cerrar un preset ─────────────
  // Con un preset abierto la home devuelve `null` (ahorro de GPU de los
  // videos) → el documento se achica y el scroll se pierde; al cerrar, la home
  // se re-renderiza EN EL TOPE. Trackeamos la última posición REAL de la home
  // (mientras NO hay preset abierto) y la restauramos al cerrar, reintentando
  // hasta que el documento vuelva a crecer lo suficiente.
  const lastHomeScrollRef = useRef(0);
  const presetOpenRef = useRef(false);
  useEffect(() => {
    const onScroll = () => {
      if (!presetOpenRef.current) lastHomeScrollRef.current = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useLayoutEffect(() => {
    const wasOpen = presetOpenRef.current;
    const isOpen = !!selectedPreset;
    presetOpenRef.current = isOpen;
    // Solo en la transición abierto → cerrado (la home se re-montó en el tope).
    if (!wasOpen || isOpen) return;
    const y = lastHomeScrollRef.current;
    if (y <= 0) return;
    let cancelled = false;
    let raf = 0;
    const start = Date.now();
    const step = () => {
      if (cancelled) return;
      window.scrollTo({ top: y });
      if (Math.abs(window.scrollY - y) > 2 && Date.now() - start < 1000) {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [selectedPreset]);
  // Flag: si el user llegó por deep-link (desde ColeccionDetalle o
  // InterfazV2Plantillas), al cerrar el sheet hacemos `navigate(-1)`
  // para volver a la categoría — no a la home de V2. Sin esto el
  // closePresetSheet solo cierra el sheet y deja al user mirando el
  // home, perdiendo su contexto.
  const cameFromDeepLinkRef = useRef(!!initialNavPreset);
  // Scroll de la home al abrir un preset in-state (plantillas embebidas
  // en desktop / rows en mobile): el contenedor de la home pasa a
  // `hidden`, el contenido del scroller se acorta y su scroll colapsa a
  // 0 — al cerrar el sheet el user quedaba arriba de todo. Guardamos la
  // posición en los handlers de apertura y la restauramos cuando
  // `selectedPreset` vuelve a null. Doble rAF: el primero deja pintar
  // la home re-mostrada; el segundo asegura que el layout ya midió.
  // OJO: siempre vía utils/appScroll — en desktop el scroller es el
  // panel `.content-panel-clip`, no el window (window.scrollY da 0).
  const homeScrollBeforeSheetRef = useRef<number | null>(null);
  const hadSelectedPresetRef = useRef(!!initialNavPreset);
  useEffect(() => {
    const hadPreset = hadSelectedPresetRef.current;
    hadSelectedPresetRef.current = !!selectedPreset;
    if (!hadPreset || selectedPreset) return;
    const y = homeScrollBeforeSheetRef.current;
    homeScrollBeforeSheetRef.current = null;
    if (y == null || y <= 0) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAppScrollY(y);
      });
    });
  }, [selectedPreset]);
  // El flujo de plantilla ahora soporta multi-selección (lista de productos).
  // Mantenemos array vacío como "ninguno seleccionado todavía".
  const [selectedProducts, setSelectedProducts] =
    useState<MockProductV2[]>(initialNavProducts);
  // Un importado que espera su análisis se analiza apenas se elige, en silencio (ADR-2026-10-05-analisis-a-pedido).
  useAnalisisAPedido(selectedProducts);
  /** La creación base del anuncio (ver `initialNavImagenBase`). */
  const [imagenBase, setImagenBase] = useState(initialNavImagenBase);
  // "Sin producto" (ADR-673) — solo el modo "Video con influencer": la
  // influencer habla a cámara sin mostrar nada. NO se modela como un producto
  // fantasma en `selectedProducts` (rompería todo lo que mapea ids a variantes):
  // es un flag aparte, y `selectedProducts` queda vacío.
  const [sinProducto, setSinProducto] = useState<boolean>(false);
  // Autogeneración del diálogo (ADR-674): al entrar a configurar con un producto
  // elegido pedimos el guion y lo dejamos escrito en el textarea. `dialogoForRef`
  // guarda la variante ya resuelta para no re-pedirlo al volver a esta pantalla
  // (pisaría lo que el usuario haya editado); cambiar de producto sí re-dispara.
  const [dialogoLoading, setDialogoLoading] = useState<boolean>(false);
  const [dialogoError, setDialogoError] = useState<string | null>(null);
  const dialogoForRef = useRef<string | null>(null);
  // `panelStep` controla qué muestra el panel lateral / bottom sheet:
  //   • choose-product → upload + ver productos (estado inicial)
  //   • pick-product   → grid de productos del usuario (inline en el panel)
  //   • configure      → opciones de Pose/Formato + Generar (después de elegir)
  // Antes había un ProductPickerSheet separado para `pick-product`; ahora
  // todo vive en el mismo panel.
  const [panelStep, moverPanelStep] = useState<
    | 'preview'
    | 'choose-product'
    | 'pick-product'
    | 'upload-product'
    | 'select-vistas'
    | 'configure'
    | 'asistente'
    // Prompt libre y Creador de video: sheet propio, como el asistente
    // (ADR-2026-09-07-modo-prompt-libre, ADR-2026-09-07-modo-creador-video).
    | 'prompt-libre'
    | 'creador-video'
  >(() => {
    // Deep-link: si entramos con un preset preseteado, arrancamos en
    // 'choose-product' para los modos "design" virtuales (no tienen
    // preview útil) y 'preview' para template / design (E-commerce).
    if (initialNavPreset) {
      const k = initialNavPreset.kind;
      // `esAsistente` cubre 'asistente' y la variante 'asistente-v2'.
      if (esAsistente(k)) return 'asistente';
      if (k === 'prompt-libre') return 'prompt-libre';
      if (k === 'creador-video') return 'creador-video';
      // Con el producto ya elegido afuera (ADR-2026-08-04-el-producto-entra-con-el-modo)
      // arrancamos donde dejaría `handleConfirmProducts`, que es lo que habría corrido
      // si el usuario lo hubiera elegido acá: los modos de catálogo siguen con sus
      // vistas, el resto va derecho a configurar.
      //
      // Infografía queda afuera del salto a propósito: no tiene picker de vistas y su
      // camino pasa por presetear una (ver `handleConfirmProducts`). Hoy no llega por
      // acá —el modo está apagado por flag y el sheet no lo ofrece—, así que se deja
      // en el flujo normal en vez de duplicar ese preseteo en un initializer.
      // Con una creación de base no hay producto que elegir: el sujeto ya está
      // (ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo).
      if (initialNavImagenBase) return pasoDeConfiguracion(k);
      if (initialNavProducts.length > 0 && k !== 'infografia') {
        // El paso lo decide `pasoDeConfiguracion` y no un `'configure'` literal: los
        // modos de una sola pantalla se configuran en `choose-product`, y mandarlos a
        // `configure` los abría con el panel apilado anterior al rediseño
        // (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar). Pasaba con
        // Modelo abierto desde un producto de Mi Marca.
        return pasoDeConfiguracion(k);
      }
      // Plantillas manda `saltearPreview`: se entra por una grilla donde ya se vio y se
      // eligió la plantilla, así que el preview repetía lo que el usuario acababa de
      // tocar (ADR-2026-09-07-plantillas-abre-directo-en-el-paso-de-producto). La misma
      // plantilla abierta desde una fila de la home NO lo manda y conserva su preview.
      //
      // Abre en el paso de PRODUCTO y no directo en la configuración
      // (ADR-2026-09-08-la-plantilla-elige-producto-antes-de-configurar, que supersede
      // ADR-2026-09-08-la-plantilla-abre-directo-en-la-configuracion): con la plantilla
      // ya elegida, lo primero que falta es sobre qué producto aplicarla, y esa elección
      // merece la pantalla entera —la grilla de productos a la izquierda, la plantilla a
      // la derecha— en vez de resolverse en un panel lateral desde la configuración.
      // Confirmado el producto, `handleConfirmProducts` pasa a `configure`, que sigue
      // siendo la pantalla de siempre: Plantilla · Producto · Modelo y el resto.
      if (initialNavSaltearPreview) return 'choose-product';
      return esDesignModelo(k) ||
        k === 'design-auto' ||
        k === 'fotos-producto' ||
        esCatalogoIA(k) ||
        k === 'catalogo-muebles' ||
        k === 'infografia' ||
        k === 'anuncio' ||
        k === 'video-influencer'
        ? 'choose-product'
        : 'preview';
    }
    return 'preview';
  });
  /**
   * El kind del modo abierto, leíble desde un callback estable. Se escribe en cada
   * render (mismo patrón que `handleBackToGridRef` en la pantalla de resultados) para que
   * `setPanelStep` no tenga que rehacerse —y re-disparar effects— cada vez que cambia el
   * preset.
   */
  const kindDelModoRef = useRef<string | null>(null);

  /**
   * Mover el paso del panel — LA red de contención de "no se muestra la interfaz vieja"
   * (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
   *
   * Los modos de una sola pantalla (Modelo, Automática) no tienen `configure`: ese paso
   * dibuja el panel apilado anterior al rediseño, que sigue montado en el shell para los
   * modos de dos pasos. Mandarlos ahí no rompe nada —no hay error, no hay pantalla en
   * blanco—, así que un camino que lo haga se descubre recién cuando un usuario lo cuenta.
   * Ya pasó tres veces por tres caminos distintos (el restore del "Atrás", el modo abierto
   * con el producto puesto, "Ajustar y regenerar").
   *
   * Por eso el corte no está en cada llamador sino acá: TODO cambio de paso pasa por esta
   * función, y `configure` en un modo de una sola pantalla se lee como lo que quiso decir
   * — su pantalla, `choose-product`.
   */
  const setPanelStep = useCallback(
    (paso: PasoDelPanel | ((prev: PasoDelPanel) => PasoDelPanel)) => {
      moverPanelStep((prev) =>
        pasoSeguro(
          typeof paso === 'function' ? paso(prev) : paso,
          kindDelModoRef.current,
        ),
      );
    },
    [],
  );
  kindDelModoRef.current = selectedPreset?.kind ?? null;
  // State para el step 'upload-product' — archivos pickeados desde el
  // input y nombre que el user puede editar antes de confirmar.
  const [pendingProductFiles, setPendingProductFiles] = useState<File[]>([]);
  const [pendingProductName, setPendingProductName] = useState<string>('');
  const [uploadingProduct, setUploadingProduct] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Subida en bloque (catálogo): cuando el user sube 2+ archivos a la vez,
  // cada archivo es un producto distinto. Mientras corre el loop mostramos
  // un overlay de carga, sin la pantalla intermedia "Nuevo producto".
  const [bulkUploadingProducts, setBulkUploadingProducts] =
    useState<boolean>(false);
  // Lista de presets sobre los que se puede navegar en el step 'preview'.
  // Cuando el user toca un preset desde un row, prepop esta lista con los
  // presets de esa misma colección — así puede swipear / cambiar a otro
  // antes de comprometerse con "Utilizar".
  const [previewList, setPreviewList] = useState<PresetV2[]>(
    initialNavPresetList,
  );
  // Multi-select: cuando el user activa "Seleccionar varias" en preview,
  // junta varios templates de la misma colección. En `handleGenerateFinal`
  // estos ids se mandan como `template_id` array para que el backend
  // genere una imagen por cada plantilla. Solo aplica a kind 'template' —
  // los 'design' no soportan multi.
  const [multiPresetIds, setMultiPresetIds] = useState<string[]>([]);
  // Cantidad de imágenes a generar en modo 'design-auto'. Default 1.
  // El control con +/- se renderiza arriba del Generar en el
  // ConfigurePanel cuando el kind del preset es 'design-auto'. El
  // payload manda `cantidad_imagenes: autoImageCount`.
  const [autoImageCount, setAutoImageCount] = useState<number>(1);
  const AUTO_IMAGE_COUNT_MAX = 6;

  /**
   * Cuándo se aparta la barra inferior de mobile —
   * ADR-2026-08-04-el-modo-se-lleva-la-barra.
   *
   * En el inicio y en el PRIMER paso de un modo (elegir el producto) la barra queda:
   * el modo todavía es un lugar del que se sale cambiando de tab, y ese paso no tiene
   * ninguna acción fija abajo con la que competir — sólo dos botones en el flujo, que
   * le dejan el hueco (ver `ALTO_NAV_INFERIOR` en `PresetDetailSheet`).
   *
   * De ahí en adelante —la grilla de productos, configurar, el preview de plantillas,
   * el picker de vistas, el asistente— la pantalla monta su propia acción fija abajo
   * ("Confirmar", "Generar") o es una conversación a pantalla completa. La barra se
   * desliza afuera: dos barras apiladas dejaban el CTA a media pantalla, y una
   * navegación por encima de un flujo a medio hacer es una salida sin aviso.
   *
   * El shell es quien la monta, así que esto sólo levanta la bandera del contexto;
   * `NavInferiorMobile` la lee y se va con su animación.
   */
  const barraSeAparta =
    !!selectedPreset &&
    // El modo Modelo es UNA sola pantalla: su `panelStep` se queda en `choose-product`
    // de punta a punta (ADR-2026-08-21-modelo-v2-una-sola-columna), así que con la regla
    // de arriba la barra no se apartaba nunca. Y sí corresponde: esa pantalla monta su
    // propia acción fija abajo —los ajustes y el Generar—, que es exactamente el caso que
    // la regla describe.
    //
    // Catálogo y Fotos de producto se suman en su paso 1 —
    // ADR-2026-09-08-la-barra-inferior-no-vuelve-en-el-paso-1-del-modo. No es que monten
    // una acción fija: es que la barra estaba SÓLO en esa pantalla y desaparecía al paso
    // siguiente, y el tab "Generar" convivía a diez centímetros del CTA del modo
    // queriendo decir otra cosa. La salida del modo es el back de arriba, que sí está en
    // todas sus pantallas.
    (panelStep !== 'choose-product' ||
      esDesignModelo(selectedPreset.kind) ||
      esCatalogoIA(selectedPreset.kind) ||
      selectedPreset.kind === 'fotos-producto' ||
      selectedPreset.kind === 'design-auto');
  useEffect(() => {
    setMobileHideBottomNav(barraSeAparta);
    return () => setMobileHideBottomNav(false);
  }, [barraSeAparta, setMobileHideBottomNav]);

  // ── State del modo Anuncio ─────────────────────────────────────────
  // Cada campo textual del anuncio (Título, Descripción, Llamada a acción)
  // tiene 2 dimensiones:
  //   • `hidden` — el user descartó el campo (icono ojo). No va al anuncio.
  //   • `value`  — texto custom; si está vacío y `hidden` es false,
  //                queda implícito el modo "automático" (Atlas decide).
  // Reseteo total en `closePresetSheet` y en el cierre URL-driven.
  type AnuncioFieldState = {
    hidden: boolean;
    value: string;
    // Familia tipográfica elegida; '' = Automática (la IA elige).
    font: string;
  };
  const ANUNCIO_FIELD_DEFAULT: AnuncioFieldState = {
    hidden: false,
    value: '',
    font: '',
  };
  const [anuncioTitulo, setAnuncioTitulo] = useState<AnuncioFieldState>(
    ANUNCIO_FIELD_DEFAULT,
  );
  const [anuncioDescripcion, setAnuncioDescripcion] =
    useState<AnuncioFieldState>(ANUNCIO_FIELD_DEFAULT);
  const [anuncioCta, setAnuncioCta] = useState<AnuncioFieldState>(
    ANUNCIO_FIELD_DEFAULT,
  );
  // Hasta 4 hex strings — colores personalizados del anuncio. Si el array
  // está vacío, el AnuncioPanel cae al default del `brand_profile`
  // (primeros 4 colores de `brandColors`). Al tocar un slot se inicializa
  // a la copia del default antes de editarse, para que el user arranque
  // editando la paleta de la marca.
  const ANUNCIO_COLORS_MAX = 4;
  const [anuncioColors, setAnuncioColors] = useState<string[]>([]);
  // Default de tipografía de marca: cuando `brandFont` se resuelve, los 3
  // textos que sigan sin fuente elegida (`font === ''`) adoptan la de la
  // marca. One-shot (ref) para no pisar una elección posterior del user
  // — incluido si elige "Automática" a propósito. Los reset points
  // restablecen `font` a `brandFontRef.current` directamente.
  useEffect(() => {
    if (!brandFont || brandFontSeededRef.current) return;
    brandFontSeededRef.current = true;
    const seed = (s: AnuncioFieldState): AnuncioFieldState =>
      s.font ? s : { ...s, font: brandFont };
    setAnuncioTitulo(seed);
    setAnuncioDescripcion(seed);
    setAnuncioCta(seed);
  }, [brandFont]);

  // Cambio de marca EN VIVO. El sidebar / "Mi marca" disparan
  // `atlas:brand-changed` al cambiar la marca activa, pero InterfazV2
  // resuelve `current_brand_id` UNA sola vez al montar (effect de
  // `userCompanyId`). Sin esto la marca quedaba "pegada": el filtro de
  // productos Y los colores/fuente del anuncio seguían en la marca
  // anterior hasta re-entrar a la pantalla. Acá re-resolvemos la marca y
  // refrescamos `currentBrandId` (→ el effect de productos refetcha solo),
  // `brandColors` y `brandFont`. Mismo patrón que `useBrandProfile`.
  useEffect(() => {
    if (!userCompanyId) return;
    let alive = true;
    const onBrandChanged = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || !alive) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('current_brand_id')
        .eq('id', user.id)
        .single();
      if (!alive) return;
      const newBrandId =
        ((profile as any)?.current_brand_id as string) || null;
      // Actualiza el filtro de productos (el effect de productos depende de
      // `currentBrandId` y refetcha la marca nueva + huérfanos).
      setCurrentBrandId(newBrandId);
      // Colores + fuente: si no hay marca activa, mismo fallback que el effect
      // de montaje → marca DEFAULT de la empresa (si no, queda vacío).
      let colorsBrandId = newBrandId;
      if (!colorsBrandId) {
        const { data: def } = await supabase
          .from('brand_profiles')
          .select('id')
          .eq('id_company', userCompanyId)
          .eq('is_default', true)
          .eq('is_draft', false)
          .maybeSingle();
        if (!alive) return;
        colorsBrandId = ((def as any)?.id as string) || null;
      }
      let nextColors: string[] = [];
      let nextFont = '';
      if (colorsBrandId) {
        const { data: brand } = await supabase
          .from('brand_profiles')
          .select('colors, fonts')
          .eq('id', colorsBrandId)
          .maybeSingle();
        if (!alive) return;
        nextColors = normalizeBrandHexColors((brand as any)?.colors);
        nextFont = resolveBrandFont((brand as any)?.fonts);
      }
      setBrandColors(nextColors);
      // Re-siembra de la fuente: los textos sin tocar (font '' o la fuente
      // vieja de marca) adoptan la nueva; los que el user eligió a mano se
      // respetan. `anuncioColors` NO se toca: si el user editó colores se
      // conservan; si están vacíos, la grilla cae sola a la paleta nueva.
      const oldFont = brandFontRef.current;
      const reseed = (s: AnuncioFieldState): AnuncioFieldState =>
        s.font === '' || s.font === oldFont ? { ...s, font: nextFont } : s;
      setAnuncioTitulo(reseed);
      setAnuncioDescripcion(reseed);
      setAnuncioCta(reseed);
      setBrandFont(nextFont);
    };
    window.addEventListener('atlas:brand-changed', onBrandChanged);
    return () => {
      alive = false;
      window.removeEventListener('atlas:brand-changed', onBrandChanged);
    };
  }, [userCompanyId]);

  // ── Restore post-generación (Atrás desde Imágenes Generadas) ─────────
  // En `handleGenerateFinal` antes de navegar persistimos el state
  // completo de la UI a `sessionStorage.atlas-v2-restore`. Al mount
  // leemos ese blob, aplicamos los setters "directos" (modelo, pose,
  // fondo, formato, etc) y guardamos los IDs que requieren lookup
  // async en este ref. Dos effects separados (uno por
  // `fetchedCollections`, otro por `userProducts`) resuelven el
  // preset abierto y los productos seleccionados cuando los catálogos
  // llegan. El blob es single-shot: se borra al leerlo.
  const pendingRestoreRef = useRef<{
    presetId?: string | null;
    selectedProductIds?: string[];
    multiSlotProductIds?: Record<string, string[]>;
  } | null>(null);
  /**
   * El modo se está reabriendo con una configuración YA HECHA (el "Atrás" desde los
   * resultados, "Seguir generando"). Lo mira el shell de Modelo para no abrir el paso de
   * elegir productos: los productos están, pero se resuelven contra el catálogo en otro
   * efecto, así que cuando el sheet monta la lista todavía está vacía y ese paso se
   * dibujaría sobre una elección que ya existe
   * (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
   *
   * Antes no hacía falta porque el restore abría el modo en `configure`, donde ese paso
   * no existe — que es justamente el bug que se arregló.
   *
   * Guarda el ID y no un booleano, y eso es el arreglo de
   * ADR-2026-09-10-lo-restaurado-es-del-preset-no-una-fase: como booleano era una FASE
   * ("estoy restaurando") que se apagaba al resolver los productos, y esa carrera la perdía
   * el sheet. Sus dos consumidores (`mobilePaso2`, `eligiendoProductos`) lo leen SÓLO en su
   * `useState` inicial, así que lo único que importa es el valor en el commit en el que el
   * sheet monta. Atado al id no hay carrera: vale mientras el preset abierto SEA el
   * restaurado, se derive cuando se derive.
   */
  const [presetRestauradoId, setPresetRestauradoId] = useState<string | null>(null);
  /**
   * Cambia cada vez que se carga una configuración EN VIVO (no al montar): es lo que
   * vuelve a disparar los dos resolvers async de abajo
   * (ADR-2026-09-09-ajustar-antes-de-regenerar).
   *
   * Hace falta porque esos resolvers esperan a que llegue su catálogo, y cuando el pedido
   * viene de "Ajustar y regenerar" los catálogos ya están cargados hace rato: sin el tick,
   * `pendingRestoreRef` se llena y nadie lo mira.
   */
  const [restoreTick, setRestoreTick] = useState(0);
  // Estado de la auto-selección de pose por defecto (solo design-modelo):
  //   • 'pending' → todavía no aplicamos el default; cuando haya producto
  //                 elegido y el catálogo cargado, seleccionamos la primera
  //                 pose RECOMENDADA para ese producto.
  //   • 'applied' → ya aplicamos el default para el producto actual; no lo
  //                 re-pisamos en recargas async del catálogo.
  //   • 'manual'  → el user eligió una pose a mano (o vino de un restore);
  //                 no la tocamos.
  // Vuelve a 'pending' al cerrar el sheet y al confirmar/cambiar de
  // producto, así cada producto arranca con su pose recomendada.
  const poseAutoSelectRef = useRef<'pending' | 'applied' | 'manual'>(
    'pending',
  );
  // Overlay blanco que cubre el "home" mientras el restore async termina
  // de abrir el preset. El initializer es lazy y mira sessionStorage en
  // el PRIMER render — así el overlay está presente desde el primer
  // paint y nunca se ve el home. (Si dependiera del useEffect, el
  // primer render mostraría el home un frame antes del setState.)
  // El overlay `restoringPreset` solo se usa para el path de
  // sessionStorage (restore post-back desde Imágenes Generadas, donde
  // el preset no se conoce sincrónicamente). El path de deep-link via
  // navigation state ya inicializa `selectedPreset` / `previewList` /
  // `panelStep` con los datos sincrónicos del `history.state.usr`, así
  // que el sheet aparece desde el primer paint y no hace falta tapar
  // el home.
  const [restoringPreset, setRestoringPreset] = useState<boolean>(() => {
    try {
      const raw = sessionStorage.getItem(CLAVE_RESTORE_V2);
      if (raw) {
        const blob = JSON.parse(raw);
        if (blob?.presetId) return true;
      }
    } catch {
      /* noop */
    }
    return false;
  });
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(CLAVE_RESTORE_V2);
    } catch {
      /* sessionStorage puede fallar en modo privado */
    }
    if (!raw) return;
    try {
      const blob = JSON.parse(raw);
      // La configuración entera vuelve de un saque —
      // ADR-2026-09-10-el-restore-guarda-la-configuracion-entera. La lista de campos y su
      // lectura viven en `restoreV2.ts`, con un test de ida y vuelta: acá quedaba una
      // tira de `if (blob.x) setX(...)` que había que acordarse de ampliar con cada campo
      // nuevo, y de los 20 que configura un modo sólo se leían 12.
      pendingRestoreRef.current = aplicarRestoreV2(blob, {
        setSinProducto,
        setImagenBase,
        setModelSelection,
        setModelSelectionsSerie,
        setModelSelectionsExtra,
        setMultiModelEnabled,
        setPoseIds,
        setMultiPoseEnabled,
        setFondoSelection,
        setFormatoVal,
        setResolucionVal,
        setOtraConfig,
        setAutoImageCount,
        setVistaIds,
        setCatalogoVistas,
        setVistaProductosExcluidos,
        setMultiPresetIds,
        setAnuncioTitulo,
        setAnuncioDescripcion,
        setAnuncioCta,
        setAnuncioColors,
        setVideoDuration,
        setVideoMultiScene,
        setVideoGanchoId,
        setVideoVoiceGuion,
      });
      if (Array.isArray(blob.poseIds)) {
        // Restore = poses ya elegidas en la generación anterior; no las pisamos con el
        // default recomendado. (Es un ref, no un setter: no entra en `aplicarRestoreV2`.)
        poseAutoSelectRef.current = 'manual';
      }
      if (blob.presetId) {
        setRestoringPreset(true);
        // El modo que va a abrir ya tiene su configuración: el paso de elegir productos
        // no corresponde aunque la lista todavía esté vacía.
        setPresetRestauradoId(blob.presetId);
        // Safety: si en 2.5s no resolvimos el preset (catálogo no
        // llegó o el id ya no existe), levantamos el overlay para no
        // dejar la UI bloqueada.
        window.setTimeout(() => setRestoringPreset(false), 2500);
      }
    } catch (err) {
      console.warn('[InterfazV2] restore parse error', err);
    }
    try {
      sessionStorage.removeItem(CLAVE_RESTORE_V2);
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore async — preset abierto. Espera a que las colecciones lleguen
  // y abre el preset directo en step 'configure' (saltando preview /
  // choose-product, porque el user ya había configurado todo).
  // Usamos `displayCollections` (no `fetchedCollections`) porque incluye
  // la fila virtual "Diseña tu imagen" con el preset 'imagen-con-modelo'
  // — sino el restore desde el modo design-modelo no encuentra el
  // preset y la pantalla queda sin abrir.
  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending?.presetId) return;
    if (displayCollections.length === 0) return;
    const found = displayCollections
      .flatMap((c) => c.presets ?? [])
      .find((p) => p.id === pending.presetId);
    if (found) {
      setSelectedPreset(found);
      // Al paso donde ESE modo se configura, no a `configure` fijo — el "Atrás" desde
      // los resultados devolvía a Modelo y a Automática a la pantalla anterior al
      // rediseño (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
      setPanelStep(pasoDeConfiguracion(found.kind));
      pendingRestoreRef.current!.presetId = null;
    }
  }, [fetchedCollections, isDesignRubro, restoreTick]);

  // Restore async — productos seleccionados (single + multi por slot).
  // Espera a que el catálogo `userProducts` cargue, resuelve los IDs a
  // objetos MockProductV2 y los aplica. Se ignoran IDs no encontrados
  // (producto borrado / renombrado entre generaciones).
  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending) return;
    if (userProducts.length === 0) return;
    if (pending.selectedProductIds && pending.selectedProductIds.length > 0) {
      const list = pending.selectedProductIds
        .map((id) => userProducts.find((p) => p.id === id))
        .filter((p): p is MockProductV2 => !!p);
      if (list.length > 0) setSelectedProducts(list);
      pending.selectedProductIds = undefined;
    }
    if (pending.multiSlotProductIds) {
      const resolved: Record<number, MockProductV2[]> = {};
      Object.entries(pending.multiSlotProductIds).forEach(([k, ids]) => {
        const list = ids
          .map((id) => userProducts.find((p) => p.id === id))
          .filter((p): p is MockProductV2 => !!p);
        if (list.length > 0) resolved[Number(k)] = list;
      });
      if (Object.keys(resolved).length > 0) setMultiSlotProducts(resolved);
      pending.multiSlotProductIds = undefined;
    }
    // Acá se apagaba la bandera, y era el bug: este efecto está declarado DESPUÉS del que
    // abre el preset, así que con los productos ya resueltos (caché de sesión, o
    // simplemente catálogo que llega antes que las colecciones) los tres corrían en el
    // mismo flush, React batcheaba los setState y el sheet montaba con la bandera ya
    // apagada. En mobile eso hacía que "Atrás" devolviera a elegir productos en vez de a la
    // configuración desde la que se generó.
    //
    // Ya no hace falta apagarla: el id sólo coincide con el preset que vino del restore, y
    // los caminos que abren un modo a mano lo limpian
    // (ADR-2026-09-10-lo-restaurado-es-del-preset-no-una-fase).
  }, [userProducts, restoreTick]);

  /**
   * "Ajustar y regenerar" — ADR-2026-09-09-ajustar-antes-de-regenerar.
   *
   * La configuración de una imagen ya generada llega por el ESTADO DE LA NAVEGACIÓN y se
   * aplica acá, con los mismos setters que usa el restore del "Atrás". No va por
   * `sessionStorage` como aquél porque ese canal se lee al MONTAR, y quien toca el botón ya
   * está dentro del modo: escribirlo y navegar no haría nada. Con el estado de navegación
   * cada click trae una `location.key` nueva, así que el efecto corre sin remontar la
   * pantalla (ni flash blanco) y sirve igual viniendo de otra pantalla.
   *
   * Lo que este efecto NO trae es la CALIDAD: no viaja por el canal de restore, así que el
   * modo abre con su default. Es aceptable justamente porque cambiarla es para lo que
   * existe esta opción.
   */
  const configsAplicadas = useRef<Set<string>>(new Set());
  useEffect(() => {
    const config = (location.state as { configRegeneracion?: ConfiguracionDeRegeneracion } | null)
      ?.configRegeneracion;
    if (!config) return;
    // Una vez por navegación: sin esto, un back/forward la volvería a aplicar encima de lo
    // que el usuario ya cambió a mano.
    const clave = location.key ?? 'sin-key';
    if (configsAplicadas.current.has(clave)) return;
    configsAplicadas.current.add(clave);

    if (config.modelo) {
      // El pedido no guarda la foto del modelo: se completa contra el catálogo. Si todavía
      // no llegó, queda el nombre y el picker muestra el chip sin miniatura.
      const guardado = config.modeloGuardadoId
        ? userModels.find((m) => String(m.id) === config.modeloGuardadoId)
        : undefined;
      setModelSelection(
        config.modelo.mode === 'preset' && guardado
          ? { mode: 'preset', id: config.modelo.id, name: guardado.nombre, imageUrl: guardado.thumbnailUrl }
          : config.modelo,
      );
    }
    if (config.poseIds.length > 0) {
      setPoseIds(config.poseIds);
      setMultiPoseEnabled(config.poseIds.length > 1);
      // Poses que ya se eligieron: el default recomendado no las pisa.
      poseAutoSelectRef.current = 'manual';
    }
    if (config.fondo) setFondoSelection(config.fondo);
    if (config.formato) setFormatoVal(config.formato);
    setOtraConfig(config.instruccion);
    /**
     * Sólo los PRODUCTOS van al resolver del restore. El preset queda deliberadamente
     * afuera: el resolver, además de abrirlo, fuerza `panelStep: 'configure'`, que es el
     * paso correcto del flujo viejo pero NO el de los modos del rediseño —ahí la
     * configuración vive en `choose-product`, y forzar `configure` achicaba la columna
     * izquierda a la mitad (lo reportó Mati con el modo Modelo abierto).
     *
     * No hace falta: quien abre el modo en su paso correcto es la RUTA, y a ella ya
     * estamos navegando. Si el modo era el que estaba abierto, el efecto de ruta sale por
     * su guard de "mismo id" y no toca nada.
     */
    pendingRestoreRef.current = {
      selectedProductIds: config.varianteIds.length > 0 ? config.varianteIds : undefined,
    };
    // El tick es lo que vuelve a correr el resolver de productos: cuando la config llega en
    // vivo, `userProducts` ya está cargado y su dep no cambia sola.
    setRestoreTick((t) => t + 1);
  }, [location.key, location.state, userModels]);

  // Animar entrada del preset sheet
  useEffect(() => {
    if (selectedPreset) {
      requestAnimationFrame(() => setPresetSheetAnimated(true));
    }
  }, [selectedPreset]);

  // El modo "Video con influencer" no usa escenas múltiples (el toggle está
  // oculto). Forzamos `videoMultiScene=false` al entrar para que un valor
  // heredado del flujo plantilla-video no duplique el costo del Generar. Ver
  // ADR-099.
  useEffect(() => {
    if (selectedPreset?.kind === 'video-influencer') setVideoMultiScene(false);
  }, [selectedPreset]);

  // Al cambiar de modo (o cerrarlo), limpiar las vistas del catálogo. Son
  // selección por sesión de modo: sin esto, las 3 vistas de muebles quedaban
  // colgadas en el estado y se filtraban al Catálogo IA (y al revés). Se hace
  // por `selectedPreset?.id` para cubrir TODOS los caminos de salida (cerrar,
  // cambiar de modo directo, deep-link), no solo `closePresetSheet`.
  // Las vistas de "Fotos de producto" van por el mismo camino y por la misma
  // razón: son selección por sesión de modo.
  // Sólo al DEJAR un modo: al montar o al abrir el modo restaurado, las vistas las acaba
  // de poner el restore del "Atrás" (que sí las guarda desde
  // ADR-2026-09-10-el-restore-guarda-la-configuracion-entera) y limpiarlas dejaba a
  // Infografía en un review vacío (ADR-2026-10-15-las-vistas-restauradas-no-se-borran).
  const presetIdPrevioRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const actual = selectedPreset?.id ?? null;
    if (seLimpianLasVistasDelModo(presetIdPrevioRef.current, actual)) {
      setCatalogoVistas([]);
      setVistaIds([]);
    }
    presetIdPrevioRef.current = actual;
  }, [selectedPreset?.id]);

  // Restore overlay: lo levantamos cuando el preset sheet ya animó
  // (presetSheetAnimated true) — así el user no ve nada del home detrás.
  useEffect(() => {
    if (restoringPreset && presetSheetAnimated) {
      setRestoringPreset(false);
    }
  }, [restoringPreset, presetSheetAnimated]);

  // Safety: si por algún motivo el deep-link no resuelve un preset (el
  // id no existe / la colección no llegó), bajamos el overlay después
  // de 2.5s para no dejar la pantalla en blanco indefinidamente.
  useEffect(() => {
    if (!restoringPreset) return;
    const t = window.setTimeout(() => setRestoringPreset(false), 2500);
    return () => window.clearTimeout(t);
  }, [restoringPreset]);

  // Si el user llega desde ColeccionDetalle vía
  // `navigate('/interfaz-v2', { state: { presetId, collectionId } })`,
  // abrimos automáticamente el sheet con la colección como previewList.
  //
  // Path "rápido": cuando `presetData` / `collectionPresets` vienen en el
  // state, el `useState` initializer ya abrió el sheet en el primer paint
  // — este effect solo se encarga de animar la entrada y limpiar el
  // state para que un refresh no re-abra.
  //
  // Path "fallback": si solo viene `presetId` (sin data), buscamos en
  // `displayCollections` cuando carguen y abrimos el sheet ahí. En este
  // path sí se ve el home por un momento — pero es el caso edge de
  // links externos / históricos.
  const appliedDeepLinkRef = useRef(false);
  useEffect(() => {
    if (appliedDeepLinkRef.current) return;
    const state = location.state as
      | {
          presetId?: string;
          collectionId?: string;
          presetData?: PresetV2;
          collectionPresets?: PresetV2[];
        }
      | null;
    if (!state?.presetId) return;
    // Si ya inicializamos sincrónicamente desde state (path rápido),
    // solo limpiamos el state y marcamos como aplicado.
    if (state.presetData) {
      appliedDeepLinkRef.current = true;
      // Animar la entrada — el sheet ya está montado desde el initializer.
      requestAnimationFrame(() => setPresetSheetAnimated(true));
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    // Fallback: lookup async esperando a que carguen las colecciones.
    const presetId = state.presetId;
    const collectionId = state.collectionId;
    const targetCollection =
      displayCollections.find((c) => c.id === collectionId) ||
      displayCollections.find((c) =>
        c.presets.some((p) => p.id === presetId),
      );
    if (!targetCollection) return;
    const preset = targetCollection.presets.find((p) => p.id === presetId);
    if (!preset) return;
    appliedDeepLinkRef.current = true;
    setSelectedPreset(preset);
    setPreviewList(targetCollection.presets);
    setPresetRestauradoId(null);
    setPanelStep('preview');
    setPresetSheetAnimated(false);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, displayCollections, navigate]);

  /**
   * "Inicio" con una PLANTILLA abierta — ADR-2026-10-04-inicio-cierra-la-plantilla-abierta.
   *
   * Una plantilla no tiene ruta propia: su panel se abre SOBRE la home, en `/interfaz-v2`.
   * Por eso el "Inicio" de la navbar (que no navega si ya está en esa ruta) y el de las
   * migas (`navigate` a la misma ruta) no hacían nada: la URL ya era la del inicio y el
   * panel no es "URL-driven". Ahora los dos llegan con `irAlInicio` en el estado de la
   * navegación y acá se cierra el panel. Se apaga antes la marca de deep-link: con ella,
   * `closePresetSheet` hace `navigate(-1)` y devolvería a Redes o a la grilla en vez de
   * dejar en el inicio.
   */
  useEffect(() => {
    if (!(location.state as { irAlInicio?: boolean } | null)?.irAlInicio) return;
    if (selectedPreset && !routeMode) {
      cameFromDeepLinkRef.current = false;
      closePresetSheet();
    }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const closePresetSheet = () => {
    // Quien abrió el modo dijo a dónde volver (el Agente) — ADR-2026-09-16-volver-a-donde-vine.
    // Vale todas las veces, no sólo la primera como el deep-link.
    const volverA = leerVolverA(location.state);
    if (volverA) {
      navigate(volverA.path, { state: volverA.state });
      return;
    }
    const fromDeepLink = cameFromDeepLinkRef.current;
    cameFromDeepLinkRef.current = false;
    // Caso deep-link: el sheet es la ÚNICA cosa visible (el "home" de
    // V2 está oculto detrás). Si hacemos el cierre animado y después
    // `navigate(-1)`, durante los 300ms de la animación el home aparece
    // — feo. Mejor disparamos `navigate(-1)` directo y InterfazV2 se
    // desmonta entero. El browser/router cambia a la categoría sin
    // pasar por el home.
    if (fromDeepLink) {
      navigate(-1);
      return;
    }
    // Si el modo abierto vino de una URL (`/interfaz-v2/modo/...`), antes
    // de animar el cierre navegamos al home — así el browser back desde
    // afuera funciona y la URL queda consistente. El useEffect de sync
    // URL→state ve que la URL volvió al home y dispara el cierre animado.
    if (routeMode) {
      navigate(rutaHome);
      return;
    }
    setPresetSheetAnimated(false);
    setTimeout(() => {
      setSelectedPreset(null);
      setSelectedProducts([]);
      // La creación base se va con el panel: si no, el próximo anuncio abierto desde un
      // producto seguiría mandando `imagen_generada_id`.
      setImagenBase(null);
      setSinProducto(false);
      dialogoForRef.current = null;
      setDialogoLoading(false);
      setDialogoError(null);
      setPanelStep('preview');
      setPreviewList([]);
      // Al cerrar el panel NO se toca el registro: ni las que corren ni las que ya
      // terminaron. Cambiar de modo —o salir a `/animar-imagen` y volver— deja la
      // columna mostrando lo que se generó en esta sesión, y recién una recarga la
      // limpia (ADR-2026-09-08-los-resultados-se-quedan-toda-la-sesion).
      setModelSelection(null);
      setModelSelectionsSerie([]);
      setModelPickerOpen(false);
      setFormatoVal(null);
      setFormatPickerOpen(false);
      setFondoVal(null);
      setOtraConfig('');
      setVideoGanchoId(null);
      setVideoVoiceGuion(getVoiceGuionDefault());
      // Vuelve al default del flujo plantilla-video — si quedara el 18s del
      // modo influencer, excedería el tope de 12s de ese slider.
      setVideoDuration(VIDEO_DURATION_DEFAULT);
      setPendingProductFiles([]);
      setPendingProductName('');
      setUploadingProduct(false);
      setUploadError(null);
      setMultiPresetIds([]);
      setPoseIds([]);
      setMultiPoseEnabled(false);
      // La Calidad/resolución vuelve al default con el resto de la configuración del
      // modo: cambia lo que se cobra, y arrastrar un 4K de la sesión anterior sin que
      // nadie lo pida sorprende con la factura.
      setResolucionVal(RESOLUCION_DEFAULT);
      // El estado multi-modelo también es por sesión de modo (ADR-513 solo
      // reseteaba poses): sin esto "varios modelos" y sus slots quedaban
      // prendidos al reabrir un modo y cruzaban modelo↔poses (poses de
      // varias personas con la UI de un solo modelo, o al revés).
      setMultiModelEnabled(false);
      setModelSelectionsExtra([]);
      setMultiSlotProducts({});
      poseAutoSelectRef.current = 'pending';
      setPosePickerOpen(false);
      setFondoSelection(null);
      setFondoPickerOpen(false);
      setFondoView('list');
      // Reset del state del modo Anuncio. La fuente vuelve a la de marca
      // (no a '') para mantener el default de tipografía entre anuncios.
      setAnuncioTitulo({ ...ANUNCIO_FIELD_DEFAULT, font: brandFontRef.current });
      setAnuncioDescripcion({
        ...ANUNCIO_FIELD_DEFAULT,
        font: brandFontRef.current,
      });
      setAnuncioCta({ ...ANUNCIO_FIELD_DEFAULT, font: brandFontRef.current });
      setAnuncioColors([]);
    }, 300);
  };

  // Sincronización URL → state para los 4 modos URL-routed
  // (`design-auto` / `design-modelo` / `catalogo-ia` / `asistente`).
  // Cuando la URL llega a `/interfaz-v2/modo/<slug>` (sea por click en
  // una card, deeplink externo o browser forward), abrimos el sheet del
  // modo correspondiente. Cuando la URL vuelve a `/interfaz-v2` (sea por
  // click en cerrar, browser back o navigate programático), animamos el
  // cierre del sheet.
  //
  // `pendingCloseRef` evita re-ejecutar el cleanup si el effect re-corre
  // durante los 300ms de animación de salida.
  const pendingCloseRef = useRef(false);
  useEffect(() => {
    if (pendingCloseRef.current) return;
    // Se va a Redes (Prompt libre / Creador de video en escritorio): no abrir el sheet
    // viejo ni un frame (ADR-2026-10-07-prompt-libre-y-creador-abren-crear).
    if (destinoCrear) return;
    // El slug se traduce a kind y DESPUÉS se lo lleva al rubro de la marca activa —
    // ADR-2026-08-20-el-slug-catalogo-no-tiene-card-en-hogar-y-deco.
    //
    // "Generar catálogo con IA" son dos kinds según el rubro, y hay entradas que llegan
    // acá con el slug escrito a mano: el tab "Catálogo" de la barra inferior de mobile
    // apunta fijo a `/interfaz-v2/modo/catalogo` para todo rubro que no sea Moda, o sea
    // que a una marca de Hogar y Decoración la mandaba a `catalogo-ia`, que es
    // justamente el modo que ese rubro no tiene. La card salía `null`, el efecto se iba
    // por el `if (!card) return` de abajo —que está para ESPERAR a que el rubro
    // resuelva— y la espera no terminaba nunca: el tab quedaba activo sin abrir nada.
    //
    // Va acá y no en cada entrada porque los llamadores hardcodean el slug sin saber de
    // rubros (la barra sólo sabe si la marca es de Moda), y los deep-links no tienen a
    // quién parchear. `esMarcaHogarDeco` ya está en las deps del efecto, así que cuando
    // el rubro resuelve async esto se recalcula solo.
    // `string | null` a propósito: el mapa local de kinds no conoce `catalogo-agente`, que
    // sale de `kindDeModoParaRubro` y se compara acá abajo.
    const expectedKind: string | null = kindDeModoParaRubro(
      routeMode ? MODE_PATH_TO_KIND[routeMode] : null,
      esMarcaHogarDeco,
      veCatalogoConAgente,
    );

    // El catálogo de Hogar y Decoración tiene pantalla propia
    // (ADR-2026-10-14-catalogo-reemplaza-a-catalogo-muebles): un link viejo a
    // `/interfaz-v2/modo/catalogo` o `/catalogo-muebles` con esa marca va a `/catalogo`.
    if (expectedKind === 'catalogo-agente') {
      navigate('/catalogo', { replace: true });
      return;
    }
    if (expectedKind) {
      // URL dice "abrir modo X". Buscar la card sintética del rubro.
      // Se pasó de cadena de ternarios a un mapa al sumar el 7º modo: con
      // `catalogo-muebles` la cadena quedaba con 7 niveles de anidado y era
      // ilegible. El comportamiento es idéntico (card o null).
      const CARD_POR_KIND: Record<string, PresetV2 | null> = {
        'design-auto': designAutoCard,
        'design-modelo': designModeloCard,
        'fotos-producto': fotosProductoCard,
        'catalogo-ia': catalogoIACard,
        'catalogo-muebles': catalogoMueblesCard,
        infografia: infografiaCard,
        asistente: asistenteCard,
        'asistente-v2': asistenteV2Card,
        'prompt-libre': promptLibreCard,
        'creador-video': creadorVideoCard,
        anuncio: anuncioCard,
        'video-influencer': videoInfluencerCard,
      };
      const card = CARD_POR_KIND[expectedKind] ?? null;
      // Si todavía no resolvió el rubro (cards null), esperamos a que
      // el useState async se resuelva — este effect re-corre con
      // `isDesignRubro` actualizado.
      if (!card) return;
      // Gating por plan (red de seguridad para deep-links / URL directa: los
      // taps ya gatean antes de navegar). Si el plan no alcanza, no abrimos el
      // modo: mostramos el modal y volvemos al home. Si el plan aún no cargó,
      // `getModePlanLock` devuelve null y dejamos pasar (backstop al generar).
      const routeModeLock = getModePlanLock(card.kind, userPlan);
      if (routeModeLock) {
        setFeatureLock(routeModeLock);
        navigate(leerVolverA(location.state)?.path ?? rutaHome);
        return;
      }
      // Ya está abierto el modo correcto — nada que hacer.
      if (selectedPreset?.id === card.id) return;
      /**
       * Veníamos de OTRO modo abierto (pasamos el guard de arriba con algo puesto):
       * cambiar de modo empieza de cero — ADR-2026-09-08-cambiar-de-modo-limpia-los-productos.
       *
       * Sin esto, los productos elegidos en el modo anterior seguían tildados y
       * Catálogo y Fotos de producto abrían en su paso 1 diciendo "1 producto elegido"
       * —o directamente en el paso 2— por una elección que el usuario hizo para otra
       * cosa. La cadena de cierre (`closePresetSheet`) sí los limpiaba, pero pasar de un
       * modo a otro nunca la ejecuta: la URL va de `/modo/auto` a `/modo/catalogo` sin
       * volver a la home, así que la rama de cierre de este mismo efecto no corre.
       *
       * `modoAnterior` y no `selectedPreset` a secas para no tocar el arranque en frío:
       * el modo que abre con un producto puesto ("el producto entra con el modo",
       * ADR-2026-08-04-el-producto-entra-con-el-modo) lo hace desde el initializer, así
       * que llega acá con el mismo `card.id` y sale por el guard de arriba; y el restore
       * post-recarga arranca sin preset y resuelve sus productos después.
       */
      const modoAnterior = selectedPreset;
      // Defaults del modo influencer al abrir: 18s de duración (el slider
      // va de 8 a 18; el formato default 'story' lo pone el effect de
      // auto-relleno de formato, que especial-casea este modo).
      if (card.kind === 'video-influencer') {
        setVideoDuration(VIDEO_INFLUENCER_DURATION_DEFAULT);
        setVideoVoiceGuion(getVoiceGuionDefault());
      }
      setSelectedPreset(card);
      setPreviewList([]);
      if (modoAnterior) {
        setSelectedProducts([]);
        setSinProducto(false);
        // Cada producto arranca con su pose recomendada; sin producto, la próxima
        // elección tiene que volver a pedirla.
        poseAutoSelectRef.current = 'pending';
        // El modo nuevo se abre en frío: sin productos y sin nada restaurado. Sin esto,
        // un modo abierto después de un restore heredaba la bandera y Modelo se saltaba
        // su paso de elegir productos.
        setPresetRestauradoId(null);
      }
      // Los modos con paso propio (asistente y su variante v2, prompt libre, creador
      // de video) no pasan por `choose-product`.
      setPanelStep(
        esAsistente(card.kind)
          ? 'asistente'
          : card.kind === 'prompt-libre'
            ? 'prompt-libre'
            : card.kind === 'creador-video'
              ? 'creador-video'
              : 'choose-product',
      );
      setPresetSheetAnimated(false);
      // La animación de entrada la dispara el useEffect que watcha
      // `selectedPreset` (más arriba).
    } else if (selectedPreset) {
      // URL volvió al home pero state tiene un modo abierto → cerrar.
      // Solo aplicamos esta lógica a los 4 modos URL-routed; templates
      // (`design`/`template`) se cierran por su propio path en
      // `closePresetSheet` y no son URL-driven.
      const isModeKind =
        selectedPreset.kind === 'design-auto' ||
        esDesignModelo(selectedPreset.kind) ||
        selectedPreset.kind === 'fotos-producto' ||
        esCatalogoIA(selectedPreset.kind) ||
        selectedPreset.kind === 'catalogo-muebles' ||
        selectedPreset.kind === 'infografia' ||
        esAsistente(selectedPreset.kind) ||
        selectedPreset.kind === 'prompt-libre' ||
        selectedPreset.kind === 'creador-video' ||
        selectedPreset.kind === 'anuncio' ||
        selectedPreset.kind === 'video-influencer';
      if (!isModeKind) return;
      pendingCloseRef.current = true;
      setPresetSheetAnimated(false);
      setTimeout(() => {
        setSelectedPreset(null);
        setSelectedProducts([]);
      // La creación base se va con el panel: si no, el próximo anuncio abierto desde un
      // producto seguiría mandando `imagen_generada_id`.
      setImagenBase(null);
        setSinProducto(false);
        dialogoForRef.current = null;
        setDialogoLoading(false);
        setDialogoError(null);
        setPanelStep('preview');
        setPreviewList([]);
        // Igual que en `closePresetSheet`: el registro no se toca, así lo generado en
        // la sesión sigue a la vista al cambiar de modo
        // (ADR-2026-09-08-los-resultados-se-quedan-toda-la-sesion).
        setModelSelection(null);
        setModelSelectionsSerie([]);
        setModelPickerOpen(false);
        setFormatoVal(null);
        setFormatPickerOpen(false);
        setFondoVal(null);
        setOtraConfig('');
        setVideoGanchoId(null);
        setVideoVoiceGuion(getVoiceGuionDefault());
        // Mismo reset que closePresetSheet: el 18s del influencer no debe
        // filtrarse al flujo plantilla-video (tope 12s).
        setVideoDuration(VIDEO_DURATION_DEFAULT);
        setPendingProductFiles([]);
        setPendingProductName('');
        setUploadingProduct(false);
        setUploadError(null);
        setMultiPresetIds([]);
        setPoseIds([]);
        setMultiPoseEnabled(false);
        // Mismo reset que closePresetSheet — ver el comentario de allá.
        setResolucionVal(RESOLUCION_DEFAULT);
        // Mismo reset que closePresetSheet: el multi-modelo es por sesión
        // de modo — ver el comentario de allá.
        setMultiModelEnabled(false);
        setModelSelectionsExtra([]);
        setMultiSlotProducts({});
        poseAutoSelectRef.current = 'pending';
        setPosePickerOpen(false);
        setFondoSelection(null);
        setFondoPickerOpen(false);
        setFondoView('list');
        // Reset del state del modo Anuncio. La fuente vuelve a la de marca
        // (no a '') para mantener el default de tipografía entre anuncios.
        setAnuncioTitulo({
          ...ANUNCIO_FIELD_DEFAULT,
          font: brandFontRef.current,
        });
        setAnuncioDescripcion({
          ...ANUNCIO_FIELD_DEFAULT,
          font: brandFontRef.current,
        });
        setAnuncioCta({ ...ANUNCIO_FIELD_DEFAULT, font: brandFontRef.current });
        setAnuncioColors([]);
        // Reset del chat del agente — si el sheet cerrado era el del
        // asistente, queremos arrancar conversación nueva la próxima vez.
        // Para los otros modos URL-driven es no-op (el chat sigue vacío).
        chat.reset();
        pendingCloseRef.current = false;
      }, 300);
    }
    // Deps mínimas: `routeMode` y de qué dependen las cards sintéticas.
    // `isDesignRubro` y `esMarcaHogarDeco` cubren su disponibilidad; el segundo
    // resuelve async (fetch del rubro de la marca), así que sin él un deep-link a
    // /modo/catalogo-muebles o /modo/infografia quedaría sin abrir
    // (ADR-2026-08-05-el-deep-link-de-un-modo-espera-a-su-rubro): entrando por URL
    // directa el rubro todavía no cargó, la card es `null`, el efecto sale por el
    // `if (!card) return` y sin el dep no volvía a correr nunca — la pantalla se
    // quedaba en la home con el ítem marcado en la sidebar.
    //
    // `brandRubro`/`userRubro` van además de `esMarcaHogarDeco` porque el
    // rediseño sumó cards y carátulas que dependen del rubro CRUDO, no sólo del
    // booleano de Hogar y Decoración.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMode, isDesignRubro, esMarcaHogarDeco, veCatalogoConAgente, brandRubro, userRubro]);

  // Confirmá el preset desde el step 'preview' → arranca el flujo normal
  // (choose-product). Permite que el user, en preview, scrollee y elija
  // "Utilizar" en otro preset distinto al que tocó originalmente.
  const handleUtilizarPreset = (preset: PresetV2) => {
    // Video de plantilla = Pro/Ultra. Si el toggle "Imagen/Video" está en video
    // y el plan no alcanza, NO avanzamos al selector de producto: mostramos el
    // modal de "función exclusiva" (mismo que el flujo de video influencer).
    // `userPlan &&` evita bloquear si el plan aún no se conoce (backstop al
    // generar). Cubre mobile y desktop: ambos usan este handler.
    if (
      previewMediaTab === 'video' &&
      preset.kind === 'template' &&
      userPlan &&
      !isProOrUltra(userPlan)
    ) {
      setShowVideoPlanRestriction(true);
      return;
    }
    setSelectedPreset(preset);
    setMultiPresetIds([]);
    // Elegido a mano: aunque sea el mismo modo que se restauró, acá se abre en frío.
    setPresetRestauradoId(null);
    setPanelStep('choose-product');
  };

  // Multi-select: el user eligió N plantillas en preview. Usamos la
  // primera como `selectedPreset` (anchor — drive del UI de configure),
  // y guardamos todos los ids en multiPresetIds. En generate, se mandan
  // todos como `template_id` array.
  const handleUtilizarMultiplePresets = (presets: PresetV2[]) => {
    if (presets.length === 0) return;
    // Mismo guard de video que el single (modo multi-plantilla en video).
    if (
      previewMediaTab === 'video' &&
      presets[0].kind === 'template' &&
      userPlan &&
      !isProOrUltra(userPlan)
    ) {
      setShowVideoPlanRestriction(true);
      return;
    }
    setSelectedPreset(presets[0]);
    setMultiPresetIds(presets.map((p) => p.id));
    // Para multi-select de 'design' (e-commerce): los presets envuelven
    // poses, así que pre-cargamos `poseIds` con todas las ids extraídas.
    // El payload builder en handleGenerateFinal manda `pose` como array,
    // que es lo que el backend espera para multi-pose en single-modelo.
    if (presets[0].kind === 'design') {
      const ids = presets
        .map((p) =>
          p.id.startsWith('pose-') ? p.id.slice('pose-'.length) : null,
        )
        .filter((id): id is string => !!id);
      if (ids.length > 0) {
        setPoseIds(ids);
        // Invariante >1 pose ⇒ toggle multi ON (por si algún flujo abre
        // el PosePicker con este set pre-cargado).
        if (ids.length > 1) setMultiPoseEnabled(true);
      }
    }
    setPanelStep('choose-product');
  };

  // Lock body scroll cuando el preset sheet está abierto. Sin esto, el
  // usuario puede inadvertidamente scrollear la página de fondo (la home
  // de Interfaz V2) mientras tiene el sheet open.
  //
  // Bonus: DashboardLayout envuelve la app en un div con `overflow-y-auto`
  // propio (mirá App.tsx). Lockear solo `body` no alcanza — ese div
  // sigue mostrando su scrollbar inutil detrás del sheet. Locamos también
  // el wrapper (es el firstElementChild de <body>).
  useEffect(() => {
    if (selectedPreset) {
      const bodyPrev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const dashboardWrapper = document.body
        .firstElementChild as HTMLElement | null;
      const dashboardPrev = dashboardWrapper?.style.overflow ?? '';
      if (dashboardWrapper) dashboardWrapper.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = bodyPrev;
        if (dashboardWrapper) dashboardWrapper.style.overflow = dashboardPrev;
      };
    }
  }, [selectedPreset]);

  // Intercepta el botón "Atrás" mobile (custom event del sidebar) para que
  // CIERRE el preset sheet en vez de navegar fuera de la página. Si no se
  // intercepta, el back lleva al usuario 2 pasos atrás porque la sheet es
  // una capa modal, no una ruta — así se sentía como "skip" de la home V2.
  useEffect(() => {
    if (selectedPreset) {
      // El back navega un step para atrás dentro del flow del sheet
      // en lugar de cerrarlo de una. design-modelo NO tiene 'preview'
      // (arranca directo en 'choose-product'), así que su flow es
      // distinto del de templates:
      //   • template:      preview ↔ choose-product ↔ configure
      //                                       ↘ upload-product ↗
      //   • design-modelo: choose-product ↔ configure
      //                                ↘ upload-product ↗
      // En la raíz de cada flow el back cierra el sheet.
      setMobileBackOverride(() => () => {
        const noPreviewKind =
          esDesignModelo(selectedPreset?.kind) ||
          selectedPreset?.kind === 'design-auto' ||
          selectedPreset?.kind === 'fotos-producto' ||
          esCatalogoIA(selectedPreset?.kind) ||
          selectedPreset?.kind === 'catalogo-muebles' ||
          selectedPreset?.kind === 'infografia' ||
          selectedPreset?.kind === 'anuncio' ||
          // video-influencer NO tiene 'preview' (arranca directo en
          // 'choose-product', igual que los modos de diseño) → atrás cierra
          // el sheet en vez de ir a la pantalla extra tipo plantilla. ADR-508.
          selectedPreset?.kind === 'video-influencer';
        if (panelStep === 'configure') {
          // catalogo-ia y catalogo-muebles: configure es el "review de cards"
          // y atrás va al picker de vistas. Otros kinds: a choose-product.
          setPanelStep(
            esCatalogoIA(selectedPreset?.kind) ||
              selectedPreset?.kind === 'catalogo-muebles'
              ? 'select-vistas'
              : 'choose-product',
          );
        } else if (panelStep === 'select-vistas') {
          setPanelStep('choose-product');
        } else if (panelStep === 'upload-product') {
          // Descartar el upload en progreso al volver — igual que el viejo
          // link "Volver" interno (que llamaba handleCancelUpload). Ahora
          // ese link está oculto y el back lo da la top bar.
          setPendingProductFiles([]);
          setPendingProductName('');
          setUploadError(null);
          setPanelStep('choose-product');
        } else if (panelStep === 'pick-product') {
          // En mobile el grid de productos es una pantalla full-screen
          // POR DEBAJO del chrome (back/menu siguen visibles). El back de la
          // top bar vuelve al origen: configure si se abrió con "Añadir
          // producto al outfit", o la pantalla de elegir producto si no.
          setPanelStep(
            pickOriginRef.current === 'configure'
              ? 'configure'
              : 'choose-product',
          );
        } else if (panelStep === 'choose-product') {
          // Una plantilla abierta desde la grilla sin pasar por su preview (el gratis que
          // recién entra, en el teléfono) vuelve a la grilla, no a un preview que no vio
          // (ADR-2026-10-27-la-plantilla-del-gratis-nuevo-va-directo-al-producto).
          if (noPreviewKind || abiertoDesdePlantillasRef.current) {
            closePresetSheet();
          } else {
            setPanelStep('preview');
          }
        } else {
          closePresetSheet();
        }
      });
      // Vacía el título de la top bar mientras el sheet está abierto, así
      // solo se ve el botón de volver (sin "Interfaz V2" centrado).
      setMobileTitleOverride(' ');
      // En step 'preview' y 'choose-product' la imagen del preset ocupa
      // toda la pantalla (preview) o la parte superior (choose-product),
      // así que pedimos al header mobile que se vuelva transparente y
      // los botones tomen fondo blanco circular semi-opaco para flotar
      // encima.
      setMobileHeaderTransparent(
        panelStep === 'preview' ||
          panelStep === 'choose-product' ||
          panelStep === 'pick-product' ||
          panelStep === 'upload-product' ||
          panelStep === 'select-vistas' ||
          panelStep === 'configure' ||
          panelStep === 'asistente' ||
          panelStep === 'prompt-libre' ||
          panelStep === 'creador-video',
      );
      // Toggle Imagen/Video en el centro del header — solo en preview.
      // El modo asistente tiene su propio toggle inline en el sheet, así
      // que no duplicamos en el chrome.
      if (panelStep === 'preview') {
        // Tipografía igual al "Generar con mi producto"
        // (text-[15.5px] font-semibold). El indicador azul es un div
        // absoluto que se desliza con translateX entre las dos
        // posiciones — los buttons solo cambian el color del texto.
        const tabBaseClass =
          'relative z-10 inline-flex items-center justify-center px-4 h-8 rounded-full text-[15.5px] font-semibold transition-colors duration-300';
        setMobileHeaderCenter(
          <div className="relative inline-grid grid-cols-2 p-0.5 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.05]">
            {/* Indicador azul deslizable */}
            <div
              className="absolute top-0.5 bottom-0.5 left-0.5 right-1/2 rounded-full bg-brand-primary transition-transform duration-300 ease-out"
              style={{
                transform:
                  previewMediaTab === 'imagen'
                    ? 'translateX(0)'
                    : 'translateX(100%)',
              }}
              aria-hidden
            />
            <button
              type="button"
              onClick={() => setPreviewMediaTab('imagen')}
              className={`${tabBaseClass} ${
                previewMediaTab === 'imagen'
                  ? 'text-white'
                  : 'text-brand-black/65'
              }`}
            >
              Imagen
            </button>
            <button
              type="button"
              onClick={() => setPreviewMediaTab('video')}
              className={`${tabBaseClass} ${
                previewMediaTab === 'video'
                  ? 'text-white'
                  : 'text-brand-black/65'
              }`}
            >
              Video
            </button>
          </div>,
        );
      } else {
        setMobileHeaderCenter(null);
      }
      // Con el selector en el centro, el encabezado va compacto: WhatsApp y la campana se
      // pliegan y los créditos quedan a la derecha. Se apaga en el cleanup, así cualquier
      // salida (otro paso, cerrar el sheet, irse de la pantalla) lo devuelve
      // (ADR-2026-10-12-el-encabezado-compacto-en-la-vista-de-plantilla).
      setMobileHeaderCompacto(panelStep === 'preview');
      return () => {
        setMobileBackOverride(null);
        setMobileTitleOverride(null);
        setMobileHeaderTransparent(false);
        setMobileHeaderCenter(null);
        setMobileHeaderCompacto(false);
      };
    }
    // Home de Interfaz V2 mobile (sin preset abierto): header
    // transparente sin back ni logo. El menu queda como pill blanca
    // circular flotando sobre el contenido — mismo chrome que las
    // pantallas internas del flow del preset, para que la transición
    // entre la home y el sheet sea continua.
    setMobileHeaderTransparent(true);
    setMobileHideBack(true);
    return () => {
      setMobileHeaderTransparent(false);
      setMobileHideBack(false);
    };
  }, [
    selectedPreset,
    panelStep,
    previewMediaTab,
    setMobileBackOverride,
    setMobileTitleOverride,
    setMobileHeaderTransparent,
    setMobileHeaderCenter,
    setMobileHeaderCompacto,
    setMobileHideBack,
  ]);

  // Configuración final
  // `modelSelection` reemplazó al antiguo `modeloVal: string | null`. Es
  // un struct con `mode: 'auto' | 'preset' | 'custom' | 'upload'` para
  // que el config panel pueda mostrar el chip con la info correcta y la
  // generación pueda usar los detalles (custom fields, preset id, etc.).
  // Arranca con el modelo que vino en el `state` de la navegación, si lo hubo
  // (ADR-2026-08-04-desde-un-modelo-se-elige-el-modo); si no, en `null` y lo completa
  // el efecto de defaults con el de la última generación.
  const [modelSelection, setModelSelection] = useState<ModelSelection | null>(
    initialNavModel,
  );
  // ── Tanda mostrada EN EL PANEL (design-modelo desktop) ──────────────
  // Al generar, la columna de configuración (Modelo + Pose) se reemplaza por el
  // panel de la tanda: un recuadro por imagen en curso + las creaciones
  // anteriores. Mientras esto no es null, ese modo NO muestra el overlay
  // full-screen ni navega a la pantalla de resultados
  // (ADR-2026-08-20-tanda-en-el-panel).
  // El visor de una creación está abierto: mientras tanto el sheet baja su z
  // para no taparlo (ver `onVisorChange` en PanelTandaCreaciones).
  const [visorTandaAbierto, setVisorTandaAbierto] = useState<boolean>(false);
  /**
   * El filtro "Videos · Todo" de Creador de video —
   * ADR-2026-09-08-creador-de-video-filtra-por-tipo-de-contenido.
   *
   * Vive acá y no en el sheet porque la columna que recorta (`columnaTandas`) se arma
   * acá; el sheet sólo dibuja el control. Arranca en `'videos'`: es un modo que sólo hace
   * videos, y la columna trae lo de TODOS los modos
   * (ADR-2026-09-06-las-tandas-se-ven-en-todos-los-modos), así que sin filtro se abre
   * mostrando mayormente fotos que no son de acá.
   */
  /**
   * "Ver videos creados" del Creador de video: fuerza la columna de resultados aunque no
   * haya tandas, así el sheet pasa al estado activo con el historial
   * (ADR-2026-09-19-el-creador-de-video-arranca-en-15-segundos-y-abre-sus-creaciones).
   * Se apaga al salir del modo: la próxima vez arranca en la portada.
   */
  const [creacionesDelCreadorAbiertas, setCreacionesDelCreadorAbiertas] = useState(false);
  useEffect(() => {
    if (selectedPreset?.kind !== 'creador-video') setCreacionesDelCreadorAbiertas(false);
  }, [selectedPreset?.kind]);
  const [filtroCreadorVideo, setFiltroCreadorVideo] =
    useState<FiltroContenido>('videos');
  /**
   * Las tandas del panel, de la MÁS NUEVA a la más vieja
   * (ADR-2026-08-20-varias-tandas-en-el-panel), ya filtradas por empresa y por el modo
   * ABIERTO (ADR-2026-09-04-la-tanda-no-se-muda-de-modo: entrar a Automática mientras
   * Modelo genera no tiene que mostrar recuadros que ese modo no pidió).
   *
   * Viven en el registro a nivel app y no en un `useState` de esta pantalla
   * (ADR-2026-09-06-registro-de-generaciones-en-curso, ADR-2026-09-06-use-tandas-panel):
   * cerrar el sheet, volver a la home o ir a Creaciones ya no las borra —ni las que
   * corren ni las que terminaron, que se quedan toda la sesión
   * (ADR-2026-09-08-los-resultados-se-quedan-toda-la-sesion)— y una recarga las reanuda
   * desde el shell. El hook concentra la creación, el cierre con el preload y la falla
   * que acá estaban repetidos rama por rama; `columnaMuestraTandas` (las tandas o la configuración
   * Modelo + Pose — "Cambiar modelo y pose" vuelve a la configuración sin tirar las que
   * corren) y `recargarToken` (la columna vuelve a pedir las creaciones cuando una tanda
   * cierra sin imágenes que dibujar) también salen de ahí.
   */
  const {
    tandasPanel,
    columnaMuestraTandas,
    setColumnaMuestraTandas,
    recargarToken,
    crearTanda,
    registrarExecutionIds,
    cerrarTanda,
    fallarTanda,
    puedeCrear,
    hayResultados,
  } = useTandasPanel(userCompanyId, selectedPreset?.kind);

  /*
   * NO hay excepción por paso: con resultados en la sesión, la columna los muestra en
   * TODOS los modos y en TODOS sus pasos —
   * ADR-2026-09-08-la-columna-de-resultados-no-vuelve-a-la-portada, que supersede
   * ADR-2026-09-08-el-paso-1-de-catalogo-y-fotos-se-queda-con-la-muestra.
   *
   * Hubo un `paso1ConservaLaMuestra` que en Catálogo y Fotos de producto le devolvía la
   * columna a la ilustración del modo mientras se elegían productos. El efecto real era
   * el contrario del buscado: como a un modo se ENTRA por su paso 1, cada cambio de modo
   * tapaba lo recién generado con una portada, y volver a verlo obligaba a avanzar un
   * paso. Lo que explica el modo ya está en el panel de la izquierda (el título y la
   * línea de "Elegí tus productos"); la columna es de los resultados.
   */

  // ── Serie de modelos (design-modelo): 1 IMAGEN POR MODELO ───────────
  // La grilla de modelos del `configure` a dos columnas acumula, igual que la
  // de poses: `modelSelection` es el primero y `modelSelectionsSerie` los que
  // siguen. En la generación se hace fan-out (un request por modelo), porque
  // el backend no acepta varios `modelo_guardado_id` en un run.
  // NO confundir con `modelSelectionsExtra` de acá abajo: eso son SLOTS de una
  // composición grupal (varios modelos en UNA foto).
  // Ver ADR-2026-08-20-modelos-multi-select-serie.
  const [modelSelectionsSerie, setModelSelectionsSerie] = useState<
    ModelSelection[]
  >([]);
  // ── Modo "varios modelos" (UI-only por ahora, sin payload backend) ──
  // El user puede activarlo desde el ModelPicker. Cuando está activo, el
  // picker muestra varios containers (slot principal = modelSelection,
  // slots extra = modelSelectionsExtra). Hasta 4 modelos en total.
  const [modelSelectionsExtra, setModelSelectionsExtra] = useState<
    (ModelSelection | null)[]
  >([]);
  const [multiModelEnabled, setMultiModelEnabled] = useState<boolean>(false);

  /*
   * El Catálogo NO tiene "Utilizar varios modelos": su grilla es de selección ÚNICA y
   * punto (ADR-2026-08-28-catalogo-un-solo-modelo, supersede
   * ADR-2026-08-28-catalogo-varios-modelos). Por eso acá no hay estado que guardar: el
   * interruptor y su flag se fueron con él. El de Modelo v2, que sí lo tiene, es el de
   * abajo.
   */

  /**
   * Modelo v2: el mismo interruptor "Utilizar varios modelos" que estrenó el Catálogo,
   * aplicado a la grilla del panel lateral (ADR-2026-08-28-modelo-v2-varios-modelos).
   *
   * Estado PROPIO y no el del Catálogo: lo que significa "varios" es distinto en cada
   * modo —acá los modelos van todos en la MISMA foto
   * (ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto), en Catálogo es una imagen
   * por modelo— y apagarlo tiene que limpiar cosas distintas.
   */
  const [modeloVariosModelos, setModeloVariosModelos] =
    useState<boolean>(false);
  const handleModeloVariosModelosChange = (v: boolean) => {
    setModeloVariosModelos(v);
    if (v) return;
    // Al APAGARLO vuelve a haber un solo modelo: el grupo se descarta y queda el
    // primero. `multiModelEnabled` es la llave del flujo grupal —filtra las poses por
    // cantidad de personas y cobra por composición—, así que apagarla es parte de volver
    // a single: sin eso el modo seguiría ofreciendo poses de 3 personas para un modelo.
    setModelSelectionsExtra([]);
    setMultiModelEnabled(false);
  };

  // ── Serie de modelos: toggle desde la grilla ────────────────────────
  // Dos selecciones son "la misma" si apuntan al mismo modelo guardado, o si
  // ambas son Automático. 'custom'/'upload' no se acumulan (vienen de un flow
  // de una sola selección), así que nunca matchean entre sí.
  const mismoModelo = (a: ModelSelection, b: ModelSelection): boolean => {
    if (a.mode !== b.mode) return false;
    if (a.mode === 'preset' && b.mode === 'preset') return a.id === b.id;
    return a.mode === 'auto';
  };
  /**
   * Tap en una card de la grilla de modelos: acumula o quita.
   * Invariantes (las mismas que el picker de poses): siempre queda ≥1 modelo,
   * y el primero de la serie es el que vive en `modelSelection` — al sacarlo,
   * el siguiente lo reemplaza.
   */
  const handleToggleModelSerie = (sel: ModelSelection) => {
    const serie = [modelSelection, ...modelSelectionsSerie].filter(
      (s): s is ModelSelection => !!s,
    );
    const idx = serie.findIndex((s) => mismoModelo(s, sel));
    if (idx >= 0) {
      // Deseleccionar. El último TAMBIÉN se puede sacar: la grilla del paso 2 muestra la
      // cuenta en el título y apaga el "Generar" en cero, así que quedarse sin modelo es
      // un estado visible y sin salida en falso. Antes el tap se ignoraba y la tarjeta
      // parecía rota.
      if (serie.length === 1) {
        setModelSelection(null);
        setModelSelectionsSerie([]);
        return;
      }
      const next = serie.filter((_, i) => i !== idx);
      setModelSelection(next[0]);
      setModelSelectionsSerie(next.slice(1));
      return;
    }
    if (serie.length === 0) {
      setModelSelection(sel);
      return;
    }
    // Tope duro, además del visual: la grilla apaga las cards que ya no entran
    // (ADR-2026-08-25-tres-modelos-como-maximo), pero el estado no depende de que
    // la UI se haya acordado de apagarlas — hay más de un picker escribiendo acá.
    if (serie.length >= MAX_MODELOS_SERIE) return;
    setModelSelectionsSerie([...modelSelectionsSerie, sel]);
  };
  /**
   * Tap en la grilla de modelos del rediseño v2 — ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto.
   *
   * Acá elegir varios modelos NO es pedir varias fotos: es pedir UNA foto con
   * todos adentro. Por eso escribe en el mismo estado que el flujo de
   * "composición grupal" que ya existía (`modelSelection` + `modelSelectionsExtra`
   * + `multiModelEnabled`) en vez de en la serie, que fan-outea un request por
   * modelo. Enchufarlo ahí trae gratis todo lo que ese flujo ya resuelve: el
   * catálogo de poses pasa a las de 2 o 3 personas (`expectedPeopleInPose` →
   * `allPosesFiltered`), la pose se limita a una, el costo se calcula por
   * personas en la composición y el payload sale con `modelo_mode: 'multiple'`.
   *
   * El PRIMER elegido queda como `modelSelection` y los que siguen como extras:
   * es el mismo orden que espera `modelo_config`.
   */
  const handleToggleModelGrupal = (sel: ModelSelection) => {
    const grupo = [modelSelection, ...modelSelectionsExtra].filter(
      (s): s is ModelSelection => !!s,
    );
    const idx = grupo.findIndex((s) => mismoModelo(s, sel));
    const next =
      idx >= 0
        ? grupo.filter((_, i) => i !== idx)
        : // El tope se respeta acá también y no sólo en la grilla, que es la que
          // apaga las cards: hay más de un picker escribiendo este estado
          // (ADR-2026-08-25-tres-modelos-como-maximo).
          grupo.length >= MAX_MODELOS_SERIE
          ? grupo
          : [...grupo, sel];
    if (next === grupo) return;
    setModelSelection(next[0] ?? null);
    setModelSelectionsExtra(next.slice(1));
    // Es la llave del flujo grupal: con uno solo hay que APAGARLA, o el modo
    // seguiría filtrando poses de N personas y cobrando por una composición que
    // ya no existe.
    setMultiModelEnabled(next.length > 1);
    // La serie y el grupo son excluyentes: si venía algo de la serie (por haber
    // pasado por otra variante del modo), se limpia para no sumar un fan-out
    // encima de la composición.
    setModelSelectionsSerie([]);
  };

  /**
   * Setter del modelo desde las UIs de selección ÚNICA (sheet mobile, "Ver
   * más", create/upload). Ahí el user ve una sola marca, así que la serie se
   * resetea: si no, quedarían modelos elegidos que esa pantalla no muestra y
   * el costo subiría sin explicación.
   */
  const handleModelChangeSingle = (sel: ModelSelection) => {
    setModelSelection(sel);
    setModelSelectionsSerie([]);
  };
  // En mobile el botón "Modelo" abre un bottom sheet (esta state). En
  // desktop se usa para abrir el sheet como side panel cuando el user
  // toca "Creá tu modelo" o "Cargá tu modelo" desde el inline picker.
  const [modelPickerOpen, setModelPickerOpen] = useState<boolean>(false);
  // Cuando el inline (desktop) está en sub-list de un slot multi y el
  // user toca Crea/Carga, delegamos al sheet pasando este idx. El sheet
  // arranca con view = 'create'/'upload' y editingMultiIdx = este idx.
  const [modelPickerInitialEditingIdx, setModelPickerInitialEditingIdx] =
    useState<number | null>(null);
  // ── Selección de productos por slot (modo varios modelos) ──
  // Cuando el user toca "Utilizando" / miniaturas en un slot, abrimos
  // el MultiSlotProductPickerSheet con `productPickerForSlot` seteado
  // al idx correspondiente. Al confirmar, los productos quedan en
  // `multiSlotProducts[idx]`. Si un slot no tiene entrada en este
  // record, hereda visualmente del `selectedProducts` global.
  const [productPickerOpen, setProductPickerOpen] = useState<boolean>(false);
  const [productPickerForSlot, setProductPickerForSlot] = useState<
    number | null
  >(null);
  const [multiSlotProducts, setMultiSlotProducts] = useState<
    Record<number, MockProductV2[]>
  >({});
  // Vista inicial del sheet cuando se abre — permite entrar directo a
  // 'create' o 'upload' (desktop design-modelo) sin pasar por 'list'.
  const [modelPickerInitialView, setModelPickerInitialView] =
    useState<ModelPickerView>('list');
  // Formato ahora se guarda como id (ej: 'square', 'story') de
  // FORMAT_OPTIONS_V2. El display label se computa con getFormatLabel.
  const [formatoVal, setFormatoVal] = useState<string | null>(null);
  /**
   * Resolución de salida de la tanda — el ajuste que la UI del rediseño llama "Calidad".
   *
   * UN solo estado para las dos superficies: la fila desplegable del panel de desktop y
   * el segmentado de `AjustesRapidosProducto`. Viaja en `image_size` y multiplica el
   * costo (ADR-2026-08-25-resolucion-de-generacion-2k-4k).
   *
   * Arranca SIEMPRE en la barata: nadie gasta de más por no haber tocado nada.
   */
  const [resolucionVal, setResolucionVal] =
    useState<ResolucionImagen>(RESOLUCION_DEFAULT);
  const [formatPickerOpen, setFormatPickerOpen] = useState<boolean>(false);
  /**
   * ¿La plantilla elegida sale en VIDEO? Corre sobre Kling, que acepta 16:9, 9:16 y 1:1
   * y nada más (ADR-2026-09-14-el-video-de-plantilla-solo-ofrece-los-formatos-que-kling-
   * acepta). El picker le muestra sólo esos tres.
   */
  const esVideoDePlantilla =
    !!selectedPreset &&
    !esDesignModelo(selectedPreset.kind) &&
    selectedPreset.kind !== 'design' &&
    selectedPreset.outputType === 'video';
  /**
   * Y se CORRIGE el que venía puesto. `formatoVal` es uno solo para toda la Interfaz y se
   * arrastra entre modos: quien venía de generar imágenes en 4:5 entraba a la plantilla de
   * video con 4:5 sin tocar nada, y eso es un 400 de Kling sin fallback. Filtrar la lista
   * no alcanza — el valor viejo sigue ahí y es el que viaja.
   */
  useEffect(() => {
    if (!esVideoDePlantilla) return;
    const valido = formatoParaVideoPlantilla(formatoVal);
    if (valido !== formatoVal) setFormatoVal(valido);
  }, [esVideoDePlantilla, formatoVal]);
  const [otraConfig, setOtraConfig] = useState('');
  // Solo aplica al kind 'design' (fila "Diseña tu imagen"). En 'template'
  // no se renderiza y el state queda en null.
  const [fondoVal, setFondoVal] = useState<string | null>(null);

  // ── Selecciones del modo 'catalogo-ia' ───────────────────────────────
  // Vistas elegidas en el step `select-vistas`. El user puede mezclar
  // poses con-modelo (subset de `allPoses`, solo 1 persona) con vistas
  // solo-producto (del catálogo `VISTAS_SOLO_PRODUCTO`). El backend
  // recibe una llamada V3 por cada (vista × producto) compatible —
  // ver `handleGenerateCatalog`.
  const [catalogoVistas, setCatalogoVistas] = useState<VistaCatalogo[]>([]);
  // ── Selecciones del modo 'fotos-producto' ────────────────────────────
  // Slugs de las vistas solo-producto elegidas (`VISTAS_SOLO_PRODUCTO`).
  // Cada vista es UNA imagen: la generación manda un request por vista
  // (ver ADR-2026-08-13-fotos-producto-fan-out-por-vista). El fondo, el
  // formato y la instrucción adicional reusan el state compartido
  // (`fondoSelection` / `formatoVal` / `otraConfig`).
  const [vistaIds, setVistaIds] = useState<string[]>([]);
  // ── Selecciones del modo 'design-modelo' ─────────────────────────────
  // Poses (multi-select) y fondo (auto / biblioteca / guardado /
  // descripción). Solo se usan cuando `esDesignModelo(selectedPreset.kind)`.
  const [poseIds, setPoseIds] = useState<string[]>([]);
  // Toggle "Seleccionar múltiples" del menú de poses (design-modelo).
  // Default OFF: cada tap reemplaza la pose. Solo con ON el user puede
  // acumular varias (1 imagen generada por pose). Mismo patrón que
  // `multiPoseToggle` de PruebaNuevaInterfaz. Invariante:
  // poseIds.length > 1 ⇒ multiPoseEnabled (la UI no permite acumular
  // con el toggle apagado; los set programáticos lo sincronizan).
  const [multiPoseEnabled, setMultiPoseEnabled] = useState<boolean>(false);
  const [posePickerOpen, setPosePickerOpen] = useState<boolean>(false);
  /** En qué vista abre el sheet de poses: la grilla, o el explicador de "Subí tu propia
   *  pose" cuando se entra por la tarjeta que cierra la grilla inline
   *  (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). */
  const [posePickerVista, setPosePickerVista] = useState<'list' | 'subir'>('list');
  // Poses personalizadas del cliente (tab "Mis poses" del PosePicker).
  const [customPoses, setCustomPoses] = useState<PoseV2[]>([]);
  const [uploadingPose, setUploadingPose] = useState<boolean>(false);
  // Carga las poses custom de la empresa (las globales viven en `allPoses`).
  useEffect(() => {
    if (!userCompanyId) return;
    let cancelled = false;
    (async () => {
      const mine = await fetchMyCustomPoses(userCompanyId);
      if (!cancelled) setCustomPoses(mine);
    })();
    return () => {
      cancelled = true;
    };
  }, [userCompanyId]);
  // Subir una pose: dispara el flujo del backend y refetchea. GRATIS desde
  // ADR-2026-08-03-subir-pose-es-gratis — por eso no dispara
  // `atlas:credits-updated` ni hace falta avisar un precio en el PosePicker.
  const handleUploadPose = async (file: File) => {
    if (!userCompanyId) return;
    setUploadingPose(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada');
      await uploadCustomPose({
        userId: user.id,
        idCompany: userCompanyId,
        file,
        cantidadPersonas: expectedPeopleInPose,
      });
      setCustomPoses(await fetchMyCustomPoses(userCompanyId));
    } catch (e) {
      console.error('[InterfazV2] handleUploadPose error', e);
      alert(
        'No pudimos procesar la pose. Probá de nuevo con otra foto.',
      );
    } finally {
      setUploadingPose(false);
    }
  };
  // Borrar una pose propia: saca la fila + la imagen, y la deselecciona.
  const handleDeletePose = async (pose: PoseV2) => {
    if (!userCompanyId) return;
    try {
      await deleteCustomPose({
        id: pose.id,
        idCompany: userCompanyId,
        filename: pose.filename,
      });
      setCustomPoses((prev) => prev.filter((p) => p.id !== pose.id));
      setPoseIds((prev) => prev.filter((id) => id !== pose.id));
      // También quitarla de las vistas del catálogo si estaba elegida.
      setCatalogoVistas((prev) =>
        prev.filter((v) => !(v.kind === 'mis-pose' && v.id === pose.id)),
      );
    } catch (e) {
      console.error('[InterfazV2] handleDeletePose error', e);
    }
  };
  // Subir una pose desde el CATÁLOGO (paso "Elegí las vistas"). Igual que
  // handleUploadPose pero forzando 1 persona (catálogo = 1 modelo) y
  // auto-seleccionando la pose creada como vista, para que el user solo
  // tenga que generar.
  const handleCatalogUploadPose = async (file: File) => {
    if (!userCompanyId) return;
    setUploadingPose(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada');
      const beforeIds = new Set(customPoses.map((p) => p.id));
      await uploadCustomPose({
        userId: user.id,
        idCompany: userCompanyId,
        file,
        cantidadPersonas: 1,
      });
      const refreshed = await fetchMyCustomPoses(userCompanyId);
      setCustomPoses(refreshed);
      // Auto-seleccionar la(s) pose(s) nueva(s) como vista del catálogo.
      const nuevas = refreshed.filter(
        (p) => !beforeIds.has(p.id) && (p.cantidadPersonas ?? 1) === 1,
      );
      if (nuevas.length > 0) {
        setCatalogoVistas((prev) => {
          const yaEsta = new Set(prev.map((v) => `${v.kind}:${v.id}`));
          const add: VistaCatalogo[] = nuevas
            .filter((p) => !yaEsta.has(`mis-pose:${p.id}`))
            .map((p) => ({
              id: p.id,
              nombre: p.nombre,
              imageUrl: p.imageUrl,
              kind: 'mis-pose' as const,
              filename: p.filename,
            }));
          return [...prev, ...add];
        });
      }
    } catch (e) {
      console.error('[InterfazV2] handleCatalogUploadPose error', e);
      alert(
        'No pudimos procesar la pose. Probá de nuevo con otra foto.',
      );
    } finally {
      setUploadingPose(false);
    }
  };
  const [fondoSelection, setFondoSelection] = useState<FondoSelection | null>(
    null,
  );
  const [fondoPickerOpen, setFondoPickerOpen] = useState<boolean>(false);
  // Vista inicial del FondoPickerSheet — permite entrar directo a
  // 'describe' o 'upload' desde el inline picker en desktop.
  const [fondoPickerInitialView, setFondoPickerInitialView] =
    useState<FondoPickerView>('list');
  // Categoría inicial de la vista 'all' — seteada al tocar una card de
  // categoría en el carrusel inline desktop (abre el sheet lateral
  // directo en esa categoría).
  const [fondoPickerInitialCategory, setFondoPickerInitialCategory] =
    useState<string | null>(null);
  // View del FondoPicker en desktop inline — controlada por el padre
  // igual que la del ModelPickerInline para que el ConfigurePanel pueda
  // esconder otras secciones cuando el user está en describe/upload.
  const [fondoView, setFondoView] = useState<FondoPickerView>('list');
  // Catálogo de fondos — biblioteca pública filtrada por rubro + los
  // subidos por el user. Se fetchean cuando el user abre un preset
  // design-modelo (lazy) para no pagar el query si no aplica.
  const [bibliotecaFondos, setBibliotecaFondos] = useState<FondoBiblioteca[]>(
    [],
  );
  const [fondosGuardados, setFondosGuardados] = useState<FondoGuardado[]>([]);
  const refreshFondosGuardados = async () => {
    if (!userCompanyId) return;
    const list = await fetchFondosGuardados(userCompanyId);
    setFondosGuardados(list);
  };
  useEffect(() => {
    if (!isDesignRubro || !userCompanyId) return;
    (async () => {
      const [biblio, guardados] = await Promise.all([
        // Sin filtro de rubro — mostramos toda la biblioteca de fondos
        // para que el user pueda ver todas las categorías en "Ver más".
        fetchBibliotecaFondos(null),
        fetchFondosGuardados(userCompanyId),
      ]);
      setBibliotecaFondos(biblio);
      setFondosGuardados(guardados);
    })();
  }, [isDesignRubro, userCompanyId, userRubro]);

  // Auto-rellenar formato/modelo/fondo/pose con los de la última
  // generación cuando el user abre un preset sheet. Copiado del flujo
  // de PruebaNuevaInterfaz: usamos el storage compartido
  // `loadLastCrearAnuncioState` para que ambas pantallas mantengan
  // continuidad. Fallbacks hardcodeados cuando no hay nada en storage:
  //   • formato → 'post'
  //   • modelo  → null (el user opta in eligiendo una card)
  //   • fondo   → { mode: 'auto' } (solo en design-modelo)
  //   • pose    → primera pose del catálogo (manejado en otro effect
  //               porque depende de `allPoses` async)
  //
  // Si el user ya tocó algo (no-null), no pisamos su elección. El
  // effect se re-dispara cuando userModels / bibliotecaFondos /
  // fondosGuardados se llenan vía fetch async, por si el restore
  // necesita un lookup por id.
  useEffect(() => {
    if (!selectedPreset) return;
    const isDesignModelo = esDesignModelo(selectedPreset.kind);
    const lastState = loadLastCrearAnuncioState();

    // ─── Formato ───────────────────────────────────────────────────
    if (formatoVal === null) {
      if (selectedPreset.kind === 'video-influencer') {
        // Video con influencer: default fijo Historias/Reels (9:16) — es un
        // video vertical UGC; no restauramos el formato de la última
        // generación de imagen (suele ser 'post'/'square' y no aplica acá).
        setFormatoVal('story');
      } else if (
        // Plantillas de Belleza y Cuidado Personal: default fijo 9:16 —
        // ADR-2026-09-10-las-plantillas-de-belleza-arrancan-en-nueve-dieciseis.
        //
        // Va en la rama del default FIJO —la de Video con influencer— y no como último
        // término del `||` de abajo, a propósito: `fromLast` y `fromLegacy` son el
        // último formato usado en TODA la app, no en este modo, así que en una cuenta
        // que también genera para Moda casi siempre traen un 'post' de otra pantalla y
        // el default nuevo no se vería nunca. Es el mismo razonamiento que ya está
        // escrito arriba para el video vertical.
        //
        // La contra: alguien que elige 4:5 a mano en una plantilla de Belleza vuelve a
        // encontrar 9:16 la próxima vez. Se acepta porque no hay memoria POR MODO donde
        // guardar esa elección; el día que la haya, esto pasa a ser el fallback de esa
        // memoria en vez de un valor fijo.
        esPlantillaDeBelleza
      ) {
        setFormatoVal('story');
      } else {
        const fromLast = lastState?.formato;
        const fromLegacy = (() => {
          try {
            return formatoEstandarDe(localStorage.getItem('atlas-v2-last-format'));
          } catch {
            return null;
          }
        })();
        setFormatoVal(fromLast || fromLegacy || 'post');
      }
    }

    // ─── Modelo ────────────────────────────────────────────────────
    // Si la plantilla declara `inputs_aceptados` sin "modelo", no
    // restauramos modelo (la UI lo esconde y el payload no lo manda).
    const templateAcceptsModelo =
      !selectedPreset.inputsAceptados ||
      selectedPreset.inputsAceptados.includes('modelo');
    if (modelSelection === null && templateAcceptsModelo) {
      const m = lastState?.modelo;
      if (m?.mode === 'ia') {
        setModelSelection({ mode: 'auto' });
      } else if (m?.mode === 'personalizado') {
        setModelSelection({
          mode: 'custom',
          fields: {
            genero: m.genero,
            edad: m.edad,
            tipoCuerpo: m.tipoCuerpo,
            tonoPiel: m.tonoPiel,
            colorPelo: m.colorPelo,
            largoPelo: m.largoPelo,
            descripcion: m.descripcion,
          },
        });
      } else if (m?.mode === 'guardados' && m.guardadoId) {
        const found = userModels.find(
          (um) => String(um.id) === String(m.guardadoId),
        );
        if (found) {
          setModelSelection({
            mode: 'preset',
            id: String(found.id),
            name: found.nombre,
            imageUrl: found.thumbnailUrl,
          });
        }
        // Si userModels aún no cargó (length 0), no hacemos nada — el
        // effect se re-dispara cuando llega y vuelve a intentar.
      } else {
        // Fallback al legacy key V2 (solo si nada en storage compartido).
        try {
          const raw = localStorage.getItem('atlas-v2-last-model');
          if (raw) {
            const parsed = JSON.parse(raw) as ModelSelection;
            if (
              parsed &&
              (parsed.mode === 'auto' ||
                parsed.mode === 'preset' ||
                parsed.mode === 'custom' ||
                parsed.mode === 'upload')
            ) {
              setModelSelection(parsed);
            }
          }
        } catch {
          /* noop */
        }
      }
    }

    // ─── Fondo (fotos-producto) ────────────────────────────────────
    // Default FIJO en blanco, sin restaurar el de la última generación: el
    // modo existe para fotos de catálogo y ese es su punto de partida
    // esperado. Si el user quiere una escena, la describe en el picker.
    if (selectedPreset.kind === 'fotos-producto' && fondoSelection === null) {
      setFondoSelection({ mode: 'blanco' });
    }

    // ─── Fondo (solo design-modelo) ────────────────────────────────
    // Los modos que requieren lookup (elegido, guardado) esperan a que
    // el catálogo cargue antes de fallback-ear a auto — si seteamos
    // auto ahora y la lista llega después, perdemos la restauración.
    if (isDesignModelo && fondoSelection === null) {
      const bg = lastState?.background;
      if (bg?.mode === 'ia' || !bg?.mode) {
        setFondoSelection({ mode: 'auto' });
      } else if (bg.mode === 'blanco') {
        setFondoSelection({ mode: 'blanco' });
      } else if (bg.mode === 'descripcion' && bg.text) {
        setFondoSelection({ mode: 'descripcion', text: bg.text });
      } else if (bg.mode === 'elegido' && bg.id) {
        const found = bibliotecaFondos.find((f) => f.id === bg.id);
        if (found) {
          setFondoSelection({
            mode: 'biblioteca',
            id: found.id,
            name: found.nombre,
            imageUrl: found.imageUrl,
          });
        } else if (bibliotecaFondos.length > 0) {
          // Catálogo cargado y el id no existe → fallback.
          setFondoSelection({ mode: 'auto' });
        }
      } else if (bg.mode === 'guardado' && bg.fileName) {
        const found = fondosGuardados.find((f) => f.fileName === bg.fileName);
        if (found) {
          setFondoSelection({
            mode: 'guardado',
            fileName: found.fileName,
            name: found.nombre,
            imageUrl: found.imageUrl,
          });
        } else if (fondosGuardados.length > 0) {
          setFondoSelection({ mode: 'auto' });
        }
      } else {
        setFondoSelection({ mode: 'auto' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset, userModels, bibliotecaFondos, fondosGuardados]);

  // ── Modo 'fotos-producto': catálogo de vistas de los productos elegidos ──
  // Las vistas compatibles dependen del `model_view` de CADA producto (un
  // zapato no tiene "maniquí invisible"). Con varios productos se ofrece la
  // UNIÓN anotada con `productIds` — a qué productos aplica cada vista
  // (ADR-2026-08-30-fotos-producto-multi-producto-2): la generación produce
  // una imagen por cada par (producto, vista) aplicable y activo.
  const vistasProducto = useMemo(
    () =>
      selectedPreset?.kind === 'fotos-producto'
        ? vistasSoloProductoParaConjunto(
            selectedProducts.map((p) => ({
              id: p.id,
              modelView: p.modelView,
            })),
          )
        : [],
    [selectedPreset?.kind, selectedProducts],
  );
  // Si el user cambia de producto después de elegir vistas, las que ya no
  // aplican a la nueva categoría se caen solas. Sin esto quedaban en el state
  // (invisibles en la grilla) y viajaban igual al backend.
  useEffect(() => {
    if (selectedPreset?.kind !== 'fotos-producto') return;
    setVistaIds((prev) => {
      const validos = new Set(vistasProducto.map((v) => v.id));
      const next = prev.filter((id) => validos.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [vistasProducto, selectedPreset?.kind]);
  // Productos DESACTIVADOS por vista (checklist del badge de la grilla,
  // ADR-2026-08-30-fotos-producto-multi-producto-2). Default: todos activos.
  // Keyed por slug de vista; las entradas de vistas deseleccionadas se
  // conservan (re-elegir la vista recuerda la elección).
  const [vistaProductosExcluidos, setVistaProductosExcluidos] = useState<
    Record<string, string[]>
  >({});
  /**
   * Plantillas de Belleza: la selección se recorta a UNO también cuando llegó armada de
   * antes — ADR-2026-09-14-la-plantilla-de-belleza-se-genera-con-un-producto.
   *
   * El picker ya no deja sumar, pero `selectedProducts` puede venir con varios de un
   * modo anterior, del restore de la sesión o del "producto entra con el modo"
   * (ADR-2026-08-04-el-producto-entra-con-el-modo). Sin esto la tanda saldría con dos
   * productos que la pantalla del modo no muestra. Se queda el PRIMERO, que es el
   * principal (el picker devuelve en orden de selección).
   */
  useEffect(() => {
    if (!esPlantillaDeBelleza) return;
    setSelectedProducts((prev) => (prev.length > 1 ? [prev[0]] : prev));
  }, [esPlantillaDeBelleza, selectedProducts]);
  // Poda: un producto que sale de la selección global no puede quedar
  // excluido en ninguna vista (volvería "muerto" si se re-agrega).
  useEffect(() => {
    if (selectedPreset?.kind !== 'fotos-producto') return;
    const validos = new Set(selectedProducts.map((p) => p.id));
    setVistaProductosExcluidos((prev) => {
      let cambio = false;
      const next: Record<string, string[]> = {};
      for (const [vistaId, ids] of Object.entries(prev)) {
        const filtrados = ids.filter((id) => validos.has(id));
        if (filtrados.length !== ids.length) cambio = true;
        if (filtrados.length > 0) next[vistaId] = filtrados;
        else if (ids.length > 0) cambio = true;
      }
      return cambio ? next : prev;
    });
  }, [selectedProducts, selectedPreset?.kind]);
  // Activar/desactivar un producto para UNA vista. El último activo no se
  // puede desactivar: cada vista seleccionada genera al menos una imagen
  // (para no generar ninguna, se desmarca la vista).
  const handleToggleVistaProducto = (vistaId: string, productId: string) => {
    const vista = vistasProducto.find((v) => v.id === vistaId);
    if (!vista) return;
    setVistaProductosExcluidos((prev) => {
      const excluidos = prev[vistaId] ?? [];
      if (excluidos.includes(productId)) {
        const next = excluidos.filter((id) => id !== productId);
        return { ...prev, [vistaId]: next };
      }
      const activos = vista.productIds.filter(
        (id) => !excluidos.includes(id),
      );
      if (activos.length <= 1) return prev; // último activo: no se toca
      return { ...prev, [vistaId]: [...excluidos, productId] };
    });
  };
  // Total de imágenes de la tanda = Σ por vista elegida de sus productos
  // activos. Alimenta el pill del Generar, el guard de créditos (ADR-518) y
  // el contador del panel.
  const fotosProductoTotalImagenes = useMemo(() => {
    if (selectedPreset?.kind !== 'fotos-producto') return 0;
    const porId = new Map(vistasProducto.map((v) => [v.id, v]));
    return vistaIds.reduce((acc, vistaId) => {
      const vista = porId.get(vistaId);
      if (!vista) return acc;
      const excluidos = vistaProductosExcluidos[vistaId] ?? [];
      return (
        acc + vista.productIds.filter((id) => !excluidos.includes(id)).length
      );
    }, 0);
  }, [
    selectedPreset?.kind,
    vistasProducto,
    vistaIds,
    vistaProductosExcluidos,
  ]);

  // (La preselección de pose por defecto vive más abajo, después de
  //  `allPosesFiltered`, porque ahora toma la primera pose RECOMENDADA.)

  // Cantidad de modelos activos: en single mode siempre 1; en multi
  // mode = 1 (slot principal) + cantidad de extras.
  const expectedPeopleInPose = multiModelEnabled
    ? 1 + modelSelectionsExtra.length
    : 1;

  /**
   * En el modo Modelo, elegir varios modelos arma UNA foto con todos, no una foto por
   * modelo (ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto).
   */
  const modeloGrupal = esDesignModelo(selectedPreset?.kind);
  /**
   * Cuántos modelos van elegidos en el GRUPO (la foto con todos adentro): el principal
   * más los slots extra ocupados. Lo lee la bajada del interruptor "Utilizar varios
   * modelos" del sheet de mobile; en desktop la misma cuenta la hace el shell con el
   * prop `modelSelectionsSerie` que recibe.
   */
  const modelosElegidosGrupo =
    (modelSelection ? 1 : 0) +
    modelSelectionsExtra.filter((s) => !!s).length;

  // Catálogo de poses filtrado por la cantidad de personas que
  // matchea la cantidad de modelos. Las poses sin `cantidad_personas`
  // explícito caen como 1 desde el mapper (ver posesApi).
  //
  // Además, ordenamos las poses RECOMENDADAS para el producto primero: si
  // elegiste una remera (model_view "torso"), van primero las poses que
  // cubren el torso. Misma lógica que PruebaNuevaInterfaz: una pose es
  // recomendada si sus `areasPrincipales` incluyen el/los model_view de los
  // productos ('cuerpo_completo' = torso + piernas). Dentro del conjunto
  // preferido, "Editorial de pie" es el default; y si el producto no tiene
  // model_view (o ninguna pose lo cubre) caemos a cuerpo completo — nunca a
  // un close-up arbitrario. (Ver detalle dentro del memo.)
  const allPosesFiltered = React.useMemo(() => {
    const base = allPoses.filter(
      (p) => p.cantidadPersonas === expectedPeopleInPose,
    );
    const modelViews = selectedProducts
      .map((p) => (p.modelView || '').trim())
      .filter((mv) => mv.length > 0);
    const expand = (mv: string): string[] =>
      mv === 'cuerpo_completo' ? ['torso', 'piernas'] : [mv];
    const isFullBody = (pose: PoseV2) =>
      pose.categoria === 'cuerpo_completo' ||
      (pose.areasPrincipales.includes('torso') &&
        pose.areasPrincipales.includes('piernas'));

    // Conjunto "preferido" que va primero en la lista → su primer ítem es
    // el default que selecciona el effect de abajo.
    //   • Con modelView → poses que cubren las áreas del/los producto(s)
    //     (misma lógica que PruebaNuevaInterfaz).
    //   • Sin modelView → poses de cuerpo completo.
    // Si el preferido queda vacío (producto con modelView que ninguna pose
    // cubre), caemos a cuerpo completo igual — así el default NUNCA arranca
    // en un close-up arbitrario (la primera por `created_at` es
    // "Close-up superior", que incluye 'torso' y se veía como "la del
    // cuello").
    const isRecommended = (pose: PoseV2) =>
      modelViews.every((mv) =>
        expand(mv).every((part) => pose.areasPrincipales.includes(part)),
      );
    let preferred =
      modelViews.length === 0 ? base.filter(isFullBody) : base.filter(isRecommended);
    if (preferred.length === 0) {
      preferred = base.filter(isFullBody);
      if (preferred.length === 0) return base;
    }

    // Dentro del conjunto preferido, "Editorial de pie" es el default
    // elegido (pose de pie, encuadre editorial) — la llevamos al frente si
    // está y es compatible. Si no, queda la primera del conjunto.
    const prefIdx = preferred.findIndex(
      (p) => p.nombre.trim().toLowerCase() === DEFAULT_FULLBODY_POSE_NAME,
    );
    const orderedPreferred =
      prefIdx > 0
        ? [preferred[prefIdx], ...preferred.filter((_, i) => i !== prefIdx)]
        : preferred;
    const prefIds = new Set(orderedPreferred.map((p) => p.id));
    const rest = base.filter((p) => !prefIds.has(p.id));
    return [...orderedPreferred, ...rest];
  }, [allPoses, expectedPeopleInPose, selectedProducts]);

  // Pose default (solo design-modelo): seleccionamos la PRIMERA pose
  // RECOMENDADA (primer ítem de `allPosesFiltered`, que ya viene ordenado
  // recomendadas-primero según el `modelView` del producto). Mismo espíritu
  // que PruebaNuevaInterfaz (arranca en el tab "recomendado").
  //
  // SIN PRODUCTO TAMBIÉN SE ELIGE — ADR-2026-09-04-el-modo-modelo-siempre-arranca-con-pose.
  //
  // Antes esto esperaba a que hubiera un producto: sin él, el orden de
  // `allPosesFiltered` todavía no es el recomendado (queda por `created_at`) y se
  // fijaba una pose arbitraria que después ya no se re-evaluaba. Pero esa espera es
  // justo lo que dejaba el modo abriendo en "Pose · Automática", que es la opción que
  // se sacó.
  //
  // La espera se reemplaza por un default PROVISORIO: se elige igual la primera del
  // orden actual, y el ref queda en 'pending' hasta que haya producto. Mientras siga
  // 'pending', cada recálculo del catálogo vuelve a aplicar la primera — o sea que al
  // elegir el producto la pose se RE-recomienda sola, en vez de quedar clavada la
  // arbitraria. El primer tap del usuario lo pasa a 'manual' (`handlePoseIdsChange`) y
  // desde ahí no se toca más.
  useEffect(() => {
    if (!selectedPreset) return;
    if (!esDesignModelo(selectedPreset.kind)) return;
    if (allPosesFiltered.length === 0) return;
    if (poseAutoSelectRef.current !== 'pending') return;
    setPoseIds([allPosesFiltered[0].id]);
    // Sólo se sella cuando la recomendación ya pudo mirar el producto; sin producto
    // queda abierto a re-recomendarse.
    if (selectedProducts.length > 0) poseAutoSelectRef.current = 'applied';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset, allPosesFiltered, selectedProducts.length]);

  // Cambio de pose hecho por el user (inline picker o PosePickerSheet).
  // Marca la selección como manual para que el default recomendado no la
  // pise en re-renders ni en cambios async del catálogo.
  const handlePoseIdsChange = useCallback(
    (next: string[]) => {
      // La selección PUEDE quedar vacía. Antes se ignoraba el cambio —el payload de pose
      // saldría vacío y rompería la generación—, pero eso hacía que la última pose no se
      // pudiera sacar y el tap pareciera roto. Ahora el vacío es un estado legítimo: la
      // grilla del paso 2 lo muestra en el título ("Pose", sin cuenta), el "Generar" del
      // panel se apaga, y `handleGenerateFinal` corta por las dudas si igual llegara.
      poseAutoSelectRef.current = 'manual';
      // La composición grupal YA fan-outea por pose —un request por pose, todos con el
      // mismo `execution_id`—, así que con 2 o 3 modelos se pueden elegir varias poses y
      // cada una sale como su propia foto grupal
      // (ADR-2026-08-28-modelo-v2-varias-poses-con-varios-modelos, supersede en este
      // punto el capado de ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto).
      //
      // En E-commerce (`design`) se conserva: ahí la pose viene fijada por la card que se
      // tocó y la cantidad de personas la decide esa pose, no el usuario.
      const capaAUnaPose =
        expectedPeopleInPose > 1 && !esDesignModelo(selectedPreset?.kind);
      setPoseIds(capaAUnaPose && next.length > 1 ? next.slice(-1) : next);
    },
    [expectedPeopleInPose, selectedPreset?.kind],
  );

  // Toggle "Seleccionar múltiples" del menú de poses. Al apagarlo con
  // varias poses elegidas, colapsamos a la PRIMERA (mismo criterio que
  // PruebaNuevaInterfaz). El PosePickerSheet ya colapsa su draft antes
  // de commitear — esto cubre el path directo (y futuros callers).
  const handleMultiPoseEnabledChange = useCallback((enabled: boolean) => {
    setMultiPoseEnabled(enabled);
    if (!enabled) {
      setPoseIds((prev) => (prev.length > 1 ? [prev[0]] : prev));
    }
  }, []);

  // ── E-commerce: lock de cantidad de modelos ──────────────────────────
  // Las plantillas de "E-commerce" son poses con `cantidadPersonas`
  // explícito en la tabla `poses`. Cuando la pose tiene > 1 persona,
  // armamos multi-mode automáticamente con esa cantidad de slots y
  // deshabilitamos add/remove (`lockSlotCount`). Para poses de 1
  // persona, single-mode normal.
  const ecommercePoseMeta = React.useMemo(() => {
    if (!selectedPreset) return null;
    if (selectedPreset.kind !== 'design') return null;
    if (!selectedPreset.id.startsWith('pose-')) return null;
    const poseId = selectedPreset.id.slice('pose-'.length);
    const pose = allPoses.find((p) => p.id === poseId);
    if (!pose) return null;
    return pose;
  }, [selectedPreset, allPoses]);
  const lockSlotCount = !!(
    ecommercePoseMeta && ecommercePoseMeta.cantidadPersonas > 1
  );
  useEffect(() => {
    if (!ecommercePoseMeta) return;
    const count = ecommercePoseMeta.cantidadPersonas;
    if (count > 1) {
      setMultiModelEnabled(true);
      setModelSelectionsExtra((prev) => {
        const expected = count - 1;
        if (prev.length === expected) return prev;
        const next = [...prev];
        while (next.length < expected) next.push(null);
        while (next.length > expected) next.pop();
        return next;
      });
    } else {
      setMultiModelEnabled(false);
      setModelSelectionsExtra([]);
    }
    // Pre-poblamos poseIds con la pose embebida en la card que el user
    // tappeó, si todavía está vacío. Para 1-persona habilitamos
    // multi-pose (el user puede agregar más desde el PosePicker); para
    // 2+ personas queda fija — el array siempre tiene exactamente la
    // pose del card.
    setPoseIds((prev) => {
      if (count > 1) {
        return [ecommercePoseMeta.id];
      }
      if (prev.length === 0) {
        return [ecommercePoseMeta.id];
      }
      return prev;
    });
  }, [ecommercePoseMeta]);

  // Si cambia la cantidad de modelos (o llega el catálogo), descartamos las
  // poses ya seleccionadas que dejaron de ser compatibles — las globales Y
  // las propias ("Mis poses" cuentan por su cantidad_personas; la versión
  // anterior solo miraba `allPosesFiltered` y descartaba SIEMPRE las custom).
  // Si quedan 0 después del descarte, la primera global compatible — espejo
  // del default recomendado de arriba. Lógica pura en posesCompatibilidad.ts.
  //
  // OJO: el efecto NO corre en su primer pase. En el remount post-generar
  // corría viendo el render PRE-restore (multi todavía apagado ⇒
  // expectedPeopleInPose=1, con `allPoses` caliente del cache de
  // sessionStorage) mientras su updater recibía el poseIds YA restaurado:
  // pisaba la pose multi-persona del restore con la default de 1 persona y
  // dejaba modelo↔poses cruzados. El efecto existe para reaccionar a
  // CAMBIOS, no para validar el mount — el default inicial ya lo pone el
  // efecto de preselección. Ver ADR-2026-08-24-estado-multi-modelo-consistente.
  // Firma de deps en vez de un boolean: el doble-setup de StrictMode (dev)
  // re-invoca el efecto con el MISMO render — con un "ready" simple el
  // segundo pase corría igual que antes del fix, con los valores pre-restore.
  const poseCompatSeenRef = useRef<string | null>(null);
  useEffect(() => {
    const firma = `${expectedPeopleInPose}|${allPoses.length}`;
    if (poseCompatSeenRef.current === firma) return;
    const esPrimerPase = poseCompatSeenRef.current === null;
    poseCompatSeenRef.current = firma;
    if (esPrimerPase) return;
    if (allPoses.length === 0) return;
    const compatiblesGlobales = allPosesFiltered.map((p) => p.id);
    const compatiblesCustom = customPoses
      .filter((p) => (p.cantidadPersonas ?? 1) === expectedPeopleInPose)
      .map((p) => p.id);
    setPoseIds((prev) =>
      ajustarPosesPorCantidadDeModelos({
        prev,
        compatiblesGlobales,
        compatiblesCustom,
        expectedPeopleInPose,
        // En E-commerce (`design`) con poses de N personas se sigue capando a 1: ahí
        // la pose la fija la card. En design-modelo NO: la tanda fan-outea por pose y
        // cada una sale como su propia foto grupal
        // (ADR-2026-08-28-modelo-v2-varias-poses-con-varios-modelos).
        permiteVariasPoses: esDesignModelo(selectedPreset?.kind),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedPeopleInPose, allPoses.length, selectedPreset?.kind]);

  // ── Generación ─────────────────────────────────────────────────────
  // Loading + error state para la llamada al webhook. `showLoadingScreen`
  // overlay full-screen mientras genera; `genError` mensaje inline en el
  // panel si algo falla. `generatingLockRef` previene doble click.
  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);
  const [loadingExiting, setLoadingExiting] = useState<boolean>(false);
  const [videoGenerating, setVideoGenerating] = useState<boolean>(false);
  // Marca si la generación en curso es de modo 'anuncio'. Se usa para
  // alargar la barra de carga (los anuncios tardan más que una imagen
  // normal). Se setea al arrancar cada generación, igual que videoGenerating.
  const [anuncioGenerating, setAnuncioGenerating] = useState<boolean>(false);
  const [completeProgressNow, setCompleteProgressNow] = useState<boolean>(false);
  const [genError, setGenError] = useState<string | null>(null);
  const generatingLockRef = useRef<boolean>(false);
  const pendingVideoIdRef = useRef<string | null>(null);
  const pendingVideoCompanyIdRef = useRef<string | null>(null);

  // La reanudación tras RECARGA ya no vive acá: la hace `ReanudadorDeGeneraciones`
  // en el shell, por los `execution_id` del registro de generaciones en curso, y sin
  // navegar a la pantalla vieja de resultados
  // (ADR-2026-09-06-registro-de-generaciones-en-curso).

  /**
   * Carrusel / Animar pedidos DESDE el visor prestado de la columna, en desktop
   * (ADR-2026-09-06-carrusel-y-animar-aterrizan-en-la-columna). El visor ya disparó la
   * API y se cerró; acá se crea la tanda con lo que va a salir y se la cierra cuando
   * la promesa resuelve —con `preloadGeneratedImages` para imágenes y con
   * `preloadVideosDeTanda` para video (el thumbnail de un video es `opt/<nombre>.png`,
   * no un `.mp4`)—. Si rechaza, la tanda se cae y el error se muestra como en
   * `handleGenerateFinal`: billing → modal de límite; accionable → su mensaje; el
   * resto → copy genérico.
   *
   * No hay `execution_id` conocido (`executionIds: []`): la tanda vive en memoria de la
   * pestaña y no se reanuda tras recargar. Y no toca `generatingLockRef`: el candado es
   * de las generaciones que NO van al panel.
   */
  // Botón "Verificar calidad" en la columna (Funciones avanzadas,
  // ADR-2026-09-07-verificacion-de-calidad-en-la-columna).
  const verificacionCalidad = useVerificacionCalidad();
  // Análisis automático de cada imagen recién generada, si además está prendido el modo
  // (ADR-2026-09-07-analisis-automatico-al-generar).
  const analisisAutomatico = useAnalisisAutomatico();

  /**
   * Enchufa un pedido YA disparado a una tanda que YA existe: cuando resuelve, precarga
   * lo que salió y la cierra; cuando rechaza, avisa como avisa `handleGenerateFinal`
   * (billing → modal de límite; accionable → su mensaje; el resto → el copy que le pase
   * el que llama) y la tira.
   *
   * Lo comparten la generación delegada desde el visor y el carrusel de "Generar
   * producción": es el mismo movimiento, con la tanda creada en otro momento
   * (ADR-2026-09-14-la-produccion-abre-todos-sus-recuadros-al-generar).
   */
  const resolverTandaConPromesa = (
    idTanda: string,
    companyId: string,
    resultado: Promise<string[]>,
    opts: { esVideo?: boolean; errorGenerico: string },
  ) => {
    resultado
      .then(async (ids) => {
        let preloaded: PreloadedImage[] = [];
        try {
          preloaded = opts.esVideo
            ? await preloadVideosDeTanda(ids, companyId)
            : await preloadGeneratedImages(ids, companyId);
        } catch (e) {
          // Sin preload la tanda cierra vacía y `cerrarTanda` pide recarga: las filas
          // ya están en la base.
          console.warn('[InterfazV2] preload de la tanda falló', e);
        }
        cerrarTanda(idTanda, preloaded, { esVideo: opts.esVideo });
        // El "listo", como en las demás ramas de generación. Faltaba acá, que es por
        // donde terminan las tandas DELEGADAS —Creador de video y Prompt libre en
        // escritorio, y Carrusel/Animar pedidos desde el visor—: en mobile suena la
        // pantalla de carga y en escritorio no sonaba nada
        // (ADR-2026-09-20-las-tandas-delegadas-tambien-suenan-al-terminar). El racimo lo
        // colapsa la guarda de módulo de `sonarListo`, incluso entre pantallas.
        sonarListo();
      })
      .catch((err: unknown) => {
        console.error('[InterfazV2] la tanda falló', err);
        if (err instanceof GenerateAdError && err.errorType === 'billing') {
          setSubscriptionLimitType(
            userPlan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
          );
          setMotivoPricing('sin');
          setShowSubscriptionLimitModal(true);
        } else if ((err instanceof GenerateAdError && err.isActionable) || err instanceof ReferenciaIrrecuperable) {
          // La irrecuperable dice cuál foto falló (la del modelo elegido): mejor eso que
          // el genérico (ADR-2026-10-06-sin-la-foto-del-modelo-no-se-genera).
          setGenError(err.message);
        } else {
          setGenError(opts.errorGenerico);
        }
        fallarTanda(idTanda);
      });
  };

  const handleGeneracionDelegada = (g: GeneracionDelegada) => {
    const companyId = userCompanyId;
    if (!companyId) return;
    // Tope de tandas en curso (ADR-2026-09-06-tope-de-tres-tandas-en-curso). El
    // pedido YA salió (la promesa la armó el visor antes de entregarla), así que no se
    // pierde: cuando resuelva se pide recarga a las grillas y el resultado entra por
    // el historial; sólo no ocupa un lugar en la columna.
    if (!puedeCrear()) {
      // Copy distinto del de Generar: acá NO se frenó nada, así que "esperá" mentiría.
      setGenError(
        `Ya hay ${MAX_TANDAS_EN_CURSO} generaciones en curso: ésta va a aparecer en Creaciones cuando termine.`,
      );
      g.resultado.then(
        () => generacionesStore.pedirRecarga(),
        () => {
          /* el visor ya cerró; sin tanda no hay recuadros que tirar */
        },
      );
      return;
    }
    const idTanda = crearTanda({
      total: g.total,
      ratio: g.ratio,
      producto: g.producto,
      esVideo: g.esVideo,
      modo: selectedPreset?.kind,
      // Con los ids la tanda se reanuda tras recargar (Prompt libre los pasa); sin
      // ellos vive sólo en memoria, como siempre (ADR-2026-09-07-modo-prompt-libre).
      executionIds: g.executionIds ?? [],
      // Lo que va a cobrar: sin esto el guard de créditos contaba un video de 8s como
      // 1 (el fallback es `total`) — ADR-2026-09-06-tope-de-tres-tandas-en-curso.
      creditos: g.creditos,
      duracionEstimadaMs: g.duracionEstimadaMs,
      // La marca que pagó, si quien delega la fijó antes del pedido (regenerar).
      marcaDelPedido: g.marcaDelPedido,
    });
    if (!idTanda) return;
    resolverTandaConPromesa(idTanda, companyId, g.resultado, {
      esVideo: g.esVideo,
      errorGenerico:
        g.tipo === 'pasar-a-1080p'
          ? 'No se pudo pasar el video a 1080p. Intentá nuevamente.'
          : g.tipo === 'animar' || g.tipo === 'creador-video'
          ? 'No se pudo generar el video. Intentá nuevamente.'
          : g.tipo === 'regenerar'
            ? 'No se pudo regenerar la imagen. Intentá nuevamente.'
            : g.tipo === 'editar'
            ? 'No se pudo editar la imagen. Intentá nuevamente.'
            : g.tipo === 'prompt-libre'
              ? 'No se pudieron generar las imágenes. Intentá nuevamente.'
              : 'No se pudo generar nuevos ángulos. Intentá nuevamente.',
    });
  };

  // Mapping de slug de formato → aspect-ratio que espera el webhook.
  // Mismo mapping que PruebaNuevaInterfaz.handleGenerate.
  const formatSlugToAspect = (slug: string | null): string => {
    switch (slug) {
      case 'story':
        return '9:16';
      case 'square':
        return '1:1';
      case 'portrait':
        return '2:3';
      case 'banner':
        return '16:9';
      case 'editorial':
        return '3:2';
      case 'billboard':
        return '21:9';
      case 'post':
      default:
        return '4:5';
    }
  };

  /** "4:5" → 0.8. Para dibujar los recuadros de la tanda con la forma real. */
  const aspectARatio = (aspect: string): number => {
    const [w, h] = aspect.split(':').map((n) => Number(n));
    return w > 0 && h > 0 ? w / h : 0.8;
  };

  // ── Gating al generar: funciones por plan + créditos ────────────────────
  // Reusa los mecanismos del resto de la app (PruebaNuevaInterfaz / video):
  //   • Funciones bloqueadas para `gratis`: catálogo IA y agente.
  //   • Video (plantillas) bloqueado para `gratis` y `emprendimiento`.
  //   • Créditos insuficientes (saldo < cost) → modal de límite
  //     (mismo `SubscriptionLimitModal` de toda la app).
  // El gating de función se chequea ANTES que los créditos. Devuelve true si
  // se puede generar. Fetchea fresco para no bloquear/dejar pasar de más.
  // `cost` = créditos que consumirá la acción (lo que muestra el pill del
  // botón Generar). Antes solo chequeábamos `<= 0` (disponibilidad), no
  // suficiencia: un user con 1 crédito disparaba producciones/catálogos/videos
  // de N créditos. Ver ADR-518.
  const checkGenerationGuards = async (
    /**
     * Qué se chequea ADEMÁS del piso del modo abierto, que corre siempre. Sólo dos casos
     * preguntan otra cosa; el resto no declara nada
     * (ADR-2026-09-12-el-respaldo-al-generar-mira-el-piso-del-modo-abierto).
     *
     * Acá vivía también `'catalogo'`, que ya no selecciona ningún comportamiento: era el
     * único que aplicaba el piso del modo, y eso pasó a ser el default.
     */
    feature?: 'video' | 'agente',
    cost?: number,
  ): Promise<boolean> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return true;
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      const idCompany = (profile as any)?.id_company as string | undefined;
      if (!idCompany) return true;
      const { data: cp } = await supabase
        .from('company_profiles')
        .select('plan')
        .eq('id_company', idCompany)
        .single();
      const planReal = ((cp as any)?.plan as string) ?? null;
      if (planReal) setUserPlanReal(planReal);
      // El plan que acaba de llegar de la base. Acá se aplicaba encima el simulado
      // del panel CONTEXTO DEV (ADR-2026-08-03-el-plan-simulado-tambien-gatea); el
      // panel se eliminó al mergear `principal` — ADR-2026-08-05-fuera-los-overrides-de-dev.
      const plan = planReal;
      /**
       * Gating por plan (antes que créditos). Backstop: los modos ya gatean al ENTRAR,
       * pero ese guard deja pasar cuando el plan todavía no cargó, así que acá se vuelve
       * a preguntar con el plan fresco de Supabase.
       *
       * Es el piso del MODO ABIERTO —"aplicá la regla que tenga este modo"— y por eso no
       * hardcodea ningún `kind`: `handleGenerateCatalog` sirve a los tres modos de
       * catálogo, y antes acá se preguntaba por `'catalogo-ia'` fijo, o sea gateando a
       * los tres con la regla de uno.
       *
       * Corre por DEFECTO y no sólo con `feature === 'catalogo'`, que es como estaba —
       * ADR-2026-09-12-el-respaldo-al-generar-mira-el-piso-del-modo-abierto. Con la
       * condición vieja, un modo con piso que generara por el camino normal no tenía
       * respaldo: `handleGenerateFinal` llama sin `feature` cuando no es video, así que
       * Prompt libre (piso de plan pago desde
       * ADR-2026-09-12-prompt-libre-y-el-chat-salen-de-interno) podía generar en la
       * prueba gratis si el plan no había cargado al entrar. Los modos SIN piso no
       * cambian: `getModePlanLock` les devuelve `null`.
       *
       * Las dos excepciones son las que abajo preguntan otra cosa: `'video'` muestra su
       * propio modal, y `'agente'` pregunta por `asistente` y no por el preset abierto.
       *
       * Sin `selectedPreset` queda el default de `getModePlanLock`: sin `kind` no
       * bloquea, igual que con el plan desconocido.
       */
      if (feature !== 'video' && feature !== 'agente') {
        const lock = getModePlanLock(selectedPreset?.kind, plan);
        if (lock) {
          setFeatureLock(lock);
          return false;
        }
      }
      if (feature === 'agente') {
        const lock = getModePlanLock('asistente', plan);
        if (lock) {
          setFeatureLock(lock);
          return false;
        }
      }
      // Video (plantillas / influencer): Pro y Ultra. `plan &&` evita bloquear
      // si el plan no se conoce (defensivo).
      if (feature === 'video' && plan && !isProOrUltra(plan)) {
        // Mismo modal "Función exclusiva" que el flujo de generación de
        // video (influencer / personalizado).
        setShowVideoPlanRestriction(true);
        return false;
      }
      // Créditos: bloqueamos si el saldo no alcanza para cubrir el costo de
      // la acción. `needed` cae a 1 si no llega un costo (nada es gratis).
      //
      // Más lo COMPROMETIDO por las tandas en curso de la empresa: el débito real es
      // al entregar, así que dos tandas de 8 con saldo 10 pasaban las dos y la segunda
      // fallaba al final con "probá de nuevo". El guard es optimista (libera al fallar)
      // y no reemplaza al débito del backend (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
      // Con facturación por marca, sólo lo comprometido por la marca que paga: el saldo es el
      // de su billetera (ADR-2026-10-28-review-greptile-plan-por-marca).
      const base = typeof cost === 'number' && cost > 0 ? Math.ceil(cost) : 1;
      const leido = await leerSaldo(idCompany).catch(() => undefined);
      const needed = base + creditosComprometidos(generacionesStore.get(), idCompany, marcaQuePaga(leido));
      const remaining = leido?.disponibles;
      if (typeof remaining === 'number' && remaining < needed) {
        setSubscriptionLimitType(
          plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        );
        // Reset explícito: este camino SÍ es "se quedó sin créditos". Sin esto, el
        // motivo que dejó el CTA de la barra quedaría pegado (ADR-2026-07-31-copy-catalogo-sigue-saldo-real).
        setMotivoPricing('sin');
        setShowSubscriptionLimitModal(true);
        return false;
      }
      return true;
    } catch (err) {
      console.error('[InterfazV2] checkGenerationGuards error', err);
      return true; // no bloquear por un fallo de chequeo
    }
  };

  /**
   * La foto de la configuración con la que se está generando — lo que el "Atrás" desde
   * los resultados tiene que devolver (ADR-2026-09-10-el-restore-guarda-la-configuracion-entera).
   *
   * Está TIPADA con `RestoreV2`, que es lo que la mantiene completa: un campo nuevo en el
   * contrato y esto no compila hasta que se le da un valor. Antes era un objeto literal
   * sin tipo y se fue quedando corto sin que nada avisara.
   *
   * La usan las DOS generaciones —la normal y la del Catálogo—: hasta ahora el Catálogo
   * no guardaba nada, así que volver de sus resultados abría el modo en blanco, con las
   * vistas y los productos por elegir de nuevo.
   */
  const construirRestoreV2 = (): RestoreV2 => ({
    presetId: selectedPreset?.id ?? null,
    selectedProductIds: selectedProducts.map((p) => p.id),
    multiSlotProductIds: Object.fromEntries(
      Object.entries(multiSlotProducts).map(([k, list]) => [
        k,
        list.map((p) => p.id),
      ]),
    ),
    sinProducto,
    imagenBase,
    modelSelection,
    modelSelectionsSerie,
    modelSelectionsExtra,
    multiModelEnabled,
    poseIds,
    multiPoseEnabled,
    fondoSelection,
    formatoVal,
    resolucionVal,
    otraConfig,
    autoImageCount,
    vistaIds,
    catalogoVistas,
    vistaProductosExcluidos,
    multiPresetIds,
    anuncio: {
      titulo: anuncioTitulo,
      descripcion: anuncioDescripcion,
      cta: anuncioCta,
      colores: anuncioColors,
    },
    video: {
      duracion: videoDuration,
      multiEscena: videoMultiScene,
      ganchoId: videoGanchoId,
      voz: videoVoiceGuion,
    },
  });

  /** Guardar la vuelta atrás. `sessionStorage` puede fallar (modo privado): nunca tira. */
  const guardarRestoreV2 = () => {
    try {
      sessionStorage.setItem(CLAVE_RESTORE_V2, JSON.stringify(construirRestoreV2()));
    } catch {
      /* non-fatal */
    }
  };

  const handleGenerateFinal = async (opts?: {
    autoCarrusel?: boolean;
    carruselCount?: number;
    // Créditos que consumirá esta generación (= pill del botón). Se pasa al
    // guard para chequear saldo suficiente. Ver ADR-518.
    cost?: number;
  }) => {
    if (!selectedPreset) return;
    /**
     * Esta generación va a la columna de tandas (`design-modelo` a dos columnas en
     * desktop). Se calcula ACÁ ARRIBA y no más abajo por el candado: es la única
     * generación que puede convivir con otra en vuelo
     * (ADR-2026-08-20-varias-tandas-en-el-panel), porque su resultado aterriza en su
     * propia tanda en vez de en una pantalla de resultados compartida.
     *
     * `isVideoOutput` no entra en la cuenta: para `design-modelo` siempre es false
     * (ver cómo se arma más abajo), así que agregarlo sería ruido.
     */
    const generacionEnPanel =
      // `design-modelo` y `design-auto` son UNA sola pantalla: su `panelStep` se queda
      // en `choose-product` para siempre (ADR-2026-08-21-modelo-v2-una-sola-columna,
      // ADR-2026-08-21-automatico-con-el-diseno-nuevo), así que sin esto su generación
      // caía en el overlay full-screen — que es justo lo que la tanda en el panel vino a
      // evitar. El `configure` sigue contando porque es de donde generan los modos que
      // todavía tienen paso 2.
      // El PASO ya no entra en la cuenta: los dos modos generan desde su única pantalla
      // y no hay ningún paso suyo en el que la tanda no tenga dónde dibujarse. Con el
      // paso en la condición, cualquier estado inesperado devolvía al usuario a la
      // pantalla vieja de resultados
      // (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla).
      (esDesignModelo(selectedPreset.kind) ||
        selectedPreset.kind === 'design-auto' ||
        // Fotos de producto entró al rediseño y tiene la misma columna donde dibujar la
        // tanda (ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo), así que
        // su generación tampoco tiene por qué llevarse la pantalla —
        // ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla.
        selectedPreset.kind === 'fotos-producto' ||
        // Plantillas: su paso de configuración es la misma anatomía de dos columnas
        // (ADR-2026-09-08-la-plantilla-abre-directo-en-la-configuracion), así que su
        // generación también tiene dónde dibujarse y no tiene por qué llevarse la
        // pantalla. Los tres `kind` de la grilla de Plantillas, que llegan al mismo paso.
        ((selectedPreset.kind === 'template' ||
          selectedPreset.kind === 'design' ||
          selectedPreset.kind === 'design-producto') &&
          panelStep === 'configure')) &&
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches;
    const isVideo =
      !esDesignModelo(selectedPreset.kind) &&
      selectedPreset.kind !== 'design' &&
      selectedPreset.outputType === 'video';
    // Video también cuando el toggle de plantilla está en 'video'.
    const isVideoOutput =
      isVideo ||
      (previewMediaTab === 'video' && selectedPreset.kind === 'template');
    // "Imagen con modelo" en desktop muestra la tanda EN EL PANEL de la derecha
    // en vez del overlay full-screen: el overlay tapaba justo lo que ahora se
    // quiere mostrar (ADR-2026-08-20-tanda-en-el-panel). En mobile no aplica —
    // no hay dos columnas— y en video tampoco (ese flujo encadena y navega).
    //
    // Se decide ACÁ ARRIBA porque el candado depende de esto: la tanda que va al panel
    // NO lo toma ni lo suelta (espeja `handleGenerateCatalog`). Antes lo tomaba igual
    // y Generar en Anuncio/Video no hacía nada mientras Modelo generaba, y la primera
    // tanda en cerrar soltaba el candado de todas
    // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
    const tandaVaAlPanel = generacionEnPanel && !isVideoOutput;
    // El candado sigue tapando el doble click en TODO lo demás: ahí dos corridas
    // pelean por el mismo overlay y por la misma navegación al final.
    if (generatingLockRef.current && !tandaVaAlPanel) return;
    // El modo "Video con influencer" SÍ genera: el branch dedicado vive
    // dentro del `try` de abajo (POST a `GENERATE_VIDEO_INFLUENCER_URL`).
    // Normalmente requiere al menos un producto (va en `variante_id`) — salvo
    // con "Sin producto" (ADR-673), donde `variante_id` directamente no viaja.
    if (selectedProducts.length === 0 && !sinProducto) return;
    // "Imagen con modelo" sin modelo no tiene qué generar: desde que la grilla deja
    // deseleccionar TODO, la selección puede quedar en cero. El CTA del panel ya se apaga;
    // esto cubre cualquier otra entrada (un Generar viejo, un atajo) para que no salga un
    // request que el backend resuelve a su gusto y el usuario paga.
    //
    // La POSE, en cambio, no es obligatoria: la grilla tiene una tarjeta "Automática" que
    // ES quedarse en cero poses, así que el vacío es una elección del usuario y no un
    // descuido, y el modo resuelve la pose por su cuenta
    // (ADR-2026-08-21-modelo-v2-una-sola-columna). El modelo se sigue exigiendo: el modo
    // entra con "Automático" puesto, así que nunca llega en null.
    if (esDesignModelo(selectedPreset.kind) && !modelSelection) {
      setGenError(`Elegí al menos un ${palabraModelo.singular} para generar.`);
      return;
    }
    // Tope de tandas en curso por empresa, contadas en el registro
    // (ADR-2026-09-06-tope-de-tres-tandas-en-curso): el tope real es el backend (100
    // jobs activos → 429) y el worker (5 de concurrencia global), y a partir de un par
    // de tandas grandes había pérdida silenciosa.
    if (tandaVaAlPanel && !puedeCrear()) {
      setGenError(
        `Esperá a que termine alguna de las generaciones en curso (máximo ${MAX_TANDAS_EN_CURSO} a la vez).`,
      );
      return;
    }
    if (!tandaVaAlPanel) generatingLockRef.current = true;
    // Historial de instrucciones reusables: se registra ACÁ, el único punto por
    // el que pasan todos los modos clásicos (los 5 lugares que arman
    // `instruccion_adicional` viven dentro de esta función). Es fire-and-forget:
    // no se espera ni puede fallar hacia afuera.
    recordPromptHistory(userCompanyId, 'instruccion', otraConfig);
    // "Generar producción": además de generar normal, al llegar al inline
    // de imágenes generadas dispara la generación de carrusel. Solo aplica
    // a la rama de plantillas (kind 'template', modo imagen).
    const wantsCarrusel = opts?.autoCarrusel === true;
    // La Calidad elegida en el pie de la plantilla: va en el `image_size` de la imagen
    // base Y en el del carrusel de la producción, para que la tanda entera salga en la
    // misma resolución que el pill cobró (ADR-2026-09-28-las-plantillas-eligen-la-calidad).
    const plantillaConCalidad = plantillaEligeCalidad(
      selectedPreset,
      previewMediaTab === 'video',
    );
    const imageSizeDelCarrusel = plantillaConCalidad ? resolucionVal : undefined;
    // Cuántas imágenes pedirle al carrusel cuando es producción. Viene del
    // stepper "x imágenes" como (cantidad elegida − 1). Si no llega, caemos
    // al default de 2 (mismo comportamiento que el carrusel manual).
    const carruselCount =
      typeof opts?.carruselCount === 'number' && opts.carruselCount > 0
        ? opts.carruselCount
        : 2;
    // Guards (plan + créditos) ANTES de mostrar el loading.
    const guardsOk = await checkGenerationGuards(
      isVideoOutput ? 'video' : undefined,
      opts?.cost,
    );
    if (!guardsOk) {
      if (!tandaVaAlPanel) generatingLockRef.current = false;
      return;
    }
    setGenError(null);
    setLoadingExiting(false);
    setCompleteProgressNow(false);
    pendingVideoIdRef.current = null;
    pendingVideoCompanyIdRef.current = null;
    // Usamos `isVideoOutput` (no `isVideo`): así el loading dice "Generando
    // video..." también cuando es una PLANTILLA con el toggle en 'video'
    // (que genera imagen y después encadena el video). Antes usaba `isVideo`
    // —solo presets outputType:'video'— y la plantilla-video mostraba
    // "Generando imágenes...".
    setVideoGenerating(isVideoOutput);
    setAnuncioGenerating(selectedPreset.kind === 'anuncio');

    /**
     * Cuántas imágenes se pidieron: son los recuadros de la tanda y el tope del poll.
     *
     * La cuenta por modo vive en `totalTandaDeLaCorrida`, con tests: acá era una
     * ternaria con una sola rama y todo lo que no fuera Automático caía en la de
     * Modelo (ADR-2026-09-06-la-tanda-de-fotos-de-producto-cuenta-producto-por-vista).
     */
    const totalTanda = totalTandaDeLaCorrida({
      kind: selectedPreset.kind,
      autoImageCount,
      max: AUTO_IMAGE_COUNT_MAX,
      modelCount: 1 + modelSelectionsSerie.length,
      poseCount: poseIds.length,
      multiModelEnabled,
      // Fotos de producto es producto × vista y no la cuenta de Modelo: sin esta rama
      // la tanda nacía con `total: 1` y abría UNA card de "generando" con seis
      // imágenes corriendo
      // (ADR-2026-09-06-la-tanda-de-fotos-de-producto-cuenta-producto-por-vista).
      fotosProductoTotal: fotosProductoTotalImagenes,
    });
    /**
     * Id de LA tanda de esta corrida, o null si esta generación no va al panel.
     *
     * Es una const del closure y no un ref: con varias generaciones en vuelo
     * (ADR-2026-08-20-varias-tandas-en-el-panel) un ref compartido diría "la última que
     * arrancó" y la que terminara primero escribiría sobre la tanda equivocada.
     *
     * La crea el registro (uuid, arriba de todo: la última que se pidió es la que el
     * usuario está esperando) con el producto y el modo del MOMENTO de generar —si
     * después se cambia de producto, la tanda que ya salió sigue diciendo de qué era—
     * y con los créditos comprometidos para el guard
     * (ADR-2026-09-06-use-tandas-panel).
     */
    /**
     * La SEGUNDA tanda de "Generar producción" —
     * ADR-2026-09-14-la-produccion-abre-todos-sus-recuadros-al-generar.
     *
     * La producción son DOS pedidos encadenados: la imagen base y, recién con su id, el
     * carrusel que saca las otras N−1. El pedido del carrusel no puede salir antes —
     * necesita la base—, pero sus RECUADROS sí: la tanda se crea acá, en el toque de
     * Generar. Antes nacía cuando la base terminaba, así que el que pedía 3 veía UNO
     * generando y los otros dos aparecían minutos después ("pedí tres y salió una").
     *
     * Va ANTES de la tanda base a propósito: `crearTanda` apila arriba, así la base queda
     * primera en la columna y el primer recuadro en llenarse es el primero de la fila.
     *
     * Los créditos del stepper se REPARTEN entre las dos (`repartirCreditosDeProduccion`)
     * en vez de ir enteros en la base: la base cierra antes de que salga el pedido del
     * carrusel, así que cargarle todo a ella dejaba la producción sin reserva justo
     * mientras el carrusel generaba. Ver el helper para el detalle.
     */
    const { base: creditosBase, carrusel: creditosCarrusel } =
      repartirCreditosDeProduccion(opts?.cost, wantsCarrusel ? carruselCount : 0);
    const idTandaProduccion =
      tandaVaAlPanel && wantsCarrusel
        ? crearTanda({
            total: carruselCount,
            ratio: aspectARatio(formatSlugToAspect(formatoVal)),
            producto: selectedProducts.map((p) => p.name).join(', '),
            modo: selectedPreset?.kind,
            creditos: creditosCarrusel,
            // Sin `execution_id`: el pedido todavía no salió. Vive en memoria de la
            // pestaña y no se reanuda tras recargar, como el resto de los encadenados.
            executionIds: [],
          })
        : null;
    const idTanda = tandaVaAlPanel
      ? crearTanda({
          total: totalTanda,
          ratio: aspectARatio(formatSlugToAspect(formatoVal)),
          producto: selectedProducts.map((p) => p.name).join(', '),
          modo: selectedPreset?.kind,
          creditos: creditosBase,
        })
      : null;
    if (!idTanda) {
      setShowLoadingScreen(true);
      // Sin tanda base no hay producción que encadenar: sus recuadros no pueden quedar
      // latiendo solos.
      if (idTandaProduccion) fallarTanda(idTandaProduccion);
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      if (!userCompanyId) throw new Error('No se encontró la empresa del usuario');

      const isDesign = selectedPreset.kind === 'design';
      const isDesignModelo = esDesignModelo(selectedPreset.kind);
      const isDesignAuto = selectedPreset.kind === 'design-auto';
      const isDesignProducto = selectedPreset.kind === 'design-producto';
      const isFotosProducto = selectedPreset.kind === 'fotos-producto';
      const isAnuncio = selectedPreset.kind === 'anuncio';
      const variantIds = selectedProducts.map((p) => p.id);
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      // `execution_id`: id de correlación que GENERA el frontend y manda al
      // webhook en TODOS los modos. El backend lo escribe en la fila de
      // `imagenes_generadas`; sirve para (a) el modo video (poll = fuente de
      // verdad) y (b) los modos de imagen (poll de respaldo en el `catch` si la
      // respuesta se pierde). Uno por generación.
      const executionId = crypto.randomUUID();

      // ── Modo 'video-influencer' (Video con influencer) ──
      // Manda producto(s) + modelo + duración al webhook
      // `video_influencer` (`GENERATE_VIDEO_INFLUENCER_URL`). El "id del
      // modelo" sale de `modelSelection`
      // cuando se eligió un modelo GUARDADO (mode 'preset'); si no se eligió
      // ninguno (o es automático/subido) va `modelo_mode: 'ia'` y el backend
      // decide. Las keys siguen el mismo dialecto del flujo de influencer
      // clásico (`variante_id`, `modelo_guardado_id`, `duracion`) — si el n8n
      // espera otros nombres, ajustar acá.
      if (selectedPreset.kind === 'video-influencer') {
        const modeloFields: Record<string, any> = {};
        if (modelSelection?.mode === 'preset') {
          modeloFields.modelo_mode = 'guardados';
          if (modelSelection.id) {
            modeloFields.modelo_guardado_id = String(modelSelection.id);
            if (modelSelection.name)
              modeloFields.modelo_guardado_nombre = modelSelection.name;
          }
        } else if (modelSelection?.mode === 'custom') {
          modeloFields.modelo_mode = 'personalizado';
          const f = modelSelection.fields;
          if (f.genero) modeloFields.genero = f.genero;
          if (f.edad) modeloFields.edad = f.edad;
          if (f.tipoCuerpo) modeloFields.tipo_cuerpo = f.tipoCuerpo;
          if (f.tonoPiel) modeloFields.tono_piel = f.tonoPiel;
          if (f.colorPelo) modeloFields.color_pelo = f.colorPelo;
          if (f.largoPelo) modeloFields.largo_pelo = f.largoPelo;
          if (f.descripcion) modeloFields.desc_modelo = f.descripcion;
        } else {
          // auto / sin selección / modelo subido → el backend decide.
          modeloFields.modelo_mode = 'ia';
        }

        // Duración efectiva (ADR-547): con guion escrito/grabado la marca el
        // GUION (duración del audio, o estimación de lectura del texto, tope
        // 14s) — es lo que viaja como `duracion` y lo que cobra el pill
        // (base 4 + duración). En Automático manda la barra de Duración.
        const influencerDuracion =
          getGuionDurationSec(videoVoiceGuion) ?? videoDuration;

        // Dirección de actuación saneada (ADR-559), solo con guion escrito.
        // Se computa una vez (sanitizeEstiloVoz es puro) y se incluye en el
        // payload solo si queda algo tras el saneo.
        const estiloVoz =
          videoVoiceGuion.guionMode === 'texto'
            ? sanitizeEstiloVoz(videoVoiceGuion.formaDeHablar)
            : '';

        // El modo video usa el `executionId` (de arriba) como FUENTE DE VERDAD:
        // dispara el webhook fire-and-forget y poolea hasta encontrar la fila.
        // (Los modos de imagen, más abajo, lo usan solo como respaldo.)
        const requestData: Record<string, any> = {
          user_id: user.id,
          // "Sin producto" (ADR-673): la key NO viaja y el backend saltea la
          // imagen de producto (Gemini y Enhancor reciben solo la influencer).
          ...(sinProducto ? {} : { variante_id: variantIds }),
          mode,
          ref_mode: 'ugc',
          // Etiqueta de modo del historial y del panel. ADR-560 la había sacado por
          // "redundante con ref_mode", pero el que lee `ref_mode` NO es el que escribe
          // la columna: sin esta key la fila de `imagenes_generadas` quedaba con
          // `modo_utilizado` NULL y la ejecución salía sin Modo en el panel (44 filas
          // en 30 días). SUPERSEDE ADR-560 para este webhook — ver
          // ADR-2026-08-26-modo-utilizado-en-toda-generacion. El backend igual pone
          // este mismo default en el enqueue; acá cubre además el camino n8n.
          modo_utilizado: 'video_influencer',
          duracion: influencerDuracion,
          aspect_ratio: formatSlugToAspect(formatoVal),
          execution_id: executionId,
          // Gancho elegido en la UI (`video_hooks.id`). La UI bloquea el
          // Generar sin elegir nada (ADR-551), pero desde ADR-671 "nada" y
          // "Sin gancho" son distintos: con `SIN_GANCHO_ID` la key NO viaja y
          // el backend saltea el clip de apertura y el concat. Con un gancho
          // real, lo usa como prompt de apertura (ADR-504).
          ...(isSinGancho(videoGanchoId)
            ? {}
            : { gancho_id: videoGanchoId }),
          // Voz y guion (ADR-525): mismos NOMBRES de campo que el modo
          // diálogo del Director de Estudio, pero desde ADR-550 el VALOR de
          // `voz_id` es el NOMBRE de la voz Gemini (tabla `voces_google`) —
          // n8n debe usar el TTS de Google acá, no ElevenLabs.
          // `dialogo_input_mode: 'auto'` = el backend genera guion Y voz
          // (voz_id: null). Con guion escrito/grabado, `voz_id` es
          // OBLIGATORIO: la UI bloquea el Generar sin voz (ADR-544/545).
          voz_id:
            videoVoiceGuion.guionMode === 'auto'
              ? null
              : videoVoiceGuion.vozId,
          // Dirección de actuación para el TTS (reemplaza a `velocidad_voz`,
          // ADR-558) — solo con guion escrito; en Automático el backend usa
          // su propio estilo (ver `estiloVoz` arriba).
          ...(estiloVoz ? { estilo_voz: estiloVoz } : {}),
          dialogo_input_mode: videoVoiceGuion.guionMode,
          ...(videoVoiceGuion.guionMode === 'texto'
            ? { dialogo: videoVoiceGuion.guionTexto.trim() }
            : {}),
          ...(videoVoiceGuion.guionMode === 'audio' && videoVoiceGuion.guionAudio
            ? {
                audio_url: videoVoiceGuion.guionAudio.url,
                audio_duration_sec: Math.round(
                  videoVoiceGuion.guionAudio.durationSec,
                ),
              }
            : {}),
          // Subtítulos (ADR-537): mismo dialecto que el flujo V1 — el
          // `caption_id` (estilo elegido) viaja SOLO si el user los activó.
          ...(videoVoiceGuion.subtitulos
            ? { caption_id: videoVoiceGuion.captionId }
            : {}),
          // "Instrucción adicional" — mismo campo que los modos de imagen.
          // El input se renderizaba desde el scaffold pero recién se manda
          // desde ADR-562; solo viaja si no está vacío.
          ...(otraConfig.trim()
            ? { instruccion_adicional: otraConfig.trim() }
            : {}),
          ...modeloFields,
        };
        console.log('[InterfazV2] video-influencer payload', requestData);

        // Disparamos el webhook "fire-and-forget": no esperamos su respuesta
        // para mostrar el resultado (lo hace el poll). Solo capturamos un error
        // temprano y DEFINITIVO (4xx / moderación) para cortar el poll antes.
        // Un timeout / conexión caída / 5xx del trigger NO es fatal: la
        // generación tarda varios minutos y el proxy (Render/Cloudflare) suele
        // cortar la respuesta larga con 502/504 alrededor de los ~100s con el
        // workflow TODAVÍA corriendo — el backend escribe la fila igual y el
        // poll la encuentra (ADR-540).
        let triggerError: string | null = null;
        void (async () => {
          try {
            // Ruteo atlas/n8n (ADR-588): mismo contrato fire-and-forget —
            // el poll por execution_id sigue siendo la fuente de verdad.
            // Atlas deriva el user del JWT (user_id no viaja).
            let res: Response;
            if (USE_ATLAS_BACKEND) {
              const { user_id: _uid, ...atlasBody } = requestData;
              void _uid;
              res = await atlasBackendFetch('/v1/videos/influencer', atlasBody, {
                signal: AbortSignal.timeout(VIDEO_INFLUENCER_POLL_TIMEOUT_MS),
              });
            } else {
              res = await fetch(GENERATE_VIDEO_INFLUENCER_URL, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: GENERATE_AUTH_TOKEN,
                },
                body: JSON.stringify(requestData),
                signal: AbortSignal.timeout(VIDEO_INFLUENCER_POLL_TIMEOUT_MS),
              });
            }
            const text = await res.text().catch(() => null);
            if (!res.ok) {
              if (res.status >= 400 && res.status < 500) {
                // Rechazo definitivo del backend (402 sin créditos, payload
                // inválido, auth…). Atlas manda JSON tipado pero el nombre del
                // campo varía por ruta (`error_message` / `error` / `message`)
                // — sin cubrir los tres, el toast mostraba el JSON crudo.
                let msg: string | null = null;
                try {
                  const j = text ? JSON.parse(text) : null;
                  msg = j?.error_message ?? j?.error ?? j?.message ?? null;
                } catch {
                  /* body no-JSON → cae al texto crudo */
                }
                triggerError = msg || text || `Error ${res.status}`;
              } else {
                console.warn(
                  '[InterfazV2] video-influencer: trigger HTTP',
                  res.status,
                  '— gateway/5xx no fatal, seguimos polleando',
                );
              }
              return;
            }
            try {
              const json = text ? JSON.parse(text) : null;
              if (json?.response === 'error_moderation') {
                triggerError =
                  `El contenido no pasó la moderación. Probá con otro producto o ${palabraModelo.singular}.`;
              }
            } catch {
              /* respuesta no-JSON: ok, manda el poll */
            }
          } catch (e) {
            // timeout / abort / conexión caída → seguimos polleando.
            console.warn(
              '[InterfazV2] video-influencer: trigger sin respuesta usable; seguimos polleando',
              e,
            );
          }
        })();

        // Poll por `execution_id` hasta el tope (VIDEO_INFLUENCER_POLL_
        // TIMEOUT_MS). Robustecido (ADR-541) para "puse a generar y volví
        // a los 20 min":
        //   • Deadline por RELOJ, no por cantidad de intentos: en background
        //     el browser throttlea los timers (sleeps de 5s pasan a minutos)
        //     y contar intentos distorsionaba el tope.
        //   • Cada consulta tolera errores de red (laptop suspendida,
        //     conexión caída al volver): warn + reintento, nunca aborta.
        //   • El chequeo va ANTES del corte por deadline: al despertar la
        //     pestaña tarde, la última iteración consulta igual — si el video
        //     ya está en la DB (el caso reportado), navega en vez de tirar
        //     timeout.
        const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
        const deadline = Date.now() + VIDEO_INFLUENCER_POLL_TIMEOUT_MS;
        // La fila puede ser el video O una fila has_error (el backend la escribe
        // en TODO fallo definitivo desde el 25/7 — censura, timeout, técnico):
        // sin mirar has_error navegábamos a la fila rota como si fuera el video.
        const checkRow = async (): Promise<{
          id: string;
          hasError: boolean;
          errorType: string | null;
        } | null> => {
          try {
            const { data: row } = await supabase
              .from('imagenes_generadas')
              .select('id, has_error, error_type')
              .eq('execution_id', executionId)
              .maybeSingle();
            return row?.id
              ? {
                  id: String(row.id),
                  hasError: row.has_error === true,
                  errorType: (row.error_type as string | null) ?? null,
                }
              : null;
          } catch (e) {
            console.warn(
              '[InterfazV2] video-influencer: poll con error de red — reintentamos',
              e,
            );
            return null;
          }
        };
        let videoRow: Awaited<ReturnType<typeof checkRow>> = null;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (triggerError) break;
          videoRow = await checkRow();
          if (videoRow) break;
          if (Date.now() >= deadline) break;
          await sleep(VIDEO_INFLUENCER_POLL_INTERVAL_MS);
        }
        if (videoRow?.hasError) {
          // Copy por tipo (censura ≠ timeout ≠ técnico), siempre aclarando que
          // no se consumieron créditos — antes: genérico o navegación rota.
          const t = (videoRow.errorType as GenerateAdErrorType) || 'technical';
          setGenError(mapErrorTypeToMessage(t));
          setShowLoadingScreen(false);
          setVideoGenerating(false);
          setAnuncioGenerating(false);
          generatingLockRef.current = false;
          return;
        }
        const videoId = videoRow?.id ?? null;
        if (!videoId) {
          // Terminales del modo manejados ACÁ y no con throw: el catch
          // genérico pisa el mensaje con "Se produjo un error…" (solo
          // GenerateAdError lo conserva) y perderíamos el detalle de
          // moderación/timeout (ADR-543).
          setGenError(
            triggerError ??
              'El video está tardando más de lo normal y puede seguir generándose en segundo plano. Revisá el historial en unos minutos; si no aparece, probá de nuevo.',
          );
          setShowLoadingScreen(false);
          setVideoGenerating(false);
          setAnuncioGenerating(false);
          generatingLockRef.current = false;
          return;
        }

        // Persistir "último utilizado" — mismo registro que los flujos de
        // imagen (`lastUsedProducts`), así el picker destaca este producto la
        // próxima vez. SOLO productos: el formato (story) y modelo del
        // influencer no deben pisar el auto-relleno de los modos de imagen.
        try {
          if (selectedProducts.length > 0) {
            localStorage.setItem(
              'atlas-v2-last-products',
              JSON.stringify(selectedProducts.map((p) => p.id)),
            );
          }
        } catch {
          /* noop */
        }
        // Última voz usada — se preselecciona en la próxima apertura del
        // modo (ADR-544). Solo si la voz REALMENTE se usó: en guion
        // Automático el backend elige la voz y el vozId local es solo el
        // preseleccionado (ADR-545).
        if (videoVoiceGuion.guionMode !== 'auto' && videoVoiceGuion.vozId) {
          saveLastUsedVoice(videoVoiceGuion.vozId, videoVoiceGuion.vozNombre);
        }

        sonarListo();
        navigate(rutaResultados, {
          state: { imageIds: [videoId], companyId: userCompanyId },
        });
        return;
      }

      // ── Modo 'design-auto' (Generar imágenes automáticamente) ──
      // Atajo: armamos un payload mínimo equivalente al tab
      // "automatico" de PruebaNuevaInterfaz (modo 'rapido' + IA decide
      // todo). NO usa modelo / pose / fondo de la UI. Mandamos al
      // mismo endpoint V3 que el resto del flow.
      if (isDesignAuto) {
        const autoRequest: Record<string, any> = {
          user_id: user.id,
          variante_id: variantIds,
          mode,
          modo: 'rapido',
          modo_utilizado: 'imagen_automatica',
          // Cantidad de imágenes elegida con el control +/- arriba del
          // botón Generar. Clamped a [1, AUTO_IMAGE_COUNT_MAX].
          cantidad_imagenes: Math.max(
            1,
            Math.min(AUTO_IMAGE_COUNT_MAX, autoImageCount),
          ),
          Format: formatSlugToAspect(formatoVal),
          // Resolución elegida: el backend la traduce a imageConfig.imageSize y
          // multiplica los créditos (ADR-2026-08-25-resolucion-de-generacion-2k-4k).
          image_size: resolucionVal,
          composicion: 'producto',
          background_mode: 'ia',
          new_ui: true,
          modo_pro: true,
          execution_id: executionId,
          // Fire-and-forget cuando la tanda va al panel: ahí manda el poll, con el
          // `expectedCount` de la cantidad pedida. Sin panel (mobile) se conserva la
          // respuesta síncrona, que es de donde salen los ids
          // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
          ...(idTanda ? { wait: false } : {}),
          ...(otraConfig.trim()
            ? { instruccion_adicional: otraConfig.trim() }
            : {}),
        };
        // Carrera respuesta-vs-poll (A). El poll corre en paralelo: si el fetch se
        // cuelga (compu dormida), el resultado se recupera igual. La tanda registra su
        // execution_id para reanudarse tras una recarga desde el shell
        // (ADR-2026-09-06-registro-de-generaciones-en-curso).
        if (idTanda) registrarExecutionIds(idTanda, [executionId]);
        const autoResult = await awaitImageGeneration({
          fetchPromise: postGenerateAd(autoRequest, { url: GENERATE_AD_V3_URL }),
          executionId,
          companyId: userCompanyId,
          expectedCount: autoRequest.cantidad_imagenes as number,
          // Tope proporcional al tamaño de la tanda del panel
          // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
          pollTimeoutMs: idTanda ? timeoutPollTanda(totalTanda) : undefined,
        });
        const autoData: any = {
          response: 'success',
          imagenes_generadas_ids: autoResult.imageIds,
          id_company: autoResult.companyId,
        };
        if (
          Array.isArray(autoData?.imagenes_generadas_ids) &&
          autoData.imagenes_generadas_ids.length > 0
        ) {
          const imageIds = autoData.imagenes_generadas_ids as number[];
          const companyIdResult =
            (autoData.id_company as string) || userCompanyId;
          let preloadedImages: PreloadedImage[] = [];
          try {
            preloadedImages = await preloadGeneratedImages(
              imageIds,
              companyIdResult,
            );
          } catch (preloadErr) {
            console.warn(
              '[InterfazV2] preloadGeneratedImages (auto) falló',
              preloadErr,
            );
          }
          sonarListo();
          // Tanda EN EL PANEL: las imágenes aterrizan en su bloque de la columna de la
          // derecha y NO se navega a la pantalla de resultados
          // (ADR-2026-08-21-automatico-con-el-diseno-nuevo). Es el mismo cierre que el de
          // `design-modelo`, pero este modo tiene su propia rama de generación y hay que
          // repetirlo acá: sin esto la tanda arrancaba en el panel y terminaba llevándose
          // la pantalla.
          //
          // Y NUNCA se navega, tampoco con el preload vacío: la tanda cierra con lo que
          // haya y el panel vuelve a pedir las creaciones
          // (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla).
          if (idTanda) {
            cerrarTanda(idTanda, preloadedImages);
            if (!tandaVaAlPanel) generatingLockRef.current = false;
            return;
          }
          const { user_id: _u, wait: _w, ...lastPayload } = autoRequest;
          void _u;
          void _w;
          navigate(rutaResultados, {
            state: {
              imageIds,
              companyId: companyIdResult,
              lastPayload,
              preloadedImages,
            },
          });
          return;
        }
        throw new Error('No se recibieron imágenes generadas.');
      }

      // ── Modo 'design-producto' (vistas solo-producto de "Fondo blanco") ──
      // Atajo estilo 'design-auto': payload propio SIN modelo / pose. Es el
      // mismo shape que manda Catálogo IA para sus vistas solo-producto
      // (ver handleGenerateCatalog): composicion 'producto' + vista_producto
      // + fondo blanco fijo (background_id 5) + modo 'personalizado'.
      // `mode: 'single'` aunque haya varios productos: igual que el combo de
      // Catálogo IA, el backend compone todos los `variante_id` en UNA imagen.
      if (isDesignProducto) {
        // `vistaProductoId` es el slug real: las cards desdobladas
        // (`vista-frente` / `vista-frente-ropa`) mandan ambas 'frente'.
        // Fallback al id sin prefijo por si un preset viejo cacheado en
        // sessionStorage no trae el campo.
        const vistaSlug =
          selectedPreset.vistaProductoId ??
          (selectedPreset.id.startsWith(VISTA_PRESET_PREFIX)
            ? selectedPreset.id.slice(VISTA_PRESET_PREFIX.length)
            : selectedPreset.id);
        const productoRequest: Record<string, any> = {
          user_id: user.id,
          variante_id: variantIds,
          mode: 'single',
          modo: 'personalizado',
          // Trackeo (ADR-512): las vistas solo-producto cuentan como plantilla.
          modo_utilizado: 'plantilla',
          // La imagen base es UNA; con más en el contador, el resto sale del carrusel
          // (ADR-2026-10-12-fondo-blanco-hace-produccion). La Calidad del pie va en las dos
          // (ADR-2026-10-12-fondo-blanco-como-las-plantillas).
          cantidad_imagenes: 1,
          image_size: resolucionVal,
          Format: formatSlugToAspect(formatoVal),
          composicion: 'producto',
          vista_producto: vistaSlug,
          background_mode: 'blanco',
          background_id: 5,
          ...(otraConfig.trim()
            ? { instruccion_adicional: otraConfig.trim() }
            : {}),
          new_ui: true,
          modo_pro: true,
          execution_id: executionId,
        };
        // Carrera respuesta-vs-poll (A) — mismo esquema que design-auto; la tanda
        // registra su execution_id (y cuántas espera) para reanudarse tras recarga.
        if (idTanda) registrarExecutionIds(idTanda, [executionId]);
        const productoResult = await awaitImageGeneration({
          fetchPromise: postGenerateAd(productoRequest, {
            url: GENERATE_AD_V3_URL,
          }),
          executionId,
          companyId: userCompanyId,
          expectedCount: 1,
          pollTimeoutMs: idTanda ? timeoutPollTanda(1) : undefined,
        });
        if (productoResult.imageIds.length > 0) {
          const imageIds = productoResult.imageIds;
          const companyIdResult = productoResult.companyId || userCompanyId;
          let preloadedImages: PreloadedImage[] = [];
          try {
            preloadedImages = await preloadGeneratedImages(
              imageIds,
              companyIdResult,
            );
          } catch (preloadErr) {
            console.warn(
              '[InterfazV2] preloadGeneratedImages (design-producto) falló',
              preloadErr,
            );
          }
          sonarListo();
          // Con la tanda en el panel NO se navega: las imágenes aterrizan en la
          // columna y la pantalla del modo se queda
          // (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla). Esta rama
          // —las vistas solo-producto de "Fondo blanco"— era la única del flujo de
          // Modelo que se lo había salteado, así que generar con una vista de ésas se
          // llevaba al usuario a la pantalla vieja de resultados.
          if (idTanda) {
            cerrarTanda(idTanda, preloadedImages);
            // Producción: con la base lista, el carrusel saca el resto, igual que en las
            // plantillas (ADR-2026-10-12-fondo-blanco-hace-produccion; el mismo encadenado
            // que la rama común, ADR-2026-09-08-la-produccion-de-plantillas-encadena-el-
            // carrusel-en-el-panel).
            if (idTandaProduccion) {
              resolverTandaConPromesa(
                idTandaProduccion,
                companyIdResult,
                postGenerateAngles({
                  user_id: user.id,
                  image_id: String(imageIds[0]),
                  cantidad_imagenes: carruselCount,
                  image_size: imageSizeDelCarrusel,
                }),
                {
                  errorGenerico:
                    'No se pudo generar el resto de la producción. Intentá nuevamente.',
                },
              );
            }
            if (!tandaVaAlPanel) generatingLockRef.current = false;
            return;
          }
          const { user_id: _u, ...lastPayload } = productoRequest;
          void _u;
          navigate(rutaResultados, {
            state: {
              imageIds,
              companyId: companyIdResult,
              lastPayload,
              preloadedImages,
              autoCarrusel: wantsCarrusel,
              autoCarruselCount: carruselCount,
              autoCarruselImageSize: imageSizeDelCarrusel,
            },
          });
          return;
        }
        throw new Error('No se recibieron imágenes generadas.');
      }

      // ── Modo 'fotos-producto' (Fotos de producto) ────────────────
      // M productos, N vistas, fondo blanco o escena descrita.
      //
      // Se manda UN REQUEST POR (PRODUCTO × VISTA) en vez de mandar
      // `vista_producto: [id1, id2]` en una sola llamada, que es lo que hace
      // el Catálogo IA. Motivo: el backend solo resuelve el multi-vista para
      // marcas del rubro "Moda e Indumentaria"; para cualquier otro rubro,
      // `composicion: 'producto'` cae en la rama del catálogo de productos,
      // que ignora la vista y entrega UNA sola imagen — mientras el gate de
      // créditos igual cuenta N y el poll se quedaría esperando filas que no
      // van a llegar. Con un request por par el modo funciona igual para
      // todos los rubros. Ver ADR-2026-08-13-fotos-producto-fan-out-por-vista
      // y ADR-2026-08-30-fotos-producto-multi-producto (el fan-out por
      // producto). Cada request lleva UN producto en `variante_id`: mandar el
      // array entero caería en la composición multi-producto del backend
      // (outfit combinado), que no es este modo.
      if (isFotosProducto) {
        const vistasElegidas = [...vistaIds];
        if (vistasElegidas.length === 0) {
          throw new Error('Elegí al menos una vista para generar.');
        }
        // Fondo: el picker de este modo ofrece "Fondo liso" (con color) o
        // escena descrita. Una descripción vacía cae al liso (no mandamos
        // `background_text: ''`, que dejaría al prompt sin escena Y sin
        // fondo). Del liso, el BLANCO va por el flujo clásico
        // (background_id 5); beige/rosa/negro no existen en la tabla
        // `fondos`, así que viajan como descripción con el prompt fijo del
        // catálogo (liso, sin texturas, luz de estudio).
        const colorLisoMeta =
          fondoSelection?.mode === 'blanco'
            ? COLORES_FONDO_LISO.find(
                (c) => c.id === (fondoSelection.colorLiso ?? 'blanco'),
              )
            : null;
        const fondoPayload: Record<string, any> =
          fondoSelection?.mode === 'descripcion' && fondoSelection.text.trim()
            ? {
                background_mode: 'descripcion',
                background_text: fondoSelection.text.trim(),
              }
            : colorLisoMeta?.prompt
              ? {
                  background_mode: 'descripcion',
                  background_text: colorLisoMeta.prompt,
                }
              : // id 5 = fondo blanco en la tabla `fondos` (mismo mapping que
                // design-modelo, las poses E-commerce y PruebaNuevaInterfaz).
                { background_mode: 'blanco', background_id: 5 };
        // Un execution_id POR REQUEST (ADR-590): compartiéndolo, las
        // ejecuciones paralelas se ven las filas entre sí y disparan un
        // fan-out que genera imágenes de más Y LAS COBRA.
        //
        // Un job por par (vista, producto APLICABLE Y ACTIVO): la unión de
        // vistas anota a qué productos aplica cada una (`productIds`) y el
        // checklist del badge puede desactivar productos por vista
        // (`vistaProductosExcluidos`). Ver
        // ADR-2026-08-30-fotos-producto-multi-producto-2.
        const vistasPorId = new Map(vistasProducto.map((v) => [v.id, v]));
        const jobs = vistasElegidas.flatMap((vista) => {
          const catalogo = vistasPorId.get(vista);
          if (!catalogo) return [];
          const excluidos = vistaProductosExcluidos[vista] ?? [];
          return catalogo.productIds
            .filter((id) => !excluidos.includes(id))
            .map((productoId) => ({
              productoId,
              vista,
              executionId: crypto.randomUUID(),
            }));
        });
        if (jobs.length === 0) {
          throw new Error('Elegí al menos una vista para generar.');
        }
        // Esperado MUTABLE: cuando un POST falla a nivel HTTP le descontamos
        // su imagen — ese request nunca va a escribir su fila y sin esto el
        // poll la esperaría hasta el tope (ADR-133).
        let expectedTotal = jobs.length;
        const sendErrors: GenerateAdError[] = [];
        const sends: Promise<unknown>[] = [];
        // Tope de llamadas en paralelo, mismo motivo que en el catálogo: con
        // el flag de atlas apagado cada request sostiene la conexión hasta
        // que el workflow termina y las de más se pierden.
        const MAX_FOTOS_EN_PARALELO = 3;
        let fotosEnVuelo = 0;
        const esperarTurnoFotos = async () => {
          while (fotosEnVuelo >= MAX_FOTOS_EN_PARALELO) {
            await new Promise((r) => setTimeout(r, 200));
          }
        };
        // Los execution_id van al registro ANTES de disparar: si el usuario recarga
        // en el medio, el shell reanuda la tanda por estos ids
        // (ADR-2026-09-06-registro-de-generaciones-en-curso).
        // El `total` va con los executionIds y no queda en la previsión: `jobs.length`
        // es UNA imagen por job (`cantidad_imagenes: 1`), o sea la cuenta exacta de lo
        // que se pidió. Es el mismo patrón que ya usa la rama de Modelo, y el que
        // rescata a la tanda si el número del pill y el fan-out real difieren
        // (ADR-2026-09-06-la-tanda-de-fotos-de-producto-cuenta-producto-por-vista).
        if (idTanda)
          registrarExecutionIds(
            idTanda,
            jobs.map((j) => j.executionId),
            jobs.length,
          );
        let primerPayload: Record<string, any> | null = null;
        for (const job of jobs) {
          const payload: Record<string, any> = {
            user_id: user.id,
            variante_id: [job.productoId],
            mode: 'single',
            modo: 'personalizado',
            // Trackeo (ADR-511/512): etiqueta propia para separar este modo
            // de la imagen personalizada y de las plantillas en el panel.
            modo_utilizado: 'fotos_producto',
            // Fire-and-forget: la completitud la define el poll por
            // execution_id, así que el atlas puede responder 202 al toque en
            // vez de sostener la conexión. n8n ignora el campo.
            wait: false,
            cantidad_imagenes: 1,
            Format: formatSlugToAspect(formatoVal),
            // La Calidad elegida, igual que en Modelo, Automático y el Catálogo: mismo
            // endpoint V3, que ya lo entiende. El modo no lo mandaba porque tampoco
            // ofrecía el selector; con la Calidad de vuelta en su pie tiene que llegar,
            // o se elegiría 4K y se generaría en 1K
            // (ADR-2026-09-06-la-calidad-de-fotos-de-producto-es-real).
            image_size: resolucionVal,
            composicion: 'producto',
            vista_producto: job.vista,
            ...fondoPayload,
            ...(otraConfig.trim()
              ? { instruccion_adicional: otraConfig.trim() }
              : {}),
            new_ui: true,
            modo_pro: true,
            execution_id: job.executionId,
          };
          if (!primerPayload) primerPayload = payload;
          await esperarTurnoFotos();
          fotosEnVuelo += 1;
          // SIN REINTENTO, a propósito (mismo criterio que el catálogo): la
          // generación NO es idempotente y reintentar una llamada caída
          // después de que el backend la recibió le cobra dos veces al
          // usuario. Perder una imagen y avisarlo es menos grave.
          sends.push(
            postGenerateAd(payload, { url: GENERATE_AD_V3_URL })
              .catch((e) => {
                console.warn(
                  '[fotos-producto] una vista falló (seguimos polleando)',
                  e,
                );
                expectedTotal -= 1;
                if (e instanceof GenerateAdError) sendErrors.push(e);
              })
              .finally(() => {
                fotosEnVuelo -= 1;
              }),
          );
        }
        const rows = await pollGeneratedByExecutionId({
          executionId: jobs.map((j) => j.executionId),
          companyId: userCompanyId,
          // Getter: el esperado puede BAJAR mientras el poll corre. Si fallan
          // TODOS queda en 0 y corta al toque en vez de esperar 5 min.
          expectedCount: () => expectedTotal,
          // Tope proporcional al tamaño de la tanda del panel
          // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
          timeoutMs: idTanda ? timeoutPollTanda(jobs.length) : undefined,
        });
        const okRows = rows.filter((r) => !r.has_error);
        if (okRows.length === 0) {
          // El poll pudo cortar con algún `.catch` sin correr: esperamos los
          // POST para elegir el mensaje con todos los errores a la vista
          // (barato con wait:false). Un error accionable (sin créditos /
          // rechazo del modelo) explica mucho mejor que el genérico.
          await Promise.allSettled(sends);
          const accionable = sendErrors.find((e) => e.isActionable);
          // Se propaga el error tipado tal cual: el catch de afuera sólo muestra el mensaje de un
          // GenerateAdError accionable (o abre el modal de créditos con billing). Envuelto en un Error
          // común se perdía y quedaba el genérico (ADR-2026-10-28-errores-bien-clasificados).
          if (accionable) throw accionable;
          throw new Error(sendErrors[0]?.message ?? 'No se pudo generar ninguna foto. Probá de nuevo.');
        }
        const imageIds = okRows.map((r) => r.id);
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(
            imageIds as unknown as number[],
            userCompanyId,
          );
        } catch (preloadErr) {
          console.warn(
            '[InterfazV2] preloadGeneratedImages (fotos-producto) falló',
            preloadErr,
          );
        }
        sonarListo();
        // ── Tanda con VARIOS productos: resultados agrupados por producto,
        // igual que el catálogo (`state.catalogo` — GeneratedImagesInline
        // renderiza un grupo con header por producto). La agrupación sale del
        // `variante_id` de cada fila (= el producto del request, uno por
        // job). Igual que el catálogo, la vista agrupada NO lleva
        // `lastPayload` (sin "Generar más" global — no habría una única
        // configuración que repetir). Ver
        // ADR-2026-08-30-fotos-producto-multi-producto-2.
        const productosDeLaTanda = selectedProducts.filter((p) =>
          jobs.some((j) => j.productoId === p.id),
        );
        if (productosDeLaTanda.length > 1) {
          const rowsPorProducto = new Map<string, string[]>();
          for (const row of okRows) {
            const k = row.variante_id || 'otros';
            const lista = rowsPorProducto.get(k) ?? [];
            lista.push(row.id);
            rowsPorProducto.set(k, lista);
          }
          // Grupos en el ORDEN de la selección; filas con `variante_id`
          // desconocido (no debería pasar) caen en un bucket final, mismo
          // criterio que el catálogo.
          const productToImageIds: Record<string, string[]> = {};
          for (const p of productosDeLaTanda) {
            const ids = rowsPorProducto.get(p.id);
            if (ids && ids.length > 0) {
              productToImageIds[p.id] = ids;
              rowsPorProducto.delete(p.id);
            }
          }
          for (const [k, ids] of rowsPorProducto) {
            if (ids.length > 0) productToImageIds[k] = ids;
          }
          const productsMeta: Record<
            string,
            { id: string; name: string; imageUrl: string }
          > = {};
          for (const p of productosDeLaTanda) {
            productsMeta[p.id] = {
              id: p.id,
              name: p.name,
              imageUrl: p.imageUrl,
            };
          }
          // Con la tanda en el panel, las imágenes aterrizan ahí y la pantalla del modo
          // se queda (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla).
          if (idTanda) {
            cerrarTanda(idTanda, preloadedImages);
            if (!tandaVaAlPanel) generatingLockRef.current = false;
            return;
          }
          navigate(rutaResultados, {
            state: {
              imageIds,
              companyId: userCompanyId,
              preloadedImages,
              catalogo: {
                productToImageIds,
                productsMeta,
                // Planeadas − llegadas OK: cubre fallos HTTP, filas con
                // error y las que nunca aparecieron dentro del tope.
                failedCount: Math.max(0, jobs.length - okRows.length),
              },
            },
          });
          return;
        }
        // ── UN producto: vista plana clásica. `lastPayload` = el de la
        // PRIMERA vista, sin `user_id` ni el execution_id de esta tanda:
        // "Generar más" en la pantalla de resultados arma su propia llamada
        // (genera un id nuevo) y pide 2 imágenes de esa misma vista.
        const { user_id: _u, execution_id: _e, wait: _w, ...lastPayload } =
          primerPayload ?? {};
        void _u;
        void _e;
        void _w;
        if (idTanda) {
          cerrarTanda(idTanda, preloadedImages);
          if (!tandaVaAlPanel) generatingLockRef.current = false;
          return;
        }
        navigate(rutaResultados, {
          state: {
            imageIds,
            companyId: userCompanyId,
            lastPayload,
            preloadedImages,
          },
        });
        return;
      }

      // ── Modo 'anuncio' (Generar anuncio) ─────────────────────────
      // Atajo similar al de 'design-auto': payload propio sin modelo /
      // pose / fondo. Manda los 3 textos (título / descripción / CTA)
      // como objetos con `incluir` + `modo` ('auto' o 'custom') + el
      // `texto` ingresado por el user cuando es custom. Si el user
      // descartó el campo con el ojo, `incluir: false`.
      //
      // Los colores efectivos = `anuncioColors` si el user editó algo;
      // si no, los primeros 4 de la paleta del brand_profile (default).
      if (isAnuncio) {
        // Builder del objeto de cada campo textual.
        const buildTextoPayload = (
          field: typeof anuncioTitulo,
        ): Record<string, unknown> => {
          if (field.hidden) return { incluir: false };
          const trimmed = field.value.trim();
          const base: Record<string, unknown> = trimmed
            ? { incluir: true, modo: 'custom', texto: trimmed }
            : { incluir: true, modo: 'auto' };
          // Fuente elegida (las mismas del editor de video). Solo se manda
          // si el user eligió una; '' = Automática ⇒ la IA decide.
          if (field.font && field.font.trim()) {
            base.fuente = field.font.trim();
          }
          return base;
        };
        const coloresEfectivos =
          anuncioColors.length > 0
            ? anuncioColors
            : brandColors.slice(0, ANUNCIO_COLORS_MAX);
        const anuncioRequest: Record<string, any> = {
          user_id: user.id,
          // El sujeto del anuncio: la creación si se entró desde una
          // (ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo), y si no los
          // productos de siempre. `modo` cambia con él porque el backend rutea por ahí:
          // con `anuncio_desde_imagen` usa la imagen como referencia en vez de componer
          // el producto.
          ...(imagenBase
            ? { imagen_generada_id: imagenBase.id, modo: 'anuncio_desde_imagen' }
            : { variante_id: variantIds, modo: 'anuncio' }),
          mode,
          modo_utilizado: 'anuncio',
          // Cantidad de imágenes a generar — mismo control que el de
          // design-auto, clamped a [1, AUTO_IMAGE_COUNT_MAX].
          cantidad_imagenes: Math.max(
            1,
            Math.min(AUTO_IMAGE_COUNT_MAX, autoImageCount),
          ),
          Format: formatSlugToAspect(formatoVal),
          textos: {
            titulo: buildTextoPayload(anuncioTitulo),
            descripcion: buildTextoPayload(anuncioDescripcion),
            cta: buildTextoPayload(anuncioCta),
          },
          // Solo mandamos colores si hay al menos uno (sea custom del
          // user o default del brand). Vacío = backend decide.
          ...(coloresEfectivos.length > 0
            ? { colores: coloresEfectivos }
            : {}),
          ...(otraConfig.trim()
            ? { instruccion_adicional: otraConfig.trim() }
            : {}),
          new_ui: true,
          modo_pro: true,
          execution_id: executionId,
        };
        // Carrera respuesta-vs-poll (A).
        const anuncioResult = await awaitImageGeneration({
          fetchPromise: postGenerateAd(anuncioRequest, {
            url: GENERATE_AD_V3_URL,
          }),
          executionId,
          companyId: userCompanyId,
          expectedCount: anuncioRequest.cantidad_imagenes as number,
          // gpt-5 en high tarda minutos POR imagen: sin estas ventanas el
          // poll cerraba la tanda con la primera fila y el resto caía
          // "después" al historial (ADR-578).
          pollTimeoutMs: ANUNCIO_POLL_TIMEOUT_MS,
          pollStableRounds: ANUNCIO_STABLE_ROUNDS,
        });
        const anuncioData: any = {
          response: 'success',
          imagenes_generadas_ids: anuncioResult.imageIds,
          id_company: anuncioResult.companyId,
        };
        if (
          Array.isArray(anuncioData?.imagenes_generadas_ids) &&
          anuncioData.imagenes_generadas_ids.length > 0
        ) {
          const imageIds = anuncioData.imagenes_generadas_ids as number[];
          const companyIdResult =
            (anuncioData.id_company as string) || userCompanyId;
          let preloadedImages: PreloadedImage[] = [];
          try {
            preloadedImages = await preloadGeneratedImages(
              imageIds,
              companyIdResult,
            );
          } catch (preloadErr) {
            console.warn(
              '[InterfazV2] preloadGeneratedImages (anuncio) falló',
              preloadErr,
            );
          }
          sonarListo();
          // Persistir última config para auto-rellenar próxima
          // generación (formato + productos). Los textos / colores
          // no los persistimos — son muy específicos del anuncio.
          try {
            if (formatoVal) {
              localStorage.setItem('atlas-v2-last-format', formatoVal);
            }
            if (selectedProducts.length > 0) {
              localStorage.setItem(
                'atlas-v2-last-products',
                JSON.stringify(selectedProducts.map((p) => p.id)),
              );
            }
          } catch {
            /* noop */
          }
          const { user_id: _u, ...lastPayload } = anuncioRequest;
          void _u;
          navigate(rutaResultados, {
            state: {
              imageIds,
              companyId: companyIdResult,
              lastPayload,
              preloadedImages,
            },
          });
          return;
        }
        throw new Error('No se recibieron imágenes generadas.');
      }

      // Modelo: el shape del payload depende del mode de la selección.
      //   • 'preset'  → guardados (mandamos modelo_guardado_id/nombre).
      //   • 'custom'  → personalizado + fields explícitos.
      //   • 'auto' / 'upload' / null → ia (default).
      // 'upload' hoy cae a 'ia' porque el avatar subido recientemente
      // queda como un modelo guardado normal — el user lo eligió desde
      // el flow, ya pasó por 'preset'. Si modelSelection sigue siendo
      // 'upload' raw, significa que el user no terminó el flow.
      //
      // Multi-modelo: cuando el user activó "varios modelos" en el
      // ModelPicker, mandamos `modelo_mode: 'multiple'` + `modelo_config`
      // (array) con la config de cada slot y los ids de productos que
      // lleva. Los campos custom/guardados del nivel raíz quedan vacíos.
      let modelo_mode: 'ia' | 'guardados' | 'personalizado' | 'multiple' = 'ia';
      const customFields: Record<string, string> = {};
      const guardadosFields: Record<string, any> = {};
      let modelo_config: Record<string, any>[] | null = null;

      // Builder de un item del array modelo_config — replica la lógica
      // single-mode pero localizado al slot, e incluye `variante_id`.
      const buildModeloConfigItem = (
        sel: ModelSelection | null,
        vIds: string[],
      ): Record<string, any> => {
        const item: Record<string, any> = { variante_id: vIds };
        if (sel?.mode === 'preset') {
          item.modelo_mode = 'guardados';
          if (sel.id) {
            item.modelo_guardado_id = String(sel.id);
            if (sel.name) item.modelo_guardado_nombre = sel.name;
          }
        } else if (sel?.mode === 'custom') {
          item.modelo_mode = 'personalizado';
          const f = sel.fields;
          if (f.genero) item.genero = f.genero;
          if (f.edad) item.edad = f.edad;
          if (f.tipoCuerpo) item.tipo_cuerpo = f.tipoCuerpo;
          if (f.tonoPiel) item.tono_piel = f.tonoPiel;
          if (f.colorPelo) item.color_pelo = f.colorPelo;
          if (f.largoPelo) item.largo_pelo = f.largoPelo;
          if (f.descripcion) item.desc_modelo = f.descripcion;
        } else {
          item.modelo_mode = 'ia';
        }
        return item;
      };

      if (multiModelEnabled) {
        modelo_mode = 'multiple';
        const allSlots = [modelSelection, ...modelSelectionsExtra];
        modelo_config = allSlots.map((s, idx) => {
          // Si el slot tiene productos propios asignados, usamos esos.
          // Si no, hereda los productos globales (selectedProducts).
          const slotProducts = multiSlotProducts[idx];
          const slotVariantIds = slotProducts
            ? slotProducts.map((p) => p.id)
            : variantIds;
          return buildModeloConfigItem(s, slotVariantIds);
        });
      } else if (modelSelection?.mode === 'preset') {
        modelo_mode = 'guardados';
        // El backend espera el id como string (igual que en las pantallas
        // viejas, donde viaja por URL params). Mandarlo como number lo
        // descarta del payload silenciosamente.
        if (modelSelection.id) {
          guardadosFields.modelo_guardado_id = String(modelSelection.id);
          if (modelSelection.name) {
            guardadosFields.modelo_guardado_nombre = modelSelection.name;
          }
        }
      } else if (modelSelection?.mode === 'custom') {
        modelo_mode = 'personalizado';
        const f = modelSelection.fields;
        if (f.genero) customFields.genero = f.genero;
        if (f.edad) customFields.edad = f.edad;
        if (f.tipoCuerpo) customFields.tipo_cuerpo = f.tipoCuerpo;
        if (f.tonoPiel) customFields.tono_piel = f.tonoPiel;
        if (f.colorPelo) customFields.color_pelo = f.colorPelo;
        if (f.largoPelo) customFields.largo_pelo = f.largoPelo;
        if (f.descripcion) customFields.desc_modelo = f.descripcion;
      }

      // Serie de modelos (design-modelo): UNA IMAGEN POR MODELO. El backend no
      // acepta varios `modelo_guardado_id` en un mismo run, así que la tanda se
      // parte en un REQUEST POR MODELO más abajo — con el MISMO `execution_id`,
      // para que el poll los junte en una sola tanda (mismo patrón que el
      // fan-out por vista de fotos-producto, ADR-2026-08-13).
      // El multi-modelo (composición grupal) queda afuera: ahí los modelos ya
      // viajan juntos en `modelo_config` y sale UNA imagen.
      const modelosTanda: ModelSelection[] =
        isDesignModelo && !multiModelEnabled && modelSelectionsSerie.length > 0
          ? ([modelSelection, ...modelSelectionsSerie].filter(
              (m) => !!m,
            ) as ModelSelection[])
          : [];

      // Pose:
      //   • 'design'        (E-commerce): el state `poseIds` ya está
      //                     pre-cargado con la pose del card que el user
      //                     tappeó (vía el useEffect de ecommercePoseMeta).
      //                     Para poses 1-persona el user puede agregar
      //                     más; para 2+ queda fija.
      //   • 'design-modelo' (nuevo): array multi-select del state `poseIds`.
      const posePayload: string[] =
        isDesignModelo || isDesign
          ? poseIds.length > 0
            ? poseIds
            : selectedPreset.id.startsWith('pose-')
              ? [selectedPreset.id.slice('pose-'.length)]
              : []
          : [];

      // Separar poses globales de las CUSTOM. Las globales viajan por id en
      // `pose: []` (V3 las busca en la tabla `poses`, como hoy). Las custom
      // viajan en `poses_custom: [{id, filename}]` → V3 baja la imagen del
      // muñeco-en-pose y se la manda a Gemini como "Pose de referencia".
      const customPoseSet = new Set(customPoses.map((p) => p.id));
      const globalPosePayload = posePayload.filter(
        (id) => !customPoseSet.has(id),
      );
      const customPosesSelected = customPoses.filter((p) =>
        poseIds.includes(p.id),
      );
      // La pose custom viaja como REFERENCIA ({id, filename}). V3 baja la
      // imagen del muñeco-en-pose desde storage (bucket `modelos`,
      // `<id_company>/poses_custom/<filename>`) y se la pasa a Gemini como
      // "Pose de referencia". El front ya no baja ni manda la b64 — lo hace n8n.
      // NUNCA mandamos una pose custom sin filename: el workflow V3 muere en
      // "sign pose custom" sin escribir fila NI devolver error (200 con body
      // vacío) y el poll espera filas que no van a llegar (ADR-134). El atlas
      // las filtra de su lado, pero acá también, para que el conteo esperado
      // del poll no cuente una pose que nadie va a generar.
      const customPosesRef = customPosesSelected
        .filter((p) => {
          if (p.filename) return true;
          console.warn(
            '[InterfazV2] pose custom sin filename, se omite de la tanda',
            p.id,
          );
          return false;
        })
        .map((p) => ({
          id: p.id,
          filename: p.filename,
        }));

      // template_id: array porque el backend soporta generar múltiples
      // plantillas en una request. Si el user activó "Seleccionar varias"
      // en preview, multiPresetIds tiene N ids; sino, cae al id único del
      // selectedPreset. Solo aplica al kind 'template' — los del row
      // "Diseña tu imagen" no son templates.
      const template_id: string[] = !isDesign && !isDesignModelo
        ? multiPresetIds.length > 0
          ? multiPresetIds
          : [selectedPreset.id]
        : [];

      // Background del modo design-modelo: 5 shapes según el FondoSelection.
      // - auto         → background_mode: 'ia'
      // - blanco       → background_mode: 'blanco' + background_id: 5
      // - biblioteca   → 'elegido' + background_id (number)
      // - guardado     → 'guardado' + background_file_name
      // - descripcion  → 'descripcion' + background_text
      const buildDesignModeloBackground = (): Record<string, any> =>
        // La regla vive en `fondoDelPayload` (pura y testeada): acá era un closure adentro
        // de este archivo y por eso la rama del liso con color no tenía forma de testearse
        // (ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo).
        backgroundDeFondo(fondoSelection, 'ia');

      // El modo del payload usa 'personalizado' (mismo endpoint / flow que
      // PruebaNuevaInterfaz) para design-modelo Y para design (poses
      // E-commerce / "Fondo blanco"). 'plantilla' queda solo para los
      // templates reales (kind 'template').
      const requestModo: 'plantilla' | 'personalizado' =
        isDesignModelo || isDesign ? 'personalizado' : 'plantilla';

      // Trackeo: modo de la UI que originó la generación (ADR-511/512). Este
      // flujo lo comparten Imagen personalizada, las poses E-commerce y las
      // plantillas — el payload era indistinguible entre ellos. Las poses
      // E-commerce (kind 'design') cuentan como plantilla.
      const modoUtilizado = isDesignModelo
        ? 'imagen_personalizada'
        : selectedPreset.outputType === 'video'
          ? 'plantilla_video'
          : 'plantilla';

      // Imagen personalizada: con UNA sola pose (o pose Automática) el
      // contador de cantidad pide N variaciones del MISMO prompt — el
      // backend usa `cantidad_imagenes` como tamaño de la ola 1 mientras no
      // sea multi-pose. Con varias poses / varios modelos el helper devuelve
      // 1 y el comportamiento queda igual que antes (1 imagen por pose).
      // Ver ADR-2026-07-30-imagen-personalizada-cantidad.
      const designModeloCount = isDesignModelo
        ? resolveDesignModeloCount({
            rawCount: autoImageCount,
            poseCount: poseIds.length,
            multiModelEnabled,
            modelCount: 1 + modelSelectionsSerie.length,
            max: AUTO_IMAGE_COUNT_MAX,
          }).count
        : 1;

      /**
       * En el modo Modelo la cantidad del stepper SIEMPRE aplica
       * (ADR-2026-08-28-modelo-v2-la-cantidad-manda).
       *
       * `resolveDesignModeloCount` la anula con 2+ poses o 2+ modelos porque el backend
       * ignora `cantidad_imagenes` en esos casos. Acá el stepper está siempre a la
       * vista, así que lo que el backend no hace lo hace el fan-out de más abajo:
       * repetir el request. La cuenta es la MISMA que muestran el botón y el pill de
       * créditos — sale del mismo helper.
       */
      const tandaModelo = esDesignModelo(selectedPreset?.kind)
        ? resolveTandaModelo({
            rawCount: autoImageCount,
            poseCount: poseIds.length,
            // Mismo origen que el sheet: en el grupal los modelos son el principal +
            // los extras, y la serie está vacía
            // (ADR-2026-09-04-los-modelos-de-la-foto-grupal-se-cobran). Para el
            // `expectedCount` de acá daba igual —en el grupal los modelos no multiplican
            // imágenes—, pero las dos llamadas tienen que decir lo mismo o el próximo
            // campo que se lea de `TandaModelo` va a valer distinto según quién pregunte.
            modelCount: multiModelEnabled
              ? Math.max(
                  1,
                  [modelSelection, ...modelSelectionsExtra].filter(Boolean).length,
                )
              : 1 + modelSelectionsSerie.length,
            grupal: multiModelEnabled,
            max: AUTO_IMAGE_COUNT_MAX,
          })
        : null;
      /**
       * ¿El backend hace las variaciones solo, o hay que pedirlas de a una?
       *
       * `atlas` usa `cantidad_imagenes` como tamaño de la ola 1 SÓLO cuando no es
       * multi-pose (`poses + customs > 1` lo anula). La composición grupal ni siquiera
       * pasa por atlas —`modelo_mode: 'multiple'` va a n8n— y no hay garantía de que
       * respete el campo. En los dos casos que no lo respetan, la cantidad se cumple
       * repitiendo el request.
       */
      const posesEnLaTanda = globalPosePayload.length + customPosesRef.length;
      const backendHaceLasVariaciones =
        shouldRouteToAtlas(
          { modo: requestModo, modelo_mode },
          GENERATE_AD_V3_URL,
        ) &&
        posesEnLaTanda <= 1 &&
        !multiModelEnabled;
      /** Cuántas veces repetir cada request para cumplir la cantidad pedida. */
      const repeticionesPorCantidad =
        tandaModelo && !backendHaceLasVariaciones ? tandaModelo.count : 1;
      const cantidadImagenesDelPayload = tandaModelo
        ? backendHaceLasVariaciones
          ? tandaModelo.count
          : 1
        : designModeloCount;

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo: requestModo,
        modo_utilizado: modoUtilizado,
        ...(template_id.length > 0 ? { template_id } : {}),
        cantidad_imagenes: cantidadImagenesDelPayload,
        // Corregido para el video de plantilla: el efecto de arriba ya lo dejó válido en
        // el estado, y acá se vuelve a pasar por la misma función porque este body se
        // arma también en el primer render tras cambiar de preset, antes de que el efecto
        // corra (ADR-2026-09-14-el-video-de-plantilla-solo-ofrece-los-formatos-que-kling-acepta).
        Format: formatSlugToAspect(
          esVideoDePlantilla ? formatoParaVideoPlantilla(formatoVal) : formatoVal,
        ),
        // Modelo, las plantillas de imagen y las de Fondo blanco tienen selector de
        // resolución (ADR-2026-09-28-las-plantillas-eligen-la-calidad,
        // ADR-2026-10-12-fondo-blanco-como-las-plantillas); las plantillas de video mandan
        // el default 1K, que es lo que generaban antes.
        image_size: isDesignModelo || plantillaConCalidad ? resolucionVal : RESOLUCION_DEFAULT,
        composicion: 'modelo',
        modelo_mode,
        ...customFields,
        ...guardadosFields,
        ...(modelo_config ? { modelo_config } : {}),
        ...(globalPosePayload.length > 0 ? { pose: globalPosePayload } : {}),
        ...(customPosesRef.length > 0 ? { poses_custom: customPosesRef } : {}),
        // Las plantillas de "E-commerce" (kind: 'design' = poses) siempre
        // se generan con fondo blanco — pedido del producto para tener
        // consistencia entre photoshoots. `background_id: 5` es el id del
        // fondo blanco en la tabla `fondos` (mismo mapping que usa la
        // UI cuando el user elige "Liso blanco" en otros flows).
        ...(isDesign ? { background_mode: 'blanco', background_id: 5 } : {}),
        ...(isDesignModelo ? buildDesignModeloBackground() : {}),
        ...(otraConfig.trim()
          ? { instruccion_adicional: otraConfig.trim() }
          : {}),
        new_ui: true,
        modo_pro: true,
        execution_id: executionId,
        // Fire-and-forget SÓLO cuando la tanda va al panel: ahí la completitud la
        // define el poll por execution_id con un `expectedCount` que sale de la misma
        // cuenta que el pill, así que el backend puede responder 202 al toque en vez
        // de sostener la conexión hasta 150 s
        // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
        //
        // `idTanda` y no `!isVideo`: este `requestData` lo comparten Modelo, las
        // plantillas de E-commerce y las plantillas con "Seleccionar varias", y esas
        // dos últimas resuelven por la RESPUESTA (un request que devuelve N ids). Con
        // 202 sin ids el poll cerraría con `expectedCount` 1 y se perderían N−1
        // imágenes ya cobradas. El video de plantilla tampoco lo lleva: tiene su
        // propio helper con su propio wait.
        ...(idTanda ? { wait: false } : {}),
      };

      // Para templates de video usamos un endpoint distinto — el pipeline
      // genera frames previos + video, vs el flow de imagen del endpoint
      // principal. design-modelo y design siempre van al endpoint de imagen
      // vía `postGenerateAd` (helper con error classification).
      const isVideoTemplate = esVideoDePlantilla;
      let data: any;
      if (isVideoTemplate) {
        // Video desde plantilla (Kling, 3-10 min) — helper con flag atlas/n8n.
        // Lo normal con atlas es 202: el helper cierra con el poll por
        // execution_id y devuelve el id REAL de la fila del video (con tipo
        // 'video', o has_error si falló) — ya no hace falta el lookup frágil
        // por created_at que había acá (ADR-581). Errores tipados (402
        // billing / policy) los muestra el catch de handleGenerateFinal.
        const videoId = await generateVideoFromTemplate(requestData, {
          companyId: userCompanyId,
        });
        pendingVideoIdRef.current = videoId;
        pendingVideoCompanyIdRef.current = userCompanyId;
        setCompleteProgressNow(true);
        return;
      } else {
        // Camino de IMAGEN (design-modelo / design / plantilla-imagen): carrera
        // respuesta-vs-poll (A) — si el fetch se cuelga (compu dormida), el poll
        // por execution_id recupera el resultado igual — + marca de pendiente
        // (B) para reanudar tras recarga. El fan-out de poses custom se arma
        // como UNA promesa (las N llamadas) y entra a la carrera como "fetch".
        // Con el atlas-backend, poses del catálogo y poses propias viajan en UN
        // request (`pose[]` + `poses_custom[]`): con más de una pose en total
        // recompone todas sobre la MISMA imagen base, así que la tanda sale con
        // el mismo modelo y el mismo fondo. Con el fan-out, en cambio, cada
        // request genera su propia base y el modelo cambiaba entre imágenes.
        // Mismo criterio que el Catálogo IA (ADR-607); el camino n8n conserva
        // el fan-out porque su workflow sólo lee `poses_custom[0]`.
        const tandaRuteaAtlas = shouldRouteToAtlas(
          requestData,
          GENERATE_AD_V3_URL,
        );
        // Los requests de la tanda. Se componen DOS fan-outs:
        //   • por MODELO — la serie de design-modelo (`modelosTanda`).
        //   • por POSE CUSTOM en el camino n8n — V3 sólo lee `poses_custom[0]`,
        //     así que va una generación por pose custom + una para las
        //     globales. Con el atlas-backend no hace falta: poses del catálogo
        //     y propias viajan juntas y se recomponen sobre la misma base.
        const fanOutPosesCustom = (
          base: Record<string, any>,
        ): Record<string, any>[] => {
          // En la composición grupal las poses las parte `fanOutPosesGrupal`, que hace lo
          // mismo pero con TODAS (globales y propias, una por request). Dejar que actúen
          // los dos re-expandía un request que ya venía partido: el que llevaba una sola
          // pose custom volvía a salir con todas las globales adentro.
          if (multiModelEnabled) return [base];
          if (customPosesRef.length === 0 || tandaRuteaAtlas) return [base];
          const baseNoPoses: Record<string, any> = { ...base };
          delete baseNoPoses.pose;
          delete baseNoPoses.poses_custom;
          const reqs: Record<string, any>[] = [];
          if (globalPosePayload.length > 0) {
            reqs.push({ ...baseNoPoses, pose: globalPosePayload });
          }
          for (const pc of customPosesRef) {
            reqs.push({ ...baseNoPoses, poses_custom: [pc] });
          }
          return reqs;
        };
        // Campos de modelo del nivel raíz. Al fan-outear por modelo hay que
        // BORRARLOS del base antes de poner los del modelo de turno: si no, los
        // del primero (que ya están en `requestData`) contaminan al resto.
        const MODELO_PAYLOAD_KEYS = [
          'modelo_mode',
          'modelo_guardado_id',
          'modelo_guardado_nombre',
          'genero',
          'edad',
          'tipo_cuerpo',
          'tono_piel',
          'color_pelo',
          'largo_pelo',
          'desc_modelo',
        ];
        const fanOutModelos = (): Record<string, any>[] => {
          if (modelosTanda.length === 0) return [requestData];
          const base: Record<string, any> = { ...requestData };
          for (const k of MODELO_PAYLOAD_KEYS) delete base[k];
          return modelosTanda.map((sel) => {
            // `buildModeloConfigItem` ya traduce una selección al shape del
            // payload; el `variante_id` es lo único propio de `modelo_config`.
            const { variante_id: _vIds, ...modeloFields } =
              buildModeloConfigItem(sel, []);
            return { ...base, ...modeloFields };
          });
        };
        /**
         * Fan-out por POSE de la composición grupal
         * (ADR-2026-08-28-modelo-v2-varias-poses-con-varios-modelos).
         *
         * Con 2 o 3 modelos el payload va a n8n con `modelo_mode: 'multiple'`, y ese
         * workflow arma UNA foto por request: mandarle `pose: [a, b]` no produce dos
         * fotos, produce una y la otra pose se pierde. Por eso las poses se parten acá:
         * un request por pose —cada uno con su PROPIO `execution_id`, ver `reqsTanda`—,
         * y cada uno sale como su propia foto grupal.
         *
         * Fuera del grupal no se toca: por el camino de atlas las poses viajan juntas y
         * se recomponen sobre la misma imagen base, que es lo que hace que la tanda salga
         * con el mismo modelo y el mismo fondo (ADR-607).
         */
        const fanOutPosesGrupal = (
          base: Record<string, any>,
        ): Record<string, any>[] => {
          if (!multiModelEnabled || posesEnLaTanda <= 1) return [base];
          const sinPoses: Record<string, any> = { ...base };
          delete sinPoses.pose;
          delete sinPoses.poses_custom;
          return [
            ...globalPosePayload.map((id) => ({ ...sinPoses, pose: [id] })),
            ...customPosesRef.map((pc) => ({ ...sinPoses, poses_custom: [pc] })),
          ];
        };
        /**
         * Fan-out por CANTIDAD: las variaciones que el backend no sabe hacer solo
         * (ADR-2026-08-28-modelo-v2-la-cantidad-manda). Con `backendHaceLasVariaciones`
         * esto es 1 y la cantidad viaja dentro del request, como siempre.
         */
        const fanOutCantidad = (
          base: Record<string, any>,
        ): Record<string, any>[] =>
          repeticionesPorCantidad <= 1
            ? [base]
            : Array.from({ length: repeticionesPorCantidad }, () => ({
                ...base,
              }));
        /**
         * UN `execution_id` POR REQUEST
         * (ADR-2026-09-06-un-execution-id-por-request-en-el-fan-out-de-modelo).
         *
         * El backend encola con `idempotencyKey: ads:<empresa>:<execution_id>` y, ante
         * el choque del índice único, devuelve la ejecución existente sin encolar ni
         * cobrar. Con N requests compartiendo el id "para que el poll los junte", del
         * segundo en adelante volvía la ejecución del primero: pedís 6 (cantidad 3 × 2
         * poses) y salen 2, sin error. Fotos de producto y Catálogo ya usaban un id por
         * llamada (ADR-590). El único request conserva el id de la corrida; el poll
         * acepta la lista y los junta.
         */
        const reqsTanda = fanOutModelos()
          .flatMap(fanOutPosesCustom)
          .flatMap(fanOutPosesGrupal)
          .flatMap(fanOutCantidad)
          .map((r, _i, arr) =>
            arr.length > 1 ? { ...r, execution_id: crypto.randomUUID() } : r,
          );
        const executionIdsTanda: string[] = reqsTanda.map(
          (r) => String(r.execution_id ?? executionId),
        );
        const fetchPromise: Promise<any> =
          reqsTanda.length > 1
            ? (async () => {
                /**
                 * Tope de llamadas en paralelo, por lo mismo que en el Catálogo: cada
                 * request sostiene una conexión larga y disparar muchas juntas hace que
                 * alguna se pierda (fetch muerto sin respuesta) — y cada imagen que se
                 * pierde es un crédito que el usuario pagó. Con los fan-outs por pose y
                 * por cantidad, una tanda de v2 puede llegar a 18 requests
                 * (3 poses × 6 variaciones), así que el tope dejó de ser opcional.
                 *
                 * No serializa: deja hasta MAX en vuelo y larga la siguiente a medida que
                 * alguna termina.
                 */
                const MAX_EN_PARALELO = 3;
                const settled: PromiseSettledResult<any>[] = [];
                for (let i = 0; i < reqsTanda.length; i += MAX_EN_PARALELO) {
                  const lote = reqsTanda.slice(i, i + MAX_EN_PARALELO);
                  settled.push(
                    ...(await Promise.allSettled(
                      lote.map((r) =>
                        postGenerateAd(r, { url: GENERATE_AD_V3_URL }),
                      ),
                    )),
                  );
                }
                const ok = settled
                  .filter((s) => s.status === 'fulfilled')
                  .map((s) => (s as PromiseFulfilledResult<any>).value);
                if (ok.length === 0) {
                  const firstErr = settled.find(
                    (s) => s.status === 'rejected',
                  ) as PromiseRejectedResult | undefined;
                  throw (
                    firstErr?.reason ?? new Error('No se pudo generar la imagen')
                  );
                }
                const mergedIds = ok.flatMap((d) =>
                  Array.isArray(d?.imagenes_generadas_ids)
                    ? d.imagenes_generadas_ids
                    : [],
                );
                return {
                  response: 'success',
                  imagenes_generadas_ids: mergedIds,
                  id_company: ok[0]?.id_company || userCompanyId,
                };
              })()
            : postGenerateAd(reqsTanda[0], { url: GENERATE_AD_V3_URL });

        // `expectedCount`: el backend escribe una fila por imagen con gaps de
        // 30-60s, así que sin esto el poll cierra la tanda por estabilidad y se
        // pierden las que faltaban. Dos casos:
        //  • Varias poses en un request (atlas): la ola 2 escribe UNA FILA POR
        //    POSE — normales y propias por igual (`poseRows.map` + `customs.map`
        //    en generateAd.ts). Con el fan-out de n8n no aplica: ahí cada
        //    request trae lo suyo y la cuenta por execution_id no es directa.
        //  • Una sola pose: son variaciones, y la cuenta es `cantidad_imagenes`.
        const totalPosesTanda =
          globalPosePayload.length + customPosesRef.length;
        // …y por cada MODELO de la serie sale esa cuenta entera otra vez (un
        // request por modelo, cada uno con su execution_id; el poll los junta).
        const modelosEnTanda = Math.max(1, modelosTanda.length);
        const porModelo =
          tandaRuteaAtlas && totalPosesTanda > 1
            ? totalPosesTanda
            : designModeloCount > 1
              ? designModeloCount
              : 1;
        // En v2 la cuenta ya está hecha, y es la MISMA que muestran el botón y el pill:
        // cantidad × poses × modelos que multiplican. Rehacerla acá con otra fórmula es
        // lo que dejaba al poll esperando un número distinto del que se cobró
        // (ADR-2026-08-28-modelo-v2-la-cantidad-manda).
        const totalEsperado = tandaModelo
          ? tandaModelo.total
          : porModelo * modelosEnTanda;
        // SIEMPRE un número, también con una sola imagen. Antes con 1 iba `undefined`
        // porque la respuesta síncrona del backend traía el id y ganaba la carrera;
        // con `wait: false` (ADR-2026-09-06-tope-de-tres-tandas-en-curso) la única
        // señal es el poll, y sin `expectedCount` recién cierra por estabilidad:
        // ~92 s de espera extra con la imagen ya lista.
        const modeloExpectedCount = Math.max(1, totalEsperado);
        // La tanda registra TODOS sus execution_id: por ellos la reanuda el shell tras
        // una recarga (ADR-2026-09-06-registro-de-generaciones-en-curso).
        // Con el total REAL: `totalTanda` se calculó antes de resolver el fan-out y en
        // la serie de modelos se queda corto — los recuadros y el reanudador esperarían
        // menos imágenes de las que salen.
        if (idTanda) {
          registrarExecutionIds(idTanda, executionIdsTanda, totalEsperado);
        }
        const modeloResult = await awaitImageGeneration({
          fetchPromise,
          executionId: executionIdsTanda,
          companyId: userCompanyId,
          expectedCount: modeloExpectedCount,
          // Tope proporcional al tamaño de la tanda del panel
          // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
          pollTimeoutMs: idTanda ? timeoutPollTanda(totalEsperado) : undefined,
        });
        data = {
          response: 'success',
          imagenes_generadas_ids: modeloResult.imageIds,
          id_company: modeloResult.companyId,
        };
      }

      // El caso video-plantilla ya retornó arriba (helper con poll por
      // execution_id) — acá solo llega el camino de IMAGEN.
      if (
        Array.isArray(data?.imagenes_generadas_ids) &&
        data.imagenes_generadas_ids.length > 0
      ) {
        const imageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult =
          (data.id_company as string) || userCompanyId;

        // Persistir la última config exitosa para auto-rellenar en la
        // próxima generación (formato, modelo, productos). Si algo no
        // está seteado, no lo guardamos — para que un fallback más
        // antiguo siga vigente.
        try {
          if (formatoVal) {
            localStorage.setItem('atlas-v2-last-format', formatoVal);
          }
          if (modelSelection) {
            localStorage.setItem(
              'atlas-v2-last-model',
              JSON.stringify(modelSelection),
            );
          }
          if (selectedProducts.length > 0) {
            localStorage.setItem(
              'atlas-v2-last-products',
              JSON.stringify(selectedProducts.map((p) => p.id)),
            );
          }
        } catch {
          /* localStorage puede fallar en modo privado — non-fatal */
        }

        // Preload images antes de navegar — así la pantalla destino
        // arranca con las imágenes ya en cache.
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(
            imageIds,
            companyIdResult,
          );
        } catch (preloadErr) {
          console.warn(
            '[InterfazV2] preloadGeneratedImages falló, continúo igual',
            preloadErr,
          );
        }

        // En modo video el "listo" real es cuando terminan los videos,
        // así que diferimos el sonido hasta después del video chain
        // (sino el user escucha el listo con 30-60s de espera por delante).
        const isVideoModeNow =
          previewMediaTab === 'video' && selectedPreset.kind === 'template';
        if (!isVideoModeNow) {
          sonarListo();
        }

        // La vuelta atrás: el "Atrás" desde la pantalla de imágenes generadas remonta
        // esta pantalla, que lee esto y reabre el modo con TODO lo que se generó. Los
        // lookups async (preset + productos) los resuelven effects dedicados cuando los
        // catálogos llegan.
        guardarRestoreV2();

        const { user_id: _uid, wait: _w, ...lastPayload } = requestData;
        void _w;
        void _uid;

        // ── Modo video (toggle 'video' + kind 'template') ──────────────
        // Después de tener las imágenes, encadenamos para cada una:
        //   1) Pedir el prompt sugerido al webhook generar_prompt_video.
        //   2) Disparar la generación del video (cinema multi o single).
        //   3) Esperar a que el row del video aparezca en imagenes_generadas.
        // Después navegamos con los IDs de video. Si falla todo, navegamos
        // igual con las imágenes (mejor algo que nada).
        const isVideoMode =
          previewMediaTab === 'video' && selectedPreset.kind === 'template';
        if (isVideoMode) {
          const videoRequestTime = new Date().toISOString();

          const generateOne = async (
            imageId: number,
          ): Promise<string | null> => {
            // 1) Prompt sugerido (helper con flag atlas/n8n) — best-effort.
            let prompt = '';
            try {
              prompt = await fetchSuggestedVideoPrompt(imageId, user.id);
            } catch (err) {
              console.warn('[InterfazV2] prompt de video sugerido falló', err);
            }

            // 2) Trigger video generation
            const videoPayload = {
              user_id: user.id,
              tipo_video: 'cinematografico',
              prompt,
              duracion: videoDuration,
              Format: '9:16',
              style_prompt: '',
              multiples_escenas: videoMultiScene,
              origen: 'plantilla v2',
              modo_utilizado: 'plantilla_video',
              scenes: [
                {
                  index: 0,
                  prompt,
                  start_frame: imageId,
                  end_frame: null,
                  duration_seconds: videoDuration,
                },
              ],
            };
            // Animar imagen (ambas ramas del toggle multi-escena): helper
            // con flag atlas/n8n — 202 → poll de respaldo por execution_id
            // (normal con Kling multi-shot); 402 billing. Ver ADR-582.
            let webhookVideoId: string | null = null;
            try {
              webhookVideoId = await generateVideoFromImage(videoPayload, {
                companyId: companyIdResult,
              });
            } catch (err) {
              console.warn('[InterfazV2] animar imagen (plantilla) falló', err);
              return null;
            }

            // 3) Encontrar el row real del video. El webhook de n8n devuelve
            // un UUID del workflow, no el id de imagenes_generadas. Buscamos
            // el último video del company creado después del request.
            const sleep = (ms: number) =>
              new Promise((r) => setTimeout(r, ms));
            for (let attempt = 0; attempt < 20; attempt++) {
              const { data: row } = await supabase
                .from('imagenes_generadas')
                .select('id, file_name')
                .eq('id_company', companyIdResult)
                .in('tipo', ['video', 'video_pro'])
                .gte('created_at', videoRequestTime)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
              if (row?.id) return String(row.id);
              await sleep(2000);
            }
            // Fallback al id devuelto por el backend si no encontramos el
            // row (en atlas ES el id real; en n8n es el UUID del workflow).
            return webhookVideoId;
          };

          const videoResults = await Promise.allSettled(
            imageIds.map((id) => generateOne(id)),
          );
          const videoIds = videoResults
            .map((r) =>
              r.status === 'fulfilled' && r.value ? r.value : null,
            )
            .filter((v): v is string => !!v);

          if (videoIds.length > 0) {
            // Sonido de "listo" — recién acá, cuando los videos
            // realmente están listos para navegar.
            sonarListo();
            navigate(rutaResultados, {
              state: {
                imageIds: videoIds,
                companyId: companyIdResult,
                lastPayload,
                // No mandamos preloadedImages porque el preload helper
                // arma signed URLs para imágenes (full/opt). Los videos
                // los carga la página destino con su propio fetch que ya
                // sabe distinguir tipo='video'.
              },
            });
            return;
          }
          // Fallback: si NINGÚN video salió, navegamos con las imágenes
          // generadas para que el user al menos las tenga.
          console.warn(
            '[InterfazV2] todos los videos fallaron, navegando solo con imágenes',
          );
        }

        // Tanda EN EL PANEL: las imágenes se muestran ahí mismo y NO se navega a la
        // pantalla de resultados (ADR-2026-08-20-tanda-en-el-panel).
        //
        // Y NUNCA se navega, ni siquiera cuando el preload volvió vacío —el `file_name`
        // tardó más que sus diez intentos—: antes ese caso raro caía a la pantalla vieja,
        // y desde el asiento del usuario eso es "generé y la interfaz se me fue"
        // (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla). Sin preload
        // la tanda cierra igual y se le pide al panel que recargue las creaciones: las
        // filas YA existen, así que las imágenes entran por la grilla, que es donde el
        // usuario está mirando.
        if (idTanda) {
          // Por id: mientras esta corría, el usuario pudo haber disparado otra. El total
          // esperado era una previsión; lo que llegó es la verdad, y sin nada que
          // dibujar la grilla las trae de la base (todo eso lo hace `cerrarTanda`).
          cerrarTanda(idTanda, preloadedImages);
          /**
           * "Generar producción" TAMBIÉN encadena el carrusel cuando la tanda va al panel
           * — ADR-2026-09-08-la-produccion-de-plantillas-encadena-el-carrusel-en-el-panel.
           *
           * El carrusel se disparaba en un solo lugar: `GeneratedImagesInline`, al montar
           * con `autoCarrusel` (lo manda el `navigate` de más abajo). Desde que Plantillas
           * genera en la columna (ADR-2026-09-08-la-plantilla-abre-directo-en-la-configuracion)
           * este `return` se lleva la corrida antes de ese navigate, así que el inline no
           * montaba nunca y la producción moría en la imagen base — con el guard ya
           * habiendo cobrado por las N del stepper.
           *
           * Acá sale el PEDIDO; los recuadros ya están abiertos desde el toque de Generar
           * (`idTandaProduccion`, ADR-2026-09-14-la-produccion-abre-todos-sus-recuadros-al-generar).
           * Sigue siendo una segunda tanda y no la primera agrandada: son dos pedidos
           * distintos al backend y el segundo necesita el id del primero, así que uno
           * termina antes que el otro —y el reanudador de la base espera SU cuenta, no la
           * de la producción entera—.
           */
          if (idTandaProduccion) {
            if (imageIds.length > 0) {
              resolverTandaConPromesa(
                idTandaProduccion,
                companyIdResult,
                postGenerateAngles({
                  user_id: user.id,
                  image_id: String(imageIds[0]),
                  cantidad_imagenes: carruselCount,
                  image_size: imageSizeDelCarrusel,
                }),
                {
                  errorGenerico:
                    'No se pudo generar el resto de la producción. Intentá nuevamente.',
                },
              );
            } else {
              // Sin imagen base no hay de dónde sacar los ángulos: los recuadros se van
              // (del error de la base ya avisa su propio camino).
              fallarTanda(idTandaProduccion);
            }
          }
          if (!tandaVaAlPanel) generatingLockRef.current = false;
          return;
        }

        navigate(rutaResultados, {
          state: {
            imageIds,
            companyId: companyIdResult,
            lastPayload,
            preloadedImages,
            autoCarrusel: wantsCarrusel,
            // Cuántas imágenes pide el carrusel automático = (cantidad
            // elegida en el stepper − 1). El inline lo manda al webhook.
            autoCarruselCount: carruselCount,
            // Y en qué resolución, la misma de la imagen base.
            autoCarruselImageSize: imageSizeDelCarrusel,
          },
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (err) {
      console.error('[InterfazV2] handleGenerateFinal error', err);
      if (err instanceof GenerateAdError && err.errorType === 'billing') {
        // Sin créditos según el backend nuevo (HTTP 402) — mismo UX que el
        // gate local de checkGenerationGuards (modal de límite, no genError).
        setSubscriptionLimitType(
          userPlan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        );
        setMotivoPricing('sin');
        setShowSubscriptionLimitModal(true);
      } else if (err instanceof GenerateAdError && err.isActionable) {
        setGenError(err.message);
      } else {
        setGenError(
          'Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.',
        );
      }
      setShowLoadingScreen(false);
      setVideoGenerating(false);
      setAnuncioGenerating(false);
      // La tanda de ESTA corrida se cae con el error: sus recuadros "generando" no
      // pueden quedar latiendo para siempre. Las otras siguen — pueden estar
      // corriendo bien. Si era la única, la columna vuelve a la configuración tal
      // como estaba; el toast de error ya explica qué pasó.
      if (idTanda) fallarTanda(idTanda);
      // Y con ella la de la producción: su pedido nunca llegó a salir.
      if (idTandaProduccion) fallarTanda(idTandaProduccion);
      // El candado sólo se tomó cuando la generación NO iba al panel; soltarlo igual
      // pisaría el de una corrida que sí lo tenga.
      if (!tandaVaAlPanel) generatingLockRef.current = false;
    }
  };

  // ── Modo 'catalogo-ia' ─────────────────────────────────────────────
  // Dispara N llamadas al endpoint V3 en paralelo, una por cada
  // combinación (vista × producto) confirmada por el user en el step
  // `configure`. Todas con fondo blanco. Loading screen se mantiene
  // hasta que todas las promesas se resuelvan.
  //
  // Payload por card:
  //   • con-modelo   → composicion: 'modelo' + modelo_mode: 'ia' + pose: [vistaId]
  //   • solo-producto → composicion: 'producto' + vista_producto: vistaId
  //
  // Resultado: navegamos a `/redes-sociales/imagenes-generadas` con el
  // listado completo de imageIds + meta `catalogo` para que la pantalla
  // de imágenes generadas pueda renderizar agrupado por producto
  // (implementado en la Fase 7 — ahora solo navega con la data).
  const handleGenerateCatalog = async (cards: CatalogoCard[]) => {
    // Este handler sirve a los DOS modos de catálogo. Se extendió en vez de
    // clonarlo: el 90% (guards, execution_id, agrupación, poll, recuperación,
    // navigate) es idéntico, y dos copias divergen en el primer bugfix que se
    // aplique a una sola. Lo específico de muebles son 3 cosas puntuales.
    // El modo Infografía (ADR-813) comparte TODO el comportamiento de muebles
    // (medidas, atributos, vistas muebles, ruteo al backend); solo cambia que su
    // única vista es infografía. Por eso entra acá como muebles.
    const esModoMuebles =
      selectedPreset?.kind === 'catalogo-muebles' ||
      selectedPreset?.kind === 'infografia';
    // Infografía genera como muebles pero muestra sus resultados en la pantalla de
    // imágenes generadas NORMAL, sin la agrupación por producto del catálogo
    // (ADR-2026-10-15-la-infografia-muestra-los-resultados-normales).
    const esInfografia = selectedPreset?.kind === 'infografia';
    /**
     * Esta generación va a la columna de tandas en vez del overlay full-screen.
     *
     * Sólo el Catálogo IA rediseñado (una sola columna) y sólo en desktop: es el mismo
     * layout de "Imagen con modelo" —panel a la izquierda, muestra a la derecha— así que
     * el lugar donde mostrar la tanda ya existe. En mobile no hay dos columnas, y los
     * otros catálogos (muebles, infografía) siguen con el overlay de siempre.
     * Ver ADR-2026-08-24-catalogo-genera-en-el-panel.
     */
    const tandaVaAlPanel =
      selectedPreset?.kind === 'catalogo-ia' &&
      // El rediseño genera desde su ÚNICA pantalla, que se queda en `choose-product`
      // para siempre. El review de cards a pantalla completa (`step === 'configure'`)
      // también llama acá y tapa las dos columnas: ahí la tanda no se vería, así que ese
      // camino se queda con el overlay.
      panelStep === 'choose-product' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches;
    // El candado tapa el doble click en todo lo demás. Con la tanda en el panel NO
    // aplica: el resultado aterriza en su propia tanda, así que se puede seguir
    // generando con una corrida en curso —que es justo lo que dice el encabezado del
    // panel— igual que en "Imagen con modelo"
    // (ADR-2026-08-20-varias-tandas-en-el-panel).
    if (generatingLockRef.current && !tandaVaAlPanel) return;
    if (cards.length === 0) return;
    if (!userCompanyId) {
      setGenError('No se encontró la empresa del usuario');
      return;
    }
    // Tope de tandas en curso por empresa (ADR-2026-09-06-tope-de-tres-tandas-en-curso),
    // igual que en `handleGenerateFinal`.
    if (tandaVaAlPanel && !puedeCrear()) {
      setGenError(
        `Esperá a que termine alguna de las generaciones en curso (máximo ${MAX_TANDAS_EN_CURSO} a la vez).`,
      );
      return;
    }
    if (!tandaVaAlPanel) generatingLockRef.current = true;
    // Guards: catálogo IA no está en el plan gratis + chequeo de créditos.
    // Costo = 1 crédito por card (cada card = 1 imagen final), igual que el
    // pill de CatalogoCardsFullscreen (`activeCards.length`). Ver ADR-518.
    // Costo = imágenes REALES, no cards: con el stepper de "foto en contexto"
    // una card puede valer hasta 3 (la base + 2 del carrusel). Contar cards
    // subestimaría el chequeo de créditos y dejaría pasar una generación que
    // el usuario no puede pagar.
    /**
     * Los modelos de ESTA tanda: SIEMPRE uno —el de `modelSelection`, que puede ser
     * `null` = Automático— porque el Catálogo elige un solo modelo
     * (ADR-2026-08-28-catalogo-un-solo-modelo, supersede
     * ADR-2026-08-28-catalogo-varios-modelos).
     *
     * Queda como lista y no como valor suelto porque el fan-out de abajo la recorre, y
     * porque es el punto exacto donde volvería a entrar el multi si alguna vez se
     * reactiva. `modelSelectionsSerie` NO se mira: es estado compartido con
     * "Imagen con modelo", y leerlo acá metería en la tanda del Catálogo los modelos que
     * quedaron elegidos en el otro modo.
     */
    const modelosDeLaTanda: (ModelSelection | null)[] = [modelSelection];
    if (modelosDeLaTanda.length === 0) modelosDeLaTanda.push(null);
    // Las cards `solo-producto` NO se multiplican: un packshot sale igual con cualquier
    // modelo, así que duplicarlo sería la misma imagen cobrada dos veces.
    const totalImagenesAGenerar = contarImagenesCatalogo(
      cards,
      modelosDeLaTanda.length,
    );
    // El saldo se chequea contra lo que REALMENTE se va a cobrar: las imágenes por el
    // multiplicador de la resolución elegida
    // (ADR-2026-08-25-resolucion-de-generacion-2k-4k). Es el mismo número que muestra
    // el pill del panel.
    const costoTandaCatalogo =
      totalImagenesAGenerar * creditosPorResolucion(resolucionVal);
    // Sin `feature`: el piso que se chequea es el del modo abierto, que es justo lo que
    // el catálogo necesita (sus tres modos hoy no tienen piso, y si mañana alguno lo
    // tiene funciona sin tocar este archivo).
    const guardsOk = await checkGenerationGuards(undefined, costoTandaCatalogo);
    if (!guardsOk) {
      if (!tandaVaAlPanel) generatingLockRef.current = false;
      return;
    }
    setGenError(null);
    setLoadingExiting(false);
    setCompleteProgressNow(false);
    setVideoGenerating(false);
    setAnuncioGenerating(false);
    /**
     * Id de LA tanda de esta corrida, o null si esta generación usa el overlay.
     *
     * Const del closure y no un ref: con dos corridas en vuelo un ref compartido diría
     * "la última que arrancó" y la que terminara primero escribiría sobre la tanda
     * equivocada.
     *
     * La crea el registro (uuid, arriba de todo: la última que se pidió es la que el
     * usuario está esperando — ADR-2026-09-06-use-tandas-panel). `total` son las
     * imágenes REALES —no las cards—: con el stepper de "foto en contexto" una card
     * puede valer hasta 3, y los recuadros tienen que ser tantos como imágenes vengan.
     * El producto y el modo son los del MOMENTO de generar: si después se cambia de
     * producto, la tanda que ya salió sigue diciendo de qué era.
     */
    const idTanda = tandaVaAlPanel
      ? crearTanda({
          total: totalImagenesAGenerar,
          ratio: aspectARatio(formatSlugToAspect(formatoVal)),
          producto: selectedProducts.map((p) => p.name).join(', '),
          modo: selectedPreset?.kind,
          creditos: costoTandaCatalogo,
        })
      : null;
    if (!idTanda) {
      setShowLoadingScreen(true);
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      // NOTA: los execution_id se registran en la tanda MÁS ABAJO, una vez armados
      // los grupos: desde ADR-590 cada llamada lleva su propio execution_id y hay que
      // guardar el conjunto, no uno solo.

      // Agrupamos las cards por (producto, kind de vista) y mandamos una
      // sola llamada por grupo. El backend ya soporta multi-pose
      // (`pose: [id1, id2]`) en composición 'modelo' y multi-vista
      // (`vista_producto: [id1, id2]`) en composición 'producto' siempre
      // que haya una sola variante por request — que es nuestro caso
      // (1 producto por grupo).
      //
      // Antes: N requests para un producto con N vistas. Ahora: hasta 2
      // requests por producto (1 con-modelo + 1 solo-producto).
      type CatalogoGroup = {
        productId: string;
        product: typeof cards[number]['product'];
        kind: typeof cards[number]['vista']['kind'];
        vistaIds: string[];
        // Combo de productos (principal primero) que se combinan en la
        // imagen. Viaja entero en `variante_id`, igual que multi-producto
        // del modo modelo.
        comboIds: string[];
        /** Medidas del producto (modo muebles). Iguales para todo el grupo:
         *  los grupos son por producto. */
        medidas?: Medidas | null;
        /** Id propio de ESTA llamada. Ver el comentario en el payload. */
        executionId: string;
        /** Config global de la infografía (modo muebles). */
        infografiaConfig?: {
          estilo: string;
          color: string;
          mostrarMedidas: boolean;
          mostrarAtributos: boolean;
        };
        /** Características curadas del producto para la infografía (máx 3).
         *  Iguales para todo el grupo (los grupos son por producto). */
        atributosInfografia?: string[];
        /** Lo que el usuario escribió sobre el outfit de este producto, en el review.
         *  Igual para todo el grupo — los grupos son por producto, que es justo el
         *  alcance del campo (ADR-2026-08-27-una-indicacion-de-outfit-por-producto). */
        instruccionOutfit?: string;
        /**
         * Payload de modelo de ESTE request. Con varios modelos la tanda se fan-outea:
         * cada grupo con-modelo se manda una vez por modelo, cada copia con su propio
         * `executionId` (ADR-2026-08-28-catalogo-varios-modelos). Sin varios modelos
         * queda `undefined` y el payload usa el de la tanda.
         */
        modelo?: Record<string, any>;
        // Solo en grupos FUSIONADOS para atlas-backend: referencias de poses
        // custom que viajan en `poses_custom` junto con las normales de
        // `vistaIds` → `pose`. El backend recompone todas sobre la misma
        // imagen base (mismo modelo/fondo en la tanda). Ver ADR-576.
        posesCustomRefs?: { id: string; filename: string }[];
      };
      // Mapa id de pose custom → filename del muñeco (para armar
      // `poses_custom:[{id,filename}]` en la request). Fuente de verdad:
      // `customPoses` (viene fresco de la DB) — igual que el modo
      // personalizado, que arma la referencia desde ahí y nunca falla.
      // Fallback: el `filename` acarreado en la vista (picker → cards).
      // NUNCA mandamos filename vacío: el workflow V3 muere en
      // "sign pose custom" sin escribir fila NI devolver error (responde
      // 200 con body vacío) y el poll se queda esperando filas que no van
      // a llegar — caso Clohé, ADR-134.
      const customPoseFilenameById = new Map<string, string>();
      for (const p of customPoses) {
        if (p.filename) customPoseFilenameById.set(p.id, p.filename);
      }
      for (const card of cards) {
        if (
          card.vista.kind === 'mis-pose' &&
          card.vista.filename &&
          !customPoseFilenameById.has(card.vista.id)
        ) {
          customPoseFilenameById.set(card.vista.id, card.vista.filename);
        }
      }
      // ── Tope de llamadas en paralelo ──────────────────────────────────
      // Cada request mantiene la conexión abierta hasta que el workflow
      // termina (V3 usa `responseMode: responseNode`), así que "disparar todas
      // juntas" abre N conexiones largas simultáneas. Con 3 ya se perdía una
      // (fetch muerto sin respuesta, "Provisional headers are shown"); con un
      // catálogo de 5 productos × 3 vistas serían 15 y se perderían muchas.
      //
      // El semáforo NO serializa: deja hasta MAX en vuelo y va largando la
      // siguiente a medida que alguna termina. Un catálogo grande tarda un poco
      // más, pero llegan todas — que es lo que importa cuando cada imagen que
      // se pierde es un crédito que el usuario pagó.
      //
      // MUEBLES VA DE A 1 (curita temporal). El modo muebles NO agrupa (cada
      // vista es su propio fondo → una ejecución por vista), así que para 3
      // imágenes abre 3× más conexiones a Supabase que el catálogo, que sí
      // agrupa. Con el pool de Postgres crónicamente al ~68% del techo (90),
      // esa ráfaga tira `EAUTHTIMEOUT` en `get user`. Serializando muebles el
      // pico baja a 1 ejecución por vez y deja de caerse. Es más lento, pero
      // llegan todas. ES UN PARCHE: el fix de fondo es migrar la credencial de
      // Postgres de n8n al POOLER de Supabase (Supavisor 6543, transaction);
      // cuando esté, volvé esto a 3. Ver docs/ y el análisis del 2026-07-22.
      const MAX_LLAMADAS_EN_PARALELO = esModoMuebles ? 1 : 3;
      let llamadasEnVuelo = 0;
      const esperarTurno = async () => {
        while (llamadasEnVuelo >= MAX_LLAMADAS_EN_PARALELO) {
          await new Promise((r) => setTimeout(r, 200));
        }
      };

      // producto → ids que devolvió el carrusel (se llena más abajo, después
      // del poll; se declara acá para que lo vean los dos bloques).
      const carruselIdsPorProducto = new Map<string, string[]>();
      const groupsMap = new Map<string, CatalogoGroup>();
      for (const card of cards) {
        const comboIds =
          card.comboProducts && card.comboProducts.length > 0
            ? card.comboProducts.map((p) => p.id)
            : [card.product.id];
        // El group-key incluye el combo ORDENADO para NO mezclar en una
        // sola request combinaciones distintas — comparten `variante_id`.
        // Las poses custom (`mis-pose`) NO se agrupan entre sí acá: el
        // workflow de n8n solo procesa `poses_custom[0]`, así que cada pose
        // custom arranca como su propio grupo (incluimos el id de la vista
        // en la key). Si la tanda rutea al atlas-backend, más abajo estos
        // grupos se FUSIONAN con el con-modelo del mismo combo (ADR-576).
        // Las vistas de MUEBLES tampoco se agrupan entre sí (igual que las
        // poses custom, y por una razón parecida): las tres son
        // `solo-producto`, así que sin esto caerían en una sola request con
        // `vista_producto: [packshot, infografia, contexto]` y un único
        // `background_mode`. Y justamente cada una necesita el suyo — el
        // packshot va sobre blanco y la foto en contexto sobre un ambiente.
        const groupKey =
          card.vista.kind === 'mis-pose'
            ? `${card.product.id}::mis-pose::${card.vista.id}::${comboIds.join(',')}`
            : esModoMuebles
              ? `${card.product.id}::mueble::${card.vista.id}::${comboIds.join(',')}`
              : `${card.product.id}::${card.vista.kind}::${comboIds.join(',')}`;
        const existing = groupsMap.get(groupKey);
        if (existing) {
          existing.vistaIds.push(card.vista.id);
        } else {
          groupsMap.set(groupKey, {
            productId: card.product.id,
            product: card.product,
            kind: card.vista.kind,
            medidas: card.medidas ?? null,
            infografiaConfig: card.infografiaConfig,
            atributosInfografia: card.atributosInfografia,
            instruccionOutfit: card.instruccionOutfit,
            executionId: crypto.randomUUID(),
            vistaIds: [card.vista.id],
            comboIds,
          });
        }
      }
      const groups = Array.from(groupsMap.values());

      // Fondo y modelo elegidos para TODA la tanda desde los botones del
      // review. Default: blanco + IA (como venía el catálogo). Misma forma
      // de payload que design-modelo.
      const buildCatalogoBackground = (): Record<string, any> =>
        // Misma función que design-modelo; sólo cambia qué mandar SIN selección (el
        // Catálogo arranca en blanco, Modelo en Automático).
        backgroundDeFondo(fondoSelection, 'blanco');
      // Recibe la selección en vez de leer `modelSelection` del closure: con varios
      // modelos la tanda arma un payload POR modelo
      // (ADR-2026-08-28-catalogo-varios-modelos).
      const buildCatalogoModelo = (
        sel: ModelSelection | null,
      ): Record<string, any> => {
        if (sel?.mode === 'preset') {
          const o: Record<string, any> = {
            modelo_mode: 'guardados',
            modelo_guardado_id: String(sel.id),
          };
          if (sel.name) o.modelo_guardado_nombre = sel.name;
          return o;
        }
        if (sel?.mode === 'custom') {
          const f = sel.fields;
          const o: Record<string, any> = { modelo_mode: 'personalizado' };
          if (f.genero) o.genero = f.genero;
          if (f.edad) o.edad = f.edad;
          if (f.tipoCuerpo) o.tipo_cuerpo = f.tipoCuerpo;
          if (f.tonoPiel) o.tono_piel = f.tonoPiel;
          if (f.colorPelo) o.color_pelo = f.colorPelo;
          if (f.largoPelo) o.largo_pelo = f.largoPelo;
          if (f.descripcion) o.desc_modelo = f.descripcion;
          return o;
        }
        return { modelo_mode: 'ia' };
      };
      const catalogoBackground = buildCatalogoBackground();
      /** Un payload de modelo por modelo elegido. Con uno solo, la lista tiene uno. */
      const catalogoModelos = modelosDeLaTanda.map(buildCatalogoModelo);
      /** El primero manda en las decisiones que son de LA TANDA (ruteo, `mis-pose`). */
      const catalogoModelo = catalogoModelos[0];

      // Total esperado de filas en la DB: una imagen FINAL por card. Es
      // MUTABLE a propósito: cuando un request falla a nivel HTTP le
      // descontamos sus cards — ese grupo nunca va a escribir filas, y si no
      // lo descontáramos el poll se quedaría esperándolas hasta el tope
      // (ADR-133). El poll lo lee vía getter en cada ronda.
      let expectedTotal = cards.length;

      // Errores HTTP de los requests de la tanda. El `.catch` de abajo los
      // descuenta de `expectedTotal` para no colgar el poll, pero ANTES los
      // tiraba a un console.warn: un 402 (sin créditos) o un 400 (validación)
      // no crean filas, así que el chequeo final los veía como "cero filas" y
      // mostraba "No se generó ninguna imagen del catálogo" — un mensaje mudo
      // que además no deja rastro server-side (el rechazo es previo a
      // `createExecution`). Los guardamos para que el error real llegue al
      // usuario. Ver ADR-677.
      const sendErrors: GenerateAdError[] = [];
      // Las promesas de los POST: con `wait:false` resuelven en ms, así que
      // esperarlas en la rama de fallo es barato y evita la carrera contra el
      // poll (podríamos decidir el mensaje con `sendErrors` todavía vacío).
      const sends: Promise<unknown>[] = [];

      // ¿La tanda rutea al atlas-backend? Toda la tanda comparte modo y
      // modelo_mode, así que la decisión es UNA sola. Con atlas, los grupos
      // `mis-pose` se FUSIONAN con el `con-modelo` del mismo combo en un
      // único request (`pose: [...]` + `poses_custom: [...]`): el backend
      // recompone todas las poses sobre la misma imagen base → mismo modelo
      // y mismo fondo en toda la tanda (antes, tandas mixtas podían salir
      // con modelos distintos). Con n8n queda el fan-out de siempre (su
      // workflow solo lee `poses_custom[0]`). Ver ADR-576.
      // Con varios modelos alcanza con mirar el primero: el ruteo sólo se cae con
      // `modelo_mode: 'multiple'` (composición grupal), y el catálogo nunca lo manda —
      // sus modos son 'ia', 'guardados' o 'personalizado', los tres soportados.
      const tandaRuteaAtlas = shouldRouteToAtlas(
        { modo: 'personalizado', ...catalogoModelo },
        GENERATE_AD_V3_URL,
      );
      let sendGroups = groups;
      if (tandaRuteaAtlas) {
        const mergedByCombo = new Map<string, CatalogoGroup>();
        const resto: CatalogoGroup[] = [];
        for (const g of groups) {
          if (g.kind !== 'con-modelo' && g.kind !== 'mis-pose') {
            resto.push(g); // solo-producto: igual que siempre
            continue;
          }
          const key = `${g.productId}::${g.comboIds.join(',')}`;
          let target = mergedByCombo.get(key);
          if (!target) {
            target = {
              productId: g.productId,
              product: g.product,
              kind: 'con-modelo',
              vistaIds: [],
              comboIds: g.comboIds,
              posesCustomRefs: [],
              // Un execution_id PROPIO también para el grupo fusionado
              // (ADR-590): cada request lleva su id para que el backend no
              // mezcle sus filas con las de otros pedidos y no cobre de más.
              executionId: crypto.randomUUID(),
            };
            mergedByCombo.set(key, target);
          }
          if (g.kind === 'mis-pose') {
            // Guard de filename (ADR-134) ACÁ: el grupo sin filename se
            // descuenta y NO entra al request fusionado.
            const fn = customPoseFilenameById.get(g.vistaIds[0]);
            if (!fn) {
              console.warn(
                '[catalogo-ia] pose custom sin filename — se omite del request fusionado',
                g.vistaIds[0],
              );
              expectedTotal -= g.vistaIds.length;
              continue;
            }
            target.posesCustomRefs!.push({ id: g.vistaIds[0], filename: fn });
          } else {
            target.vistaIds.push(...g.vistaIds);
          }
        }
        sendGroups = [
          ...resto,
          // Un grupo fusionado puede quedar vacío si TODAS sus poses custom
          // venían sin filename y no había poses normales — no se manda.
          ...[...mergedByCombo.values()].filter(
            (g) =>
              g.vistaIds.length > 0 || (g.posesCustomRefs?.length ?? 0) > 0,
          ),
        ];
      }

      /**
       * Fan-out por modelo (ADR-2026-08-28-catalogo-varios-modelos).
       *
       * El backend lleva UN `modelo_guardado_id` por run, así que "una imagen por
       * modelo" se resuelve mandando el mismo grupo N veces, una por modelo. Es el
       * mismo patrón que usa la serie de "Imagen con modelo"
       * (ADR-2026-08-20-modelos-multi-select-serie).
       *
       * Sólo se duplican los grupos que llevan a alguien puesto: un `solo-producto`
       * (packshot, plano lateral, infografía) sale igual con cualquier modelo, así que
       * copiarlo generaría la misma imagen N veces y la cobraría N veces.
       *
       * Cada copia estrena `executionId`: compartirlo hace que cada ejecución vea las
       * filas de las otras y dispare un fan-out que cobra de más (ADR-590).
       */
      if (catalogoModelos.length > 1) {
        const conFanOut: CatalogoGroup[] = [];
        for (const g of sendGroups) {
          if (g.kind !== 'con-modelo' && g.kind !== 'mis-pose') {
            conFanOut.push(g);
            continue;
          }
          const cardsDelGrupo =
            g.vistaIds.length + (g.posesCustomRefs?.length ?? 0);
          catalogoModelos.forEach((modelo, i) => {
            conFanOut.push(
              i === 0
                ? { ...g, modelo }
                : { ...g, modelo, executionId: crypto.randomUUID() },
            );
          });
          // El poll espera una fila por imagen: las copias suman las suyas.
          expectedTotal += cardsDelGrupo * (catalogoModelos.length - 1);
        }
        sendGroups = conFanOut;
      }

      // Marca de "generación pendiente" para reanudar si se recarga/descarta la
      // página. Va ACÁ —después de armar sendGroups, antes de disparar— y NO
      // después del loop: muebles serializa (1 request en vuelo) y cada uno
      // mantiene la conexión abierta hasta que el workflow termina, así que el
      // loop puede tardar minutos. Si el user recarga en el medio, las llamadas
      // YA disparadas (y cobradas) se quedarían sin recuperación si la marca se
      // escribiera al final.
      //
      // Usa los ids de `sendGroups` (los grupos REALES que se mandan, ya
      // fusionados si va a atlas): un execution_id por request (ADR-590).
      // expectedCount = `cards.length` (imágenes BASE), NO el total con extras
      // del carrusel: ese corre DESPUÉS del poll con OTROS ids que no están en
      // esta marca, así que reanudar por estos execution_id solo produce las base.
      // Con la tanda en el panel, van al registro (ADR-2026-09-06-registro-de-generaciones-en-curso);
      // el reanudador del shell espera `total` filas por estos ids.
      if (idTanda) {
        registrarExecutionIds(
          idTanda,
          sendGroups.map((g) => g.executionId),
        );
      }

      // Disparamos las generaciones fire-and-forget: NO usamos sus respuestas
      // para saber cuándo terminó. Con multi-pose una respuesta puede volver
      // antes de que estén TODAS las imágenes, así que la COMPLETITUD la
      // define el poll por `execution_id`: el backend escribe una fila por
      // imagen —tanto las OK como las que fallan (`has_error`)— y esperamos a
      // que aparezcan las `expectedTotal` filas (o venza el tope) y recién
      // ahí mostramos.
      for (const g of sendGroups) {
        const isMisPose = g.kind === 'mis-pose';
        const isConModelo = g.kind === 'con-modelo';
        // Cards que cubre este grupo (para descontar del esperado si el
        // request falla): poses normales + poses custom fusionadas.
        const groupCardCount =
          g.vistaIds.length + (g.posesCustomRefs?.length ?? 0);
        // Guard mis-pose (solo camino n8n — con atlas los mis-pose ya se
        // fusionaron arriba): sin filename NO se manda el request — ese
        // payload mata el workflow V3 sin dejar rastro (ver comentario del
        // mapa) y el grupo quedaría colgando el poll. Lo descontamos como
        // fallo conocido; entra en `failedCount` y se avisa en la pantalla
        // de generadas.
        const misPoseFilename = isMisPose
          ? customPoseFilenameById.get(g.vistaIds[0])
          : undefined;
        if (isMisPose && !misPoseFilename) {
          console.warn(
            '[catalogo-ia] pose custom sin filename — grupo omitido',
            g.vistaIds[0],
          );
          expectedTotal -= g.vistaIds.length;
          continue;
        }
        // Para solo-producto, replicamos el shape de PruebaNuevaInterfaz:
        // string cuando hay una sola vista, array cuando hay varias.
        const vistaProductoPayload =
          g.vistaIds.length === 1 ? g.vistaIds[0] : g.vistaIds;
        const payload: Record<string, any> = {
          user_id: user.id,
          // Combo completo (principal + combinados). El backend los compone
          // en una sola imagen, igual que el multi-producto del modo modelo.
          variante_id: g.comboIds,
          mode: 'single',
          modo: 'personalizado',
          // Trackeo (ADR-503). Los dos modos de catálogo se distinguen acá.
          modo_utilizado: esModoMuebles ? 'catalogo-muebles' : 'catalogo',
          // Rubro de la MARCA activa — SOLO en modo muebles, a propósito.
          //
          // n8n rutea los prompts por el rubro de la EMPRESA (`get company`),
          // que en cuentas multi-marca es el dato equivocado. Con este campo,
          // `If es moda1` puede leer `body.rubro` y caer al de la empresa si no
          // viene.
          //
          // NO se manda en el Catálogo IA ni en los demás modos ADREDE: si lo
          // hiciéramos, toda cuenta multi-marca cuyo rubro de marca difiera del
          // de la empresa cambiaría de prompt de un día para el otro, sin
          // pedirlo. Acá el modo es nuevo y no hay nada que romper.
          ...(esModoMuebles && brandRubro ? { rubro: brandRubro } : {}),
          // Estas llamadas son fire-and-forget (la completitud la define el
          // poll por execution_id): `wait: false` hace que el atlas-backend
          // responda 202 al toque en vez de sostener la conexión hasta 150s.
          // Los 400/402 sincrónicos (que descuentan expectedTotal en el
          // .catch) llegan igual — la validación y el chequeo de créditos
          // corren antes de encolar. n8n ignora el campo.
          wait: false,
          cantidad_imagenes: 1,
          Format: formatSlugToAspect(formatoVal),
          new_ui: true,
          // La Calidad elegida, igual que en Automático y en Modelo. Mismo endpoint
          // (V3) y mismo `modo` que esos dos, así que el backend ya lo entiende —
          // ADR-2026-08-25-resolucion-de-generacion-2k-4k. El Catálogo no lo mandaba
          // porque su selector nació con el upscale encadenado, que se retiró.
          image_size: resolucionVal,
          modo_pro: true,
          // Un id POR LLAMADA, no uno compartido por toda la tanda.
          //
          // Compartirlo era un bug caro: las ejecuciones paralelas de V3 buscan
          // sus filas con `get gen images ... where execution_id = X`, así que
          // cada una veía también las filas de las otras, y eso disparaba un
          // fan-out que generaba imágenes de más Y LAS COBRABA. Medido: 3
          // vistas → 12 imágenes, 9 basura, 1 crédito cada una. Con UNA sola
          // vista salía 1 imagen. Ver ADR-590.
          //
          // El poll ahora busca por el conjunto de ids (acepta array), así que
          // no se pierde la recuperación de toda la tanda con una query.
          execution_id: g.executionId,
          // `mis-pose` también es "con modelo": el muñeco define la POSE y el
          // modelo lo pone la selección del catálogo.
          composicion: isMisPose || isConModelo ? 'modelo' : 'producto',
          // En muebles el fondo se DERIVA de la vista (no hay selector global:
          // se sacó a pedido, porque cada vista ya define el suyo). Es lo que
          // manda a cada vista a su rama del árbol de prompts de V3.
          ...(esModoMuebles
            ? backgroundParaVistaMueble(g.vistaIds[0])
            : catalogoBackground),
          // Guardrail de escala (ADR-584). Va en las TRES vistas, no solo en la
          // infografía: la slide 9 del deck de Sodimac muestra que la foto en
          // contexto también se rompe por escala (un producto mal proporcionado
          // dentro de una habitación). `proporcion` es la relación normalizada,
          // que un modelo de imagen respeta mejor que los números absolutos.
          // Si el producto no tiene medidas cargadas no se manda nada, para no
          // ensuciar el payload con un objeto vacío.
          ...(medidasParaPayload(g.medidas) ?? {}),
          // Config de la infografía — SOLO en esa vista. Son dos flags que el
          // prompt de n8n usa para armar o no armar el bloque de cotas y el de
          // características. No filtran nada acá: la decisión de qué dibujar es
          // del prompt, igual que con `instruccion_adicional`.
          // La indicación de outfit del producto viaja por `instruccion_adicional`, el
          // mismo campo de texto libre que ya usan los otros modos: es una instrucción
          // más para el prompt, no un parámetro nuevo. Sólo si el usuario escribió algo
          // (ADR-2026-08-27-una-indicacion-de-outfit-por-producto).
          ...(g.instruccionOutfit?.trim()
            ? { instruccion_adicional: g.instruccionOutfit.trim() }
            : {}),
          ...(esModoMuebles &&
          g.vistaIds[0] === VISTA_MUEBLE_INFOGRAFIA &&
          g.infografiaConfig
            ? {
                estilo_infografia: g.infografiaConfig.estilo,
                color_infografia: g.infografiaConfig.color,
                mostrar_medidas: g.infografiaConfig.mostrarMedidas,
                mostrar_atributos: g.infografiaConfig.mostrarAtributos,
                // Etiquetas que el usuario curó (máx 3). Antes se juntaban en la
                // UI y se descartaban acá: solo viajaba el flag de arriba. n8n
                // las dibuja cuando `mostrar_atributos` es true. Ver ADR-808.
                atributos_infografia: g.atributosInfografia ?? [],
              }
            : {}),
          ...(isMisPose
            ? {
                // Camino n8n: 1 sola pose custom por request (V3 lee
                // `poses_custom[0]`). SIN `pose`: el muñeco se baja por la
                // cadena de poses_custom (ADR-030/036) y se aplica como la
                // pose de la imagen.
                poses_custom: [
                  // `misPoseFilename` está garantizado acá por el guard de
                  // arriba (grupo sin filename no llega a este punto).
                  { id: g.vistaIds[0], filename: misPoseFilename },
                ],
                ...(g.modelo ?? catalogoModelo),
              }
            : isConModelo
              ? {
                  // Grupo con-modelo (fusionado en atlas): poses normales en
                  // `pose` + custom en `poses_custom` — el backend escribe
                  // una fila por pose de ambas listas, recompuestas sobre la
                  // misma imagen base (ADR-576).
                  ...(g.vistaIds.length > 0 ? { pose: g.vistaIds } : {}),
                  ...(g.posesCustomRefs && g.posesCustomRefs.length > 0
                    ? { poses_custom: g.posesCustomRefs }
                    : {}),
                  ...(g.modelo ?? catalogoModelo),
                }
              : { vista_producto: vistaProductoPayload }),
        };
        // SIN REINTENTO, a propósito.
        //
        // Se probó reintentar la llamada caída y se sacó: la generación NO es
        // idempotente. Si la conexión se cae DESPUÉS de que n8n recibió el
        // pedido —que es exactamente el síntoma observado, un fetch que muere
        // sin respuesta— el reintento encola una segunda generación y le cobra
        // dos veces al usuario. Perder una imagen y avisarlo es mucho menos
        // grave que cobrar de más.
        //
        // La pérdida ocasional se cubre avisando ("no se pudo generar") en vez
        // de reintentando a ciegas. Si hace falta reintentar de verdad, primero
        // hay que hacer el endpoint idempotente (una clave por llamada que n8n
        // respete). Ver ADR-590.
        // Espera un lugar libre antes de disparar (ver el semáforo arriba).
        await esperarTurno();
        llamadasEnVuelo += 1;
        // El promise va a `sends`: si la tanda termina vacía, se espera a
        // TODOS los POST para explicar el porqué con el error real (ADR-677).
        sends.push(
          postGenerateAd(payload, { url: GENERATE_AD_V3_URL })
            .catch((e) => {
              console.warn(
                '[catalogo] una llamada falló (seguimos polleando)',
                e,
              );
              // Este grupo no va a escribir sus filas: lo sacamos del total
              // esperado así el poll no lo espera hasta el tope (cards del grupo
              // = poses normales + custom fusionadas).
              expectedTotal -= groupCardCount;
              // Guardamos el motivo: si la tanda termina sin ninguna imagen, es
              // lo único que explica POR QUÉ (el backend no escribió filas).
              if (e instanceof GenerateAdError) sendErrors.push(e);
            })
            .finally(() => {
              llamadasEnVuelo -= 1;
            }),
        );
      }

      const rows = await pollGeneratedByExecutionId({
        // Todos los ids de la tanda (uno por request REAL, ya fusionados si
        // la tanda va a atlas). Antes era uno solo compartido — ver ADR-590.
        executionId: sendGroups.map((g) => g.executionId),
        companyId: userCompanyId,
        // Getter: el esperado puede BAJAR mientras el poll corre (fallos
        // HTTP de arriba). Si TODOS fallan queda en 0 y el poll corta al
        // toque en vez de colgarse 5 min.
        expectedCount: () => expectedTotal,
        // Tope proporcional al tamaño de la tanda del panel
        // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
        timeoutMs: idTanda ? timeoutPollTanda(totalImagenesAGenerar) : undefined,
      });

      const okRows = rows.filter((r) => !r.has_error);
      const erroredCount = rows.length - okRows.length;
      if (okRows.length === 0) {
        // El poll pudo cortar apenas `expectedTotal` llegó a 0, con algún
        // `.catch` todavía sin correr: esperamos los POST para decidir el
        // mensaje con TODOS los errores a la vista (baratísimo con wait:false).
        await Promise.allSettled(sends);
        // Un error accionable (sin créditos / rechazo del modelo) explica la
        // tanda mucho mejor que el genérico, y le dice al usuario qué hacer.
        // Si no hay ninguno, igual preferimos el técnico concreto al mudo.
        const accionable = sendErrors.find((e) => e.isActionable);
        throw new Error(
          accionable?.message ??
            sendErrors[0]?.message ??
            (erroredCount > 0
              ? 'Todas las imágenes del catálogo fallaron. Probá de nuevo.'
              : 'No se generó ninguna imagen del catálogo.'),
        );
      }

      const allImageIds = okRows.map((r) => r.id);

      // ── Carrusel: tomas alternativas de "Foto en contexto" ──────────────
      // El stepper de la card pide N imágenes; la generación de arriba produjo
      // 1 (la base). Las N-1 restantes salen del carrusel, que parte de esa
      // imagen base y varía ángulo manteniendo mueble y ambiente.
      //
      // Se hace ACÁ y no en la pantalla de resultados porque el webhook
      // devuelve los ids de lo que generó: así las imágenes nuevas entran en el
      // mismo `imageIds` que se le pasa a la pantalla y el user las ve en la
      // misma tanda, sin tener que refrescar.
      const cardsConExtras = esModoMuebles
        ? cards.filter((c) => (c.cantidad ?? 1) > 1)
        : [];
      if (cardsConExtras.length > 0) {
        try {
          // El poll solo trae `id, variante_id, has_error`, así que no alcanza
          // para saber qué vista es cada fila. Se consulta `datos_generacion`
          // solo de estas filas, en vez de engordar el select del poll, que es
          // compartido con el Catálogo IA.
          const { data: filas } = await supabase
            .from('imagenes_generadas')
            .select('id, variante_id, datos_generacion')
            .in('id', allImageIds);

          // producto → id de su imagen de "foto en contexto"
          const imagenContextoPorProducto = new Map<string, string>();
          for (const fila of (filas ?? []) as any[]) {
            let datos = fila.datos_generacion;
            // n8n lo guarda con JSON.stringify, pero la columna puede venir ya
            // parseada según el driver — se contemplan los dos casos.
            if (typeof datos === 'string') {
              try {
                datos = JSON.parse(datos);
              } catch {
                continue;
              }
            }
            const vista = datos?.vista_producto;
            const esContexto = Array.isArray(vista)
              ? vista.includes(VISTA_MUEBLE_CONTEXTO)
              : vista === VISTA_MUEBLE_CONTEXTO;
            if (esContexto && fila.variante_id) {
              imagenContextoPorProducto.set(fila.variante_id, String(fila.id));
            }
          }

          const pedidos = cardsConExtras
            .map((card) => {
              const imageId = imagenContextoPorProducto.get(card.product.id);
              if (!imageId) return null;
              const extra = (card.cantidad ?? 1) - 1;
              const medidasPayload = medidasParaPayload(card.medidas);
              return pedirNuevosAngulos({
                userId: user.id,
                imageId,
                cantidad: extra,
                medidas: medidasPayload?.medidas ?? null,
                proporcion: medidasPayload?.proporcion ?? null,
              }).then((ids) => ({ productId: card.product.id, ids }));
            })
            .filter(Boolean) as Promise<{
            productId: string;
            ids: string[];
          }>[];

          // allSettled: si un carrusel falla, el resto (y las imágenes base)
          // siguen su curso. El usuario ve lo que sí salió.
          const resultados = await Promise.allSettled(pedidos);
          for (const r of resultados) {
            if (r.status !== 'fulfilled' || r.value.ids.length === 0) continue;
            allImageIds.push(...r.value.ids);
            carruselIdsPorProducto.set(r.value.productId, r.value.ids);
          }
        } catch (e) {
          console.warn('[catalogo-muebles] carrusel falló, seguimos', e);
        }
      }
      // Agrupación producto → imágenes desde el `variante_id` de cada fila
      // (= product.id en este flujo). Los combos quedan bajo el producto que el
      // backend haya escrito como `variante_id`.
      const productToImageIds: Record<string, string[]> = {};
      for (const row of okRows) {
        const k = row.variante_id || 'otros';
        (productToImageIds[k] ??= []).push(row.id);
      }
      // Las del carrusel van bajo el mismo producto que su imagen de origen,
      // así la pantalla de resultados las agrupa donde corresponde.
      for (const [productId, ids] of carruselIdsPorProducto) {
        (productToImageIds[productId] ??= []).push(...ids);
      }
      // Meta de productos desde las cards locales (id → name/imageUrl).
      const productsMeta: Record<
        string,
        { id: string; name: string; imageUrl: string }
      > = {};
      for (const card of cards) {
        if (!productsMeta[card.product.id]) {
          productsMeta[card.product.id] = {
            id: card.product.id,
            name: card.product.name,
            imageUrl: card.product.imageUrl,
          };
        }
      }
      // Cuántas NO salieron OK = fallidas (has_error) + fallos HTTP (ya
      // descontados de `expectedTotal`, por eso usamos `cards.length`) + las
      // que nunca aparecieron dentro del tope.
      // Cuenta IMÁGENES, no cards: con el stepper de "foto en contexto" una
      // card puede valer hasta 3. `totalImagenesAGenerar` ya lo contempla.
      const failedCount = Math.max(0, totalImagenesAGenerar - okRows.length);

      // Preload (instant display) antes de navegar.
      let preloadedImages: PreloadedImage[] = [];
      try {
        preloadedImages = await preloadGeneratedImages(
          allImageIds as unknown as number[],
          userCompanyId,
        );
      } catch (preloadErr) {
        console.warn(
          '[InterfazV2] preloadGeneratedImages (catalogo) falló',
          preloadErr,
        );
      }

      sonarListo();

      // La vuelta atrás, igual que en la generación normal —
      // ADR-2026-09-10-el-restore-guarda-la-configuracion-entera. El Catálogo era el
      // único modo que no la guardaba: volver de sus resultados abría el modo en blanco y
      // había que volver a elegir productos Y vistas.
      guardarRestoreV2();

      // Tanda EN EL PANEL: las imágenes se muestran ahí mismo y NO se navega a la
      // pantalla de resultados (ADR-2026-08-24-catalogo-genera-en-el-panel). Tampoco
      // con el preload vacío —caso raro: el file_name tardó más que los intentos—: la
      // tanda cierra igual y la columna vuelve a pedir las creaciones, como el resto de
      // los modos (ADR-2026-09-06-registro-de-generaciones-en-curso, punto (e); antes
      // acá se caía a la pantalla vieja). El total esperado era una previsión; lo que
      // llegó es la verdad. Las que fallaron ya se cuentan en `failedCount`, que el
      // toast de error explica aparte.
      if (idTanda) {
        cerrarTanda(idTanda, preloadedImages);
        return;
      }

      navigate(rutaResultados, {
        state: {
          imageIds: allImageIds,
          companyId: userCompanyId,
          preloadedImages,
          // Marca de catálogo + agrupación. La pantalla de
          // GeneratedImagesInline detecta `state.catalogo` y cambia el
          // render (Fase 7). Infografía no la lleva: va a la vista normal.
          ...(esInfografia
            ? {}
            : {
                catalogo: {
                  productToImageIds,
                  productsMeta,
                  failedCount,
                },
              }),
        },
      });
    } catch (err) {
      console.error('[catalogo-ia] generación falló', err);
      setShowLoadingScreen(false);
      setGenError(
        err instanceof GenerateAdError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Falló la generación del catálogo',
      );
      // La tanda de ESTA corrida se cae con el error: sus recuadros "generando" no
      // pueden quedar latiendo para siempre. Las otras siguen — pueden estar corriendo
      // bien. Si era la única, la columna vuelve a la muestra del modo; el toast de
      // error ya explica qué pasó.
      if (idTanda) fallarTanda(idTanda);
    } finally {
      // El candado sólo se toma cuando la generación NO va al panel; soltarlo igual
      // pisaría el de una corrida que sí lo tenga.
      if (!tandaVaAlPanel) generatingLockRef.current = false;
    }
  };

  // Origen del picker de productos: 'choose' (flujo inicial, viene de la
  // pantalla de elegir producto) o 'configure' ("Añadir producto al outfit"
  // desde la configuración). Define a dónde vuelve el back/cancel del picker.
  const pickOriginRef = useRef<'choose' | 'configure'>('choose');
  // Handler para "Ver mis productos" — el grid de productos aparece
  // dentro del MISMO panel en vez de un sheet separado.
  const handleShowProducts = () => {
    pickOriginRef.current = 'choose';
    setPanelStep('pick-product');
  };
  // "Añadir producto al outfit" desde configure — abre el mismo picker pero
  // vuelve a configure al confirmar/cancelar. La selección actual va
  // premarcada (ver initialSelectedIds) para que el user SUME productos al
  // outfit en vez de reemplazarlos.
  const handleAddProducts = () => {
    pickOriginRef.current = 'configure';
    setPanelStep('pick-product');
  };
  // Multi-selección: el grid toggle-ea cada producto. El user confirma con
  // un botón cuando termina, y ahí avanzamos al configure (o a
  // 'select-vistas' si el preset es 'catalogo-ia' — ese flow tiene un
  // step intermedio para elegir las vistas que se generan).
  // ── Diálogo autogenerado del influencer (ADR-674) ────────────────────
  // Dispara al entrar a `configure` en el modo influencer CON producto. No
  // corre con "Sin producto" (ADR-673): el guion sale de la foto del producto,
  // sin foto no hay nada que generar y el textarea arranca vacío.
  //
  // El resultado PISA el textarea a propósito: es el reemplazo del viejo modo
  // "Automático". Solo se pide una vez por variante (`dialogoForRef`), así que
  // volver a esta pantalla no borra lo que el usuario haya editado; cambiar de
  // producto sí regenera, porque el guion viejo hablaba de otra cosa.
  useEffect(() => {
    if (panelStep !== 'configure') return;
    if (selectedPreset?.kind !== 'video-influencer') return;
    if (sinProducto) return;
    const varianteId = selectedProducts[0]?.id;
    if (!varianteId) return;
    if (dialogoForRef.current === varianteId) return;
    dialogoForRef.current = varianteId;

    let alive = true;
    setDialogoLoading(true);
    setDialogoError(null);
    void fetchInfluencerDialogo(varianteId)
      .then((texto) => {
        if (!alive) return;
        if (texto) {
          setVideoVoiceGuion((v) => ({ ...v, guionTexto: texto }));
        }
      })
      .catch((e) => {
        if (!alive) return;
        console.warn('[InterfazV2] no se pudo autogenerar el diálogo', e);
        setDialogoError(e instanceof Error ? e.message : 'error');
        // Permitir reintentar si el usuario vuelve a entrar a la pantalla.
        dialogoForRef.current = null;
      })
      .finally(() => {
        if (alive) setDialogoLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [panelStep, selectedPreset?.kind, sinProducto, selectedProducts]);

  const handleConfirmProducts = (products: MockProductV2[]) => {
    setSelectedProducts(products);
    setSinProducto(false);
    // Producto confirmado/cambiado → re-recomendar la pose por defecto para
    // este producto (salvo que después el user elija una a mano).
    poseAutoSelectRef.current = 'pending';
    // Modo Infografía (ADR-813): NO tiene picker de vistas —su única vista es
    // infografía—. Se presetea esa vista y va DIRECTO al review (la misma pantalla
    // del catálogo de productos, pero solo con la infografía).
    if (selectedPreset?.kind === 'infografia') {
      const infoVista = VISTAS_MUEBLES.find(
        (v) => v.id === VISTA_MUEBLE_INFOGRAFIA,
      );
      setCatalogoVistas(infoVista ? [infoVista] : []);
      setPanelStep('configure');
      return;
    }
    // Los modos de una sola pantalla no llegan acá —confirman con `onProductosEnVivo`,
    // que deja los productos arriba sin mover el paso— pero el helper los cubre igual:
    // el paso de un modo se decide en un solo lugar
    // (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
    setPanelStep(pasoDeConfiguracion(selectedPreset?.kind));
  };

  /** "Sin producto" (ADR-673, solo video-influencer): saltea la elección y va
   *  directo a configurar. Fuerza el guion a "Escribir" porque el modo
   *  Automático depende del producto — es Gemini mirando la foto para decidir
   *  qué decir; sin producto no tiene de qué hablar (el backend además lo
   *  rechaza con 400). */
  const handleSkipProduct = () => {
    setSelectedProducts([]);
      // La creación base se va con el panel: si no, el próximo anuncio abierto desde un
      // producto seguiría mandando `imagen_generada_id`.
      setImagenBase(null);
    setSinProducto(true);
    setVideoVoiceGuion((v) =>
      v.guionMode === 'texto' ? v : { ...v, guionMode: 'texto' },
    );
    setPanelStep('configure');
  };
  const handleBackToChoose = () =>
    setPanelStep(
      pickOriginRef.current === 'configure' ? 'configure' : 'choose-product',
    );

  // ── El chat puede leer y configurar este modo ───────────────────────
  // Registra en el puente la ficha del modo abierto (campos, valores, opciones) y las
  // acciones para tocarlos: las MISMAS que usan los paneles, así el chat hereda sus
  // reglas (ADR-2026-09-10-el-agente-toca-la-pantalla). Se llama en cada render con el
  // estado de ese render; con el sheet cerrado registra null.
  useControladorDelModo(
    {
      kind: selectedPreset?.kind ?? null,
      plantillaTitulo: selectedPreset?.title,
      ruta: location.pathname,
      paso: panelStep,
      productos: selectedProducts,
      // El tope que está aplicando el picker, no el de la tabla por kind: en Belleza la
      // plantilla va con UNO (ADR-2026-09-14-la-plantilla-de-belleza-se-genera-con-un-producto).
      topeProductos: esPlantillaDeBelleza ? 1 : undefined,
      catalogo: userProducts,
      sinProducto,
      modelo: modelSelection,
      modelos: userModels,
      poses: poseIds,
      posesDisponibles: allPosesFiltered,
      posesPropias: customPoses,
      variasPoses: multiPoseEnabled,
      fondo: fondoSelection,
      fondosBiblioteca: bibliotecaFondos,
      fondosGuardados,
      formato: formatoVal,
      resolucion: resolucionVal,
      // La misma regla que dibuja la Calidad en el pie de la plantilla
      // (ADR-2026-09-28-las-plantillas-eligen-la-calidad).
      eligeCalidad: plantillaEligeCalidad(selectedPreset, previewMediaTab === 'video'),
      instruccion: otraConfig,
      cantidad: autoImageCount,
      cantidadMax: AUTO_IMAGE_COUNT_MAX,
      vistas: vistaIds,
      vistasDisponibles: vistasProducto,
      anuncio: {
        titulo: anuncioTitulo,
        descripcion: anuncioDescripcion,
        cta: anuncioCta,
        colores: anuncioColors,
        coloresMarca: brandColors,
        coloresMax: ANUNCIO_COLORS_MAX,
      },
      video: {
        duracion: videoDuration,
        min: VIDEO_INFLUENCER_DURATION_MIN,
        max: isSinGancho(videoGanchoId)
          ? VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO
          : VIDEO_INFLUENCER_DURATION_MAX,
        multiEscena: videoMultiScene,
        ganchoId: videoGanchoId,
        voz: videoVoiceGuion,
      },
    },
    {
      confirmarProductos: handleConfirmProducts,
      sinProducto: handleSkipProduct,
      cambiarModelo: handleModelChangeSingle,
      cambiarPoses: handlePoseIdsChange,
      cambiarVariasPoses: handleMultiPoseEnabledChange,
      cambiarFondo: setFondoSelection,
      cambiarFormato: setFormatoVal,
      cambiarResolucion: setResolucionVal,
      cambiarInstruccion: setOtraConfig,
      cambiarCantidad: setAutoImageCount,
      cambiarVistas: setVistaIds,
      cambiarTitulo: setAnuncioTitulo,
      cambiarDescripcion: setAnuncioDescripcion,
      cambiarCta: setAnuncioCta,
      cambiarColores: setAnuncioColors,
      cambiarDuracion: setVideoDuration,
      cambiarMultiEscena: setVideoMultiScene,
      cambiarGancho: handleGanchoIdChange,
      cambiarVoz: setVideoVoiceGuion,
    },
  );

  // ── Upload de producto nuevo ────────────────────────────────────────
  // Paso del GRID donde aterrizar tras subir, según viewport: en DESKTOP es
  // `choose-product` (grid fusionado, que conserva el botón "Subir nuevo
  // producto" arriba); en MOBILE el grid vive en `pick-product` (full-screen).
  // Así, tras subir, el user cae viendo sus productos tildados y —en desktop—
  // con el botón para seguir subiendo.
  const gridStepForViewport = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 768px)').matches
      ? ('choose-product' as const)
      : ('pick-product' as const);
  // Subida en bloque para CATÁLOGO con 2+ archivos: cada archivo es un
  // PRODUCTO distinto (no variantes del mismo). Los subimos uno por uno
  // —secuencial, para no romper el conteo del nombre placeholder "Producto
  // N"; el backend los renombra con IA—, refrescamos, los dejamos todos
  // seleccionados y caemos en el grid (`pick-product`) listos para
  // Confirmar. No pasa por la pantalla "Nuevo producto" (que agruparía
  // todo en un solo producto).
  const handleBulkUploadCatalog = async (files: File[]) => {
    if (!userCompanyId) {
      setUploadError('No se encontró la empresa del usuario.');
      return;
    }
    setBulkUploadingProducts(true);
    setUploadError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const created: MockProductV2[] = [];
      const failures: string[] = [];
      for (const file of files) {
        try {
          const { variantId, finalName } = await uploadNewProduct({
            companyId: userCompanyId,
            userId: user.id,
            name: '', // vacío → el backend le pone nombre con IA
            files: [file],
            brandId: currentBrandId,
          });
          created.push({ id: variantId, name: finalName, imageUrl: '' });
        } catch (err) {
          console.error(
            '[InterfazV2] bulk upload — archivo falló',
            file.name,
            err,
          );
          failures.push(file.name);
        }
      }
      if (created.length === 0) {
        // Falló todo: caemos a la pantalla "Nuevo producto" con el error
        // para que el user lo vea y pueda reintentar (es el único lugar
        // del flujo que renderiza `uploadError`).
        setPendingProductFiles(files);
        setPendingProductName('');
        setUploadError('No pudimos subir los productos. Probá de nuevo.');
        setPanelStep('upload-product');
        return;
      }
      // Refrescar para tener las filas reales (con URL/thumbnail). Si alguna
      // todavía no aparece (replicación), usamos el stub que ya armamos.
      const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
      persistUserProducts(refreshed);
      const byId = new Map(refreshed.map((p) => [p.id, p]));
      const selected = created.map((c) => byId.get(c.id) || c);
      setSelectedProducts(selected);
      // Productos nuevos recién subidos → re-recomendar pose por defecto.
      poseAutoSelectRef.current = 'pending';
      if (failures.length > 0) {
        console.warn(
          `[InterfazV2] bulk upload: ${failures.length} archivo(s) fallaron`,
          failures,
        );
      }
      setPanelStep(gridStepForViewport());
    } catch (err) {
      console.error('[InterfazV2] handleBulkUploadCatalog error', err);
      setPendingProductFiles(files);
      setPendingProductName('');
      setUploadError(
        err instanceof Error
          ? err.message
          : 'No pudimos subir los productos. Probá de nuevo.',
      );
      setPanelStep('upload-product');
    } finally {
      setBulkUploadingProducts(false);
    }
  };
  // ── "Subir varios productos" (ADR-684, ajustado por ADR-688) ──────────
  // Antes era un file picker directo y SOLO en Catálogo. Ahora abre el MISMO
  // popup que Mi Marca (AgregarVariosModal: drag & drop, nombres editables,
  // agrupar por nombre de archivo, Tiendanube, tope por plan) y está en todos
  // los modos — pero vive en UNA sola pantalla: el estado vacío de
  // `upload-product` ("Nuevo producto"). Las pantallas de ELEGIR producto
  // quedan con dos opciones y nada más (ADR-688).
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const handleOpenBulkUpload = () => {
    // El grid al que caemos después hereda su "volver" de este ref. El popup
    // solo se abre desde el flujo de elegir producto, así que el origen es
    // siempre `choose`; lo fijamos explícito para que un back no termine en
    // `configure` si el ref venía sucio de una vuelta anterior.
    pickOriginRef.current = 'choose';
    setBulkModalOpen(true);
  };
  // El popup ya creó los productos (y se cierra solo). Refrescamos el catálogo
  // y caemos en el grid: los recién creados vienen ordenados por created_at
  // desc → quedan primeros, SIN tildar (el user elige cuál usar). Qué paso es
  // "el grid" depende del viewport (gridStepForViewport): en desktop es el
  // panel fusionado y en mobile la pantalla full-screen. Navegar importa sobre
  // todo en mobile: ahí la pantalla de origen son dos botones, así que sin
  // movernos no se vería nada de lo recién subido.
  const handleBulkModalSaved = async () => {
    if (!userCompanyId) return;
    const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
    persistUserProducts(refreshed);
    setPanelStep(gridStepForViewport());
  };
  // Disparado desde "Subir nuevo producto" en ChooseProductPanel /
  // PickProductPanel. Recibe los archivos del file picker.
  const handleStartUpload = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    // Catálogo + 2 o más archivos → subida en bloque (cada uno su propio
    // producto, sin pantalla intermedia). Con 1 solo archivo —o en los
    // demás modos— sí mostramos "Nuevo producto" (para sumar ángulos del
    // mismo producto y/o ponerle nombre).
    if (esModoCatalogo(selectedPreset?.kind) && arr.length >= 2) {
      void handleBulkUploadCatalog(arr);
      return;
    }
    setPendingProductFiles(arr);
    setPendingProductName('');
    setUploadError(null);
    setPanelStep('upload-product');
  };
  // "Subir nuevo producto" desde el ChooseProductPanel: en vez de abrir el
  // file picker directo, entra al paso `upload-product` SIN archivos. Ese
  // panel, en estado vacío, muestra la pantalla explicativa (cómo subir el
  // producto: ejemplos + tips) y desde ahí se elige la foto. La pantalla es
  // full-screen en mobile (portal) y panel lateral en desktop (igual que el
  // resto del flujo de upload).
  const handleStartNewProduct = () => {
    setPendingProductFiles([]);
    setPendingProductName('');
    setUploadError(null);
    setPanelStep('upload-product');
  };
  // Agregar más imágenes desde la pantalla intermedia.
  const handleAddMoreFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setPendingProductFiles((prev) => [...prev, ...arr]);
  };
  const handleRemovePendingFile = (idx: number) => {
    setPendingProductFiles((prev) => prev.filter((_, i) => i !== idx));
  };
  // Cancelar el upload — volver a choose-product y resetear pending state.
  const handleCancelUpload = () => {
    setPendingProductFiles([]);
    setPendingProductName('');
    setUploadError(null);
    setPanelStep('choose-product');
  };
  // Confirmar upload: subir todo a Supabase, refrescar productos, y
  // auto-seleccionar la variante creada para que el user pueda generar.
  const handleConfirmUpload = async () => {
    if (uploadingProduct) return;
    if (pendingProductFiles.length === 0) return;
    if (!userCompanyId) {
      setUploadError('No se encontró la empresa del usuario.');
      return;
    }
    setUploadingProduct(true);
    setUploadError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const { variantId, finalName } = await uploadNewProduct({
        companyId: userCompanyId,
        userId: user.id,
        name: pendingProductName,
        files: pendingProductFiles,
        brandId: currentBrandId,
      });
      // Refrescar la lista de productos para que el chip + futuros
      // pickers reflejen la nueva variante.
      const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
      persistUserProducts(refreshed);
      // Buscar la variante recién creada y auto-seleccionarla. Fallback:
      // armar un MockProductV2 stub si por alguna razón no aparece en el
      // refresh (carrera con replicación, etc.).
      const created = refreshed.find((p) => p.id === variantId) || {
        id: variantId,
        name: finalName,
        imageUrl: '',
      };
      setSelectedProducts([created]);
      // Producto nuevo recién subido → re-recomendar pose por defecto.
      poseAutoSelectRef.current = 'pending';
      setPendingProductFiles([]);
      setPendingProductName('');
      // Catálogo: tras subir (1 producto, con sus ángulos), volvemos al grid
      // con el producto ya tildado (desktop = grid fusionado con botón
      // "Subir"; mobile = grid full-screen), para que el user pueda sumar más
      // o Confirmar (→ elegir vistas). En los demás modos (diseño) vamos
      // directo a configurar, como antes.
      setPanelStep(
        esModoCatalogo(selectedPreset?.kind)
          ? gridStepForViewport()
          : 'configure',
      );
    } catch (err) {
      console.error('[InterfazV2] handleConfirmUpload error', err);
      setUploadError(
        err instanceof Error
          ? err.message
          : 'No pudimos subir el producto. Probá de nuevo.',
      );
    } finally {
      setUploadingProduct(false);
    }
  };

  // ── Datos para la home nueva (ADR-2026-07-31-inicio-se-funde-interfaz-v2) ──────
  //
  // `HomeMinimal` no decide nada: acá se traduce el estado de esta pantalla a lo que
  // necesita dibujar. Las tres derivaciones viven al final del componente, después
  // de todos los `useState`/`useRef` que consumen, y no junto a `displayCollections`:
  // ahí arriba `selectedPreset`, `previewList` y `homeScrollBeforeSheetRef` todavía
  // no existen y `abrirPreset` quedaría en la zona muerta del `const`.

  /**
   * Abrir un preset. Es el MISMO `presetClick` que estaba cableado en el JSX de la
   * home vieja, movido acá sin cambios de comportamiento: gating por plan primero,
   * después el guardado del scroll, y recién ahí la bifurcación entre modo con URL
   * propia (navega) y plantilla (sheet en estado).
   *
   * Lo usan la tira de modos, las plantillas de la home y —sin tocar— las filas de
   * mobile.
   */
  const abrirPreset = (collection: CollectionV2) => (p: PresetV2) => {
    // Card bloqueada → visor de ejemplos (nota del plan + "Mejorar plan"); sin
    // ejemplos, el modal clásico.
    const modeLock = getModePlanLock(p.kind, userPlan);
    if (modeLock) {
      if (!openLockedModeExamples(p, modeLock)) {
        setFeatureLock(modeLock);
      }
      return;
    }
    // Para restaurar el scroll de la home al cerrar el sheet (el contenedor pasa a
    // `hidden` y el scroll colapsa a 0).
    homeScrollBeforeSheetRef.current = getAppScrollY();
    // Modos con PANTALLA propia (Animar imagen, Carrusel, Shorts Studio): no hay
    // sheet que abrir, se va derecho a su ruta. Va ANTES de `MODE_KIND_TO_PATH`
    // porque ese mapa se deriva de `MODES` y por lo tanto también los contiene:
    // sin este corte caerían en `rutaModo(...)`, que abre el sheet de un modo que
    // no tiene panel — ver ADR-2026-08-14-modo-animar-imagen y -modo-carrusel.
    const externalRoute = p.kind ? MODE_KIND_TO_ROUTE[p.kind] : null;
    if (externalRoute) {
      navigate(externalRoute);
      return;
    }
    // Modos URL-routed: navegar; el useEffect de sync abre el sheet. Browser back
    // queda funcional sin tocar el state machine.
    const modePath = p.kind ? MODE_KIND_TO_PATH[p.kind] : null;
    if (modePath) {
      navigate(rutaModo(modePath));
      return;
    }
    // Templates / E-commerce: legacy in-state path.
    setSelectedPreset(p);
    setPreviewList(collection.presets);
    setPanelStep('preview');
    setPresetSheetAnimated(false);
  };

  /**
   * Los modos de la tira. Salen de `design-row`, o sea de la MISMA lista que pintaba
   * `CollectionRow`: respeta los feature flags, el rubro y el orden (incluido el
   * `promoteModeloFirst`). En desktop se muestran TODOS —los que el plan no incluye
   * salen con candado—; el filtrado por prueba gratis es sólo de mobile.
   */
  const modosHome = React.useMemo(() => {
    const designRow = displayCollections.find((c) => c.id === 'design-row');
    if (!designRow) return [];
    const cards = designRow.presets
      .map((p) => {
        const path = p.kind ? MODE_KIND_TO_PATH[p.kind] : null;
        // Un preset sin path de modo no es un modo (no tendría a dónde ir); se cae
        // de la tira en vez de renderizar una card muerta.
        if (!path) return null;
        const bloqueado = !!getModePlanLock(p.kind, userPlan);
        return {
          path,
          titulo: p.title,
          imagen: p.poster || p.imageUrl,
          bloqueado,
          // El pill "Ver ejemplos" de la card bloqueada, y que tocarla abra el visor
          // en vez de la pantalla de planes: sólo si el modo TIENE ejemplos cargados
          // (ADR-2026-08-25-pill-ver-ejemplos-en-cards-bloqueadas). Es la misma
          // condición que evalúa `abrirPreset` acá abajo —`openLockedModeExamples`
          // devuelve false y cae al `FeatureLockedModal` cuando la lista está
          // vacía—, así que el cartel no puede prometer algo que el click no cumpla.
          conEjemplos: bloqueado && getModeExamples(p.kind).length > 0,
          // Imagen o video, por el MISMO criterio que el sheet de "Generar" de mobile
          // (`useModosGenerar`): lo que el modo devuelve. La tira de la home real lo
          // ignora —muestra todos los modos juntos—; lo lee el sandbox de variantes,
          // donde elegir la vertiente es el primer paso.
          familia: (p.outputType === 'video' ? 'video' : 'imagen') as
            | 'imagen'
            | 'video',
          onAbrir: () => abrirPreset(designRow)(p),
          // El destino real de la card, para que Cmd/Ctrl+clic la abra en otra pestaña
          // (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links). El modo bloqueado
          // NO lo lleva: su click va a los planes o al visor de ejemplos, no al modo.
          href: bloqueado ? undefined : (rutaDeModo(path) ?? undefined),
        };
      })
      .filter((m): m is NonNullable<typeof m> => m !== null);
    // Anuncio tercero, la misma posición que ocupa en la sidebar del shell
    // (ADR-2026-08-03-anuncio-tercero-tira-y-sidebar). Va acá y no en `design-row`
    // para no mover las filas de mobile, que consumen la misma colección.
    const ordenados = anuncioTercero(cards, (m) => m.path);

    // ── Entradas que no salen de `design-row` — ADR-2026-09-01-los-popups-de-vertiente-suman-plantillas-y-chat ──
    //
    // Van al FINAL de su familia: es el camino que no es "un modo con su pantalla"
    // —arrancar de una plantilla ya armada—, y la lista tiene que abrir con los modos.
    // Eran dos hasta que "Crear desde cero" salió de las listas (ver abajo).
    const extras: typeof ordenados = [];

    // Acá se duplicaba "Crear desde cero" en la familia Videos, porque el chat no es un
    // modo de salida fija y mostrarlo sólo en Imágenes lo dejaba afuera de media home.
    // El modo salió de las listas (ADR-2026-09-13-crear-desde-cero-sale-de-las-listas) y
    // con él se fue esta entrada: para pedir un video escribiéndolo está el Agente, y en
    // la vertiente Videos ya están Creador de video, Animar imagen y Video con influencer.

    // Plantillas en las DOS familias, con el mismo criterio que el sheet de "Generar"
    // de mobile (`useModosGenerar`, ADR-2026-08-04-plantillas-es-una-opcion-de-generar):
    // una plantilla se puede generar como imagen o como video —es el toggle que ya vive
    // en el preview del preset— y desde qué familia se entró es lo que decide con cuál
    // arranca. `?media=video` viaja hasta acá y deja el toggle en Video; sin él abre en
    // Imagen, que es su default. Entrar por Videos y encontrarse generando una imagen
    // sería contradecir la elección del paso anterior.
    //
    // Sólo para los rubros que ven plantillas (`basePlantillas`: Moda o Belleza, cada
    // uno con su pantalla): es la misma bandera que apaga la sección de la home y las
    // filas de mobile, y ofrecer acá un catálogo que después no existe sería mandar a
    // una pantalla vacía.
    if (basePlantillas) {
      // Una sola definición del destino: la navegación del click y el `href` de la card
      // tienen que ser la misma URL, si no el Cmd+clic abriría otra pantalla.
      const rutaPlantillas = (familia: 'imagen' | 'video') =>
        familia === 'video' ? `${basePlantillas}?media=video` : basePlantillas;
      const cardPlantillas = (familia: 'imagen' | 'video') => ({
        // El `path` es la key de la lista y la clave con la que la home busca copy y
        // carátula. Las dos entradas comparten el mismo porque viven en familias
        // distintas, así que nunca se listan juntas.
        path: 'plantillas',
        titulo: nombreCortoModo('plantillas'),
        // La carátula de Plantillas sale del catálogo como la de cualquier modo
        // (ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo).
        imagen: caratulaModo('plantillas').poster,
        // El catálogo se mira con cualquier plan: el candado, si toca, lo pone la
        // plantilla al generar. Y sin candado no hay pill de "Ver ejemplos" que valga:
        // `conEjemplos` sólo aplica a lo bloqueado.
        bloqueado: false,
        conEjemplos: false,
        familia,
        onAbrir: () => navigate(rutaPlantillas(familia)),
        href: rutaPlantillas(familia),
      });
      extras.push(cardPlantillas('imagen'), cardPlantillas('video'));
    }

    // El Estudio de Video en la familia Videos
    // (ADR-2026-09-24-el-estudio-de-video-entra-al-popup-de-la-home). Tampoco es un
    // modo de `MODES` —es una herramienta de `HERRAMIENTAS_EXTRA`, como Plantillas—, así
    // que `design-row` no lo traía y el popup de videos era la única lista de modos de
    // desktop sin él (`/modos` y la sidebar lo tienen). Candado por el MISMO
    // `getModePlanLock` que la sidebar: es de Ultra. Sin ejemplos cargados, el click
    // bloqueado va a los planes, como cualquier modo bloqueado sin ejemplos.
    const estudio = HERRAMIENTAS_EXTRA.find((h) => h.id === 'editor-video');
    if (estudio) {
      const bloqueado = !!getModePlanLock('editor-video', userPlan);
      extras.push({
        path: 'editor-video',
        titulo: nombreCortoModo('editor-video', estudio.nombre),
        // Respaldo para la ilustración de la card de vertiente; el popup dibuja la
        // carátula propia (`EstudioVideoCover`), la misma de `/modos`.
        imagen: estudio.poster,
        bloqueado,
        conEjemplos: bloqueado && getModeExamples('editor-video').length > 0,
        familia: 'video',
        onAbrir: () => navigate(estudio.ruta),
        href: bloqueado ? undefined : estudio.ruta,
      });
    }

    return [...ordenados, ...extras];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCollections, userPlan, basePlantillas, navigate]);

  /**
   * Los modos de la home de ESCRITORIO: los de siempre más "Crear" (el modo
   * Redes) — ADR-2026-10-05-la-home-ofrece-crear-o-copiar-contenido.
   *
   * Redes es una pantalla propia (`/redes`) y `design-row` no la trae, así que la card
   * "Redes sociales" se quedaba sólo con Plantillas y, al haber una sola opción, iba derecho
   * ahí en vez de dar a elegir. Va en una lista aparte y no en `modosHome` porque esa
   * también alimenta la home del TELÉFONO, y Redes es sólo de computadora. Sin candado: la
   * imagen es de todos los planes y el video lo frena la pantalla al generar.
   */
  const modosHomeEscritorio = React.useMemo(() => {
    let modos = modosHome;
    // El Catálogo de Hogar y Decoración (ADR-2026-10-15-el-catalogo-entra-a-la-home): una
    // pantalla propia y sólo de computadora, como Redes, así que tampoco sale de
    // `design-row` (ahí sigue la card de `catalogo-muebles` para el teléfono, que en la tira
    // de escritorio se caía por no tener path). Va donde iba el de muebles, antes de
    // Infografía: en el grupo C cae en el popup "Catálogos" (`MODOS_DE_CATALOGO`) y en el
    // resto en "Crear imágenes". Sin candado: generar chequea el saldo adentro.
    // Desde ADR-2026-10-26-catalogo-con-el-agente-para-los-rubros-no-principales, también los
    // rubros no principales (Gastronomía, Otros…): sin Infografía, va primero.
    if (veCatalogoConAgente && isDesignRubro && !modos.some((m) => m.path === 'catalogo-agente')) {
      const catalogo = {
        path: 'catalogo-agente',
        titulo: nombreCortoModo('catalogo-agente'),
        imagen: caratulaModo('catalogo-agente', rubroMarcaActiva).poster,
        bloqueado: false,
        conEjemplos: false,
        familia: 'imagen' as const,
        onAbrir: () => navigate('/catalogo'),
        href: '/catalogo' as string | undefined,
      };
      const i = modos.findIndex((m) => m.path === 'infografia');
      modos = i === -1 ? [catalogo, ...modos] : [...modos.slice(0, i), catalogo, ...modos.slice(i)];
    }
    // Sólo en la interfaz nueva: en la home de siempre (vertientes) la puerta a Crear es
    // Prompt libre, que en escritorio abre Redes
    // (ADR-2026-10-07-prompt-libre-y-creador-abren-crear).
    if (!uiNueva || modos.some((m) => m.path === 'redes')) return modos;
    return [
      ...modos,
      {
        path: 'redes',
        titulo: nombreCortoModo('redes'),
        imagen: caratulaModo('redes').poster,
        bloqueado: false,
        conEjemplos: false,
        familia: 'imagen' as const,
        onAbrir: () => navigate('/redes'),
        href: '/redes' as string | undefined,
      },
    ];
  }, [modosHome, navigate, uiNueva, veCatalogoConAgente, isDesignRubro, rubroMarcaActiva]);

  /* La lista de modos y las ediciones rápidas del sheet de "Generar" se mudaron a
     `useModosGenerar` (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell). Salían de
     `design-row` —una colección que esta pantalla arma en su render— y por eso el
     sheet sólo podía existir acá; ahora se rearman desde `MODES`, el mismo catálogo
     que consumen `/modos` y el sheet de Mi marca, y el shell las monta en todas las
     pantallas. */

  /**
   * Las filas de plantillas: todas las colecciones menos `design-row`, que son los
   * modos. Es el mismo `restCollections` que alimentaba a `HomeTemplatesSection`.
   */
  /**
   * Las plantillas del catálogo AGRUPADAS por categoría — para el panel lateral que
   * permite cambiar de plantilla sin volver a la grilla
   * (ADR-2026-09-08-la-plantilla-se-elige-desde-el-panel).
   *
   * Agrupadas y no planas porque el panel filtra por categoría con los mismos chips que
   * la pantalla de Plantillas (ADR-2026-09-08-el-panel-de-plantillas-filtra-por-categoria):
   * con 267 plantillas en una grilla sola, encontrar una era scrollear.
   *
   * Se van dos cosas:
   *  • `design-row`, que son los MODOS y no plantillas.
   *  • Las vistas "solo producto" (`design-producto`: plano frente, maniquí invisible…),
   *    que viajan al final de la colección "Fondo blanco" para el flujo de Catálogo. Acá
   *    se está eligiendo una plantilla, y esas vistas no son plantillas — ofrecerlas era
   *    mezclar dos catálogos en la misma grilla.
   *
   * Una categoría que se queda sin presets después del filtro no se lista: un chip que
   * abre una grilla vacía es peor que no estar.
   */
  const categoriasDePlantillas = React.useMemo(
    () =>
      displayCollections
        .filter((c) => c.id !== 'design-row')
        .map((c) => ({
          id: c.id,
          titulo: c.title,
          presets: c.presets.filter((p) => p.kind !== 'design-producto'),
        }))
        .filter((c) => c.presets.length > 0),
    [displayCollections],
  );

  const todasLasFilasPlantillas = React.useMemo(
    () =>
      // Sin plantillas para los rubros que no ven ninguno de los dos modos
      // (ADR-2026-08-03-plantillas-solo-para-moda,
      // ADR-2026-09-10-plantillas-belleza-modo-propio). Se corta acá, en la fuente de
      // las filas, y no en la home: así se apagan de una vez la sección de desktop,
      // las filas de mobile y el "Ver todo", que las tres consumen esta lista.
      // `basePlantillas` lee el rubro de la MARCA activa —no `brandRubro`—: si acá no
      // coincidiera con el guard de la pantalla, el "Ver más" de estas filas rebota
      // (ADR-2026-08-21-plantillas-rebotaban-por-dos-marcas-activas).
      !basePlantillas
        ? []
        : displayCollections
        .filter((c) => c.id !== 'design-row')
        .map((collection) => ({
          id: collection.id,
          titulo: collection.title,
          bajada: collection.subtitle,
          items: collection.presets.map((p) => ({
            id: p.id,
            src: p.imageUrl,
            titulo: p.title,
            onAbrir: () => abrirPreset(collection)(p),
          })),
          onVerMas: () => {
            saveHomeScroll();
            navigate(rutaPlantillasCategoria(collection.id, basePlantillas));
          },
          // La misma URL que navega el click, para que "Ver más" sea un enlace de
          // verdad (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links).
          hrefVerMas: rutaPlantillasCategoria(collection.id, basePlantillas),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayCollections, userPlan, basePlantillas],
  );

  /**
   * De todas las colecciones, la home muestra 3 — y otras 3 en cada entrada o
   * refresh (ADR-2026-07-31-plantillas-home-rotan-tres). Hay 10 categorías activas:
   * listarlas todas hace de la home un scroll interminable, y mostrar siempre las
   * mismas esconde el resto del catálogo para siempre.
   *
   * Se rota acá y no dentro de `HomeMinimal` porque es una decisión sobre QUÉ datos
   * se muestran, no sobre cómo se dibujan: la home sigue recibiendo una lista de
   * filas y pintándolas todas.
   *
   * "Ver todo" sigue llevando a `/interfaz-v2/plantillas`, que las muestra todas —
   * es lo que hace que esconder 7 no sea esconderlas.
   */
  const filasPlantillasHome = useRotacionPlantillas(todasLasFilasPlantillas, 3);

  /**
   * Cuántas plantillas tiene el catálogo COMPLETO — lo dice el cierre de la sección
   * de plantillas de la home.
   *
   * Se cuenta acá y no en la home porque la home sólo recibe las 3 filas sorteadas:
   * desde ahí el total es invisible. Sale de la misma lista que alimenta el "Ver
   * todo", así que el número y el destino no pueden divergir (y sigue el filtro por
   * rubro: sin plantillas, la sección entera no se dibuja).
   */
  const totalPlantillas = React.useMemo(
    () => todasLasFilasPlantillas.reduce((n, f) => n + f.items.length, 0),
    [todasLasFilasPlantillas],
  );

  /**
   * Breadcrumb del sheet — ADR-2026-08-03-breadcrumbs-pantallas-de-modo.
   *
   * Se arma acá y no en `PresetDetailSheet` porque las tres cosas que decide son de
   * esta pantalla: si lo que está abierto es un MODO o el detalle de una plantilla
   * (`MODE_KIND_TO_PATH` mapea justamente los kinds de las cards sintéticas), cómo
   * se llama el paso actual, y qué significa "volver" (`closePresetSheet`, que ya
   * sabe si tiene que navegar —cuando se entró por URL— o cerrar con animación).
   *
   * `null` para plantillas y poses: ahí el header sigue con su botón "Volver".
   *
   * Sólo el primer nivel es link. El del medio es el modo, que es donde ya estás
   * parado —el paso es un momento DENTRO de esa pantalla, no otra pantalla—, así que
   * no hay a dónde llevar.
   */
  /**
   * Qué shell de sheet se monta.
   *
   * El modo Modelo tiene el SUYO (`PresetDetailSheetModelo`): salió del banco de pruebas
   * del rediseño —era el shell de la variante v2, ADR-2026-08-21-modelo-shell-por-variante—
   * y se quedó como el sheet del modo cuando esa variante pasó a ser el modo
   * (ADR-2026-09-02-modelo-v2-es-el-modo-modelo). Su layout es de una sola pantalla y no
   * tiene nada que ver con el de dos columnas del resto, así que sigue separado en vez de
   * volver a meterse en `PresetDetailSheet` con banderas. Los props son los mismos: los
   * dos shells reciben exactamente lo mismo.
   */
  const SheetDelModo = esDesignModelo(selectedPreset?.kind)
    ? PresetDetailSheetModelo
    : PresetDetailSheet;

  /**
   * La columna de resultados —las tandas—, armada UNA vez para todos los shells que la
   * muestran: los dos forks del sheet la reciben por `columnaConfiguracion` y Prompt
   * libre por `columnaResultados` (ADR-2026-09-07-modo-prompt-libre). `null` sin tandas
   * que dibujar: ahí cada shell muestra su configuración o la muestra del modo.
   */
  const columnaTandas =
    // `hayResultados` y no `tandasPanel.length`: mientras la empresa se resuelve, la lista
    // filtrada está vacía y la columna caía a la portada por unos cientos de ms
    // (ADR-2026-09-08-la-portada-no-parpadea-mientras-se-resuelve-la-empresa).
    (columnaMuestraTandas && hayResultados) ||
    (creacionesDelCreadorAbiertas && selectedPreset?.kind === 'creador-video') ? (
      <PanelTandaCreaciones
        // TODAS las tandas de la empresa, no sólo las de este modo: las de otro
        // se rotulan con su nombre y así el que cambia de modo con algo
        // generando no las da por perdidas
        // (ADR-2026-09-06-las-tandas-se-ven-en-todos-los-modos).
        tandas={tandasPanel}
        modoActual={selectedPreset?.kind}
        recargarToken={recargarToken}
        companyId={userCompanyId}
        // Sin filtro en cuentas de una sola marca — el guard de Creaciones
        // (ADR-2026-09-13-brand-id-en-imagenes-generadas, D8). `null` = sin filtro.
        brandId={marcaParaFiltrar(currentBrandId, cuentaMultiMarca)}
        onVisorChange={setVisorTandaAbierto}
        // Carrusel / Animar desde el visor de una creación aterrizan acá como
        // una tanda más (ADR-2026-09-06-carrusel-y-animar-aterrizan-en-la-columna).
        onGeneracionDelegada={handleGeneracionDelegada}
        verificacionActiva={verificacionCalidad}
        analisisAutomatico={analisisAutomatico}
        // A pantalla completa la grilla reparte por ancho; en la columna de al lado del
        // panel sigue a tres (ADR-2026-09-07-modo-creador-video).
        columnas={selectedPreset?.kind === 'creador-video' ? 'auto' : 3}
        // El filtro es SÓLO de Creador de video: el resto de los modos sigue viendo la
        // columna completa (ADR-2026-09-08-creador-de-video-filtra-por-tipo-de-contenido).
        filtro={
          selectedPreset?.kind === 'creador-video' ? filtroCreadorVideo : 'todo'
        }
      />
    ) : null;

  /**
   * ¿El panel se abrió DESDE la pantalla de Plantillas?
   *
   * En un ref y no leyendo el state en cada render: el efecto del deep-link limpia el
   * `history.state` apenas aplica (para que un refresh no re-abra), así que a partir del
   * segundo render `initialNavSaltearPreview` vale `false`. El ref se queda con el valor
   * del montaje, que es cuando la pregunta tiene respuesta.
   */
  const abiertoDesdePlantillasRef = useRef(initialNavSaltearPreview);
  const abiertoDesdeRedesRef = useRef(initialNavDesdeRedes);

  /**
   * Las migas del paso de producto de una plantilla —
   * ADR-2026-09-07-el-paso-de-producto-de-plantillas-se-ve-como-una-pantalla.
   *
   * `migasModo` no las arma: las plantillas no son un modo y no están en
   * `MODE_KIND_TO_PATH`. Son las MISMAS dos de la pantalla de la que se viene, así que
   * el encabezado del panel no cambia entre una y otra: "Plantillas" sigue siendo
   * "Plantillas", y ahora además vuelve a la grilla.
   */
  const migasDePlantillas = React.useMemo(
    () =>
      abiertoDesdePlantillasRef.current
        ? [
            // Con `irAlInicio`: la plantilla está abierta sobre esta misma ruta, y sin la
            // señal navegar al inicio no la cierra
            // (ADR-2026-10-04-inicio-cierra-la-plantilla-abierta).
            { label: 'Inicio', onClick: () => navigate(rutaHome, { state: { irAlInicio: true } }) },
            {
              // "Plantillas", o "Recrear" en la interfaz nueva.
              label: nombreCortoModo('plantillas', '', { uiNueva }),
              // La grilla de la que se viene es la del modo del rubro
              // (ADR-2026-09-10-plantillas-belleza-modo-propio); con la ruta fija, a
              // Belleza el breadcrumb la mandaba a la de Moda y rebotaba a la home.
              //
              // Si se vino desde Redes, vuelve a Redes, scrolleado a sus plantillas
              // (ADR-2026-10-03-redes-las-migas-vuelven-a-redes).
              onClick: () =>
                abiertoDesdeRedesRef.current
                  ? navigate('/redes', { state: { irAPlantillas: true } })
                  : navigate(basePlantillas ?? '/interfaz-v2/plantillas'),
            },
            // El tercer nivel es la plantilla elegida. No es decorativo: el último nivel
            // de un breadcrumb nunca es link, así que sin él "Plantillas" quedaba como
            // "acá estás" y no había forma de volver a la grilla —el "Volver" del
            // encabezado viejo se fue con el marco—. Con la plantilla al final,
            // "Plantillas" vuelve a ser el camino de vuelta.
            { label: selectedPreset?.title ?? 'Plantilla' },
          ]
        : undefined,
    [navigate, selectedPreset?.title, basePlantillas, uiNueva],
  );

  const migasModo = React.useMemo(() => {
    const kind = selectedPreset?.kind;
    const path = kind ? MODE_KIND_TO_PATH[kind] : null;
    if (!path) return undefined;

    const migas: { label: string; onClick?: () => void }[] = [
      { label: 'Inicio', onClick: closePresetSheet },
      // El nombre sale de `nombreCortoModo` —o sea de `COPY_TIRA`— y NO del
      // `title` del preset (ADR-2026-08-03-un-solo-nombre-por-modo): ésa es la
      // misma tabla que usan el carrusel de la home, la sidebar y el sheet de
      // Generar en mobile. Con `p.title` el breadcrumb decía "Video con
      // influencer" mientras el carrusel y la sidebar decían "Video".
      //
      // El modo es la RAÍZ de su propio flujo, así que su link vuelve al primer
      // paso. `setPanelStep` directo y no `handleBackToChoose`: ése respeta
      // `pickOriginRef` y puede devolverte a `configure` —correcto para su botón
      // "volver", pero acá el usuario está clickeando el nombre del modo y espera
      // el principio, no el paso del que salió.
      {
        label: nombreCortoModo(path, selectedPreset!.title),
        onClick: () => setPanelStep('choose-product'),
      },
    ];

    // Elegir producto NO es un nivel: es el estado de arranque del modo, así que
    // ahí la hoja es el modo mismo. `pick-product` es la misma pantalla en mobile
    // (grilla full-screen) y cuenta igual.
    if (panelStep === 'choose-product' || panelStep === 'pick-product') return migas;

    // Los modos de catálogo eligen vistas ANTES de configurar, así que ese paso va
    // en el camino. `infografia` se saltea el picker (usa una vista fija), por eso
    // se excluye — el mismo criterio que usa su botón de volver.
    const pasaPorVistas = esModoCatalogo(kind) && kind !== 'infografia';
    if (panelStep === 'configure' && pasaPorVistas) {
      migas.push({
        label: ETIQUETA_PASO['select-vistas'],
        onClick: () => setPanelStep('select-vistas'),
      });
    }

    const etiqueta = ETIQUETA_PASO[panelStep];
    // Sin etiqueta —un paso nuevo que nadie agregó al mapa— el breadcrumb se queda
    // en los niveles que sí sabe nombrar en vez de mostrar un nivel vacío.
    if (etiqueta) migas.push({ label: etiqueta });
    return migas;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset, panelStep, closePresetSheet]);

  // ── Render ────────────────────────────────────────────────────────────
  // NOTA: NO aplicar `paddingLeft: sidebarWidth` acá — `DashboardLayout`
  // ya lo hace correctamente (con check de mobile vs desktop). Si lo
  // duplicamos, en mobile el contenido se squisha al borde derecho y en
  // desktop queda asimétricamente offseteado a la derecha.
  return (
    // La tuerca del modelo de imagen es del CHAT: su provider envuelve todas las rutas
    // (ChatAtlasRaiz), y las tarjetas del asistente son las mismas. Acá se corta a null
    // para que "Crear desde cero" siga por la cadena de producción
    // (ADR-2026-09-12-gpt-image-2-5-en-el-harness-y-en-el-agente).
    <SinProveedorDeImagenForzado>
    <ProductsContext.Provider value={userProducts}>
     <ProductsLoadedContext.Provider value={userProductsLoaded}>
     <ProductEditContext.Provider value={productEditContextValue}>
     <ModelsContext.Provider value={userModels}>
     {/* Este provider PISA al del entorno del Chat para todo su subárbol, así que tiene
         que estar completo: le faltaban `brandId`, `plan` y `variasMarcas`, y sin ellos
         `agregar_productos` creaba productos SIN marca (huérfanos) y las tarjetas que
         listan creaciones no filtraban — ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat.
         `currentBrandId` acá ya sigue a la marca en vivo (el listener de más arriba). */}
     <AgentEnvContext.Provider
       value={{
         userId: userAuthId,
         companyId: userCompanyId,
         shortName,
         companyName,
         refreshModels: refreshUserModels,
         plan: userPlan ?? undefined,
         brandId: currentBrandId,
         variasMarcas: cuentaMultiMarca,
       }}
     >
      {/* Fondo fixed del teléfono: tapa el gap que `DashboardLayout` deja entre la
          top-bar mobile y el `padding-top` del main, donde asomaba el `bg-[#EFF0F5]` del
          wrapper exterior. En desktop se desactiva (`sm:hidden`) para que la página se
          integre con el fondo gris del shell.

          Va en `bg-surface-mobile` y NO en blanco — ADR-2026-09-21-el-fondo-del-telefono-no-es-blanco.
          Era blanco de cuando la app en el teléfono era blanca; desde
          ADR-2026-08-04-mobile-usa-el-gris-de-desktop el fondo mobile es el mismo gris de
          escritorio (`--app-mobile-bg`), así que el único tramo donde este overlay se
          veía —la franja de la top-bar— quedaba como un RECTÁNGULO BLANCO arriba de todo,
          con la home gris abajo y una línea recta entre los dos. Con el gris, la franja
          desaparece y la estela del Agente llega hasta el borde. */}
      <div
        className="fixed inset-0 bg-surface-mobile pointer-events-none sm:hidden"
        aria-hidden="true"
      />
      {/* Skeleton overlay: tapa la home mientras restauramos scroll.
          Sin esto el user ve la home llenarse en fases y un salto al
          final. Se levanta cuando `isRestoringHomeScroll === false`. */}
      {isRestoringHomeScroll && (
        <div className="fixed inset-0 z-[60] bg-surface-mobile sm:bg-surface-content overflow-hidden">
          <HomeSkeleton />
        </div>
      )}
      {/* Acá vivía el panel CONTEXTO DEV, que se fue de las pantallas de la app —
          ADR-2026-08-03-fuera-panel-contexto-dev-de-las-pantallas. Con él se va la
          guarda `!dentroDelShell` que evitaba que se duplicara con el del shell. */}
      {/* Fondo de la página. Dentro del shell nuevo va TRANSPARENTE
          (ADR-2026-07-31-fondo-pagina-cede-shell): `surface-content` es el casi-blanco
          `#FDFDFE` del panel de `DashboardLayout`, y pintado acá adentro tapaba el
          gris `#F9F9F9` del shell — que es el que separa las tarjetas del lienzo y
          hace que la home se lea como bloques y no como una hoja en blanco.

          Fuera del shell (mobile) no cambia nada: el fondo lo tiene que poner esta
          pantalla, porque `DashboardLayout` no pinta el suyo abajo de `md:`. */}
      <div
        className={`relative min-h-[100dvh] md:min-h-full ${
          dentroDelShell ? '' : 'bg-surface-mobile sm:bg-surface-content'
        }`}
      >
      {/* MOBILE HERO — edge-to-edge, top-to-edge. Vive FUERA del
          contenedor centrado y usa margin-top negativo para anular el
          `mainTopPadding` que aplica `DashboardLayout` al `<main>`
          (env(safe-area-inset-top) + 3.5rem + 0.75rem). Así la
          imagen sube hasta el borde físico superior y el chrome
          flotante (back/menu pills + counter de créditos) queda por
          encima. El gradient inferior del hero fade-to-white se
          continúa visualmente con el bg de la página. */}
      {(() => {
        const designRow = displayCollections.find((c) => c.id === 'design-row');
        if (!designRow) return null;
        // Con un preset/panel abierto, la home queda DETRÁS del panel. No la
        // renderizamos: sus videos + imágenes seguirían consumiendo memoria de
        // GPU (y en la práctica se "colaban" por el panel cuando la GPU se
        // saturaba). Ver también el gate del contenedor de contenido más abajo.
        if (selectedPreset) return null;
        // Los rubros que abren con el Agente NO tienen este carrusel: el pedido fue
        // sacar la presentación de los modos del encabezado del teléfono y poner al
        // Agente directo (ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente).
        // El hero que lo reemplaza va adentro del contenedor centrado de más abajo,
        // porque no es un bloque a sangre: es una caja de texto.
        if (abreConElAgente) return null;
        if (uiNuevaEnElTelefono) return null;
        return (
          <div
            className="sm:hidden"
            style={{
              marginTop:
                'calc(0px - env(safe-area-inset-top, 0px) - 3.5rem - 0.75rem)',
            }}
          >
            <MobileModeHero
              cards={
                // Prueba gratis: en la home mobile mostramos una selección corta de
                // modos; el resto sigue accesible desde la página Modos, que no se
                // filtra. CUÁLES depende del rubro —
                // ADR-2026-08-20-la-home-gratis-de-hogar-muestra-automatico-y-fotos.
                //
                // La lista estaba escrita acá a mano (automático + personalizado +
                // "Crear desde cero"), una sola para todos y pensada para una marca de
                // ropa: a Hogar y Decoración le quedaban DOS de los tres —
                // `design-modelo` tiene `ocultoEnHogarDeco`— y uno de esos dos era el
                // chat.
                //
                // OJO: este filtro es APARTE del gating por plan. Abrir un modo en
                // `getModePlanLock` NO alcanza para que aparezca acá — ver la nota de
                // `kindsHomeGratis`, que es donde vive ahora la lista.
                isFreePlan
                  ? designRow.presets.filter(
                      (p) => !!p.kind && kindsGratisHome.has(p.kind),
                    )
                  : designRow.presets
              }
              isLocked={(preset) => !!getModePlanLock(preset.kind, userPlan)}
              rubro={brandRubro ?? userRubro}
              onCreate={(preset) => {
                // Gating por plan: card bloqueada → visor de ejemplos (nota
                // del plan + "Mejorar plan"); sin ejemplos, el modal clásico.
                const modeLock = getModePlanLock(preset.kind, userPlan);
                if (modeLock) {
                  if (!openLockedModeExamples(preset, modeLock)) {
                    setFeatureLock(modeLock);
                  }
                  return;
                }
                // Para restaurar el scroll de la home al cerrar el sheet.
                homeScrollBeforeSheetRef.current = getAppScrollY();
                // Modos con pantalla propia (Animar imagen, Shorts Studio):
                // no hay sheet que abrir, se va derecho a su ruta.
                const externalRoute = preset.kind
                  ? MODE_KIND_TO_ROUTE[preset.kind]
                  : null;
                if (externalRoute) {
                  navigate(externalRoute);
                  return;
                }
                // Modos URL-routed (auto/modelo/catalogo/asistente):
                // navegamos a su URL y el useEffect de sync abre el sheet.
                // Browser back queda funcional sin tocar el state machine.
                const modePath = preset.kind
                  ? MODE_KIND_TO_PATH[preset.kind]
                  : null;
                if (modePath) {
                  navigate(rutaModo(modePath));
                  return;
                }
                // Templates / E-commerce: legacy in-state path.
                setSelectedPreset(preset);
                setPreviewList(designRow.presets);
                setPanelStep('preview');
                setPresetSheetAnimated(false);
              }}
            />
          </div>
        );
      })()}
      {/* ── Barra de "sin créditos" (ADR-2026-07-30-barra-superior-creditos-home-ancho) ──
          Va DESPUÉS del hero mobile — ADR-2026-08-04-la-barra-de-creditos-no-va-arriba-del-hero.
          Era el primer hijo del root, y en mobile eso rompía dos cosas a la vez: el
          hero cancela el padding superior del `<main>` con un margen negativo que
          asume estar PRIMERO, así que con la barra delante ese margen se comía la
          barra en vez del padding —quedaba una franja blanca arriba y la barra
          cortada al medio—. Corrida abajo del hero, el margen negativo vuelve a
          hacer lo suyo y la barra se ve entera.
          Sigue fuera del `max-w-content` para ocupar el 100% del panel (edge-to-edge,
          sin la sidebar) y en flujo, no `fixed`: así no hay que compensar
          `sidebarWidth` ni pelearse con el scroll container. */}
      {mostrarTopBar && (
        // El `fixed` y el ref viven en un wrapper y no en la barra: `CreditsTopBar` es
        // una función sin `forwardRef`, y envolverla sale más barato que cambiarle la
        // firma a un componente que montan tres pantallas.
        <div
          ref={refBarraCreditos}
          className="fixed inset-x-0 top-0 z-[45] sm:static sm:z-auto"
        >
        <CreditsTopBar
          /* En PWA standalone (status bar translúcida, `viewport-fit=cover`) el
             wrapper fixed arranca DEBAJO de la hora y la batería: el padding mete
             el inset adentro de la barra —el degradé pinta el padding-box, así la
             franja del notch queda azul— y el texto baja a zona visible. El efecto
             que publica `--barra-creditos-mobile` mide el wrapper, así que el
             header se corre solo. `sm:pt-0` porque de 640px para arriba la barra
             vuelve al flujo, debajo del header del shell que ya compensa. */
          className="pt-[env(safe-area-inset-top,0px)] sm:pt-0"
          estado={estadoTopBar}
          restantes={homeBanners.freeImagesRemaining ?? undefined}
          discountPercent={homeBanners.discountPercent}
          onDismiss={descartarTopBar}
          /* En MOBILE la barra queda fija arriba de todo —
             ADR-2026-08-04-en-mobile-la-barra-de-creditos-queda-fija. Un aviso de saldo
             que se va con el scroll es un aviso que se pierde: en el teléfono la home
             es larga y a los dos gestos ya no está.
             El header mobile —donde vive la pastilla de créditos— se corre abajo de la
             barra leyendo `--barra-creditos-mobile`, que publica el efecto de más
             arriba midiéndola (ADR-2026-08-04-la-pastilla-de-creditos-va-abajo-del-cartel).
             `sm:static` la devuelve al flujo de 640px para arriba, que es donde ya
             estaba bien (y donde el shell de desktop monta la suya aparte). */
          // El CTA abre el catálogo COMO OVERLAY sobre esta pantalla en vez de
          // navegar a `/subscription` (ADR-2026-07-31-fuera-cartel-30-imagenes-gratis). Se reusa el `SubscriptionLimitModal`
          // que esta pantalla ya monta: desde ADR-2026-07-30-copy-catalogo-segun-contexto-abrio los tipos de crédito los
          // renderiza `CreditsPricingSheet`, que es exactamente el overlay que se
          // quiere. Levantar otro sheet acá duplicaría las ramas de cobro.
          onUpgrade={() => {
            // El copy sigue al estado que la barra acaba de anunciar, así el
            // catálogo no contradice al cartel que lo abrió (ADR-2026-07-31-copy-catalogo-sigue-saldo-real). Va por
            // `motivo` y NO por `type`: los dos tipos distinguen POR QUÉ se
            // acabaron los créditos, no SI quedan.
            setMotivoPricing(estadoTopBar);
            setSubscriptionLimitType('plan-limit-reached');
            setShowSubscriptionLimitModal(true);
          }}
        />
        </div>
      )}
      {/* Outer container con max-w-7xl para que las colecciones aprovechen
          el espacio horizontal en desktop. El greeting + prompt bar viven
          dentro de un sub-container más angosto centrado, para que esos
          elementos no se vean estirados en pantallas anchas. */}
      {/* `lg:px-0 lg:pt-0`: dentro del shell nuevo la sangría de 32px la pone el
          wrapper (`.shell-seccion`, ADR-2026-08-03-sangria-32-la-pone-el-shell). Este
          contenedor es NIETO del wrapper, así que el reset de padding —que alcanza
          sólo al hijo directo— nunca lo tocaba: la home terminaba 32px a la derecha
          y 32px más abajo que las secciones, que sí lo perdían. Fuera del shell
          (mobile / `DashboardLayout`) el padding sigue siendo de esta pantalla. */}
      <div
        className={`max-w-content mx-auto px-5 sm:px-8 pt-4 sm:pt-8 pb-24 md:pb-8 lg:px-0 lg:pt-0 ${
          selectedPreset ? 'hidden' : ''
        }`}
      >
        {/* ── HOME DESKTOP: el diseño de `/inicio` con la lógica de acá ──────
            ADR-2026-07-31-inicio-se-funde-interfaz-v2.

            Acá vivían el saludo chico, `CollectionRow` con los modos y
            `HomeTemplatesSection` (chips + masonry). Los reemplaza `HomeMinimal`,
            que es la misma pantalla que servía `/inicio`: saludo grande con los
            accesos de cuenta, tira de modos, creaciones recientes, plantillas y
            ediciones rápidas.

            `HomeMinimal` es PRESENTACIONAL: no decide nada. Todo lo que sigue —qué
            modos existen, el gating por plan, el guardado del scroll, abrir el sheet
            o navegar— se calcula acá y entra por props, que es exactamente la lógica
            que antes estaba cableada en el JSX de esta sección.

            `hidden sm:block`: en mobile sigue todo como estaba (`MobileModeHero`
            arriba y los `CollectionRow` de abajo), porque `HomeMinimal` asume el
            shell de desktop —de él saca el estado de cuenta del encabezado— y en
            teléfono ese contexto no existe. */}
        <div className="hidden sm:block">
          <HomeMinimal
            banners={
              /* Los mismos banners de siempre, en el lugar que les toca en el
                 layout nuevo. `bannerLateral` sigue mandando: cuando la barra
                 superior ya dice lo mismo, el upsell no se repite
                 (ADR-2026-07-31-banners-desktop-solo-upsell-cede). */
              /* El cobro rechazado SÍ sale en escritorio (los otros carteles de estado
                 siguen sólo en mobile): pide actuar antes de una fecha
                 (ADR-2026-10-26-cobros-rechazados-en-la-plataforma). */
              homeBanners.cobro ? (
                <HomeBannersView state={homeBanners} className="mt-12" noMargin showStatus />
              ) : bannerLateral ? (
                /* `mt-12` y no `mt-6`: es el mismo respiro que la tira de modos
                   deja por debajo (`section.mt-12`), así saludo → banner → tira
                   quedan a la misma distancia. Con `mt-6` el banner se leía
                   pegado al saludo y suelto del resto. */
                <HomeBannersView state={homeBanners} className="mt-12" />
              ) : null
            }
            /* Hasta que `useHomeBanners` no sepa qué cartel va, la home entera va en
               esqueleto: `kind: null` significa a la vez "ninguno" y "todavía no sé",
               así que sin esto la home se dibujaba sin cartel y lo metía después,
               empujando la tira de modos hacia abajo
               (ADR-2026-08-03-home-aparece-entera-o-skeleton). Se nota sobre todo al
               cambiar de marca, que rehace la consulta. */
            cargando={!homeBanners.resuelto}
            modos={modosHomeEscritorio}
            /* Qué cabecera: por destino (grupo C) o por vertiente
               (ADR-2026-10-07-la-home-elige-su-cabecera). */
            uiNueva={uiNueva}
            /* Los rubros sin catálogo propio abren con la caja del Agente y con los
               modos en la otra mitad del switch
               (ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente). Sólo
               cuenta en la cabecera de vertientes. */
            abreConElAgente={abreConElAgente}
            agenteBloqueado={agenteBloqueado}
            /* La barra que presenta el Agente, arriba de "Elegí qué querés crear"
               (ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final). */
            conPresentacionDelAgente={conPresentacionDelAgente}
            plantillas={filasPlantillasHome}
            totalPlantillas={totalPlantillas}
            onVerTodasPlantillas={() => {
              saveHomeScroll();
              navigate(basePlantillas ?? '/interfaz-v2/plantillas');
            }}
            hrefTodasPlantillas={basePlantillas ?? "/interfaz-v2/plantillas"}
          />
        </div>

        {/* ── El Agente, primero — SÓLO MOBILE y sólo en los rubros sin catálogo
            propio (ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente).

            Es el MISMO componente que monta `HomeMinimal` en escritorio, con
            `variante="mobile"`: el pedido era que el teléfono se comportara igual, y
            dos heroes distintos se desincronizan al primer retoque. Ocupa el lugar del
            `MobileModeHero`, que en esta rama no se dibuja.

            `onAbrirModo` delega en el `onAbrir` que `modosHome` ya trae (`abrirPreset`),
            que es el que reparte entre el visor de ejemplos, el modal de plan y la
            pantalla del modo — el mismo reparto que hacía el carrusel. Y los planes se
            NAVEGAN en vez de abrir el catálogo del shell: acá abajo ese shell no
            existe, igual que en el sheet de Generar. */}
        {uiNuevaEnElTelefono && (
          <HomeMobilePorDestino
            nombre={shortName}
            idCompany={userCompanyId}
            idMarca={currentBrandId}
            // El producto del onboarding, sólo mientras sea el ÚNICO: con dos o más, la home no
            // destaca ninguno ni lo monta sobre los accesos, y los modos abren sin producto
            // elegido (ADR-2026-10-27-tu-primer-producto-solo-con-un-producto-2).
            productoReciente={userProductsLoaded && userProducts.length === 1 ? userProducts[0] : null}
            // El aviso de cobro rechazado, debajo del saludo: al pie quedaba debajo de Academia y
            // podía no verse (ADR-2026-11-03-el-aviso-de-cobro-debajo-del-saludo-en-el-telefono).
            aviso={homeBanners.cobro ? <HomeBannersView state={homeBanners} noMargin showStatus /> : null}
          />
        )}
        {abreConElAgente && !uiNuevaEnElTelefono && (
          /* `relative isolate` + la estela colgada del techo: acá el techo de la home es
             este bloque, que es lo primero del contenedor
             (ADR-2026-09-21-la-estela-arranca-en-el-techo-de-la-home). En escritorio la
             monta `HomeMinimal`, que es el techo allá.

             `-mx-5 px-5` lo saca del margen del contenedor y le devuelve la sangría a
             sus hijos: el bloque pasa a medir la PANTALLA ENTERA sin mover ni un píxel
             del hero. Es lo que hace que el `overflow-x-clip` recorte la estela en el
             borde del teléfono y no a 20px de él
             (ADR-2026-09-21-la-estela-del-telefono-se-recorta-en-el-borde): recortada
             adentro del margen se le veían los dos cantos rectos, con gris afuera, y la
             luz parecía una lámina pegada en el medio en vez de seguir fuera de cuadro. */
          <div className="relative isolate -mx-5 overflow-x-clip px-5 sm:hidden">
            <EstelaDelAgente variante="mobile" />
            <HeroAgenteHome
              variante="mobile"
              saludo={<SaludoDeLaHome nombre={shortName} tamano="telefono" pregunta={null} />}
              modos={modosHome}
              rubroMarca={brandRubro ?? userRubro}
              agenteBloqueado={agenteBloqueado}
              onAbrirModo={(m) => m.onAbrir()}
              onIrAPlanes={() => navigate('/subscription')}
            />
          </div>
        )}

        {/* Banner en mobile: debajo del hero de modos. En la interfaz nueva lo de arriba
            termina en la card de Academia, sin margen propio: el `mt-6` los separa (pedido de
            Matías, 2026-10-26: el aviso de cobro quedaba pegado a Academia). */}
        {/* En la interfaz nueva, 12 px desde Academia (el `pb-2` de su sección + `mt-1`) y sin el
            `mb-4` propio: el aire de abajo lo pone la sección siguiente
            (ADR-2026-10-27-menos-aire-entre-academia-el-aviso-y-creaciones). */}
        {/* El de cobro rechazado de la interfaz nueva ya salió debajo del saludo. */}
        {!(uiNuevaEnElTelefono && homeBanners.cobro) && (
          <HomeBannersView
            state={homeBanners}
            className={uiNuevaEnElTelefono ? 'mt-1 sm:hidden' : 'sm:hidden'}
            noMargin={uiNuevaEnElTelefono}
            showStatus
          />
        )}

        {/* ── El Agente, presentado — SÓLO MOBILE ───────────────────────────
            ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final.

            El MISMO componente que monta `HomeMinimal` en escritorio: el pedido era que
            el teléfono se viera igual de bien, y dos copias de la misma franja se
            desincronizan al primer retoque. Lo único que cambia acá es el margen —el
            carrusel de modos termina a sangre justo arriba— y que se dibuja sólo en el
            teléfono (`sm:hidden`): de 640 para arriba la monta la otra home, y montadas
            las dos la franja saldría dos veces.

            No se dibuja en los rubros que abren con el Agente: ahí el hero YA es el
            Agente, y `conPresentacionDelAgente` es la negación de `abreConElAgente`.

            Los planes se NAVEGAN en vez de abrir el catálogo del shell, igual que el
            hero del Agente de acá abajo: en el teléfono ese shell no existe. */}
        {/* En la interfaz nueva el Agente no se presenta en la home (tampoco en escritorio,
            donde vive en la sidebar): en el teléfono está en el sheet de Crear. */}
        {conPresentacionDelAgente && !uiNuevaEnElTelefono && (
          <PresentacionDelAgente
            rubro={brandRubro ?? userRubro}
            bloqueado={agenteBloqueado}
            onIrAPlanes={() => navigate('/subscription')}
            className="mt-6 sm:hidden"
          />
        )}

        {/* ── Cuerpo de la home — SÓLO MOBILE ──────────────────────────
            ADR-2026-07-31-home-mobile-hero-nav-sheet-generar.

            Acá había una fila (`CollectionRow`) por colección, con scroll
            horizontal. Ahora mobile muestra LAS MISMAS secciones que desktop
            —creaciones recientes, plantillas por colección y ediciones rápidas—,
            que es el mismo componente con las mismas props: las grillas ya traían
            `grid-cols-2` para teléfono.

            Lo de arriba sigue siendo distinto en cada uno: desktop abre con el
            saludo y la tira de modos; mobile, con el hero a sangre y su barra
            inferior. */}
        {/* Academia Atlas — SÓLO MOBILE, debajo de los modos y la presentación del
            Agente, igual que en escritorio
            (ADR-2026-10-07-la-card-de-academia-va-debajo-de-las-herramientas). */}
        {/* En la interfaz nueva Academia va arriba de las cards (`HomeMobilePorDestino`). */}
        {/* El gratis que recién entra va sin Academia
            (ADR-2026-10-23-la-home-del-telefono-para-el-gratis-que-recien-entra). */}
        {!uiNuevaEnElTelefono && !gratisQueRecienEntra && !cuentaCargando && <CardAcademyHome className="mt-6 sm:hidden" />}

        {/* El gratis que recién entra no ve ninguna de estas secciones —creaciones recientes,
            plantillas, herramientas— (ADR-2026-10-23-la-home-del-telefono-para-el-gratis-que-recien-entra-3). */}
        {!gratisQueRecienEntra && !cuentaCargando && (
        <div className={uiNuevaEnElTelefono ? 'mt-8 sm:hidden [&>section:first-of-type]:mt-0' : 'mt-4 sm:hidden'}>
          {/* En la interfaz nueva, 32 px hasta la primera sección: sin esto sumaba el `mt-4` de
              acá, el `mt-12` de la sección y el `mb-4` del aviso (80 px). */}
          <SeccionesHome
            idCompany={userCompanyId}
            // Sin filtro por marca en mobile: el filtro de "Creaciones recientes"
            // (ADR-2026-08-03-creaciones-recientes-por-marca) se apoya en el selector
            // de marca de la sidebar del shell nuevo, que acá no existe. `null` es lo
            // que la sección ya recibía antes de que ese prop existiera.
            idMarca={null}
            plantillas={filasPlantillasHome}
            totalPlantillas={totalPlantillas}
            onVerTodasPlantillas={() => {
              saveHomeScroll();
              navigate(basePlantillas ?? '/interfaz-v2/plantillas');
            }}
            hrefTodasPlantillas={basePlantillas ?? "/interfaz-v2/plantillas"}
            // Interfaz nueva: las herramientas de la sidebar en lugar de "Ediciones rápidas"
            // (ADR-2026-10-08-la-interfaz-nueva-llega-al-telefono), debajo de las plantillas
            // y no de recientes como en escritorio
            // (ADR-2026-10-18-plantillas-antes-que-herramientas-en-el-telefono).
            sinEdicionesRapidas={uiNuevaEnElTelefono}
            slotDespuesDePlantillas={uiNuevaEnElTelefono ? <HerramientasMobile /> : undefined}
          />
        </div>
        )}
      </div>

      {/* La barra inferior y el sheet de "Generar" se mudaron al shell
          (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell): son navegación de la
          app, no de esta pantalla, y montados acá existían sólo en el inicio. Lo que
          esta pantalla sigue decidiendo es cuándo se aparta — ver `barraSeAparta`. */}

      {/* ── Preset detail sheet ─────────────────────────────────── */}
      {selectedPreset && esAsistente(selectedPreset.kind) && (
        <AsistenteMarketingSheet
          animated={presetSheetAnimated}
          sidebarWidth={sidebarWidth}
          onClose={closePresetSheet}
          chat={chat}
          // Misma pantalla, dos variantes: v2 sin el switch Imagen/Video ni el
          // desplegable de Ayuda de IA — ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo.
          variante={selectedPreset.kind === 'asistente-v2' ? 'v2' : 'v1'}
        />
      )}
      {/* Prompt libre: sheet propio, como el asistente (ADR-2026-09-07-modo-prompt-libre).
          Formato y Calidad son el estado compartido de los modos —así el picker de
          formato es el mismo—; la tanda entra a la columna por el canal delegado. */}
      {selectedPreset && selectedPreset.kind === 'prompt-libre' && (
        <PromptLibreSheet
          animated={presetSheetAnimated}
          sidebarWidth={sidebarWidth}
          onClose={closePresetSheet}
          formatoVal={formatoVal}
          onOpenFormato={() => setFormatPickerOpen(true)}
          calidad={resolucionVal}
          onCalidadChange={setResolucionVal}
          columnaResultados={columnaTandas}
          onGeneracionDelegada={handleGeneracionDelegada}
          verificarGuards={(costo) => checkGenerationGuards(undefined, costo)}
          cederZIndex={visorTandaAbierto}
        />
      )}
      {/* Creador de video: sheet propio (ADR-2026-09-07-modo-creador-video). La grilla de
          resultados es la misma columna, a pantalla completa; el guard es el de VIDEO
          (piso Pro/Ultra + saldo). */}
      {selectedPreset && selectedPreset.kind === 'creador-video' && (
        <CreadorVideoSheet
          animated={presetSheetAnimated}
          sidebarWidth={sidebarWidth}
          onClose={closePresetSheet}
          columnaResultados={columnaTandas}
          onGeneracionDelegada={handleGeneracionDelegada}
          verificarGuards={(costo) => checkGenerationGuards('video', costo)}
          cederZIndex={visorTandaAbierto}
          filtro={filtroCreadorVideo}
          onFiltroChange={setFiltroCreadorVideo}
          onVerCreaciones={() => setCreacionesDelCreadorAbiertas(true)}
        />
      )}
      {selectedPreset &&
        !esAsistente(selectedPreset.kind) &&
        selectedPreset.kind !== 'prompt-libre' &&
        selectedPreset.kind !== 'creador-video' && (
        <SheetDelModo
          preset={selectedPreset}
          animated={presetSheetAnimated}
          migas={migasModo ?? migasDePlantillas}
          step={panelStep}
          // El modo se abre con la configuración ya hecha: su paso de elegir productos no
          // corresponde aunque la lista todavía no haya resuelto
          // (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
          configuracionRestaurada={!!selectedPreset && presetRestauradoId === selectedPreset.id}
          primerProductoId={
            (location.state as { primerProductoId?: string } | null)?.primerProductoId ?? null
          }
          previewList={previewList}
          // Las plantillas del catálogo por categoría, para el panel lateral
          // (ADR-2026-09-08-la-plantilla-se-elige-desde-el-panel). `previewList` es sólo
          // la colección de la que salió la abierta.
          categoriasDePlantillas={categoriasDePlantillas}
          onElegirPlantilla={(p) => {
            setSelectedPreset(p);
            // La colección de la plantilla NUEVA, no la de la anterior: `previewList` es
            // de dónde el sheet saca la muestra, y arrastrando la vieja la columna de la
            // derecha mostraba una plantilla que no era la elegida.
            const col = displayCollections.find((c) =>
              c.presets.some((x) => x.id === p.id),
            );
            setPreviewList(col?.presets ?? [p]);
            // Y el paso se queda en configurar: cambiar de plantilla NO es volver a
            // empezar, es cambiar una card de la pantalla en la que ya se está
            // (ADR-2026-09-08-la-plantilla-se-elige-desde-el-panel). Sin esto el sheet
            // caía al preview, que es su paso por defecto al abrir un preset.
            setPanelStep('configure');
          }}
          rubro={userRubro}
          rubroMarca={brandRubro ?? userRubro}
          // Belleza genera la plantilla con UN producto
          // (ADR-2026-09-14-la-plantilla-de-belleza-se-genera-con-un-producto). Se
          // decide acá y no adentro del sheet porque el rubro que manda es el de la
          // MARCA activa (`rubroDePlantillas`), y lo que le llega al sheet como
          // `rubroMarca` sale de `brandRubro`, que resuelve la marca sólo por
          // `profiles.current_brand_id` (ADR-2026-08-21-plantillas-rebotaban-por-dos-marcas-activas).
          unSoloProducto={esPlantillaDeBelleza}
          lockSlotCount={lockSlotCount}
          catalogoVistas={catalogoVistas}
          onCatalogoVistasChange={setCatalogoVistas}
          onConfirmCatalogoVistas={() => setPanelStep('configure')}
          onBackFromCatalogoConfigure={() =>
            // Infografía salteó el picker de vistas → el "back" vuelve a elegir
            // productos, no a 'select-vistas' (que en ese modo no existe).
            selectedPreset?.kind === 'infografia'
              ? handleBackToChoose()
              : setPanelStep('select-vistas')
          }
          onGenerateCatalog={handleGenerateCatalog}
          brandId={currentBrandId}
          onProductsRefreshed={async () => {
            if (!userCompanyId) return;
            const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
            persistUserProducts(refreshed);
          }}
          selectedProducts={selectedProducts}
          imagenBase={imagenBase}
          modelSelection={modelSelection}
          fondoVal={fondoVal}
          formatoVal={formatoVal}
          resolucion={resolucionVal}
          otraConfig={otraConfig}
          sidebarWidth={sidebarWidth}
          onClose={closePresetSheet}
          onUtilizarMultiple={handleUtilizarMultiplePresets}
          multiPresetIds={multiPresetIds}
          onUtilizarPreset={handleUtilizarPreset}
          onShowProducts={handleShowProducts}
          onAddProducts={handleAddProducts}
          onConfirmProducts={handleConfirmProducts}
          // Automático: la selección viaja en vivo (no hay confirm, el CTA es Generar).
          // Sólo el estado de productos — no toca el paso ni la recomendación de pose,
          // que en ese modo no aplican (ADR-2026-08-21-automatico-con-el-diseno-nuevo).
          onProductosEnVivo={(ps) => {
            setSelectedProducts(ps);
            setSinProducto(false);
          }}
          onBackToChoose={handleBackToChoose}
          onStartUpload={handleStartUpload}
          onStartNewProduct={handleStartNewProduct}
          onSkipProduct={handleSkipProduct}
          sinProducto={sinProducto}
          dialogoLoading={dialogoLoading}
          dialogoError={dialogoError}
          onBulkUpload={handleOpenBulkUpload}
          userPlan={userPlan}
          onAddMoreFiles={handleAddMoreFiles}
          onRemovePendingFile={handleRemovePendingFile}
          onCancelUpload={handleCancelUpload}
          onConfirmUpload={handleConfirmUpload}
          pendingProductFiles={pendingProductFiles}
          pendingProductName={pendingProductName}
          onPendingNameChange={setPendingProductName}
          uploadingProduct={uploadingProduct}
          uploadError={uploadError}
          bulkUploadingProducts={bulkUploadingProducts}
          onClearProducts={() => {
            setSelectedProducts([]);
      // La creación base se va con el panel: si no, el próximo anuncio abierto desde un
      // producto seguiría mandando `imagen_generada_id`.
      setImagenBase(null);
            // Volver a la pantalla de producto limpia también "Sin producto":
            // si no, la card seguiría diciendo "Sin producto" mientras el user
            // está justamente eligiendo uno (ADR-673).
            setSinProducto(false);
            // Al volver a elegir producto, re-recomendar la pose por defecto.
            poseAutoSelectRef.current = 'pending';
            setPanelStep('choose-product');
          }}
          // Quitar UN producto (fotos-producto multi-producto, ADR-2026-08-30).
          // Sin productos restantes cae al mismo destino que onClearProducts —
          // defensivo: el panel manda el último X por onClearProducts.
          onRemoveProduct={(id) => {
            const next = selectedProducts.filter((p) => p.id !== id);
            setSelectedProducts(next);
            if (next.length === 0) {
              setImagenBase(null);
              setSinProducto(false);
              poseAutoSelectRef.current = 'pending';
              setPanelStep('choose-product');
            }
          }}
          onModelChange={handleModelChangeSingle}
          // Al generar, la columna de Modelo + Pose deja lugar a la tanda: los
          // recuadros de las imágenes en curso y, debajo, las creaciones
          // anteriores (ADR-2026-08-20-tanda-en-el-panel).
          cederZIndex={visorTandaAbierto}
          columnaConfiguracion={columnaTandas ?? undefined}
          // "Cambiar modelo y pose" en el panel izquierdo: con la columna en las tandas
          // es LA forma de volver a la configuración sin esperar a que termine nada
          // (ADR-2026-08-20-cambiar-modelo-y-pose-destacado). Con la configuración a la
          // vista no aparece — sería un botón para ir a donde ya se está.
          onCambiarModeloYPose={
            // Sólo en "Imagen con modelo": automático no tiene configuración a la que
            // volver —lo que se elige está todo en este mismo panel—, así que ahí el
            // bloque no va (ADR-2026-08-21-automatico-con-el-diseno-nuevo).
            esDesignModelo(selectedPreset?.kind) &&
            columnaMuestraTandas &&
            hayResultados
              ? () => setColumnaMuestraTandas(false)
              : undefined
          }
          // Qué modelos van marcados en la grilla. En v2 son los del GRUPO —la
          // foto con todos adentro—, en el resto los de la serie
          // (ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto). El prop se
          // llama "serie" porque es el que la grilla ya usaba para pintar checks;
          // lo que cambia es qué significa lo que se le pasa.
          modelSelectionsSerie={
            modeloGrupal
              ? // Con el interruptor apagado v2 elige de a uno: pasarle el grupo dejaría
                // la cuenta del panel ("N modelos elegidos") y los checks de la grilla
                // contando modelos que el próximo tap va a reemplazar
                // (ADR-2026-08-28-modelo-v2-varios-modelos).
                modeloVariosModelos
                ? (modelSelectionsExtra.filter(
                    (s): s is ModelSelection => !!s,
                  ) as ModelSelection[])
                : []
              : modelSelectionsSerie
          }
          // La grilla multi-select es exclusiva de 'design-modelo': es el único
          // modo cuyo payload se puede fan-outear por modelo (o, en v2, componer
          // en una sola imagen).
          onToggleModelSerie={
            modeloGrupal
              ? // v2 arranca en selección única, igual que el Catálogo: la grilla acumula
                // recién cuando el usuario prende "Utilizar varios modelos"
                // (ADR-2026-08-28-modelo-v2-varios-modelos).
                modeloVariosModelos
                ? handleToggleModelGrupal
                : undefined
              : // El Catálogo quedó AFUERA: su grilla es de selección única siempre, así
                // que el tap va por `onModelChange` y nunca acumula
                // (ADR-2026-08-28-catalogo-un-solo-modelo).
                esDesignModelo(selectedPreset?.kind)
                ? handleToggleModelSerie
                : undefined
          }
          // El interruptor "Utilizar varios modelos" es de Modelo v2 y de nadie más
          // (ADR-2026-08-28-catalogo-un-solo-modelo). Sin `onVariosModelosChange` el
          // shell no lo dibuja, y con `variosModelos` en false su grilla queda de
          // selección única: es el mismo apagado que ya usaba el resto de los modos.
          variosModelos={modeloGrupal ? modeloVariosModelos : false}
          onVariosModelosChange={
            modeloGrupal ? handleModeloVariosModelosChange : undefined
          }
          modelSelectionsExtra={modelSelectionsExtra}
          onModelChangeExtras={setModelSelectionsExtra}
          multiModelEnabled={multiModelEnabled}
          onMultiModelEnabledChange={setMultiModelEnabled}
          onOpenModelPicker={() => {
            setModelPickerInitialView('list');
            setModelPickerOpen(true);
          }}
          onOpenModelSheet={(initialView) => {
            setModelPickerInitialView(initialView);
            setModelPickerInitialEditingIdx(null);
            setModelPickerOpen(true);
          }}
          onOpenModelSheetForMultiSlot={(slotIdx, initialView) => {
            setModelPickerInitialView(initialView);
            setModelPickerInitialEditingIdx(slotIdx);
            setModelPickerOpen(true);
          }}
          multiProductsPerSlot={multiSlotProducts}
          onTapSlotProducts={(slotIdx) => {
            setProductPickerForSlot(slotIdx);
            setProductPickerOpen(true);
          }}
          // v2 elige el outfit de cada modelo en su panel lateral, sin sheet ni
          // confirmación: cada tap ya queda guardado
          // (ADR-2026-08-25-que-lleva-cada-modelo-en-el-panel).
          onSlotProductsChange={(slotIdx, products) =>
            setMultiSlotProducts((prev) => {
              // `null` = sacar la asignación propia. El slot vuelve a heredar los
              // productos de la foto, que es lo que hace el payload cuando no hay
              // entrada en este record.
              if (products === null) {
                if (!(slotIdx in prev)) return prev;
                const siguiente = { ...prev };
                delete siguiente[slotIdx];
                return siguiente;
              }
              return { ...prev, [slotIdx]: products };
            })
          }
          onOpenFondoSheet={(initialView) => {
            setFondoPickerInitialView(initialView);
            setFondoPickerInitialCategory(null);
            setFondoPickerOpen(true);
          }}
          onFondoChange={setFondoVal}
          onFormatoChange={setFormatoVal}
          onResolucionChange={setResolucionVal}
          onOpenFormatPicker={() => setFormatPickerOpen(true)}
          onOtraChange={setOtraConfig}
          ganchoId={videoGanchoId}
          onGanchoIdChange={handleGanchoIdChange}
          voiceGuion={videoVoiceGuion}
          onVoiceGuionChange={setVideoVoiceGuion}
          onVideoDurationChange={setVideoDuration}
          onGenerate={(cost) => handleGenerateFinal({ cost })}
          onGenerateProduction={(carruselCount, cost) =>
            handleGenerateFinal({ autoCarrusel: true, carruselCount, cost })
          }
          autoImageCount={autoImageCount}
          onAutoImageCountChange={setAutoImageCount}
          autoImageCountMax={AUTO_IMAGE_COUNT_MAX}
          userModels={userModels}
          userAuthId={userAuthId}
          userCompanyId={userCompanyId}
          onModelCreated={refreshUserModels}
          allPoses={allPosesFiltered}
          poseIds={poseIds}
          onPoseIdsChange={handlePoseIdsChange}
          vistasProducto={vistasProducto}
          vistaIds={vistaIds}
          onVistaIdsChange={setVistaIds}
          vistaProductosExcluidos={vistaProductosExcluidos}
          onToggleVistaProducto={handleToggleVistaProducto}
          fotosProductoTotalImagenes={fotosProductoTotalImagenes}
          multiPoseEnabled={multiPoseEnabled}
          // El interruptor "Utilizar varias poses" ahora también vive en la grilla del
          // panel de desktop (ADR-2026-09-04-el-interruptor-de-varias-poses-tambien-en-desktop),
          // así que el commit tiene que llegar hasta el sheet del modo. Es el MISMO
          // handler que recibe el `PosePickerSheet` de mobile: un solo estado para las
          // dos superficies.
          onMultiPoseEnabledChange={handleMultiPoseEnabledChange}
          // Con esto la segunda pantalla de mobile sabe cuándo cerrarse: recién cuando
          // la carga está arriba, no al tocar Generar
          // (ADR-2026-09-04-el-generar-de-mobile-espera-a-que-la-carga-este-arriba).
          generacionEnCurso={showLoadingScreen}
          onOpenPosePicker={() => {
            setPosePickerVista('list');
            setPosePickerOpen(true);
          }}
          onSubirPose={() => {
            setPosePickerVista('subir');
            setPosePickerOpen(true);
          }}
          customPoses={customPoses.filter(
            // Mismo filtro dinámico que el PosePickerSheet: con "varios
            // modelos" las propias de 2-3 personas TIENEN que estar acá —
            // el panel resuelve las poses seleccionadas contra esta lista
            // (DesignModeloPanel.selectedPoses) y con el filtro fijo a 1
            // la custom elegida no aparecía como seleccionada en la preview.
            (p) => (p.cantidadPersonas ?? 1) === expectedPeopleInPose,
          )}
          onUploadCustomPose={handleCatalogUploadPose}
          uploadingCustomPose={uploadingPose}
          onDeleteCustomPose={handleDeletePose}
          bibliotecaFondos={bibliotecaFondos}
          fondosGuardados={fondosGuardados}
          calidad={resolucionVal}
          onCalidadChange={setResolucionVal}
          fondoSelection={fondoSelection}
          onFondoSelectionChange={setFondoSelection}
          onOpenFondoPicker={() => {
            // Resetear a 'list' (la vista raíz: en mobile el sheet lo
            // mapea a 'all' — menú de categorías estilo
            // PruebaNuevaInterfaz), sino el sheet podría reabrirse en
            // describe/upload por un onOpenSheet previo.
            setFondoPickerInitialView('list');
            setFondoPickerInitialCategory(null);
            setFondoPickerOpen(true);
          }}
          fondoView={fondoView}
          onFondoViewChange={setFondoView}
          onFondoSaved={refreshFondosGuardados}
          previewMediaTab={previewMediaTab}
          onPreviewMediaTabChange={setPreviewMediaTab}
          videoDuration={videoDuration}
          videoMultiScene={videoMultiScene}
          videoCreditCostPerImage={videoCreditCostPerImage}
          onOpenVideoConfig={() => setShowVideoConfigSheet(true)}
          anuncioTitulo={anuncioTitulo}
          onAnuncioTituloChange={setAnuncioTitulo}
          anuncioDescripcion={anuncioDescripcion}
          onAnuncioDescripcionChange={setAnuncioDescripcion}
          anuncioCta={anuncioCta}
          onAnuncioCtaChange={setAnuncioCta}
          anuncioColors={anuncioColors}
          onAnuncioColorsChange={setAnuncioColors}
          anuncioBrandColors={brandColors}
        />
      )}
      {/* Sheet/modal de configuración de video — se abre desde el botón
          "Configurar video" del ConfigurePanel cuando el toggle del
          header está en 'video' y el preset es 'template'. */}
      <VideoConfigSheet
        open={showVideoConfigSheet}
        duration={videoDuration}
        onDurationChange={setVideoDuration}
        multiScene={videoMultiScene}
        onMultiSceneChange={setVideoMultiScene}
        onClose={() => setShowVideoConfigSheet(false)}
        // El modo "Video con influencer" usa 8-18s (14 sin gancho, ADR-671) y
        // sin escenas múltiples. El resto, 4-12s con escenas múltiples. Desde
        // ADR-671 ningún caso tiene costo fijo: el influencer cobra 1 crédito
        // por segundo de video final y nada más (antes sumaba +1 por el
        // frame, ver ADR-099).
        minDuration={
          selectedPreset?.kind === 'video-influencer'
            ? VIDEO_INFLUENCER_DURATION_MIN
            : undefined
        }
        maxDuration={
          selectedPreset?.kind !== 'video-influencer'
            ? undefined
            : isSinGancho(videoGanchoId)
              ? VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO
              : VIDEO_INFLUENCER_DURATION_MAX
        }
        showMultiScene={selectedPreset?.kind !== 'video-influencer'}
        baseCost={0}
      />
      </div>
      <ChatPanelV2 chat={chat} open={chatOpen} onClose={closeChat} />
      {/* Picker de modelo para mobile — vive a nivel root para que se vea
          encima de todo (sheets, top bars, etc.) via portal. En desktop
          el picker está inline en el ConfigurePanel, este componente no
          se renderiza en esa breakpoint (md+) gracias al check de width. */}
      <ModelPickerSheet
        open={modelPickerOpen}
        selection={modelSelection}
        onChange={handleModelChangeSingle}
        // Influencer: el modelo se elige sí o sí (sin tile "Automático").
        hideAuto={selectedPreset?.kind === 'video-influencer'}
        onClose={() => {
          setModelPickerOpen(false);
          // Resetear el slot pre-set para que la próxima apertura
          // (sin pre-config) caiga en list/multi como corresponda.
          setModelPickerInitialEditingIdx(null);
        }}
        userModels={userModels}
        userId={userAuthId}
        companyId={userCompanyId}
        onModelCreated={refreshUserModels}
        initialView={modelPickerInitialView}
        initialEditingMultiIdx={modelPickerInitialEditingIdx}
        selectionsExtra={modelSelectionsExtra}
        // Multi-modelo se ofrece en 'design-modelo' (opt-in) y en
        // 'design' (E-commerce, forzado por la pose). Plantillas
        // tradicionales (kind 'template') NO soportan varios modelos.
        onChangeExtras={
          esDesignModelo(selectedPreset?.kind) ||
          selectedPreset?.kind === 'design'
            ? setModelSelectionsExtra
            : undefined
        }
        multiEnabled={
          (esDesignModelo(selectedPreset?.kind) ||
            selectedPreset?.kind === 'design') && multiModelEnabled
        }
        // El toggle del multi solo se muestra en design-modelo. En
        // E-commerce (design) la cantidad la fija la pose y no debería
        // ser togglable — por eso pasamos undefined y `lockSlotCount`.
        onMultiEnabledChange={
          esDesignModelo(selectedPreset?.kind)
            ? setMultiModelEnabled
            : undefined
        }
        lockSlotCount={lockSlotCount}
        // ── El sheet de mobile toma la forma del panel de desktop ────────────────
        // ADR-2026-09-04-el-picker-de-modelo-de-mobile-toma-la-forma-de-desktop: la
        // grilla del teléfono estrena el mismo interruptor "Utilizar varios modelos" y
        // la misma acumulación con checks que el panel lateral. Es el MISMO estado
        // (`modeloVariosModelos` + `handleToggleModelGrupal`), así que lo elegido en el
        // teléfono es lo mismo que se ve al abrir en escritorio.
        //
        // Sólo en Modelo: el interruptor es de este modo y de nadie más
        // (ADR-2026-08-28-catalogo-un-solo-modelo). En el resto, sin
        // `onVariosModelosChange`, el sheet queda de selección única como siempre.
        variosModelos={modeloGrupal ? modeloVariosModelos : false}
        onVariosModelosChange={
          modeloGrupal ? handleModeloVariosModelosChange : undefined
        }
        // La misma bajada que el panel de desktop: acá "varios" es UNA foto con todos
        // adentro (ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto), y lo dice
        // antes del primer tap, que es cuando todavía se puede elegir otra cosa.
        bajadaVariosModelos={
          !modeloVariosModelos
            ? `Salen todos en la misma foto. Hasta ${MAX_MODELOS_SERIE}.`
            : // Llegado al tope, las cards que no están elegidas se apagan: la bajada
              // explica por qué dejaron de responder y cuál es la salida. En desktop eso
              // lo dice el subtítulo del panel, que acá no existe
              // (ADR-2026-08-25-tres-modelos-como-maximo).
              modelosElegidosGrupo >= MAX_MODELOS_SERIE
              ? `Llegaste al máximo de ${MAX_MODELOS_SERIE} ${palabraModelo.plural}. Sacá uno para elegir otro.`
              : `${modelosElegidosGrupo} ${
                  modelosElegidosGrupo === 1
                    ? `${palabraModelo.singular} elegido`
                    : `${palabraModelo.plural} elegidos`
                } — salen todos en la misma foto.`
        }
        selectionsSerie={
          modeloGrupal && modeloVariosModelos
            ? (modelSelectionsExtra.filter(
                (s): s is ModelSelection => !!s,
              ) as ModelSelection[])
            : []
        }
        onToggleSelection={
          modeloGrupal && modeloVariosModelos
            ? handleToggleModelGrupal
            : undefined
        }
        multiProducts={selectedProducts.map((p) => ({
          id: p.id,
          name: p.name,
          imageUrl: p.imageUrl,
        }))}
        multiProductsPerSlot={Object.fromEntries(
          Object.entries(multiSlotProducts).map(([idx, list]) => [
            idx,
            list.map((p) => ({
              id: p.id,
              name: p.name,
              imageUrl: p.imageUrl,
            })),
          ]),
        )}
        onTapSlotProducts={(slotIdx) => {
          setProductPickerForSlot(slotIdx);
          setProductPickerOpen(true);
        }}
      />
      {/* Popup "Subir varios productos" — EL MISMO de Mi Marca (ADR-684). Se
          abre desde la pantalla "Nuevo producto" (estado vacío de
          `upload-product`) en todos los modos. Vive a nivel root y portalea a
          body; el zIndex 220 lo deja por encima del PresetDetailSheet
          (z-100/101) y de la pantalla full-screen mobile (z-35), que si no lo
          taparían. */}
      <AgregarVariosModal
        open={bulkModalOpen}
        companyId={userCompanyId}
        brandId={currentBrandId}
        userPlan={userPlan ?? ''}
        zIndex={220}
        onClose={() => setBulkModalOpen(false)}
        onSaved={handleBulkModalSaved}
      />
      {/* Picker de formato — vive a nivel root vía portal. Mobile = bottom
          sheet, desktop = side panel. Se abre desde el botón Formato del
          ConfigurePanel (en cualquier breakpoint). */}
      {/* Picker de productos para un slot específico del modo
          varios-modelos. Mismas opciones (Choose / Pick / Upload) que
          el flow principal, pero la confirmación va al slot, no al
          state global `selectedProducts`. */}
      <MultiSlotProductPickerSheet
        open={productPickerOpen}
        onClose={() => {
          setProductPickerOpen(false);
          setProductPickerForSlot(null);
        }}
        currentSelection={
          productPickerForSlot !== null
            ? multiSlotProducts[productPickerForSlot] ?? selectedProducts
            : []
        }
        onConfirm={(products) => {
          if (productPickerForSlot !== null) {
            setMultiSlotProducts((prev) => ({
              ...prev,
              [productPickerForSlot]: products,
            }));
          }
          setProductPickerOpen(false);
          setProductPickerForSlot(null);
        }}
        userCompanyId={userCompanyId}
        brandId={currentBrandId}
        rubro={userRubro}
        userPlan={userPlan}
        tiposProductoSugeridos={
          selectedPreset?.tiposProductoSugeridos ?? null
        }
        onProductsRefreshed={async () => {
          if (!userCompanyId) return;
          const refreshed = await fetchUserProducts(userCompanyId, currentBrandId);
          persistUserProducts(refreshed);
        }}
      />
      <FormatPicker
        open={formatPickerOpen}
        selectedId={formatoVal}
        onChange={setFormatoVal}
        onClose={() => setFormatPickerOpen(false)}
        soloSlugs={esVideoDePlantilla ? SLUGS_VIDEO_PLANTILLA : undefined}
      />
      {/* Pickers del modo 'design-modelo' — siempre montados (portal a
          body). Cubren el caso mobile y el "Ver más" desde el desktop. */}
      <PosePickerSheet
        // Con varios modelos, sólo E-commerce queda en selección única: en design-modelo
        // la tanda fan-outea por pose (ADR-2026-08-28-modelo-v2-varias-poses-con-varios-modelos).
        singleSelect={
          expectedPeopleInPose > 1 && !esDesignModelo(selectedPreset?.kind)
        }
        open={posePickerOpen}
        vistaInicial={posePickerVista}
        poses={allPosesFiltered}
        selectedIds={poseIds}
        onChange={handlePoseIdsChange}
        multiPoseEnabled={multiPoseEnabled}
        onMultiPoseEnabledChange={handleMultiPoseEnabledChange}
        onClose={() => setPosePickerOpen(false)}
        customPoses={customPoses.filter(
          (p) => p.cantidadPersonas === expectedPeopleInPose,
        )}
        onUploadPose={handleUploadPose}
        onDeletePose={handleDeletePose}
        uploading={uploadingPose}
      />
      <FondoPickerSheet
        open={fondoPickerOpen}
        selection={fondoSelection}
        onChange={setFondoSelection}
        onClose={() => setFondoPickerOpen(false)}
        biblioteca={bibliotecaFondos}
        guardados={fondosGuardados}
        companyId={userCompanyId}
        onFondoSaved={refreshFondosGuardados}
        initialView={fondoPickerInitialView}
        initialCategory={fondoPickerInitialCategory}
        // Los modos que usan la biblioteca completa (Catálogo IA y, desde que se
        // unificaron las dos, Imagen personalizada): la segunda acción del picker es
        // "Agregar fondo" (subir uno propio) en vez de describir el fondo. NO se usa
        // `esModoCatalogo`, que además incluye muebles e infografía — esos dos ni
        // siquiera tienen selector global de fondo (cada vista deriva el suyo).
        accionSecundaria={
          usaBibliotecaDeFondos(selectedPreset?.kind) ? 'anadir' : 'describir'
        }
        subtitulo={
          esCatalogoIA(selectedPreset?.kind)
            ? 'Elegí el fondo o escenario para todas las imágenes del catálogo.'
            : undefined
        }
        // Los ajustes de tono/luz viven DENTRO del picker: en el Catálogo porque es el
        // único lugar donde se define el fondo de toda la tanda, y en Imagen
        // personalizada porque el rediseño de la pantalla sacó la sección Fondo del panel
        // de configuración —que era donde estaban— y sin esto quedaban sin acceso.
        mostrarAjustesBlanco={usaBibliotecaDeFondos(selectedPreset?.kind)}
        selectedProductNames={selectedProducts.map((p) => p.name)}
        rubro={userRubro}
      />
      {/* Restore overlay — bloque blanco que cubre el "home" del V2
          mientras el async restore del preset (post-back desde Imágenes
          Generadas) abre el sheet. Se levanta cuando el sheet ya está
          completamente animado. */}
      {restoringPreset && (
        <div
          className="fixed inset-0 z-[209] bg-white pointer-events-none"
          aria-hidden
        />
      )}
      {/* Loading overlay full-screen mientras se genera. Mismo componente
          que usa PruebaNuevaInterfaz para que la experiencia sea idéntica. */}
      {showLoadingScreen && (
        <LoadingAnimation
          key={videoGenerating ? 'video' : 'image'}
          message={videoGenerating ? 'Generando video...' : 'Generando imágenes...'}
          // El influencer declara su demora REAL (10-20 min): "unos minutos"
          // prometía de menos justo en el modo más largo y la espera se leía
          // como colgada. El resto de los videos sigue con el genérico — no
          // tienen una medición propia que declarar.
          subMessage={
            videoGenerating && selectedPreset?.kind === 'video-influencer'
              ? 'La generación puede tardar entre 10 y 20 minutos'
              : videoGenerating || multiModelEnabled
                ? 'Esto puede tardar unos minutos'
                : 'Esto puede tardar unos segundos'
          }
          durationMs={
            videoGenerating
              ? // El modo influencer tarda bastante más que la animación de
                // plantilla-video: barra paceada a ~11 min (ADR-539).
                selectedPreset?.kind === 'video-influencer'
                ? VIDEO_INFLUENCER_LOADING_BAR_MS
                : 360_000
              : anuncioGenerating
                ? 210_000
                : DURACION_BARRA_IMAGEN_MS
          }
          completeNow={completeProgressNow}
          onCompleted={() => {
            setShowLoadingScreen(false);
            setVideoGenerating(false);
            setAnuncioGenerating(false);
            setCompleteProgressNow(false);
            generatingLockRef.current = false;
            const vid = pendingVideoIdRef.current;
            const vcId = pendingVideoCompanyIdRef.current;
            pendingVideoIdRef.current = null;
            pendingVideoCompanyIdRef.current = null;
            if (vid) {
              console.log('[InterfazV2] onCompleted — navigating to results, videoId:', vid, 'companyId:', vcId || userCompanyId);
              navigate(rutaResultados, {
                state: { imageIds: [vid], companyId: vcId || userCompanyId },
              });
            }
          }}
          exiting={loadingExiting}
          onExitComplete={() => setShowLoadingScreen(false)}
        />
      )}
      {/* Error de generación — card fija (ADR-541: se seteaba y nunca se dibujaba). Compartida
          con los modos de pantalla propia, como Videos cortos. */}
      <TarjetaDeErrorDeGeneracion mensaje={genError} onCerrar={() => setGenError(null)} />
      {/* Créditos agotados — mismo modal que el resto de la app. */}
      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
        motivo={motivoPricing}
      />
      {/* Función bloqueada por plan (catálogo / agente). */}
      <FeatureLockedModal
        open={featureLock !== null}
        onClose={() => setFeatureLock(null)}
        title={featureLock?.title ?? ''}
        message={featureLock?.message ?? ''}
      />
      {/* Visor de ejemplos al tocar un modo bloqueado (nota del plan + CTA
          "Mejorar plan"). Mismo visor que la pantalla Modos. */}
      <ModeExamplesModal
        open={lockedModeExamples !== null}
        onClose={() => setLockedModeExamples(null)}
        title={lockedModeExamples?.title ?? ''}
        description={lockedModeExamples?.description}
        examples={lockedModeExamples?.examples ?? []}
        actionLabel="Ver planes"
        onAction={() => navigate('/subscription')}
        locked
        planNote={lockedModeExamples?.planNote}
      />
      {/* Video bloqueado por plan — mismo modal que el flujo de video. */}
      <VideoPlanRestrictionModal
        open={showVideoPlanRestriction}
        onClose={() => setShowVideoPlanRestriction(false)}
        userPlan={userPlan}
      />

      {/* Prompt "Instalar Atlas como app" / "Abrir en el navegador" — mismo
          componente y comportamiento que la pantalla clásica de seleccionar
          productos. Sólo en la home V2 (sin preset/panel abierto): así no
          interrumpe mientras el user está configurando un anuncio. El
          componente se auto-gatea por dispositivo, si ya está instalado
          (standalone) y por las reglas de descarte persistidas, y se portaléa
          a document.body por su cuenta. */}
      {!selectedPreset && (
        <AddToHomescreenPrompt
          delayMs={2000}
          origen="home"
        />
      )}
     </AgentEnvContext.Provider>
     </ModelsContext.Provider>
     </ProductEditContext.Provider>
     </ProductsLoadedContext.Provider>
    </ProductsContext.Provider>
    </SinProveedorDeImagenForzado>
  );
};

export default InterfazV2;



